import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { supabase } from './supabaseClient';
import betGrading from './shared/betGrading';
import api from './api';
import BaseCard from './components/cards/BaseCard';
import Rare999Card from './components/cards/Rare999Card';
import RareAutoCard from './components/cards/RareAutoCard';
import DualCard from './components/cards/DualCard';
import RarePatchCard from './components/cards/RarePatchCard';
import LegendaryPatchAutoCard from './components/cards/LegendaryPatchAutoCard';
import ChromeAutoCard from './components/cards/ChromeAutoCard';
import AutoPatchCard from './components/cards/AutoPatchCard';
import PackReveal from './components/cards/PackReveal';
import PoolsView from './components/pools/PoolsView';
import { TeamAvatar } from './components/pools/poolFx';
import AnimatedNumber from './components/AnimatedNumber';
import WhatsNew, { shouldShowWhatsNew } from './components/WhatsNew';
import UpdateNote, { shouldShowUpdate } from './components/UpdateNote';
import Jackpot from './components/Jackpot';
import GameRecap from './components/GameRecap';
import F1Races, { teamLogo as f1TeamLogo } from './components/F1Races';
import NflGames from './components/NflGames';
import EmptyState from './components/EmptyState';
import JackpotWin from './components/JackpotWin';
import Standings from './components/Standings';
import Leaders from './components/Leaders';
import { demoNfl, demoF1 } from './dev/demo';
import { APP_VERSION } from './version';
import { vibrate, hapticsEnabled, setHapticsEnabled, canVibrate } from './haptics';
import { calculateCardValue, formatValue, getDailyMarket, getMarketLabel, applyMarket } from './data/cardValues';
import { catalog } from './data/cardCatalog';
import { formatMoney } from './utils/format';
import { fmtDateTime, fmtTime } from './utils/time';
import { quoteBet, setOddsBoost, oddsBoost, setMoneylineBoost, moneylineBoost, nhlMarkets, nhlPlayerMarkets, nhlTeamTotals, mlbMarkets, mlbBatterMarkets, nbaMarkets, nbaPlayerMarkets, nbaFieldMarkets, NBA_MARGIN_BANDS } from './odds';
import { scanBets, scoutSignals, scoreBets, rankBets, buildPlan } from './scout';
import ScoutReport from './components/ScoutReport';
import PredictNight from './components/PredictNight';
import PredictWin from './components/PredictWin';
import SlotMachine from './components/SlotMachine';
import Wordmark from './components/Wordmark';
import Chat from './components/Chat';
import ChatFab from './components/ChatFab';
import BetLike from './components/BetLike';
import Feed from './components/Feed';
import { FEED_BETS, useFeed, useLikes, withDemoBets } from './social';
import { fmtOdds, fmtMoneyline } from './utils/oddsFmt';

// odds on a button: "—" when the bet can't be priced (it's then not offered)
const fmtLine = (x) => (x > 0 ? `+${x}` : `${x}`);

// NHL 2025-26 Season Rosters (Updated January 2026) - 12-15 players per team
const defaultPlayers = [
  // MONTREAL CANADIENS
  { id: 'mtl-1', name: 'Cole Caufield', team: 'MTL', position: 'F' },
  { id: 'mtl-2', name: 'Nick Suzuki', team: 'MTL', position: 'F' },
  { id: 'mtl-3', name: 'Patrik Laine', team: 'MTL', position: 'F' },
  { id: 'mtl-4', name: 'Lane Hutson', team: 'MTL', position: 'D' },
  { id: 'mtl-5', name: 'Juraj Slafkovsky', team: 'MTL', position: 'F' },
  { id: 'mtl-6', name: 'Kirby Dach', team: 'MTL', position: 'F' },
  { id: 'mtl-7', name: 'Alex Newhook', team: 'MTL', position: 'F' },
  { id: 'mtl-8', name: 'Mike Matheson', team: 'MTL', position: 'D' },
  { id: 'mtl-9', name: 'Josh Anderson', team: 'MTL', position: 'F' },
  { id: 'mtl-10', name: 'Brendan Gallagher', team: 'MTL', position: 'F' },
  { id: 'mtl-11', name: 'Ivan Demidov', team: 'MTL', position: 'F' },
  { id: 'mtl-12', name: 'Jake Evans', team: 'MTL', position: 'F' },
  { id: 'mtl-13', name: 'Kaiden Guhle', team: 'MTL', position: 'D' },
  { id: 'mtl-14', name: 'Arber Xhekaj', team: 'MTL', position: 'D' },
  { id: 'mtl-15', name: 'Sam Montembeault', team: 'MTL', position: 'G' },
  // TORONTO MAPLE LEAFS
  { id: 'tor-1', name: 'Auston Matthews', team: 'TOR', position: 'F' },
  { id: 'tor-2', name: 'William Nylander', team: 'TOR', position: 'F' },
  { id: 'tor-3', name: 'John Tavares', team: 'TOR', position: 'F' },
  { id: 'tor-4', name: 'Max Domi', team: 'TOR', position: 'F' },
  { id: 'tor-5', name: 'Matthew Knies', team: 'TOR', position: 'F' },
  { id: 'tor-6', name: 'Bobby McMann', team: 'TOR', position: 'F' },
  { id: 'tor-7', name: 'Pontus Holmberg', team: 'TOR', position: 'F' },
  { id: 'tor-8', name: 'David Kampf', team: 'TOR', position: 'F' },
  { id: 'tor-9', name: 'Morgan Rielly', team: 'TOR', position: 'D' },
  { id: 'tor-10', name: 'Chris Tanev', team: 'TOR', position: 'D' },
  { id: 'tor-11', name: 'Oliver Ekman-Larsson', team: 'TOR', position: 'D' },
  { id: 'tor-12', name: 'Conor Timmins', team: 'TOR', position: 'D' },
  { id: 'tor-13', name: 'Joseph Woll', team: 'TOR', position: 'G' },
  // EDMONTON OILERS
  { id: 'edm-1', name: 'Connor McDavid', team: 'EDM', position: 'F' },
  { id: 'edm-2', name: 'Leon Draisaitl', team: 'EDM', position: 'F' },
  { id: 'edm-3', name: 'Zach Hyman', team: 'EDM', position: 'F' },
  { id: 'edm-4', name: 'Ryan Nugent-Hopkins', team: 'EDM', position: 'F' },
  { id: 'edm-5', name: 'Jeff Skinner', team: 'EDM', position: 'F' },
  { id: 'edm-6', name: 'Adam Henrique', team: 'EDM', position: 'F' },
  { id: 'edm-7', name: 'Vasily Podkolzin', team: 'EDM', position: 'F' },
  { id: 'edm-8', name: 'Connor Brown', team: 'EDM', position: 'F' },
  { id: 'edm-9', name: 'Evan Bouchard', team: 'EDM', position: 'D' },
  { id: 'edm-10', name: 'Darnell Nurse', team: 'EDM', position: 'D' },
  { id: 'edm-11', name: 'Mattias Ekholm', team: 'EDM', position: 'D' },
  { id: 'edm-12', name: 'Brett Kulak', team: 'EDM', position: 'D' },
  { id: 'edm-13', name: 'Stuart Skinner', team: 'EDM', position: 'G' },
  // BOSTON BRUINS
  { id: 'bos-1', name: 'David Pastrnak', team: 'BOS', position: 'F' },
  { id: 'bos-2', name: 'Elias Lindholm', team: 'BOS', position: 'F' },
  { id: 'bos-3', name: 'Pavel Zacha', team: 'BOS', position: 'F' },
  { id: 'bos-4', name: 'Brad Marchand', team: 'BOS', position: 'F' },
  { id: 'bos-5', name: 'Morgan Geekie', team: 'BOS', position: 'F' },
  { id: 'bos-6', name: 'Trent Frederic', team: 'BOS', position: 'F' },
  { id: 'bos-7', name: 'Cole Koepke', team: 'BOS', position: 'F' },
  { id: 'bos-8', name: 'Charlie Coyle', team: 'BOS', position: 'F' },
  { id: 'bos-9', name: 'Charlie McAvoy', team: 'BOS', position: 'D' },
  { id: 'bos-10', name: 'Hampus Lindholm', team: 'BOS', position: 'D' },
  { id: 'bos-11', name: 'Mason Lohrei', team: 'BOS', position: 'D' },
  { id: 'bos-12', name: 'Nikita Zadorov', team: 'BOS', position: 'D' },
  { id: 'bos-13', name: 'Jeremy Swayman', team: 'BOS', position: 'G' },
  // COLORADO AVALANCHE
  { id: 'col-1', name: 'Nathan MacKinnon', team: 'COL', position: 'F' },
  { id: 'col-2', name: 'Mikko Rantanen', team: 'COL', position: 'F' },
  { id: 'col-3', name: 'Artturi Lehkonen', team: 'COL', position: 'F' },
  { id: 'col-4', name: 'Ross Colton', team: 'COL', position: 'F' },
  { id: 'col-5', name: 'Jonathan Drouin', team: 'COL', position: 'F' },
  { id: 'col-6', name: 'Casey Mittelstadt', team: 'COL', position: 'F' },
  { id: 'col-7', name: 'Logan OConnor', team: 'COL', position: 'F' },
  { id: 'col-8', name: 'Valeri Nichushkin', team: 'COL', position: 'F' },
  { id: 'col-9', name: 'Cale Makar', team: 'COL', position: 'D' },
  { id: 'col-10', name: 'Devon Toews', team: 'COL', position: 'D' },
  { id: 'col-11', name: 'Josh Manson', team: 'COL', position: 'D' },
  { id: 'col-12', name: 'Samuel Girard', team: 'COL', position: 'D' },
  { id: 'col-13', name: 'Mackenzie Blackwood', team: 'COL', position: 'G' },
  // FLORIDA PANTHERS
  { id: 'fla-1', name: 'Aleksander Barkov', team: 'FLA', position: 'F' },
  { id: 'fla-2', name: 'Matthew Tkachuk', team: 'FLA', position: 'F' },
  { id: 'fla-3', name: 'Sam Reinhart', team: 'FLA', position: 'F' },
  { id: 'fla-4', name: 'Carter Verhaeghe', team: 'FLA', position: 'F' },
  { id: 'fla-5', name: 'Sam Bennett', team: 'FLA', position: 'F' },
  { id: 'fla-6', name: 'Evan Rodrigues', team: 'FLA', position: 'F' },
  { id: 'fla-7', name: 'Eetu Luostarinen', team: 'FLA', position: 'F' },
  { id: 'fla-8', name: 'Anton Lundell', team: 'FLA', position: 'F' },
  { id: 'fla-9', name: 'Aaron Ekblad', team: 'FLA', position: 'D' },
  { id: 'fla-10', name: 'Gustav Forsling', team: 'FLA', position: 'D' },
  { id: 'fla-11', name: 'Dmitry Kulikov', team: 'FLA', position: 'D' },
  { id: 'fla-12', name: 'Niko Mikkola', team: 'FLA', position: 'D' },
  { id: 'fla-13', name: 'Sergei Bobrovsky', team: 'FLA', position: 'G' },
  // NEW YORK RANGERS
  { id: 'nyr-1', name: 'Artemi Panarin', team: 'NYR', position: 'F' },
  { id: 'nyr-2', name: 'Mika Zibanejad', team: 'NYR', position: 'F' },
  { id: 'nyr-3', name: 'Chris Kreider', team: 'NYR', position: 'F' },
  { id: 'nyr-4', name: 'Alexis Lafreniere', team: 'NYR', position: 'F' },
  { id: 'nyr-5', name: 'Vincent Trocheck', team: 'NYR', position: 'F' },
  { id: 'nyr-6', name: 'Filip Chytil', team: 'NYR', position: 'F' },
  { id: 'nyr-7', name: 'Will Cuylle', team: 'NYR', position: 'F' },
  { id: 'nyr-8', name: 'Kaapo Kakko', team: 'NYR', position: 'F' },
  { id: 'nyr-9', name: 'Adam Fox', team: 'NYR', position: 'D' },
  { id: 'nyr-10', name: 'K\'Andre Miller', team: 'NYR', position: 'D' },
  { id: 'nyr-11', name: 'Jacob Trouba', team: 'NYR', position: 'D' },
  { id: 'nyr-12', name: 'Braden Schneider', team: 'NYR', position: 'D' },
  { id: 'nyr-13', name: 'Igor Shesterkin', team: 'NYR', position: 'G' },
  // VEGAS GOLDEN KNIGHTS
  { id: 'vgk-1', name: 'Jack Eichel', team: 'VGK', position: 'F' },
  { id: 'vgk-2', name: 'Mark Stone', team: 'VGK', position: 'F' },
  { id: 'vgk-3', name: 'Tomas Hertl', team: 'VGK', position: 'F' },
  { id: 'vgk-4', name: 'Ivan Barbashev', team: 'VGK', position: 'F' },
  { id: 'vgk-5', name: 'William Karlsson', team: 'VGK', position: 'F' },
  { id: 'vgk-6', name: 'Pavel Dorofeyev', team: 'VGK', position: 'F' },
  { id: 'vgk-7', name: 'Brett Howden', team: 'VGK', position: 'F' },
  { id: 'vgk-8', name: 'Keegan Kolesar', team: 'VGK', position: 'F' },
  { id: 'vgk-9', name: 'Shea Theodore', team: 'VGK', position: 'D' },
  { id: 'vgk-10', name: 'Noah Hanifin', team: 'VGK', position: 'D' },
  { id: 'vgk-11', name: 'Alex Pietrangelo', team: 'VGK', position: 'D' },
  { id: 'vgk-12', name: 'Brayden McNabb', team: 'VGK', position: 'D' },
  { id: 'vgk-13', name: 'Adin Hill', team: 'VGK', position: 'G' },
  // DALLAS STARS
  { id: 'dal-1', name: 'Jason Robertson', team: 'DAL', position: 'F' },
  { id: 'dal-2', name: 'Roope Hintz', team: 'DAL', position: 'F' },
  { id: 'dal-3', name: 'Wyatt Johnston', team: 'DAL', position: 'F' },
  { id: 'dal-4', name: 'Tyler Seguin', team: 'DAL', position: 'F' },
  { id: 'dal-5', name: 'Matt Duchene', team: 'DAL', position: 'F' },
  { id: 'dal-6', name: 'Jamie Benn', team: 'DAL', position: 'F' },
  { id: 'dal-7', name: 'Mason Marchment', team: 'DAL', position: 'F' },
  { id: 'dal-8', name: 'Logan Stankoven', team: 'DAL', position: 'F' },
  { id: 'dal-9', name: 'Miro Heiskanen', team: 'DAL', position: 'D' },
  { id: 'dal-10', name: 'Thomas Harley', team: 'DAL', position: 'D' },
  { id: 'dal-11', name: 'Esa Lindell', team: 'DAL', position: 'D' },
  { id: 'dal-12', name: 'Ilya Lyubushkin', team: 'DAL', position: 'D' },
  { id: 'dal-13', name: 'Jake Oettinger', team: 'DAL', position: 'G' },
  // NEW JERSEY DEVILS
  { id: 'njd-1', name: 'Jack Hughes', team: 'NJD', position: 'F' },
  { id: 'njd-2', name: 'Nico Hischier', team: 'NJD', position: 'F' },
  { id: 'njd-3', name: 'Jesper Bratt', team: 'NJD', position: 'F' },
  { id: 'njd-4', name: 'Timo Meier', team: 'NJD', position: 'F' },
  { id: 'njd-5', name: 'Dawson Mercer', team: 'NJD', position: 'F' },
  { id: 'njd-6', name: 'Ondrej Palat', team: 'NJD', position: 'F' },
  { id: 'njd-7', name: 'Erik Haula', team: 'NJD', position: 'F' },
  { id: 'njd-8', name: 'Stefan Noesen', team: 'NJD', position: 'F' },
  { id: 'njd-9', name: 'Dougie Hamilton', team: 'NJD', position: 'D' },
  { id: 'njd-10', name: 'Luke Hughes', team: 'NJD', position: 'D' },
  { id: 'njd-11', name: 'Brett Pesce', team: 'NJD', position: 'D' },
  { id: 'njd-12', name: 'Johnathan Kovacevic', team: 'NJD', position: 'D' },
  { id: 'njd-13', name: 'Jacob Markstrom', team: 'NJD', position: 'G' },
  // OTTAWA SENATORS
  { id: 'ott-1', name: 'Brady Tkachuk', team: 'OTT', position: 'F' },
  { id: 'ott-2', name: 'Tim Stutzle', team: 'OTT', position: 'F' },
  { id: 'ott-3', name: 'Drake Batherson', team: 'OTT', position: 'F' },
  { id: 'ott-4', name: 'Claude Giroux', team: 'OTT', position: 'F' },
  { id: 'ott-5', name: 'Josh Norris', team: 'OTT', position: 'F' },
  { id: 'ott-6', name: 'Shane Pinto', team: 'OTT', position: 'F' },
  { id: 'ott-7', name: 'Ridly Greig', team: 'OTT', position: 'F' },
  { id: 'ott-8', name: 'Michael Amadio', team: 'OTT', position: 'F' },
  { id: 'ott-9', name: 'Thomas Chabot', team: 'OTT', position: 'D' },
  { id: 'ott-10', name: 'Jake Sanderson', team: 'OTT', position: 'D' },
  { id: 'ott-11', name: 'Artem Zub', team: 'OTT', position: 'D' },
  { id: 'ott-12', name: 'Nick Jensen', team: 'OTT', position: 'D' },
  { id: 'ott-13', name: 'Linus Ullmark', team: 'OTT', position: 'G' },
  // VANCOUVER CANUCKS
  { id: 'van-1', name: 'Elias Pettersson', team: 'VAN', position: 'F' },
  { id: 'van-2', name: 'J.T. Miller', team: 'VAN', position: 'F' },
  { id: 'van-3', name: 'Brock Boeser', team: 'VAN', position: 'F' },
  { id: 'van-4', name: 'Conor Garland', team: 'VAN', position: 'F' },
  { id: 'van-5', name: 'Jake DeBrusk', team: 'VAN', position: 'F' },
  { id: 'van-6', name: 'Pius Suter', team: 'VAN', position: 'F' },
  { id: 'van-7', name: 'Nils Hoglander', team: 'VAN', position: 'F' },
  { id: 'van-8', name: 'Teddy Blueger', team: 'VAN', position: 'F' },
  { id: 'van-9', name: 'Quinn Hughes', team: 'VAN', position: 'D' },
  { id: 'van-10', name: 'Filip Hronek', team: 'VAN', position: 'D' },
  { id: 'van-11', name: 'Tyler Myers', team: 'VAN', position: 'D' },
  { id: 'van-12', name: 'Carson Soucy', team: 'VAN', position: 'D' },
  { id: 'van-13', name: 'Thatcher Demko', team: 'VAN', position: 'G' },
  // WINNIPEG JETS
  { id: 'wpg-1', name: 'Kyle Connor', team: 'WPG', position: 'F' },
  { id: 'wpg-2', name: 'Mark Scheifele', team: 'WPG', position: 'F' },
  { id: 'wpg-3', name: 'Gabriel Vilardi', team: 'WPG', position: 'F' },
  { id: 'wpg-4', name: 'Cole Perfetti', team: 'WPG', position: 'F' },
  { id: 'wpg-5', name: 'Nikolaj Ehlers', team: 'WPG', position: 'F' },
  { id: 'wpg-6', name: 'Adam Lowry', team: 'WPG', position: 'F' },
  { id: 'wpg-7', name: 'Nino Niederreiter', team: 'WPG', position: 'F' },
  { id: 'wpg-8', name: 'Mason Appleton', team: 'WPG', position: 'F' },
  { id: 'wpg-9', name: 'Josh Morrissey', team: 'WPG', position: 'D' },
  { id: 'wpg-10', name: 'Neal Pionk', team: 'WPG', position: 'D' },
  { id: 'wpg-11', name: 'Dylan DeMelo', team: 'WPG', position: 'D' },
  { id: 'wpg-12', name: 'Dylan Samberg', team: 'WPG', position: 'D' },
  { id: 'wpg-13', name: 'Connor Hellebuyck', team: 'WPG', position: 'G' },
  // PITTSBURGH PENGUINS
  { id: 'pit-1', name: 'Sidney Crosby', team: 'PIT', position: 'F' },
  { id: 'pit-2', name: 'Evgeni Malkin', team: 'PIT', position: 'F' },
  { id: 'pit-3', name: 'Bryan Rust', team: 'PIT', position: 'F' },
  { id: 'pit-4', name: 'Rickard Rakell', team: 'PIT', position: 'F' },
  { id: 'pit-5', name: 'Michael Bunting', team: 'PIT', position: 'F' },
  { id: 'pit-6', name: 'Drew OConnor', team: 'PIT', position: 'F' },
  { id: 'pit-7', name: 'Kevin Hayes', team: 'PIT', position: 'F' },
  { id: 'pit-8', name: 'Lars Eller', team: 'PIT', position: 'F' },
  { id: 'pit-9', name: 'Erik Karlsson', team: 'PIT', position: 'D' },
  { id: 'pit-10', name: 'Kris Letang', team: 'PIT', position: 'D' },
  { id: 'pit-11', name: 'Marcus Pettersson', team: 'PIT', position: 'D' },
  { id: 'pit-12', name: 'Ryan Graves', team: 'PIT', position: 'D' },
  { id: 'pit-13', name: 'Tristan Jarry', team: 'PIT', position: 'G' },
  // WASHINGTON CAPITALS
  { id: 'wsh-1', name: 'Alex Ovechkin', team: 'WSH', position: 'F' },
  { id: 'wsh-2', name: 'Dylan Strome', team: 'WSH', position: 'F' },
  { id: 'wsh-3', name: 'Tom Wilson', team: 'WSH', position: 'F' },
  { id: 'wsh-4', name: 'Connor McMichael', team: 'WSH', position: 'F' },
  { id: 'wsh-5', name: 'Pierre-Luc Dubois', team: 'WSH', position: 'F' },
  { id: 'wsh-6', name: 'Aliaksei Protas', team: 'WSH', position: 'F' },
  { id: 'wsh-7', name: 'Nic Dowd', team: 'WSH', position: 'F' },
  { id: 'wsh-8', name: 'Andrew Mangiapane', team: 'WSH', position: 'F' },
  { id: 'wsh-9', name: 'John Carlson', team: 'WSH', position: 'D' },
  { id: 'wsh-10', name: 'Jakob Chychrun', team: 'WSH', position: 'D' },
  { id: 'wsh-11', name: 'Martin Fehervary', team: 'WSH', position: 'D' },
  { id: 'wsh-12', name: 'Matt Roy', team: 'WSH', position: 'D' },
  { id: 'wsh-13', name: 'Charlie Lindgren', team: 'WSH', position: 'G' },
  // CAROLINA HURRICANES
  { id: 'car-1', name: 'Sebastian Aho', team: 'CAR', position: 'F' },
  { id: 'car-2', name: 'Andrei Svechnikov', team: 'CAR', position: 'F' },
  { id: 'car-3', name: 'Seth Jarvis', team: 'CAR', position: 'F' },
  { id: 'car-4', name: 'Jack Roslovic', team: 'CAR', position: 'F' },
  { id: 'car-5', name: 'Jordan Staal', team: 'CAR', position: 'F' },
  { id: 'car-6', name: 'Martin Necas', team: 'CAR', position: 'F' },
  { id: 'car-7', name: 'Jesper Fast', team: 'CAR', position: 'F' },
  { id: 'car-8', name: 'Jordan Martinook', team: 'CAR', position: 'F' },
  { id: 'car-9', name: 'Jaccob Slavin', team: 'CAR', position: 'D' },
  { id: 'car-10', name: 'Brent Burns', team: 'CAR', position: 'D' },
  { id: 'car-11', name: 'Dmitry Orlov', team: 'CAR', position: 'D' },
  { id: 'car-12', name: 'Jalen Chatfield', team: 'CAR', position: 'D' },
  { id: 'car-13', name: 'Pyotr Kochetkov', team: 'CAR', position: 'G' },
  // CALGARY FLAMES
  { id: 'cgy-1', name: 'Nazem Kadri', team: 'CGY', position: 'F' },
  { id: 'cgy-2', name: 'Jonathan Huberdeau', team: 'CGY', position: 'F' },
  { id: 'cgy-3', name: 'Yegor Sharangovich', team: 'CGY', position: 'F' },
  { id: 'cgy-4', name: 'Matt Coronato', team: 'CGY', position: 'F' },
  { id: 'cgy-5', name: 'Blake Coleman', team: 'CGY', position: 'F' },
  { id: 'cgy-6', name: 'Connor Zary', team: 'CGY', position: 'F' },
  { id: 'cgy-7', name: 'Mikael Backlund', team: 'CGY', position: 'F' },
  { id: 'cgy-8', name: 'Martin Pospisil', team: 'CGY', position: 'F' },
  { id: 'cgy-9', name: 'MacKenzie Weegar', team: 'CGY', position: 'D' },
  { id: 'cgy-10', name: 'Rasmus Andersson', team: 'CGY', position: 'D' },
  { id: 'cgy-11', name: 'Kevin Bahl', team: 'CGY', position: 'D' },
  { id: 'cgy-12', name: 'Daniil Miromanov', team: 'CGY', position: 'D' },
  { id: 'cgy-13', name: 'Dustin Wolf', team: 'CGY', position: 'G' },
  // DETROIT RED WINGS
  { id: 'det-1', name: 'Dylan Larkin', team: 'DET', position: 'F' },
  { id: 'det-2', name: 'Lucas Raymond', team: 'DET', position: 'F' },
  { id: 'det-3', name: 'Alex DeBrincat', team: 'DET', position: 'F' },
  { id: 'det-4', name: 'Patrick Kane', team: 'DET', position: 'F' },
  { id: 'det-5', name: 'J.T. Compher', team: 'DET', position: 'F' },
  { id: 'det-6', name: 'Andrew Copp', team: 'DET', position: 'F' },
  { id: 'det-7', name: 'Michael Rasmussen', team: 'DET', position: 'F' },
  { id: 'det-8', name: 'Jonatan Berggren', team: 'DET', position: 'F' },
  { id: 'det-9', name: 'Moritz Seider', team: 'DET', position: 'D' },
  { id: 'det-10', name: 'Simon Edvinsson', team: 'DET', position: 'D' },
  { id: 'det-11', name: 'Ben Chiarot', team: 'DET', position: 'D' },
  { id: 'det-12', name: 'Jeff Petry', team: 'DET', position: 'D' },
  { id: 'det-13', name: 'Ville Husso', team: 'DET', position: 'G' },
  // BUFFALO SABRES
  { id: 'buf-1', name: 'Tage Thompson', team: 'BUF', position: 'F' },
  { id: 'buf-2', name: 'Alex Tuch', team: 'BUF', position: 'F' },
  { id: 'buf-3', name: 'Jack Quinn', team: 'BUF', position: 'F' },
  { id: 'buf-4', name: 'Dylan Cozens', team: 'BUF', position: 'F' },
  { id: 'buf-5', name: 'Zach Benson', team: 'BUF', position: 'F' },
  { id: 'buf-6', name: 'JJ Peterka', team: 'BUF', position: 'F' },
  { id: 'buf-7', name: 'Peyton Krebs', team: 'BUF', position: 'F' },
  { id: 'buf-8', name: 'Jordan Greenway', team: 'BUF', position: 'F' },
  { id: 'buf-9', name: 'Rasmus Dahlin', team: 'BUF', position: 'D' },
  { id: 'buf-10', name: 'Owen Power', team: 'BUF', position: 'D' },
  { id: 'buf-11', name: 'Bowen Byram', team: 'BUF', position: 'D' },
  { id: 'buf-12', name: 'Henri Jokiharju', team: 'BUF', position: 'D' },
  { id: 'buf-13', name: 'Ukko-Pekka Luukkonen', team: 'BUF', position: 'G' },
  // PHILADELPHIA FLYERS
  { id: 'phi-1', name: 'Travis Konecny', team: 'PHI', position: 'F' },
  { id: 'phi-2', name: 'Matvei Michkov', team: 'PHI', position: 'F' },
  { id: 'phi-3', name: 'Owen Tippett', team: 'PHI', position: 'F' },
  { id: 'phi-4', name: 'Sean Couturier', team: 'PHI', position: 'F' },
  { id: 'phi-5', name: 'Joel Farabee', team: 'PHI', position: 'F' },
  { id: 'phi-6', name: 'Scott Laughton', team: 'PHI', position: 'F' },
  { id: 'phi-7', name: 'Morgan Frost', team: 'PHI', position: 'F' },
  { id: 'phi-8', name: 'Bobby Brink', team: 'PHI', position: 'F' },
  { id: 'phi-9', name: 'Cam York', team: 'PHI', position: 'D' },
  { id: 'phi-10', name: 'Travis Sanheim', team: 'PHI', position: 'D' },
  { id: 'phi-11', name: 'Jamie Drysdale', team: 'PHI', position: 'D' },
  { id: 'phi-12', name: 'Nick Seeler', team: 'PHI', position: 'D' },
  { id: 'phi-13', name: 'Samuel Ersson', team: 'PHI', position: 'G' },
  // ANAHEIM DUCKS
  { id: 'ana-1', name: 'Leo Carlsson', team: 'ANA', position: 'F' },
  { id: 'ana-2', name: 'Mason McTavish', team: 'ANA', position: 'F' },
  { id: 'ana-3', name: 'Troy Terry', team: 'ANA', position: 'F' },
  { id: 'ana-4', name: 'Cutter Gauthier', team: 'ANA', position: 'F' },
  { id: 'ana-5', name: 'Frank Vatrano', team: 'ANA', position: 'F' },
  { id: 'ana-6', name: 'Ryan Strome', team: 'ANA', position: 'F' },
  { id: 'ana-7', name: 'Alex Killorn', team: 'ANA', position: 'F' },
  { id: 'ana-8', name: 'Robby Fabbri', team: 'ANA', position: 'F' },
  { id: 'ana-9', name: 'Pavel Mintyukov', team: 'ANA', position: 'D' },
  { id: 'ana-10', name: 'Olen Zellweger', team: 'ANA', position: 'D' },
  { id: 'ana-11', name: 'Cam Fowler', team: 'ANA', position: 'D' },
  { id: 'ana-12', name: 'Radko Gudas', team: 'ANA', position: 'D' },
  { id: 'ana-13', name: 'Lukas Dostal', team: 'ANA', position: 'G' },
  // MINNESOTA WILD
  { id: 'min-1', name: 'Kirill Kaprizov', team: 'MIN', position: 'F' },
  { id: 'min-2', name: 'Matt Boldy', team: 'MIN', position: 'F' },
  { id: 'min-3', name: 'Joel Eriksson Ek', team: 'MIN', position: 'F' },
  { id: 'min-4', name: 'Mats Zuccarello', team: 'MIN', position: 'F' },
  { id: 'min-5', name: 'Marco Rossi', team: 'MIN', position: 'F' },
  { id: 'min-6', name: 'Ryan Hartman', team: 'MIN', position: 'F' },
  { id: 'min-7', name: 'Frederick Gaudreau', team: 'MIN', position: 'F' },
  { id: 'min-8', name: 'Marcus Foligno', team: 'MIN', position: 'F' },
  { id: 'min-9', name: 'Brock Faber', team: 'MIN', position: 'D' },
  { id: 'min-10', name: 'Jonas Brodin', team: 'MIN', position: 'D' },
  { id: 'min-11', name: 'Jared Spurgeon', team: 'MIN', position: 'D' },
  { id: 'min-12', name: 'Jake Middleton', team: 'MIN', position: 'D' },
  { id: 'min-13', name: 'Filip Gustavsson', team: 'MIN', position: 'G' },
  // NASHVILLE PREDATORS
  { id: 'nsh-1', name: 'Filip Forsberg', team: 'NSH', position: 'F' },
  { id: 'nsh-2', name: 'Steven Stamkos', team: 'NSH', position: 'F' },
  { id: 'nsh-3', name: 'Jonathan Marchessault', team: 'NSH', position: 'F' },
  { id: 'nsh-4', name: 'Ryan O\'Reilly', team: 'NSH', position: 'F' },
  { id: 'nsh-5', name: 'Gustav Nyquist', team: 'NSH', position: 'F' },
  { id: 'nsh-6', name: 'Tommy Novak', team: 'NSH', position: 'F' },
  { id: 'nsh-7', name: 'Colton Sissons', team: 'NSH', position: 'F' },
  { id: 'nsh-8', name: 'Luke Evangelista', team: 'NSH', position: 'F' },
  { id: 'nsh-9', name: 'Roman Josi', team: 'NSH', position: 'D' },
  { id: 'nsh-10', name: 'Alexandre Carrier', team: 'NSH', position: 'D' },
  { id: 'nsh-11', name: 'Brady Skjei', team: 'NSH', position: 'D' },
  { id: 'nsh-12', name: 'Jeremy Lauzon', team: 'NSH', position: 'D' },
  { id: 'nsh-13', name: 'Juuse Saros', team: 'NSH', position: 'G' },
  // ST. LOUIS BLUES
  { id: 'stl-1', name: 'Robert Thomas', team: 'STL', position: 'F' },
  { id: 'stl-2', name: 'Jordan Kyrou', team: 'STL', position: 'F' },
  { id: 'stl-3', name: 'Pavel Buchnevich', team: 'STL', position: 'F' },
  { id: 'stl-4', name: 'Brandon Saad', team: 'STL', position: 'F' },
  { id: 'stl-5', name: 'Brayden Schenn', team: 'STL', position: 'F' },
  { id: 'stl-6', name: 'Jake Neighbours', team: 'STL', position: 'F' },
  { id: 'stl-7', name: 'Mathieu Joseph', team: 'STL', position: 'F' },
  { id: 'stl-8', name: 'Oskar Sundqvist', team: 'STL', position: 'F' },
  { id: 'stl-9', name: 'Colton Parayko', team: 'STL', position: 'D' },
  { id: 'stl-10', name: 'Justin Faulk', team: 'STL', position: 'D' },
  { id: 'stl-11', name: 'Nick Leddy', team: 'STL', position: 'D' },
  { id: 'stl-12', name: 'Philip Broberg', team: 'STL', position: 'D' },
  { id: 'stl-13', name: 'Jordan Binnington', team: 'STL', position: 'G' },
  // CHICAGO BLACKHAWKS
  { id: 'chi-1', name: 'Connor Bedard', team: 'CHI', position: 'F' },
  { id: 'chi-2', name: 'Tyler Bertuzzi', team: 'CHI', position: 'F' },
  { id: 'chi-3', name: 'Teuvo Teravainen', team: 'CHI', position: 'F' },
  { id: 'chi-4', name: 'Taylor Hall', team: 'CHI', position: 'F' },
  { id: 'chi-5', name: 'Philipp Kurashev', team: 'CHI', position: 'F' },
  { id: 'chi-6', name: 'Nick Foligno', team: 'CHI', position: 'F' },
  { id: 'chi-7', name: 'Ryan Donato', team: 'CHI', position: 'F' },
  { id: 'chi-8', name: 'Lukas Reichel', team: 'CHI', position: 'F' },
  { id: 'chi-9', name: 'Seth Jones', team: 'CHI', position: 'D' },
  { id: 'chi-10', name: 'Alex Vlasic', team: 'CHI', position: 'D' },
  { id: 'chi-11', name: 'Connor Murphy', team: 'CHI', position: 'D' },
  { id: 'chi-12', name: 'Kevin Korchinski', team: 'CHI', position: 'D' },
  { id: 'chi-13', name: 'Petr Mrazek', team: 'CHI', position: 'G' },
  // SEATTLE KRAKEN
  { id: 'sea-1', name: 'Matty Beniers', team: 'SEA', position: 'F' },
  { id: 'sea-2', name: 'Jared McCann', team: 'SEA', position: 'F' },
  { id: 'sea-3', name: 'Jordan Eberle', team: 'SEA', position: 'F' },
  { id: 'sea-4', name: 'Shane Wright', team: 'SEA', position: 'F' },
  { id: 'sea-5', name: 'Chandler Stephenson', team: 'SEA', position: 'F' },
  { id: 'sea-6', name: 'Jaden Schwartz', team: 'SEA', position: 'F' },
  { id: 'sea-7', name: 'Oliver Bjorkstrand', team: 'SEA', position: 'F' },
  { id: 'sea-8', name: 'Andre Burakovsky', team: 'SEA', position: 'F' },
  { id: 'sea-9', name: 'Brandon Montour', team: 'SEA', position: 'D' },
  { id: 'sea-10', name: 'Vince Dunn', team: 'SEA', position: 'D' },
  { id: 'sea-11', name: 'Adam Larsson', team: 'SEA', position: 'D' },
  { id: 'sea-12', name: 'Jamie Oleksiak', team: 'SEA', position: 'D' },
  { id: 'sea-13', name: 'Philipp Grubauer', team: 'SEA', position: 'G' },
  // NEW YORK ISLANDERS
  { id: 'nyi-1', name: 'Mathew Barzal', team: 'NYI', position: 'F' },
  { id: 'nyi-2', name: 'Bo Horvat', team: 'NYI', position: 'F' },
  { id: 'nyi-3', name: 'Anders Lee', team: 'NYI', position: 'F' },
  { id: 'nyi-4', name: 'Brock Nelson', team: 'NYI', position: 'F' },
  { id: 'nyi-5', name: 'Kyle Palmieri', team: 'NYI', position: 'F' },
  { id: 'nyi-6', name: 'Jean-Gabriel Pageau', team: 'NYI', position: 'F' },
  { id: 'nyi-7', name: 'Simon Holmstrom', team: 'NYI', position: 'F' },
  { id: 'nyi-8', name: 'Casey Cizikas', team: 'NYI', position: 'F' },
  { id: 'nyi-9', name: 'Noah Dobson', team: 'NYI', position: 'D' },
  { id: 'nyi-10', name: 'Ryan Pulock', team: 'NYI', position: 'D' },
  { id: 'nyi-11', name: 'Alexander Romanov', team: 'NYI', position: 'D' },
  { id: 'nyi-12', name: 'Scott Mayfield', team: 'NYI', position: 'D' },
  { id: 'nyi-13', name: 'Ilya Sorokin', team: 'NYI', position: 'G' },
  // LOS ANGELES KINGS
  { id: 'lak-1', name: 'Anze Kopitar', team: 'LAK', position: 'F' },
  { id: 'lak-2', name: 'Adrian Kempe', team: 'LAK', position: 'F' },
  { id: 'lak-3', name: 'Kevin Fiala', team: 'LAK', position: 'F' },
  { id: 'lak-4', name: 'Quinton Byfield', team: 'LAK', position: 'F' },
  { id: 'lak-5', name: 'Phillip Danault', team: 'LAK', position: 'F' },
  { id: 'lak-6', name: 'Trevor Moore', team: 'LAK', position: 'F' },
  { id: 'lak-7', name: 'Tanner Jeannot', team: 'LAK', position: 'F' },
  { id: 'lak-8', name: 'Warren Foegele', team: 'LAK', position: 'F' },
  { id: 'lak-9', name: 'Drew Doughty', team: 'LAK', position: 'D' },
  { id: 'lak-10', name: 'Mikey Anderson', team: 'LAK', position: 'D' },
  { id: 'lak-11', name: 'Vladislav Gavrikov', team: 'LAK', position: 'D' },
  { id: 'lak-12', name: 'Jordan Spence', team: 'LAK', position: 'D' },
  { id: 'lak-13', name: 'Darcy Kuemper', team: 'LAK', position: 'G' },
  // SAN JOSE SHARKS
  { id: 'sjs-1', name: 'Macklin Celebrini', team: 'SJS', position: 'F' },
  { id: 'sjs-2', name: 'William Eklund', team: 'SJS', position: 'F' },
  { id: 'sjs-3', name: 'Mikael Granlund', team: 'SJS', position: 'F' },
  { id: 'sjs-4', name: 'Tyler Toffoli', team: 'SJS', position: 'F' },
  { id: 'sjs-5', name: 'Fabian Zetterlund', team: 'SJS', position: 'F' },
  { id: 'sjs-6', name: 'Luke Kunin', team: 'SJS', position: 'F' },
  { id: 'sjs-7', name: 'Ty Dellandrea', team: 'SJS', position: 'F' },
  { id: 'sjs-8', name: 'Alexander Wennberg', team: 'SJS', position: 'F' },
  { id: 'sjs-9', name: 'Mario Ferraro', team: 'SJS', position: 'D' },
  { id: 'sjs-10', name: 'Cody Ceci', team: 'SJS', position: 'D' },
  { id: 'sjs-11', name: 'Jan Rutta', team: 'SJS', position: 'D' },
  { id: 'sjs-12', name: 'Jake Walman', team: 'SJS', position: 'D' },
  { id: 'sjs-13', name: 'Mackenzie Blackwood', team: 'SJS', position: 'G' },
  // COLUMBUS BLUE JACKETS
  { id: 'cbj-1', name: 'Adam Fantilli', team: 'CBJ', position: 'F' },
  { id: 'cbj-2', name: 'Kirill Marchenko', team: 'CBJ', position: 'F' },
  { id: 'cbj-3', name: 'Cole Sillinger', team: 'CBJ', position: 'F' },
  { id: 'cbj-4', name: 'Kent Johnson', team: 'CBJ', position: 'F' },
  { id: 'cbj-5', name: 'Yegor Chinakhov', team: 'CBJ', position: 'F' },
  { id: 'cbj-6', name: 'Sean Monahan', team: 'CBJ', position: 'F' },
  { id: 'cbj-7', name: 'James van Riemsdyk', team: 'CBJ', position: 'F' },
  { id: 'cbj-8', name: 'Justin Danforth', team: 'CBJ', position: 'F' },
  { id: 'cbj-9', name: 'Zach Werenski', team: 'CBJ', position: 'D' },
  { id: 'cbj-10', name: 'Ivan Provorov', team: 'CBJ', position: 'D' },
  { id: 'cbj-11', name: 'Damon Severson', team: 'CBJ', position: 'D' },
  { id: 'cbj-12', name: 'Erik Gudbranson', team: 'CBJ', position: 'D' },
  { id: 'cbj-13', name: 'Elvis Merzlikins', team: 'CBJ', position: 'G' },
  // TAMPA BAY LIGHTNING
  { id: 'tbl-1', name: 'Nikita Kucherov', team: 'TBL', position: 'F' },
  { id: 'tbl-2', name: 'Brayden Point', team: 'TBL', position: 'F' },
  { id: 'tbl-3', name: 'Brandon Hagel', team: 'TBL', position: 'F' },
  { id: 'tbl-4', name: 'Anthony Cirelli', team: 'TBL', position: 'F' },
  { id: 'tbl-5', name: 'Jake Guentzel', team: 'TBL', position: 'F' },
  { id: 'tbl-6', name: 'Nick Paul', team: 'TBL', position: 'F' },
  { id: 'tbl-7', name: 'Conor Sheary', team: 'TBL', position: 'F' },
  { id: 'tbl-8', name: 'Mitchell Chaffee', team: 'TBL', position: 'F' },
  { id: 'tbl-9', name: 'Victor Hedman', team: 'TBL', position: 'D' },
  { id: 'tbl-10', name: 'Erik Cernak', team: 'TBL', position: 'D' },
  { id: 'tbl-11', name: 'Ryan McDonagh', team: 'TBL', position: 'D' },
  { id: 'tbl-12', name: 'Darren Raddysh', team: 'TBL', position: 'D' },
  { id: 'tbl-13', name: 'Andrei Vasilevskiy', team: 'TBL', position: 'G' },
  // UTAH HOCKEY CLUB
  { id: 'uta-1', name: 'Clayton Keller', team: 'UTA', position: 'F' },
  { id: 'uta-2', name: 'Nick Schmaltz', team: 'UTA', position: 'F' },
  { id: 'uta-3', name: 'Lawson Crouse', team: 'UTA', position: 'F' },
  { id: 'uta-4', name: 'Barrett Hayton', team: 'UTA', position: 'F' },
  { id: 'uta-5', name: 'Dylan Guenther', team: 'UTA', position: 'F' },
  { id: 'uta-6', name: 'Logan Cooley', team: 'UTA', position: 'F' },
  { id: 'uta-7', name: 'Jack McBain', team: 'UTA', position: 'F' },
  { id: 'uta-8', name: 'Alexander Kerfoot', team: 'UTA', position: 'F' },
  { id: 'uta-9', name: 'Mikhail Sergachev', team: 'UTA', position: 'D' },
  { id: 'uta-10', name: 'Sean Durzi', team: 'UTA', position: 'D' },
  { id: 'uta-11', name: 'Juuso Valimaki', team: 'UTA', position: 'D' },
  { id: 'uta-12', name: 'Michael Kesselring', team: 'UTA', position: 'D' },
  { id: 'uta-13', name: 'Connor Ingram', team: 'UTA', position: 'G' },
];

// NHL Team Primary Colors
const teamColors = {
  'ANA': '#F47A38', 'ARI': '#8C2633', 'BOS': '#FFB81C', 'BUF': '#002654',
  'CGY': '#D2001C', 'CAR': '#CC0000', 'CHI': '#CF0A2C', 'COL': '#6F263D',
  'CBJ': '#002654', 'DAL': '#006847', 'DET': '#CE1126', 'EDM': '#041E42',
  'FLA': '#041E42', 'LAK': '#111111', 'MIN': '#154734', 'MTL': '#AF1E2D',
  'NSH': '#FFB81C', 'NJD': '#CE1126', 'NYI': '#00539B', 'NYR': '#0038A8',
  'OTT': '#C52032', 'PHI': '#F74902', 'PIT': '#FCB514', 'SJS': '#006D75',
  'SEA': '#99D9D9', 'STL': '#002F87', 'TBL': '#002868', 'TOR': '#00205B',
  'UTA': '#6CACE4', 'VAN': '#00205B', 'VGK': '#B4975A', 'WSH': '#041E42',
  'WPG': '#041E42'
};

// Translations
const translations = {
  en: {
    appName: 'BarDownBets.com',
    tagline: 'Bet on NHL games with your crew',
    signIn: 'Sign In',
    signUp: 'Sign Up',
    username: 'Username',
    email: 'Email',
    password: 'Password',
    chooseName: 'Choose a username',
    enterEmail: 'Enter your email',
    enterPassword: 'Enter your password',
    staySignedIn: 'Stay signed in',
    createAccount: 'Create Account',
    bonus: 'Get $1,000 free credits to start!',
    loginTagline: 'Bets & pools with your crew',
    loginPerkBets: 'Bet on NHL, NBA & MLB games',
    loginPerkPools: 'Pools with live drafts',
    loginPerkBoard: 'Climb the leaderboard',
    loginPerkStart: 'Start with $1,000 in credits',
    forgotPassword: 'Forgot password?',
    resetTitle: 'Reset your password',
    resetDesc: "Enter your email and we'll send you a link to choose a new password.",
    sendResetLink: 'Send reset link',
    resetSent: 'Check your email: we sent you a link to reset your password.',
    backToSignIn: 'Back to sign in',
    newPasswordTitle: 'Choose a new password',
    newPassword: 'New password',
    confirmPassword: 'Confirm password',
    savePassword: 'Save password',
    passwordsDontMatch: "The passwords don't match.",
    passwordTooShort: 'At least 6 characters.',
    passwordUpdated: 'Password updated!',
    games: 'Games',
    myBets: 'My Bets',
    leaderboard: 'Leaderboard',
    pools: 'Pools',
    balance: 'Balance',
    signOut: 'Sign Out',
    quickFilters: 'Quick Filters',
    allGames: 'All Games',
    today: 'Today',
    tomorrow: 'Tomorrow',
    live: 'Live',
    habs: 'Habs',
    homeTeam: 'HOME',
    betTypes: 'Bet Types',
    nhlGames: 'NHL Games',
    gamesCount: 'games',
    noGames: 'No games found',
    active: 'Active',
    history: 'History',
    noActiveBets: 'No active bets',
    placeBetStart: 'Place a bet to get started!',
    noHistory: 'No history yet',
    pending: 'PENDING',
    odds: 'Odds',
    stake: 'Stake',
    toWin: 'To Win',
    payout: 'Payout',
    betSlip: 'Bet Slip',
    betSlipEmpty: 'Bet slip empty',
    clickOdds: 'Click odds to add bets',
    totalStake: 'Total Stake',
    potentialWin: 'Potential Win',
    placeBet: 'Place Bet',
    placeBets: 'Place Bets',
    singles: 'Singles',
    parlay: 'Combined',
    combinedOdds: 'Combined Odds',
    parlayPlaced: 'Combined bet placed!',
    parlayMinLegs: 'Combined bet requires at least 2 selections',
    parlayLeg: 'leg',
    parlayLegs: 'legs',
    placeParlay: 'Place Combined Bet',
    // Bet types
    moneyline: 'Moneyline',
    moneylineDesc: 'Pick the team that will win the game',
    puckLine: 'Puck Line (Spread)',
    puckLineDesc: 'Bet on the margin of victory',
    winsBy2: 'Wins by 2+',
    wontLoseBy2: "Won't lose by 2+",
    totalGoals: 'Total Goals',
    totalGoalsDesc: 'Predict if combined score will be over or under the line',
    enterTotal: 'Enter total',
    over: 'Over',
    under: 'Under',
    teamFirst: 'Team to Score First',
    teamFirstDesc: 'Which team will score the opening goal',
    bothScore: 'Both Teams to Score',
    bothScoreDesc: 'Will both teams score at least one goal',
    yes: 'Yes',
    no: 'No',
    periodWinner: 'Period Winner',
    periodWinnerDesc: 'Who will be leading after each period',
    period1: '1st Period',
    period2: '2nd Period',
    period3: '3rd Period',
    draw: 'Draw',
    gameProps: 'Game Props',
    gamePropsDesc: 'Special game events',
    overtime: 'Overtime',
    shutout: 'Shutout',
    fight: 'Fight',
    penaltyMins: 'Total Penalty Minutes',
    penaltyMinsDesc: 'Total penalty minutes for both teams',
    firstGoal: 'First Goal Scorer',
    firstGoalDesc: 'Player to score the first goal of the game',
    anytimeGoal: 'Anytime Goal Scorer',
    anytimeGoalDesc: 'Player to score at least one goal',
    playerPoints: 'Player to Record a Point',
    playerPointsDesc: 'Player to get a goal or assist',
    playerShots: 'Player Shots on Goal',
    playerShotsDesc: 'How many shots will this player take',
    shots: 'shots',
    totalShots: 'Total Shots on Goal',
    totalShotsDesc: 'Combined shots on goal for both teams',
    winningGoal: 'Winning Goal Scorer',
    winningGoalDesc: 'Player to score the game-winning goal',
    correctScore: 'Correct Score',
    correctScoreDesc: 'Predict the exact final score (Home-Away)',
    homeScore: 'Home',
    awayScore: 'Away',
    addScore: 'Add Score Bet',
    // MLB player props
    mlbBatterProps: 'Batter Props',
    mlbBatterPropsDesc: 'Bet on hits, HR, RBI, stolen bases and more',
    mlbPitcherProps: 'Pitcher Strikeouts',
    mlbPitcherPropsDesc: 'Bet on how many batters a pitcher will strike out',
    runLineDesc: 'Bet on the margin of victory (±1.5 runs)',
    totalRunsDesc: 'Predict if combined runs will be over or under the line',
    anytimeHR: 'Anytime Home Run',
    anytimeRBI: 'Anytime RBI',
    anytimeSB: 'Anytime Stolen Base',
    pitcherKs: 'Pitcher Strikeouts',
    enterPlayers: 'Enter Player Names',
    enterPlayersDesc: 'Add players for goal scorer bets',
    playerName: 'Player name',
    addPlayer: 'Add Player',
    // Notifications
    addedToSlip: 'Added to slip',
    alreadyInSlip: 'Already in slip',
    gameStarted: 'Game already started',
    insufficientBalance: 'Insufficient balance',
    enterStake: 'Enter stake amounts',
    betPlaced: 'bet placed!',
    betsPlaced: 'bets placed!',
    winner: 'Winner!',
    betLost: 'Bet lost',
    accountCreated: 'Account created! You can now login.',
    usernameMin: 'Username must be at least 3 characters',
    cancelBet: 'Cancel',
    cancelBetConfirm: 'Cancel this bet? Your stake will be refunded.',
    betCancelled: 'Bet cancelled! Stake refunded:',
    cancelNotAllowed: 'Cannot cancel - game has already started',
    cancelling: 'Cancelling...',
    // Injuries
    injuries: 'Injuries',
    injuryReport: 'Injury Report',
    noInjuries: 'No injuries reported',
    noInjuriesForGames: 'No injuries for today\'s games',
    out: 'Out',
    dayToDay: 'Day-to-Day',
    injured: 'Injured',
    injuredPlayer: 'Injured',
    // Scout Report
    scoutTitle: 'BARDOWN SCOUT REPORT',
    scoutGenerated: 'Generated',
    scoutAnalysed: 'analysed',
    scoutGame: 'game',
    scoutGames: 'games',
    scoutLoading: 'Loading...',
    scoutAnalysing: 'Analysing team data... first load may take 20–30 seconds.',
    scoutBack: '← Back to Games',
    scoutRefresh: '⟳ Refresh',
    scoutWinRates: '📊 Win rates on BarDownBets:',
    scoutNoNHL: 'No NHL games today.',
    scoutNoMLB: 'No MLB games today.',
    scoutUnlocked: '🔒 Scout Report unlocked',
    statistics: 'STATISTICS',
    loadingStats: 'Loading stats...',
    teamHR: 'Team Home Runs',
    teamHRDesc: 'Bet on how many home runs a team will hit',
    favorites: 'Favorites',
    favoriteTeam: 'Favorite Team',
    themeSettings: 'Theme Settings',
    theme_none: 'Default',
    theme_accent: 'Accent Only',
    theme_cards: 'Cards Only',
    theme_custom: 'Full',
    noFavoriteTeam: 'None',
    // NBA
    nbaNoGames: 'No NBA games available',
    nbaSpread: 'Point Spread',
    nbaSpreadDesc: 'Pick the winner with the point spread',
    nbaTotalPoints: 'Total Points',
    nbaTotalPointsDesc: 'Over or under the total points line',
    nbaPlayerProps: 'Player Props',
    nbaPlayerPropsDesc: 'Bet on individual player performances',
    // Stats labels
    statsUnavailable: 'Stats unavailable',
    statPoints: 'POINTS',
    statRebounds: 'REBOUNDS',
    statAssists: 'ASSISTS',
    statSteals: 'STEALS',
    statBlocks: 'BLOCKS',
    statTurnovers: 'TURNOVERS',
    statFGPct: 'FG%',
    stat3PTPct: '3PT%',
    statFTPct: 'FT%',
    statFastBreak: 'FAST BREAK PTS',
    statPaintPts: 'POINTS IN PAINT',
    statBenchPts: 'BENCH POINTS',
    statHits: 'HITS',
    statHomeRuns: 'HOME RUNS',
    statRBI: 'RBI',
    statStolenBases: 'STOLEN BASES',
    statStrikeoutsBat: 'STRIKEOUTS (BAT)',
    statWalks: 'WALKS',
    statLeftOnBase: 'LEFT ON BASE',
    statStrikeoutsPitch: 'STRIKEOUTS (PITCH)',
    statPitchesThrown: 'PITCHES THROWN',
    recentPlays: 'RECENT PLAYS',
    // Scout
    scoutPick: 'PICK',
    scoutConfidence: 'Confidence',
    scoutGoalScorers: 'Goal Scorers',
    scoutPointsLeaders: 'Points Leaders',
    scoutGameTotals: 'GAME TOTALS',
    scoutGoalsTotal: 'Goals Total',
    scoutPenaltyMins: 'Penalty Mins',
    scoutWatch: 'Watch',
    scoutPPEdge: 'PP Edge',
    scoutTeamStats: 'TEAM STATS (season)',
    scoutPropPicks: 'PROP PICKS',
    scoutTotalPoints: 'Total Points',
    scoutTotalRuns: 'Total Runs',
    scoutReboundEdge: 'Rebound Edge',
    scout3PTEdge: '3PT Edge',
    scoutHRThreat: 'HR Threat',
    scoutSBThreat: 'SB Threat',
    scoutStrikeouts: 'Strikeouts',
    scoutTeamHR: 'Team HR',
    scoutStolenBases: 'Stolen Bases',
    scoutSpread: 'Spread',
    scoutGoalsOU: 'Goals O/U',
    scoutGoalScorer: 'Goal Scorer',
    scoutPlayerPoints: 'Player Points',
    scoutPuckLine: 'Puck Line',
    scoutTopBets: 'Top Bets — click to add',
    scoutAddToSlip: 'Add to bet slip',
    scoutNoNBA: 'No NBA games today.',
    scoutNoBets: 'No bets available for this game right now.',
    scoutExpected: 'expected',
    scoutAnytime: 'anytime',
    scoutError: 'Scout Report unavailable — try Refresh.',
    scoutRecord: 'Record',
    scoutPPG: 'PPG',
    scoutRPG: 'RPG',
    scoutAPG: 'APG',
    scoutOPS: 'OPS',
    scoutERA: 'ERA',
    scoutFGPct: 'FG%',
    scout3Pct: '3PT%',
    processing: 'Processing...',
    // NHL stats
    statSOG: 'SHOTS ON GOAL',
    statFaceoffPct: 'FACEOFF %',
    statPowerPlay: 'POWER PLAY',
    statBlockedShots: 'BLOCKED SHOTS',
    statPenaltyMins: 'PENALTY MINUTES',
    statGiveaways: 'GIVEAWAYS',
    statTakeaways: 'TAKEAWAYS',
    // Play-by-play action labels
    pbpGoal: 'Goal',
    pbpShotOnGoal: 'Shot on goal',
    pbpMissedShot: 'Missed shot',
    pbpBlockedShot: 'Blocked shot',
    pbpHit: 'Hit',
    pbpHitOn: 'hit on',
    pbpPenalty: 'penalty',
    pbpPeriodStart: 'Period start',
    pbpPeriodEnd: 'Period end',
    pbpStoppage: 'stoppage',
    // MLB PBP
    pbpHomeRun: 'Home Run',
    pbpSingle: 'Single',
    pbpDouble: 'Double',
    pbpTriple: 'Triple',
    pbpWalk: 'Walk',
    pbpStrikeout: 'Strikeout',
    pbpGroundout: 'Groundout',
    pbpFlyout: 'Flyout',
    pbpLineout: 'Lineout',
    pbpPopOut: 'Pop Out',
    pbpFieldersChoice: "Fielder's Choice",
    pbpSacFly: 'Sac Fly',
    pbpSacBunt: 'Sac Bunt',
    pbpHitByPitch: 'Hit By Pitch',
    pbpError: 'Error',
    pbpStolenBase: 'Stolen Base',
    pbpCaughtStealing: 'Caught Stealing',
    pbpDoublePlay: 'Double Play',
    pbpTriplePlay: 'Triple Play',
    pbpWildPitch: 'Wild Pitch',
    pbpPassedBall: 'Passed Ball',
    pbpBalk: 'Balk',
    pbpForceOut: 'Force Out',
    pbpFieldOut: 'Field Out',
    // NBA PBP
    pbpMade: 'Made',
    pbpMissed: 'Missed',
    pbpFreeThrow: 'Free Throw',
    pbpRebound: 'Rebound',
    pbpTurnover: 'Turnover',
    pbpFoul: 'Foul',
    pbpBlock: 'Block',
    pbpSteal: 'Steal',
    pbpSubstitution: 'Substitution',
    pbpTimeout: 'Timeout',
    pbpJumpBall: 'Jump Ball',
    pbpViolation: 'Violation',
  },
  fr: {
    appName: 'BarDownBets.com',
    tagline: 'Pariez sur les matchs de la LNH avec vos amis',
    signIn: 'Connexion',
    signUp: 'Inscription',
    username: "Nom d'utilisateur",
    email: 'Courriel',
    password: 'Mot de passe',
    chooseName: "Choisissez un nom d'utilisateur",
    enterEmail: 'Entrez votre courriel',
    enterPassword: 'Entrez votre mot de passe',
    staySignedIn: 'Rester connecté',
    createAccount: 'Créer un compte',
    bonus: 'Obtenez 1 000$ en crédits gratuits!',
    loginTagline: 'Paris et pools entre amis',
    loginPerkBets: 'Pariez sur la LNH, la NBA et la MLB',
    loginPerkPools: 'Pools avec repêchage en direct',
    loginPerkBoard: 'Grimpez au classement',
    loginPerkStart: 'Commencez avec 1 000 $ en crédits',
    forgotPassword: 'Mot de passe oublié?',
    resetTitle: 'Réinitialiser le mot de passe',
    resetDesc: 'Entrez votre courriel et on vous enverra un lien pour choisir un nouveau mot de passe.',
    sendResetLink: 'Envoyer le lien',
    resetSent: 'Vérifiez vos courriels : on vous a envoyé un lien pour réinitialiser votre mot de passe.',
    backToSignIn: 'Retour à la connexion',
    newPasswordTitle: 'Choisissez un nouveau mot de passe',
    newPassword: 'Nouveau mot de passe',
    confirmPassword: 'Confirmez le mot de passe',
    savePassword: 'Enregistrer',
    passwordsDontMatch: 'Les mots de passe ne correspondent pas.',
    passwordTooShort: 'Au moins 6 caractères.',
    passwordUpdated: 'Mot de passe mis à jour!',
    games: 'Matchs',
    myBets: 'Mes Paris',
    leaderboard: 'Classement',
    pools: 'Pool',
    balance: 'Solde',
    signOut: 'Déconnexion',
    quickFilters: 'Filtres rapides',
    allGames: 'Tous les matchs',
    today: "Aujourd'hui",
    tomorrow: 'Demain',
    live: 'En direct',
    habs: 'Canadiens',
    homeTeam: 'DOMICILE',
    betTypes: 'Types de paris',
    nhlGames: 'Matchs LNH',
    gamesCount: 'matchs',
    noGames: 'Aucun match trouvé',
    active: 'Actifs',
    history: 'Historique',
    noActiveBets: 'Aucun pari actif',
    placeBetStart: 'Placez un pari pour commencer!',
    noHistory: "Pas encore d'historique",
    pending: 'EN ATTENTE',
    odds: 'Cotes',
    stake: 'Mise',
    toWin: 'Gain potentiel',
    payout: 'Paiement',
    betSlip: 'Panier de paris',
    betSlipEmpty: 'Panier vide',
    clickOdds: 'Cliquez sur les cotes pour ajouter',
    totalStake: 'Mise totale',
    potentialWin: 'Gain potentiel',
    placeBet: 'Placer le pari',
    placeBets: 'Placer les paris',
    singles: 'Simples',
    parlay: 'Combiné',
    combinedOdds: 'Cotes combinées',
    parlayPlaced: 'Pari combiné placé!',
    parlayMinLegs: 'Le pari combiné nécessite au moins 2 sélections',
    parlayLeg: 'sélection',
    parlayLegs: 'sélections',
    placeParlay: 'Placer le pari combiné',
    // Bet types
    moneyline: 'Vainqueur',
    moneylineDesc: 'Choisissez l\'équipe qui gagnera le match',
    puckLine: 'Écart (Puck Line)',
    puckLineDesc: 'Pariez sur la marge de victoire',
    winsBy2: 'Gagne par 2+',
    wontLoseBy2: 'Ne perdra pas par 2+',
    totalGoals: 'Total de buts',
    totalGoalsDesc: 'Prédisez si le score combiné sera plus ou moins que la ligne',
    enterTotal: 'Entrez le total',
    over: 'Plus de',
    under: 'Moins de',
    teamFirst: 'Première équipe à marquer',
    teamFirstDesc: 'Quelle équipe marquera le premier but',
    bothScore: 'Les deux équipes marquent',
    bothScoreDesc: 'Les deux équipes marqueront-elles au moins un but',
    yes: 'Oui',
    no: 'Non',
    periodWinner: 'Gagnant de période',
    periodWinnerDesc: 'Qui mènera après chaque période',
    period1: '1ère période',
    period2: '2ème période',
    period3: '3ème période',
    draw: 'Égalité',
    gameProps: 'Props de match',
    gamePropsDesc: 'Événements spéciaux du match',
    overtime: 'Prolongation',
    shutout: 'Blanchissage',
    fight: 'Bagarre',
    penaltyMins: 'Minutes de pénalité',
    penaltyMinsDesc: 'Total des minutes de pénalité pour les deux équipes',
    firstGoal: 'Premier buteur',
    firstGoalDesc: 'Joueur qui marquera le premier but du match',
    anytimeGoal: 'Buteur (n\'importe quand)',
    anytimeGoalDesc: 'Joueur qui marquera au moins un but',
    playerPoints: 'Joueur avec un point',
    playerPointsDesc: 'Joueur qui obtiendra un but ou une aide',
    playerShots: 'Tirs au but du joueur',
    playerShotsDesc: 'Combien de tirs ce joueur prendra-t-il',
    shots: 'tirs',
    totalShots: 'Total des tirs au but',
    totalShotsDesc: 'Tirs au but combinés des deux équipes',
    winningGoal: 'Buteur gagnant',
    winningGoalDesc: 'Joueur qui marquera le but gagnant',
    correctScore: 'Score exact',
    correctScoreDesc: 'Prédisez le score final exact (Local-Visiteur)',
    homeScore: 'Local',
    awayScore: 'Visiteur',
    addScore: 'Ajouter pari score',
    // MLB player props
    mlbBatterProps: 'Paris frappeurs',
    mlbBatterPropsDesc: 'Pariez sur les coups sûrs, circuits, points produits et plus',
    mlbPitcherProps: 'Retraits au bâton',
    mlbPitcherPropsDesc: 'Pariez sur le nombre de retraits au bâton d\'un lanceur',
    runLineDesc: 'Pariez sur la marge de victoire (±1.5 points)',
    totalRunsDesc: 'Prédisez si le total de points sera au-dessus ou en-dessous de la ligne',
    anytimeHR: 'Circuit (n\'importe quand)',
    anytimeRBI: 'Point produit (n\'importe quand)',
    anytimeSB: 'But volé (n\'importe quand)',
    pitcherKs: 'Retraits au bâton du lanceur',
    enterPlayers: 'Entrez les noms des joueurs',
    enterPlayersDesc: 'Ajoutez des joueurs pour les paris buteurs',
    playerName: 'Nom du joueur',
    addPlayer: 'Ajouter joueur',
    // Notifications
    addedToSlip: 'Ajouté au panier',
    alreadyInSlip: 'Déjà dans le panier',
    gameStarted: 'Match déjà commencé',
    insufficientBalance: 'Solde insuffisant',
    enterStake: 'Entrez les mises',
    betPlaced: 'pari placé!',
    betsPlaced: 'paris placés!',
    winner: 'Gagné!',
    betLost: 'Pari perdu',
    accountCreated: 'Compte créé! Vous pouvez maintenant vous connecter.',
    usernameMin: "Le nom d'utilisateur doit avoir au moins 3 caractères",
    cancelBet: 'Annuler',
    cancelBetConfirm: 'Annuler ce pari? Votre mise sera remboursée.',
    betCancelled: 'Pari annulé! Mise remboursée:',
    cancelNotAllowed: 'Impossible d\'annuler - le match a déjà commencé',
    cancelling: 'Annulation...',
    // Injuries
    injuries: 'Blessures',
    injuryReport: 'Rapport de blessures',
    noInjuries: 'Aucune blessure signalée',
    noInjuriesForGames: 'Aucune blessure pour les matchs d\'aujourd\'hui',
    out: 'Absent',
    dayToDay: 'Jour le jour',
    injured: 'Blessé',
    injuredPlayer: 'Blessé',
    // Scout Report
    scoutTitle: 'RAPPORT ÉCLAIREUR BARDOWN',
    scoutGenerated: 'Généré',
    scoutAnalysed: 'analysé(s)',
    scoutGame: 'match',
    scoutGames: 'matchs',
    scoutLoading: 'Chargement...',
    scoutAnalysing: 'Analyse des données... le premier chargement peut prendre 20–30 secondes.',
    scoutBack: '← Retour aux matchs',
    scoutRefresh: '⟳ Actualiser',
    scoutWinRates: '📊 Taux de victoire sur BarDownBets:',
    scoutNoNHL: 'Aucun match LNH aujourd\'hui.',
    scoutNoMLB: 'Aucun match MLB aujourd\'hui.',
    scoutUnlocked: '🔒 Rapport éclaireur débloqué',
    statistics: 'STATISTIQUES',
    loadingStats: 'Chargement des stats...',
    teamHR: 'Circuits par équipe',
    teamHRDesc: 'Pariez sur le nombre de circuits d\'une équipe',
    favorites: 'Favoris',
    favoriteTeam: 'Équipe favorite',
    themeSettings: 'Paramètres du thème',
    theme_none: 'Par défaut',
    theme_accent: 'Couleur accent seulement',
    theme_cards: 'Cartes seulement',
    theme_custom: 'Complet',
    noFavoriteTeam: 'Aucune',
    // NBA
    nbaNoGames: 'Aucun match NBA disponible',
    nbaSpread: 'Écart de points',
    nbaSpreadDesc: 'Choisissez le gagnant avec l\'écart de points',
    nbaTotalPoints: 'Total de points',
    nbaTotalPointsDesc: 'Plus ou moins que le total de points',
    nbaPlayerProps: 'Props joueurs',
    nbaPlayerPropsDesc: 'Pariez sur les performances des joueurs',
    // Stats labels
    statsUnavailable: 'Stats indisponibles',
    statPoints: 'POINTS',
    statRebounds: 'REBONDS',
    statAssists: 'PASSES',
    statSteals: 'INTERCEPTIONS',
    statBlocks: 'CONTRES',
    statTurnovers: 'REVIREMENTS',
    statFGPct: 'TIR%',
    stat3PTPct: '3PT%',
    statFTPct: 'LF%',
    statFastBreak: 'PTS CONTRE-ATTAQUE',
    statPaintPts: 'PTS DANS LA RAQUETTE',
    statBenchPts: 'PTS DU BANC',
    statHits: 'COUPS SÛRS',
    statHomeRuns: 'CIRCUITS',
    statRBI: 'POINTS PRODUITS',
    statStolenBases: 'BUTS VOLÉS',
    statStrikeoutsBat: 'RETRAITS (FRAPPEUR)',
    statWalks: 'BUTS SUR BALLES',
    statLeftOnBase: 'LAISSÉS SUR BUTS',
    statStrikeoutsPitch: 'RETRAITS (LANCEUR)',
    statPitchesThrown: 'LANCERS',
    recentPlays: 'JEUX RÉCENTS',
    // Scout
    scoutPick: 'CHOIX',
    scoutConfidence: 'Confiance',
    scoutGoalScorers: 'Buteurs',
    scoutPointsLeaders: 'Meneurs de points',
    scoutGameTotals: 'TOTAUX DU MATCH',
    scoutGoalsTotal: 'Total de buts',
    scoutPenaltyMins: 'Min. pénalité',
    scoutWatch: 'À surveiller',
    scoutPPEdge: 'Avantage PP',
    scoutTeamStats: 'STATS D\'ÉQUIPE (saison)',
    scoutPropPicks: 'CHOIX DE PROPS',
    scoutTotalPoints: 'Total de points',
    scoutTotalRuns: 'Total de points',
    scoutReboundEdge: 'Avantage rebonds',
    scout3PTEdge: 'Avantage 3PT',
    scoutHRThreat: 'Menace circuit',
    scoutSBThreat: 'Menace but volé',
    scoutStrikeouts: 'Retraits',
    scoutTeamHR: 'Circuits équipe',
    scoutStolenBases: 'Buts volés',
    scoutSpread: 'Écart',
    scoutGoalsOU: 'Buts O/U',
    scoutGoalScorer: 'Buteur',
    scoutPlayerPoints: 'Points joueur',
    scoutPuckLine: 'Puck Line',
    scoutTopBets: 'Meilleurs paris — cliquez pour ajouter',
    scoutAddToSlip: 'Ajouter au panier',
    scoutNoNBA: 'Aucun match NBA aujourd\'hui.',
    scoutNoBets: 'Aucun pari disponible pour ce match pour le moment.',
    scoutExpected: 'prévu',
    scoutAnytime: 'buteur',
    scoutError: 'Rapport indisponible — essayez Rafraîchir.',
    scoutRecord: 'Fiche',
    scoutPPG: 'PPM',
    scoutRPG: 'RPM',
    scoutAPG: 'PD/M',
    scoutOPS: 'OPS',
    scoutERA: 'MPM',
    scoutFGPct: 'TIR%',
    scout3Pct: '3PT%',
    processing: 'Traitement...',
    // NHL stats
    statSOG: 'TIRS AU BUT',
    statFaceoffPct: 'MISES EN JEU %',
    statPowerPlay: 'AVANTAGE NUMÉRIQUE',
    statBlockedShots: 'TIRS BLOQUÉS',
    statPenaltyMins: 'MINUTES DE PÉNALITÉ',
    statGiveaways: 'REVIREMENTS',
    statTakeaways: 'RÉCUPÉRATIONS',
    // Play-by-play action labels
    pbpGoal: 'But',
    pbpShotOnGoal: 'Tir au but',
    pbpMissedShot: 'Tir raté',
    pbpBlockedShot: 'Tir bloqué',
    pbpHit: 'Mise en échec',
    pbpHitOn: 'mise en échec sur',
    pbpPenalty: 'pénalité',
    pbpPeriodStart: 'Début de période',
    pbpPeriodEnd: 'Fin de période',
    pbpStoppage: 'arrêt de jeu',
    // MLB PBP
    pbpHomeRun: 'Circuit',
    pbpSingle: 'Simple',
    pbpDouble: 'Double',
    pbpTriple: 'Triple',
    pbpWalk: 'But sur balles',
    pbpStrikeout: 'Retrait au bâton',
    pbpGroundout: 'Roulant retiré',
    pbpFlyout: 'Ballon retiré',
    pbpLineout: 'Flèche retirée',
    pbpPopOut: 'Chandelle retirée',
    pbpFieldersChoice: 'Choix du défenseur',
    pbpSacFly: 'Ballon sacrifice',
    pbpSacBunt: 'Amorti sacrifice',
    pbpHitByPitch: 'Frappé par un lancer',
    pbpError: 'Erreur',
    pbpStolenBase: 'But volé',
    pbpCaughtStealing: 'Retiré en tentative',
    pbpDoublePlay: 'Double jeu',
    pbpTriplePlay: 'Triple jeu',
    pbpWildPitch: 'Lancer fou',
    pbpPassedBall: 'Balle passée',
    pbpBalk: 'Feinte illégale',
    pbpForceOut: 'Retrait forcé',
    pbpFieldOut: 'Retrait défensif',
    // NBA PBP
    pbpMade: 'Réussi',
    pbpMissed: 'Raté',
    pbpFreeThrow: 'Lancer franc',
    pbpRebound: 'Rebond',
    pbpTurnover: 'Revirement',
    pbpFoul: 'Faute',
    pbpBlock: 'Contre',
    pbpSteal: 'Interception',
    pbpSubstitution: 'Substitution',
    pbpTimeout: 'Temps mort',
    pbpJumpBall: 'Entre-deux',
    pbpViolation: 'Violation',
  }
};

const NHL_TEAM_COLORS = {
  ANA:{primary:'#F47A38',secondary:'#B9975B',third:'#000000'},ARI:{primary:'#8C2633',secondary:'#E2D6B5',third:'#000000'},
  BOS:{primary:'#FFB81C',secondary:'#000000'},BUF:{primary:'#002654',secondary:'#FCB514'},
  CAR:{primary:'#CC0000',secondary:'#000000',third:'#A2AAAD'},CBJ:{primary:'#002654',secondary:'#CE1141',third:'#A4A9AD'},
  CGY:{primary:'#D2001C',secondary:'#FAAF19',third:'#111111'},CHI:{primary:'#CF0A2C',secondary:'#000000'},
  COL:{primary:'#6F263D',secondary:'#236192',third:'#A2AAAD'},DAL:{primary:'#006847',secondary:'#8F8F8C',third:'#111111'},
  DET:{primary:'#CE1126',secondary:'#FFFFFF'},EDM:{primary:'#041E42',secondary:'#FF4C00'},
  FLA:{primary:'#041E42',secondary:'#C8102E',third:'#B9975B'},LAK:{primary:'#111111',secondary:'#A2AAAD'},
  MIN:{primary:'#154734',secondary:'#A6192E',third:'#EAAA00'},MTL:{primary:'#AF1E2D',secondary:'#192168'},
  NJD:{primary:'#CE1126',secondary:'#000000'},NSH:{primary:'#FFB81C',secondary:'#041E42'},
  NYI:{primary:'#00539B',secondary:'#F47D30'},NYR:{primary:'#0038A8',secondary:'#CE1126'},
  OTT:{primary:'#C52032',secondary:'#C2912C',third:'#000000'},PHI:{primary:'#F74902',secondary:'#000000'},
  PIT:{primary:'#FCB514',secondary:'#000000'},SEA:{primary:'#99D9D9',secondary:'#001628',third:'#355464'},
  SJS:{primary:'#006D75',secondary:'#EA7200',third:'#000000'},STL:{primary:'#002F87',secondary:'#FCB514',third:'#041E42'},
  TBL:{primary:'#002868',secondary:'#FFFFFF'},TOR:{primary:'#00205B',secondary:'#FFFFFF'},
  UTA:{primary:'#71AFE5',secondary:'#000000'},VAN:{primary:'#00205B',secondary:'#00843D'},
  VGK:{primary:'#B4975A',secondary:'#333F42',third:'#C8102E'},WPG:{primary:'#041E42',secondary:'#004C97',third:'#AC162C'},
  WSH:{primary:'#C8102E',secondary:'#041E42'},
};

const MLB_TEAM_COLORS = {
  ARI:{primary:'#A71930',secondary:'#E3D4AD',third:'#000000'},ATL:{primary:'#CE1141',secondary:'#13274F',third:'#EAAA00'},
  BAL:{primary:'#DF4601',secondary:'#000000'},BOS:{primary:'#BD3039',secondary:'#0C2340'},
  CHC:{primary:'#0E3386',secondary:'#CC3433'},CWS:{primary:'#27251F',secondary:'#C4CED4'},
  CIN:{primary:'#C6011F',secondary:'#000000'},CLE:{primary:'#E31937',secondary:'#002B5C'},
  COL:{primary:'#33006F',secondary:'#C4CED4',third:'#000000'},DET:{primary:'#0C2340',secondary:'#FA4616'},
  HOU:{primary:'#002D62',secondary:'#EB6E1F'},KC:{primary:'#004687',secondary:'#BD9B60'},
  LAA:{primary:'#BA0021',secondary:'#003263',third:'#C4CED4'},LAD:{primary:'#005A9C',secondary:'#EF3E42',third:'#A5ACAF'},
  MIA:{primary:'#00A3E0',secondary:'#EF3340',third:'#000000'},MIL:{primary:'#12284B',secondary:'#FFC52F'},
  MIN:{primary:'#002B5C',secondary:'#D31145',third:'#B9975B'},NYM:{primary:'#002D72',secondary:'#FF5910'},
  NYY:{primary:'#003087',secondary:'#E4002C',third:'#C4CED3'},OAK:{primary:'#003831',secondary:'#EFB21E'},ATH:{primary:'#003831',secondary:'#EFB21E'},
  PHI:{primary:'#E81828',secondary:'#002D72'},PIT:{primary:'#27251F',secondary:'#FDB827'},
  SD:{primary:'#2F241D',secondary:'#FFC425'},SEA:{primary:'#0C2C56',secondary:'#005C5C',third:'#C4CED4'},
  SF:{primary:'#FD5A1E',secondary:'#27251F',third:'#EFD19F'},STL:{primary:'#C41E3A',secondary:'#0C2340',third:'#FEDB00'},
  TB:{primary:'#092C5C',secondary:'#8FBCE6',third:'#F5D130'},TEX:{primary:'#003278',secondary:'#C0111F'},
  TOR:{primary:'#134A8E',secondary:'#E8291C',third:'#1D2D5C',theme:{g1:'#134A8E',g2:'#E8291C',g3:'#ffffff',g4:null}},WSH:{primary:'#AB0003',secondary:'#14225A'},
};

const MLB_TEAM_IDS = {
  ATH:133,OAK:133,PIT:134,SD:135,SEA:136,SF:137,STL:138,TB:139,TEX:140,TOR:141,
  MIN:142,PHI:143,ATL:144,CWS:145,MIA:146,NYY:147,MIL:158,LAA:108,ARI:109,AZ:109,
  BAL:110,BOS:111,CHC:112,CIN:113,CLE:114,COL:115,DET:116,HOU:117,KC:118,LAD:119,
  WSH:120,NYM:121
};

const NBA_TEAM_COLORS = {
  ATL:{primary:'#E03A3E',secondary:'#C1D32F',third:'#26282A'},BOS:{primary:'#007A33',secondary:'#BA9653',third:'#000000'},
  BKN:{primary:'#000000',secondary:'#FFFFFF'},CHA:{primary:'#1D1160',secondary:'#00788C',third:'#A1A1A4'},
  CHI:{primary:'#CE1141',secondary:'#000000'},CLE:{primary:'#860038',secondary:'#FDBB30',third:'#041E42'},
  DAL:{primary:'#00538C',secondary:'#002B5E',third:'#B8C4CA'},DEN:{primary:'#0E2240',secondary:'#FEC524',third:'#8B2131'},
  DET:{primary:'#C8102E',secondary:'#1D42BA',third:'#BEC0C2'},GSW:{primary:'#1D428A',secondary:'#FFC72C'},
  HOU:{primary:'#CE1141',secondary:'#000000',third:'#C4CED4'},IND:{primary:'#002D62',secondary:'#FDBB30',third:'#BEC0C2'},
  LAC:{primary:'#C8102E',secondary:'#1D428A',third:'#BEC0C2'},LAL:{primary:'#552583',secondary:'#FDB927',third:'#000000'},
  MEM:{primary:'#5D76A9',secondary:'#12173F',third:'#F5B112'},MIA:{primary:'#98002E',secondary:'#F9A01B',third:'#000000'},
  MIL:{primary:'#00471B',secondary:'#EEE1C6',third:'#0077C0'},MIN:{primary:'#0C2340',secondary:'#236192',third:'#78BE20'},
  NOP:{primary:'#0C2340',secondary:'#C8102E',third:'#85714D'},NYK:{primary:'#006BB6',secondary:'#F58426',third:'#BEC0C2'},
  OKC:{primary:'#007AC1',secondary:'#EF6100',third:'#002D62'},ORL:{primary:'#0077C0',secondary:'#C4CED4',third:'#000000'},
  PHI:{primary:'#006BB6',secondary:'#ED174C',third:'#002B5C'},PHX:{primary:'#1D1160',secondary:'#E56020',third:'#000000'},
  POR:{primary:'#E03A3E',secondary:'#000000'},SAC:{primary:'#5A2D81',secondary:'#63727A',third:'#000000'},
  SAS:{primary:'#C4CED4',secondary:'#000000'},TOR:{primary:'#CE1141',secondary:'#000000',third:'#A1A1A4'},
  UTA:{primary:'#002B5C',secondary:'#00471B'},WAS:{primary:'#002B5C',secondary:'#E31837',third:'#C4CED4'},
};

const NBA_TEAM_IDS = {
  ATL:1610612737,BOS:1610612738,BKN:1610612751,CHA:1610612766,CHI:1610612741,
  CLE:1610612739,DAL:1610612742,DEN:1610612743,DET:1610612765,GSW:1610612744,
  HOU:1610612745,IND:1610612754,LAC:1610612746,LAL:1610612747,MEM:1610612763,
  MIA:1610612748,MIL:1610612749,MIN:1610612750,NOP:1610612740,NYK:1610612752,
  OKC:1610612760,ORL:1610612753,PHI:1610612755,PHX:1610612756,POR:1610612757,
  SAC:1610612758,SAS:1610612759,TOR:1610612761,UTA:1610612762,WAS:1610612764,
};

// Team nicknames for the favourite-team pickers
// NFL teams: ESPN abbreviations and colours (the same ones the NFL games use)
const NFL_TEAM_COLORS = {
  ARI:{primary:'#A40227',secondary:'#FFFFFF',third:'#000000'}, ATL:{primary:'#A71930',secondary:'#000000',third:'#A5ACAF'}, BAL:{primary:'#29126F',secondary:'#000000',third:'#9E7C0C'},
  BUF:{primary:'#00338D',secondary:'#D50A0A'}, CAR:{primary:'#0085CA',secondary:'#000000',third:'#BFC0BF'}, CHI:{primary:'#0B1C3A',secondary:'#E64100'},
  CIN:{primary:'#FB4F14',secondary:'#000000'}, CLE:{primary:'#472A08',secondary:'#FF3C00'}, DAL:{primary:'#002A5C',secondary:'#B0B7BC',third:'#003594'},
  DEN:{primary:'#0A2343',secondary:'#FC4C02'}, DET:{primary:'#0076B6',secondary:'#BBBBBB',third:'#000000'}, GB:{primary:'#204E32',secondary:'#FFB612'},
  HOU:{primary:'#021018',secondary:'#EB0028'}, IND:{primary:'#003B75',secondary:'#FFFFFF'}, JAX:{primary:'#007487',secondary:'#D7A22A',third:'#101820'},
  KC:{primary:'#E31837',secondary:'#FFB612'}, LAC:{primary:'#0080C6',secondary:'#FFC20E'}, LAR:{primary:'#003594',secondary:'#FFD100'},
  LV:{primary:'#000000',secondary:'#A5ACAF'}, MIA:{primary:'#008E97',secondary:'#FC4C02',third:'#005778'}, MIN:{primary:'#4F2683',secondary:'#FFC62F'},
  NE:{primary:'#002A5C',secondary:'#C60C30',third:'#B0B7BC'}, NO:{primary:'#D3BC8D',secondary:'#000000'}, NYG:{primary:'#003C7F',secondary:'#C9243F',third:'#A5ACAF'},
  NYJ:{primary:'#115740',secondary:'#FFFFFF',third:'#000000'}, PHI:{primary:'#06424D',secondary:'#000000',third:'#A5ACAF'}, PIT:{primary:'#000000',secondary:'#FFB612'},
  SEA:{primary:'#002A5C',secondary:'#69BE28',third:'#A5ACAF'}, SF:{primary:'#AA0000',secondary:'#B3995D'}, TB:{primary:'#BD1C36',secondary:'#3E3A35',third:'#FF7900'},
  TEN:{primary:'#4495D2',secondary:'#001532',third:'#C8102E'}, WSH:{primary:'#5A1414',secondary:'#FFB612'},
};
// F1 teams (2026): key = the formula1.com logo name (vector logos); colours from OpenF1 (their car's livery),
// except Red Bull: its brand navy (OpenF1 gives the light livery blue)
const F1_TEAM_COLORS = {
  alpine:{primary:'#00A1E8',secondary:'#15151E',name:'Alpine'},
  astonmartin:{primary:'#229971',secondary:'#15151E',name:'Aston Martin'},
  audi:{primary:'#F50537',secondary:'#15151E',name:'Audi'},
  cadillac:{primary:'#909090',secondary:'#15151E',name:'Cadillac'},
  ferrari:{primary:'#ED1131',secondary:'#15151E',name:'Ferrari'},
  haasf1team:{primary:'#9C9FA2',secondary:'#15151E',name:'Haas'},
  mclaren:{primary:'#F47600',secondary:'#15151E',name:'McLaren'},
  mercedes:{primary:'#00D7B6',secondary:'#15151E',name:'Mercedes'},
  racingbulls:{primary:'#6C98FF',secondary:'#15151E',name:'Racing Bulls'},
  redbullracing:{primary:'#121F45',secondary:'#CC1E4A',third:'#FFC906',name:'Red Bull Racing'},
  williams:{primary:'#1868DB',secondary:'#15151E',name:'Williams'},
};
const TEAM_NICKNAMES = {
  nhl: {
    ANA:'Ducks',ARI:'Coyotes',BOS:'Bruins',BUF:'Sabres',CAR:'Hurricanes',CBJ:'Blue Jackets',
    CGY:'Flames',CHI:'Blackhawks',COL:'Avalanche',DAL:'Stars',DET:'Red Wings',EDM:'Oilers',
    FLA:'Panthers',LAK:'Kings',MIN:'Wild',MTL:'Canadiens',NJD:'Devils',NSH:'Predators',
    NYI:'Islanders',NYR:'Rangers',OTT:'Senators',PHI:'Flyers',PIT:'Penguins',SEA:'Kraken',
    SJS:'Sharks',STL:'Blues',TBL:'Lightning',TOR:'Maple Leafs',UTA:'Mammoth',VAN:'Canucks',
    VGK:'Golden Knights',WPG:'Jets',WSH:'Capitals',
  },
  mlb: {
    ARI:'Diamondbacks',ATL:'Braves',BAL:'Orioles',BOS:'Red Sox',CHC:'Cubs',CWS:'White Sox',
    CIN:'Reds',CLE:'Guardians',COL:'Rockies',DET:'Tigers',HOU:'Astros',KC:'Royals',
    LAA:'Angels',LAD:'Dodgers',MIA:'Marlins',MIL:'Brewers',MIN:'Twins',NYM:'Mets',
    NYY:'Yankees',OAK:'Athletics',ATH:'Athletics',PHI:'Phillies',PIT:'Pirates',SD:'Padres',
    SEA:'Mariners',SF:'Giants',STL:'Cardinals',TB:'Rays',TEX:'Rangers',TOR:'Blue Jays',
    WSH:'Nationals',
  },
  nba: {
    ATL:'Hawks',BOS:'Celtics',BKN:'Nets',CHA:'Hornets',CHI:'Bulls',CLE:'Cavaliers',
    DAL:'Mavericks',DEN:'Nuggets',DET:'Pistons',GSW:'Warriors',HOU:'Rockets',IND:'Pacers',
    LAC:'Clippers',LAL:'Lakers',MEM:'Grizzlies',MIA:'Heat',MIL:'Bucks',MIN:'Timberwolves',
    NOP:'Pelicans',NYK:'Knicks',OKC:'Thunder',ORL:'Magic',PHI:'76ers',PHX:'Suns',
    POR:'Trail Blazers',SAC:'Kings',SAS:'Spurs',TOR:'Raptors',UTA:'Jazz',WAS:'Wizards',
  },
  nfl: {
    ARI:'Cardinals',ATL:'Falcons',BAL:'Ravens',BUF:'Bills',CAR:'Panthers',CHI:'Bears',CIN:'Bengals',CLE:'Browns',DAL:'Cowboys',DEN:'Broncos',DET:'Lions',GB:'Packers',HOU:'Texans',IND:'Colts',JAX:'Jaguars',KC:'Chiefs',LAC:'Chargers',LAR:'Rams',LV:'Raiders',MIA:'Dolphins',MIN:'Vikings',NE:'Patriots',NO:'Saints',NYG:'Giants',NYJ:'Jets',PHI:'Eagles',PIT:'Steelers',SEA:'Seahawks',SF:'49ers',TB:'Buccaneers',TEN:'Titans',WSH:'Commanders',
  },
  f1: Object.fromEntries(Object.entries(F1_TEAM_COLORS).map(([k, v]) => [k, v.name]))
};

// a finished game keeps its final score on the list until midnight, the day it was played
const stillOnList = (game) => {
  if (game.status !== 'OFF' && game.status !== 'FINAL') return true;
  return !!game.game_time && new Date(game.game_time).toDateString() === new Date().toDateString();
};

// Header logo → back to the top, quick (the browser's 'smooth' scroll is slow on long pages)
function scrollToTop() {
  const targets = [document.scrollingElement || document.documentElement, ...document.querySelectorAll('.main-content')]
    .filter((el) => el && el.scrollTop > 0);
  if (!targets.length) return;
  const from = targets.map((el) => el.scrollTop);
  const t0 = performance.now();
  const DURATION = 220;
  const step = (now) => {
    const k = Math.min(1, (now - t0) / DURATION);
    const ease = 1 - Math.pow(1 - k, 3);
    targets.forEach((el, i) => { el.scrollTop = from[i] * (1 - ease); });
    if (k < 1) requestAnimationFrame(step);
  };
  requestAnimationFrame(step);
}

const teamLogoUrl = (sport, abbrev) => {
  if (!abbrev) return null;
  if (sport === 'nhl') return `https://assets.nhle.com/logos/nhl/svg/${abbrev}_light.svg`;
  if (sport === 'nba') return NBA_TEAM_IDS[abbrev] ? `https://cdn.nba.com/logos/nba/${NBA_TEAM_IDS[abbrev]}/global/L/logo.svg` : null;
  if (sport === 'nfl') return `https://a.espncdn.com/i/teamlogos/nfl/500/${abbrev.toLowerCase()}.png`;
  if (sport === 'f1') return F1_TEAM_COLORS[abbrev] ? `https://media.formula1.com/image/upload/v1740000001/common/f1/2026/${abbrev}/2026${abbrev}logo.svg` : null;
  return MLB_TEAM_IDS[abbrev] ? `https://www.mlbstatic.com/team-logos/${MLB_TEAM_IDS[abbrev]}.svg` : null;
};
// same logos, versions made for dark backgrounds (the scores ticker)
const teamLogoUrlDark = (sport, abbrev) => {
  if (!abbrev) return null;
  if (sport === 'nhl') return `https://assets.nhle.com/logos/nhl/svg/${abbrev}_dark.svg`;
  if (sport === 'nba') return NBA_TEAM_IDS[abbrev] ? `https://cdn.nba.com/logos/nba/${NBA_TEAM_IDS[abbrev]}/global/D/logo.svg` : null;
  if (sport === 'nfl') return `https://a.espncdn.com/i/teamlogos/nfl/500-dark/${abbrev.toLowerCase()}.png`;
  if (sport === 'f1') return F1_TEAM_COLORS[abbrev] ? `https://media.formula1.com/image/upload/v1740000001/common/f1/2026/${abbrev}/2026${abbrev}logowhite.svg` : null;
  return MLB_TEAM_IDS[abbrev] ? `https://www.mlbstatic.com/team-logos/team-cap-on-dark/${MLB_TEAM_IDS[abbrev]}.svg` : null;
};

// the leaderboard picture: the favorite team of the league picked in My Teams (avatar_team = 'nfl'), else the NHL one
const avatarOf = (player) => {
  const sport = player.avatar_team && player[`favorite_${player.avatar_team}`] ? player.avatar_team : 'nhl';
  const abbrev = player[`favorite_${sport}`];
  if (!abbrev) return null;
  const name = sport === 'f1' ? TEAM_NICKNAMES.f1[abbrev] || abbrev : abbrev;
  return { abbrev, name, logo: teamLogoUrl(sport, abbrev) };
};

function App() {
  const [lang, setLang] = useState(localStorage.getItem('lang') || 'en');
  const t = translations[lang];

  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [games, setGames] = useState([]);
  const [players, setPlayers] = useState([]);
  const [customPlayers, setCustomPlayers] = useState([]);
  const [newPlayerName, setNewPlayerName] = useState('');
  const [betSlip, setBetSlip] = useState([]);
  const [parlayMode, setParlayMode] = useState(false);
  const [parlayStake, setParlayStake] = useState(10);
  const [activeBets, setActiveBets] = useState([]);
  const [betHistory, setBetHistory] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [activeTab, setActiveTab] = useState('games');
  const [notification, setNotification] = useState(null);
  const [showWhatsNew, setShowWhatsNew] = useState(false);
  const [showUpdate, setShowUpdate] = useState(false);
  const [updating, setUpdating] = useState(false);   // the server is in MAINTENANCE: show the Updating screen // once per device, after logging in to a new version
  const [tickerOn, setTickerOn] = useState(() => { try { return localStorage.getItem('tickerEnabled') !== 'off'; } catch (e) { return true; } }); // scores ticker switch
  const [hapticsOn, setHapticsOn] = useState(hapticsEnabled()); // Vibrations switch in the settings menu (phones / tablets)
  const notifQueue = useRef([]);
  const [boostWeek, setBoostWeek] = useState(null);
  // DEV ONLY: localhost/?boost=2 = the whole site as on a Boost Weekend ('markets' kind: retro + doubled prices).
  // Bets are blocked on that page (localhost uses the live database). Plain localhost = the normal site.
  const boostPreview = process.env.NODE_ENV === 'development' && typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('boost') === '2';
  // the BDB logo (public/brand/bdb-logo.svg): the browser tab icon and the login screen. The header keeps the BarDownBets.com wordmark.
  const logoSrc = './brand/bdb-logo.svg';
  useEffect(() => {
    let link = document.querySelector('link[rel="icon"]');
    if (!link) { link = document.createElement('link'); link.rel = 'icon'; document.head.appendChild(link); }
    link.type = 'image/svg+xml';
    link.href = logoSrc;
    // FULL addresses: a relative url() in a CSS variable resolves from the stylesheet that uses it — on the live
    // site that's /static/css/, so './brand/…' pointed to a file that doesn't exist (the marks never showed)
    const abs = (path) => `url(${new URL(path, window.location.origin + '/').href})`;
    document.documentElement.style.setProperty('--bdb-logo', abs('brand/bdb-logo.svg'));     // the live-stats watermark
    document.documentElement.style.setProperty('--ice-marks', abs('brand/ice-marks.svg'));   // the skate marks on the ice
  }, []);
  const [showroomCards, setShowroomCards] = useState([]);
  const [showroomEditing, setShowroomEditing] = useState(false);
  const [showroomSelection, setShowroomSelection] = useState(new Set());
  const [showroomSaving, setShowroomSaving] = useState(false);
  const [publicShowroom, setPublicShowroom] = useState(null);
  const [showroomList, setShowroomList] = useState([]);
  const [viewingShowroom, setViewingShowroom] = useState(null);
  const [marketplaceListings, setMarketplaceListings] = useState([]);
  const [marketplaceFilter, setMarketplaceFilter] = useState('all');
  const [marketplaceSort, setMarketplaceSort] = useState('newest');
  const [listingCard, setListingCard] = useState(null);
  const [tradeOfferListing, setTradeOfferListing] = useState(null);
  const [tradeProposals, setTradeProposals] = useState([]);
  const [cardOptionsOpen, setCardOptionsOpen] = useState(false);
  const [shopSportTab, setShopSportTab] = useState('mlb');
  const [marketLoading, setMarketLoading] = useState(false);
  // Boost Week status: asked again every minute (and when you come back to the tab), so the banner
  // never says "active" after the boost ended — bets are priced by the server at the moment they're placed
  const [boostNow, setBoostNow] = useState(Date.now());   // re-renders the countdown
  // a Boost Weekend is on: odds × 2 and the whole site goes RETRO (retro.css, the patch's navy / red / cream)
  const boostOn = !!(boostWeek && boostWeek.active && !(boostWeek.endsAt && boostNow >= new Date(boostWeek.endsAt).getTime()));
  // the weekend's kind: 'markets' = ×2 on every market priced in the browser (odds.js price) · 'moneyline' = the
  // server already doubled the moneylines (this only marks them). Parlays are never boosted.
  setOddsBoost(boostOn ? (boostWeek.marketMultiplier || 1) : 1);
  setMoneylineBoost(boostOn ? (boostWeek.moneylineMultiplier || 1) : 1);
  useEffect(() => {
    const load = () => {
      setBoostNow(Date.now());
      if (boostPreview) {
        setBoostWeek({ active: true, kind: 'weekend', mode: 'markets', multiplier: 2, marketMultiplier: 2, moneylineMultiplier: 1, daysLeft: 3, endsAt: new Date(Date.now() + 2.5 * 86400000).toISOString() });
        return;
      }
      // localhost is always the main site, even during a real Boost Weekend (?boost=2 shows the boosted one)
      if (process.env.NODE_ENV === 'development') {
        setBoostWeek(null);
        return;
      }
      fetch(`${window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' ? (process.env.REACT_APP_API_URL || 'http://localhost:3001') : ''}/api/boost-week`, { cache: 'no-store' })
        .then(r => r.json()).then(setBoostWeek).catch(() => {});
    };
    load();
    const id = setInterval(load, 60 * 1000);
    const onVisible = () => { if (document.visibilityState === 'visible') load(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => { clearInterval(id); document.removeEventListener('visibilitychange', onVisible); };
  }, []);
  const [scoutReport, setScoutReport] = useState(null);
  const [scoutLoading, setScoutLoading] = useState(false);
  const [scoutRecord, setScoutRecord] = useState(null);   // the Scout's graded picks, last 30 days (scout_picks)
  const scoutLogged = useRef(new Set());
  const [scoutError, setScoutError] = useState(false);
  const secretBuffer = useRef('');
  const [loading, setLoading] = useState(true);
  const [authMode, setAuthMode] = useState('login');
  const [authForm, setAuthForm] = useState({ username: '', email: '', password: '', stayLoggedIn: true });
  const [authError, setAuthError] = useState('');
  const [authInfo, setAuthInfo] = useState('');            // green message on the login page (account created, reset link sent…)
  const [authBusy, setAuthBusy] = useState(false);
  const [passwordRecovery, setPasswordRecovery] = useState(false);   // came back from a "reset password" email link
  const [newPw, setNewPw] = useState({ pw: '', confirm: '' });
  const [expandedGame, setExpandedGame] = useState(null);
  const [liveStats, setLiveStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [mlbLiveStats, setMlbLiveStats] = useState(null);
  const [loadingMLBStats, setLoadingMLBStats] = useState(false);
  const [hrAnimations, setHrAnimations] = useState({}); // { [gameId]: { label, batter } }
  const lastHRPlayId = useRef({});
  const lastHRCount = useRef({});
  const [goalAnimations, setGoalAnimations] = useState({}); // { [gameId]: { team, logo } }
  const lastNHLScore = useRef({});
  const gamesRef = useRef([]);
  const mlbGamesRef = useRef([]);
  const expandedGameRef = useRef(null);
  const isRefreshingRef = useRef(false);
  const isSettlingRef = useRef(false);
  const headerRef = useRef(null);
  const [filter, setFilter] = useState('all');
  const [favoriteNHL, setFavoriteNHL] = useState(() => localStorage.getItem('favoriteNHL') || '');
  const [favoriteMLB, setFavoriteMLB] = useState(() => localStorage.getItem('favoriteMLB') || '');
  const [favoriteNFL, setFavoriteNFL] = useState(() => localStorage.getItem('favoriteNFL') || '');   // ESPN abbreviation (KC)
  const [favoriteF1, setFavoriteF1] = useState(() => localStorage.getItem('favoriteF1') || '');      // F1_TEAM_COLORS key (ferrari)
  // the leaderboard picture: which of your favorite teams it shows, by league ('nfl') · '' = your NHL team
  const [avatarTeam, setAvatarTeam] = useState(() => localStorage.getItem('avatarTeam') || '');
  const [themeMode, setThemeMode] = useState(() => localStorage.getItem('themeMode') || 'none');
  const [themeSource, setThemeSource] = useState(() => localStorage.getItem('themeSource') || 'nhl');
  const [customTotal, setCustomTotal] = useState({});
  const [customScore, setCustomScore] = useState({});
  const [updateStatus, setUpdateStatus] = useState(null);
  const [cancellingBetId, setCancellingBetId] = useState(null);
  const [gameInjuries, setGameInjuries] = useState({});
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [selectedSport, setSelectedSport] = useState('nhl');
  const [collectablesSubTab, setCollectablesSubTab] = useState('shop');
  const [selectedCard, setSelectedCard] = useState(null);
  const cardProtection = true;
  const [catalogCards, setCatalogCards] = useState([]);
  const [userCards, setUserCards] = useState([]);
  const [packRevealCards, setPackRevealCards] = useState(null);
  const [packRevealType, setPackRevealType] = useState('');
  const [packBuying, setPackBuying] = useState(false);
  const [collectionFilter, setCollectionFilter] = useState('all');
  const [collectionVisible, setCollectionVisible] = useState(50);
  const [collectionLoading, setCollectionLoading] = useState(false);
  const [collectablesReady, setCollectablesReady] = useState(false);
  const [shopReady, setShopReady] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedCards, setSelectedCards] = useState(new Set());
  const [selling, setSelling] = useState(false);
  const [catalogTypeFilter, setCatalogTypeFilter] = useState('all');
  const [catalogTeamFilter, setCatalogTeamFilter] = useState('all');
  const [catalogVisible, setCatalogVisible] = useState(50);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogImagesReady, setCatalogImagesReady] = useState(false);
  const [mlbGames, setMlbGames] = useState([]);
  const [mlbRosters, setMlbRosters] = useState({});
  const mlbRosterFetched = React.useRef(new Set());
  const [nbaGames, setNbaGames] = useState([]);
  const [nbaRosters, setNbaRosters] = useState({});
  const nbaRosterFetched = React.useRef(new Set());
  const [nbaLiveStats, setNbaLiveStats] = useState(null);
  const [loadingNBAStats, setLoadingNBAStats] = useState(false);
  const nbaGamesRef = useRef([]);
  const [favoriteNBA, setFavoriteNBA] = useState(() => localStorage.getItem('favoriteNBA') || '');
  const [rosterPlayers, setRosterPlayers] = useState(defaultPlayers);
  // players' per-game rates for player bets (odds engine): NHL + NBA all players, MLB per team
  const [playerRates, setPlayerRates] = useState({ nhl: null, nba: null, mlb: {} });
  const [nbaPropSide, setNbaPropSide] = useState('over');   // NBA player props: the Over or the Under of every line
  const mlbRatesFetched = useRef(new Set());
  const [f1Races, setF1Races] = useState(null);   // F1: last race + next ones (null = loading)
  const [nflGames, setNflGames] = useState(null);   // NFL: this week's games (null = loading)
  const nflPropsAsked = useRef(new Set());   // scout report: NFL props already requested
  const [nflProps, setNflProps] = useState({});     // NFL: gameId -> { home: [players], away: [players] }
  const [sportView, setSportView] = useState('games');   // NHL / NBA / MLB / NFL page: 'games' | 'standings'
  const [rosterUpdating, setRosterUpdating] = useState(false);

  // Lock body scroll when mobile menu or card viewer is open
  useEffect(() => {
    document.body.style.overflow = (mobileMenuOpen || selectedCard) ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen, selectedCard]);

  // Listen for auto-update status from Electron
  useEffect(() => {
    if (window.electronAPI?.onUpdateStatus) {
      window.electronAPI.onUpdateStatus((data) => {
        setUpdateStatus(data);
        // Auto-hide "up to date" message after 3 seconds
        if (data.status === 'not-available') {
          setTimeout(() => setUpdateStatus(null), 3000);
        }
      });
    }
  }, []);

  const changeLang = (newLang) => {
    setLang(newLang);
    localStorage.setItem('lang', newLang);
  };

  // ── Favorite Teams & Theming ──
  const setFavoriteTeam = async (sport, abbrev) => {
    if (sport === 'nhl') {
      setFavoriteNHL(abbrev);
      localStorage.setItem('favoriteNHL', abbrev);
    } else if (sport === 'nba') {
      setFavoriteNBA(abbrev);
      localStorage.setItem('favoriteNBA', abbrev);
    } else if (sport === 'nfl') {
      setFavoriteNFL(abbrev);
      localStorage.setItem('favoriteNFL', abbrev);
    } else if (sport === 'f1') {
      setFavoriteF1(abbrev);
      localStorage.setItem('favoriteF1', abbrev);
    } else {
      setFavoriteMLB(abbrev);
      localStorage.setItem('favoriteMLB', abbrev);
    }
    if (profile?.id) {
      const col = { nhl: 'favorite_nhl', nba: 'favorite_nba', nfl: 'favorite_nfl', f1: 'favorite_f1' }[sport] || 'favorite_mlb';
      await supabase.from('profiles').update({ [col]: abbrev || null }).eq('id', profile.id);
    }
  };

  const saveAvatarTeam = async (value) => {
    setAvatarTeam(value);
    localStorage.setItem('avatarTeam', value);
    if (profile?.id) {
      await supabase.from('profiles').update({ avatar_team: value || null }).eq('id', profile.id);
      setLeaderboard((rows) => rows.map((r) => (r.id === profile.id ? { ...r, avatar_team: value || null } : r)));
    }
  };

  const saveThemeMode = async (mode) => {
    setThemeMode(mode);
    localStorage.setItem('themeMode', mode);
    if (profile?.id) {
      await supabase.from('profiles').update({ theme_mode: mode }).eq('id', profile.id);
    }
  };

  const saveThemeSource = (src) => {
    setThemeSource(src);
    localStorage.setItem('themeSource', src);
  };

  // Hex color helpers
  const hexToRgb = (hex) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return { r, g, b };
  };
  const darkenHex = (hex, amount = 30) => {
    const { r, g, b } = hexToRgb(hex);
    const clamp = v => Math.max(0, Math.min(255, v - amount));
    return `#${clamp(r).toString(16).padStart(2,'0')}${clamp(g).toString(16).padStart(2,'0')}${clamp(b).toString(16).padStart(2,'0')}`;
  };
  const lightenHex = (hex, amount = 30) => darkenHex(hex, -amount);
  // the DETAIL accent (orange on the default theme: header line, balance, wordmark, main buttons) for a team:
  // its first colour that shows on the black header AND carries white button text; else its main colour, adjusted
  const lum = (hex) => {
    const c = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16) / 255).map((x) => (x <= 0.03928 ? x / 12.92 : ((x + 0.055) / 1.055) ** 2.4));
    return 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2];
  };
  const contrast = (a, b) => { const x = lum(a), y = lum(b); return (Math.max(x, y) + 0.05) / (Math.min(x, y) + 0.05); };
  const hexes = (colors) => colors.filter((x) => x && /^#[0-9a-f]{6}$/i.test(x));
  const detailAccent = (header, ...colors) => {
    const ok = (hex) => contrast(hex, header) >= 2.2 && contrast(hex, '#ffffff') >= 2.6;
    const list = hexes(colors);
    const good = list.find(ok);
    if (good) return good;
    let c = list.find((x) => x.toLowerCase() !== header.toLowerCase()) || '#ff5722';
    for (let i = 0; i < 14 && !ok(c); i++) c = contrast(c, header) < 2.2 ? lightenHex(c, 12) : darkenHex(c, 12);
    return c;
  };

  const isLight = (hex) => {
    const { r, g, b } = hexToRgb(hex);
    return (r * 299 + g * 587 + b * 114) / 1000 > 160;
  };

  // Determine which favorite team drives the theme
  // 'auto' = match current sport tab, 'nhl'/'mlb'/'nba' = fixed
  const favBySport = { nhl: favoriteNHL, mlb: favoriteMLB, nba: favoriteNBA, nfl: favoriteNFL, f1: favoriteF1 };
  const COLORS_BY_SPORT = { nhl: NHL_TEAM_COLORS, mlb: MLB_TEAM_COLORS, nba: NBA_TEAM_COLORS, nfl: NFL_TEAM_COLORS, f1: F1_TEAM_COLORS };
  // 'auto' = the current sport tab's favorite (else the first one picked: NHL, MLB, NBA, NFL, F1), else that sport only
  const themeTeamSport = themeSource === 'auto'
    ? (favBySport[selectedSport] ? selectedSport : ['nhl', 'mlb', 'nba', 'nfl', 'f1'].find((sp) => favBySport[sp]) || null)
    : (favBySport[themeSource] ? themeSource : null);
  const themeTeam = themeTeamSport ? favBySport[themeTeamSport] : null;
  const themeTeamColors = themeTeam ? (COLORS_BY_SPORT[themeTeamSport]?.[themeTeam] || null) : null;

  // Apply theme CSS variables
  useEffect(() => {
    const root = document.documentElement;
    const teamColors = themeTeamColors;

    // Default/reset values
    const defaults = {
      // ink accent (selected states, titles); orange is the brand: header line, logo, balance and the main action buttons
      accent: '#17181b', accentDark: '#000000', accentHover: '#2b2d32', accentRgb: '23, 24, 27',
      headerBg: '#151619', headerAccent: '#ff5722', logoColor: '#ff5722', logoDotColor: null, logoDomainColor: '#ffffff',
      balanceColor: '#ff5722', balanceLabelColor: '#ffffff',
      navTextColor: '#a7adb5', navTextActiveColor: '#151619', navActiveBg: '#ffffff',
      pageBg: '#ffffff', cardBg: '#f4f7fa', cardHover: '#edf1f5', border: '#e2e7ed',
      text: '#17181b', textMuted: '#5d6570', favBorderColor: null, uiAccent: null
    };

    const applyTheme = (v) => {
      const rgb = hexToRgb(v.accent);
      root.style.setProperty('--accent', v.accent);
      root.style.setProperty('--accent-dark', v.accentDark);
      root.style.setProperty('--accent-hover', v.accentHover);
      root.style.setProperty('--accent-rgb', `${rgb.r}, ${rgb.g}, ${rgb.b}`);
      root.style.setProperty('--header-bg', v.headerBg);
      root.style.setProperty('--header-accent', v.headerAccent);
      root.style.setProperty('--team-primary', v.teamPrimary || v.headerAccent);
      root.style.setProperty('--logo-color', v.logoColor);
      root.style.setProperty('--balance-color', v.balanceColor);
      root.style.setProperty('--balance-label-color', v.balanceLabelColor);
      root.style.setProperty('--logo-dot-color', v.logoDotColor || v.logoColor);
      root.style.setProperty('--logo-domain-color', v.logoDomainColor || '#ffffff');
      // the sticker wordmark sits on a WHITE outline: each part takes the theme's first colour dark enough to read on
      // white (team themes give the logo white, made for a dark header), and ".com" never matches "BarDownBets"
      const readable = (...c) => c.filter((x) => x && /^#[0-9a-f]{6}$/i.test(x) && lum(x) < 0.75);   // only near-white is skipped
      const nameCol = readable(v.logoColor, v.headerAccent, v.accent, v.uiAccent, v.headerBg)[0] || '#ff5722';
      const comCol = readable(v.logoDomainColor, v.uiAccent, v.headerBg, v.accent, v.headerAccent).find((x) => x.toLowerCase() !== nameCol.toLowerCase()) || '#151619';
      root.style.setProperty('--logo-name', nameCol);
      root.style.setProperty('--logo-com', comCol);
      root.style.setProperty('--nav-text-color', v.navTextColor);
      root.style.setProperty('--nav-text-active-color', v.navTextActiveColor);
      root.style.setProperty('--nav-active-bg', v.navActiveBg);
      root.style.setProperty('--bg-dark', v.pageBg);
      root.style.setProperty('--bg-card', v.cardBg);
      root.style.setProperty('--bg-hover', v.cardHover);
      root.style.setProperty('--border', v.border);
      root.style.setProperty('--text', v.text);
      root.style.setProperty('--text-muted', v.textMuted);
      if (v.favBorderColor) root.style.setProperty('--fav-border-color', v.favBorderColor);
      else root.style.removeProperty('--fav-border-color');
      if (v.uiAccent) {
        root.style.setProperty('--ui-accent', v.uiAccent);
      } else {
        root.style.removeProperty('--ui-accent');
      }
    };

    root.classList.toggle('retro', boostOn);
    // accent theme, toned down: the team colour only on what's SELECTED (tabs, chips, counts, the wordmark);
    if (boostOn) {
      // Boost Weekend: the retro patch colors for everyone, whatever their team theme
      applyTheme({
        accent: '#12284b', accentDark: '#0a1a33', accentHover: '#1d3a66', accentRgb: '18, 40, 75',
        headerBg: '#12284b', headerAccent: '#c8102e', logoColor: '#efe5cc', logoDotColor: '#efe5cc', logoDomainColor: '#efe5cc',
        balanceColor: '#efe5cc', balanceLabelColor: '#c9bfa6',
        navTextColor: '#efe5cc', navTextActiveColor: '#12284b', navActiveBg: '#efe5cc',
        pageBg: '#fbf6ea', cardBg: '#f4ecd8', cardHover: '#efe5cc', border: '#cdbf9c',
        text: '#12284b', textMuted: '#4a5568', favBorderColor: null, uiAccent: '#c8102e'
      });
      return;
    }
    if (themeMode === 'none' || !teamColors) {
      applyTheme(defaults);
      return;
    }

    // Auto-generate groups for teams without explicit theme
    const groups = teamColors.theme || {
      g1: teamColors.primary,
      g2: teamColors.secondary || teamColors.primary,
      g3: '#ffffff',
      g4: null
    };

    // The themes are steps, each one adds to the one before:
    //   Cards Only = the default look + the watermark and your team's game card side line (both in the page)
    //   Accent Only = Cards Only + the ORANGE becomes the team's colour (header line, wordmark, balance, main buttons)
    //   Full ('custom') = Accent Only + the header in the team's colour
    if (themeMode === 'cards') {
      applyTheme(defaults);
      return;
    }
    // Accent Only: the orange becomes the team's SECOND colour, exactly as it is (Habs: navy); ".com" in the MAIN
    // colour (Habs: red); the balance white. Full = the same + the header in the team's MAIN colour.
    const second = teamColors.secondary || teamColors.primary;
    const theme = { ...defaults, teamPrimary: teamColors.primary, headerAccent: second, balanceColor: '#ffffff', logoColor: second, logoDomainColor: teamColors.primary };
    if (themeMode === 'custom' || themeMode === 'full') {
      const light = isLight(teamColors.primary);
      Object.assign(theme, {
        headerBg: teamColors.primary,
        logoDomainColor: teamColors.third || teamColors.primary,   // ".com": the team's 3rd colour, when it has one (never white)
        navTextColor: light ? 'rgba(23, 24, 27, 0.7)' : 'rgba(255, 255, 255, 0.78)',
        balanceColor: light ? '#17181b' : '#ffffff',
        balanceLabelColor: light ? '#17181b' : '#ffffff',
      });
    }
    applyTheme(theme);
  }, [themeMode, themeTeam, themeTeamColors, selectedSport, themeSource, boostOn]);

  const showNotification = (message, type = 'success') => {
    if (type === 'win') {
      // Queue win notifications — show immediately if nothing displayed, else enqueue
      setNotification(prev => {
        if (!prev || prev.type !== 'win') return { message, type };
        notifQueue.current.push({ message, type });
        return prev;
      });
    } else {
      setNotification({ message, type });
      setTimeout(() => setNotification(null), 3000);
    }
  };

  const dismissNotification = () => {
    const next = notifQueue.current.shift();
    setNotification(next || null);
  };

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setLoading(false);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      if (event === 'PASSWORD_RECOVERY') setPasswordRecovery(true);
    });
    return () => subscription.unsubscribe();
  }, []);

  const fetchProfile = useCallback(async () => {
    if (!session) return;

    // First, try to fetch existing profile
    const { data } = await supabase.from('profiles').select('*').eq('id', session.user.id).maybeSingle();

    if (data) {
      setProfile(data);
      // Sync favorites from server
      if (data.favorite_nhl) { setFavoriteNHL(data.favorite_nhl); localStorage.setItem('favoriteNHL', data.favorite_nhl); }
      if (data.favorite_mlb) { setFavoriteMLB(data.favorite_mlb); localStorage.setItem('favoriteMLB', data.favorite_mlb); }
      if (data.favorite_nba) { setFavoriteNBA(data.favorite_nba); localStorage.setItem('favoriteNBA', data.favorite_nba); }
      if (data.favorite_nfl) { setFavoriteNFL(data.favorite_nfl); localStorage.setItem('favoriteNFL', data.favorite_nfl); }
      if (data.favorite_f1) { setFavoriteF1(data.favorite_f1); localStorage.setItem('favoriteF1', data.favorite_f1); }
      if (data.avatar_team) { setAvatarTeam(data.avatar_team); localStorage.setItem('avatarTeam', data.avatar_team); }
      if (data.theme_mode) { const mode = data.theme_mode === 'full' ? 'custom' : data.theme_mode; setThemeMode(mode); localStorage.setItem('themeMode', mode); }
      return;
    }

    // Profile doesn't exist, create one with unique username
    const baseUsername = session.user.user_metadata?.username || session.user.email?.split('@')[0] || 'Player';
    const uniqueUsername = `${baseUsername}_${Math.random().toString(36).substring(2, 6)}`;

    // Try to insert new profile with unique username
    const { data: newProfile, error: insertError } = await supabase.from('profiles').insert({
      id: session.user.id,
      username: uniqueUsername,
      balance: 1000.00
    }).select().maybeSingle();

    if (newProfile) {
      setProfile(newProfile);
      console.log('Profile created:', newProfile);
      return;
    }

    if (insertError) {
      console.error('Profile creation failed:', insertError.message);

      // Try one more time with timestamp-based username
      const fallbackUsername = `Player_${Date.now().toString(36)}`;
      const { data: retryProfile, error: retryError } = await supabase.from('profiles').insert({
        id: session.user.id,
        username: fallbackUsername,
        balance: 1000.00
      }).select().maybeSingle();

      if (retryProfile) {
        setProfile(retryProfile);
        console.log('Profile created on retry:', retryProfile);
        return;
      }

      if (retryError) {
        console.error('Profile creation retry failed:', retryError.message);
        // Don't set a fake profile - user won't be able to place bets
        alert('Failed to create profile. Please try logging out and back in.');
      }
    }
  }, [session]);

  // ═══ CARD SYSTEM FUNCTIONS ═══
  // Helper: preload ALL card images (action photo, headshot, team logo, fabric, autographs)
  const preloadCardImages = useCallback((cards, maxWait = 8000) => {
    const urls = [];
    cards.forEach(c => {
      const d = c.card_data || c;
      const ct = c.card_type;
      if (!d) return;
      const addAllImages = (p) => {
        const sport = p.sport || 'mlb';
        // Action photo
        if (p.photoUrl) urls.push(p.photoUrl);
        else if (p.playerId) {
          urls.push(sport === 'nhl'
            ? `https://assets.nhle.com/mugs/actionshots/1296x729/${p.playerId}.jpg`
            : `./cards/photos/players/${p.playerId}.jpg`);
        }
        // Headshot (card back)
        if (p.playerId) {
          urls.push(sport === 'nhl'
            ? `https://assets.nhle.com/mugs/nhl/latest/${p.playerId}.png`
            : `https://img.mlbstatic.com/mlb-photos/image/upload/w_200,q_auto:best/v1/people/${p.playerId}/headshot/silo/current`);
        }
        // Team logo
        if (p.teamLogoId) urls.push(`https://www.mlbstatic.com/team-logos/${p.teamLogoId}.svg`);
        if (sport === 'nhl' && p.team) urls.push(`https://assets.nhle.com/logos/nhl/svg/${p.team}_light.svg`);
      };
      if (d.playerId) addAllImages(d);
      if (d.player1) addAllImages(d.player1);
      if (d.player2) addAllImages(d.player2);
      // Fabric texture for patch cards
      if (ct && ['rare_patch', 'patch_auto', 'auto_patch'].includes(ct)) {
        urls.push('./cards/photos/fabric.jpg');
      }
      // Autograph images
      if (d.autoImage) urls.push(d.autoImage);
      if (d.auto1Image) urls.push(d.auto1Image);
      if (d.auto2Image) urls.push(d.auto2Image);
    });
    const unique = [...new Set(urls)];
    if (unique.length === 0) return Promise.resolve();
    return new Promise(resolve => {
      let loaded = 0;
      const done = () => { loaded++; if (loaded >= unique.length) resolve(); };
      setTimeout(resolve, maxWait);
      unique.forEach(url => { const img = new Image(); img.onload = done; img.onerror = done; img.src = url; });
    });
  }, []);

  const fetchCatalogCards = useCallback(async () => {
    setCatalogLoading(true);
    setCatalogImagesReady(false);
    try {
      const { data, error } = await supabase
        .from('card_definitions')
        .select('*')
        .eq('is_active', true)
        .order('card_number');
      if (error) throw error;
      setCatalogCards(data || []);
    } catch (err) {
      console.error('Failed to fetch catalog:', err.message);
    } finally {
      setCatalogLoading(false);
    }
  }, []);

  const fetchUserCards = useCallback(async () => {
    if (!session?.user?.id) return;
    setCollectionLoading(true);
    try {
      const { data, error } = await supabase
        .from('user_cards')
        .select('*')
        .eq('user_id', session.user.id)
        .order('pulled_at', { ascending: false });
      if (error) throw error;
      setUserCards(data || []);
      await preloadCardImages((data || []).slice(0, 50));
    } catch (err) {
      console.error('Failed to fetch user cards:', err.message);
    } finally {
      setCollectionLoading(false);
    }
  }, [session]);

  const openPack = useCallback(async (packType) => {
    if (!session?.user?.id || packBuying) return;
    setPackBuying(true);
    try {
      const { data, error } = await supabase.rpc('open_pack', {
        p_user_id: session.user.id,
        p_pack_type: packType,
      });
      if (error) throw error;
      if (!data.success) {
        alert(data.error || 'Failed to open pack');
        return;
      }
      // Update balance in profile state
      setProfile(prev => prev ? { ...prev, balance: data.new_balance } : prev);
      // Open reveal overlay
      setPackRevealType(packType);
      setPackRevealCards(data.cards);
      // Refresh user cards after reveal
      fetchUserCards();
    } catch (err) {
      console.error('Pack open error:', err.message);
      alert('Failed to open pack: ' + err.message);
    } finally {
      setPackBuying(false);
    }
  }, [session, packBuying, fetchUserCards]);

  // Test pack — simulates drops client-side, no cost, no DB writes
  const testOpenPack = useCallback(async (packType) => {
    if (packBuying) return;
    setPackBuying(true);
    try {
      // Fetch card definitions grouped by type
      const { data: defs, error } = await supabase
        .from('card_definitions')
        .select('*')
        .eq('is_active', true);
      if (error) throw error;

      const byType = {};
      (defs || []).forEach(d => {
        if (!byType[d.card_type]) byType[d.card_type] = [];
        byType[d.card_type].push(d);
      });

      const usedIds = new Set();
      const pick = (type) => {
        let actualType = type;
        let filterFn = null;

        // Special types
        if (type === 'rare_auto_top100') {
          actualType = 'rare_auto';
          filterFn = d => d.max_supply && d.max_supply <= 99;
        } else if (type === 'rare_auto') {
          // Default rare_auto picks non-top100 (/300 supply)
          actualType = 'rare_auto';
          filterFn = d => d.max_supply && d.max_supply >= 100;
        } else if (type === 'dual_patch_ohtani') {
          actualType = 'dual_patch';
          filterFn = d => d.card_number === 'BDB-2026-MLB-DUAL-001';
        }

        let pool = (byType[actualType] || byType['base'] || []).filter(d => !usedIds.has(d.id));
        if (filterFn) pool = pool.filter(filterFn);
        if (pool.length === 0) {
          // Fallback: try without filter, then base
          pool = (byType[actualType] || byType['base'] || []).filter(d => !usedIds.has(d.id));
          if (pool.length === 0) pool = byType['base'] || [];
        }
        const card = pool[Math.floor(Math.random() * pool.length)];
        usedIds.add(card.id);
        return card;
      };

      // Standard hot slot
      const rollStandardHot = () => {
        const r = Math.random();
        if (r < 0.005) return 'chrome_auto';        // 0.5%
        if (r < 0.015) return 'auto_patch';          // 1%
        if (r < 0.045) return 'rare_auto';           // 3%
        if (r < 0.085) return 'rare_dual';           // 4%
        return 'rare_999';                            // 91.5%
      };

      // Jumbo hot slot (harder)
      const rollJumboHot = () => {
        const r = Math.random();
        if (r < 0.003) return 'chrome_auto';         // 0.3%
        if (r < 0.010) return 'auto_patch';          // 0.7%
        if (r < 0.020) return 'rare_auto';           // 1%
        if (r < 0.040) return 'rare_dual';           // 2%
        return 'rare_999';                            // 96%
      };

      // Premium slot
      const rollPremiumSlot = () => {
        const r = Math.random();
        if (r < 0.02) return 'patch_auto';           // 2%
        if (r < 0.07) return 'chrome_auto';          // 5%
        if (r < 0.15) return 'auto_patch';           // 8%
        return 'rare_auto_top100';                    // 85%
      };

      // Ultimate slot
      const rollUltimateSlot = () => {
        const r = Math.random();
        if (r < 0.0001) return 'dual_patch_ohtani';  // 0.01% Ohtani/Judge
        if (r < 0.0011) return 'dual_patch';         // 0.1%
        return 'patch_auto';                          // 99.89%
      };

      // Guaranteed auto helper — 98% non-top100 (/100), 2% top100 (/25)
      const rollGuaranteedAuto = () => {
        return Math.random() < 0.02 ? 'rare_auto_top100' : 'rare_auto';
      };

      let positions = [];
      if (packType === 'standard') {
        for (let i = 1; i <= 7; i++) positions.push('base');
        positions.push(rollStandardHot());
      } else if (packType === 'jumbo') {
        for (let i = 1; i <= 21; i++) positions.push('base');
        positions.push(rollJumboHot());
        positions.push(rollJumboHot());
        positions.push(rollGuaranteedAuto());
      } else if (packType === 'premium') {
        positions.push('rare_auto');          // non-top100
        positions.push('rare_auto');          // non-top100
        positions.push('rare_auto');          // non-top100
        positions.push('rare_auto_top100');   // top100
        positions.push(rollPremiumSlot());
      } else if (packType === 'ultimate') {
        positions.push('rare_auto_top100');
        positions.push('auto_patch');
        positions.push('chrome_auto');
        positions.push('patch_auto');
        positions.push(rollUltimateSlot());
      }

      // Team patch color palettes — all 30 MLB teams
      // Jersey fabric colors ONLY — no logo accents, no gold trims
      const teamPatchColors = {
        TOR: ['#134A8E', '#E8291C', '#1D2D5C'],
        PIT: ['#FDB827', '#27251F'],
        LAD: ['#005A9C'],
        NYY: ['#003087', '#1C2841'],
        NYM: ['#002D72', '#FF5910'],
        BOS: ['#BD3039', '#0C2340'],
        LAA: ['#BA0021'],
        HOU: ['#002D62', '#EB6E1F'],
        ATL: ['#CE1141', '#13274F'],
        PHI: ['#E81828', '#002D72'],
        SD:  ['#2F241D', '#FFC425'],
        SF:  ['#FD5A1E', '#27251F'],
        CHC: ['#0E3386', '#CC3433'],
        CWS: ['#27251F'],
        CLE: ['#00385D', '#E31937'],
        DET: ['#0C2340', '#FA4616'],
        MIN: ['#002B5C', '#D31145'],
        KC:  ['#004687', '#7BB2DD'],
        TEX: ['#003278', '#C0111F'],
        SEA: ['#0C2C56', '#005C5C'],
        TB:  ['#092C5C', '#8FBCE6'],
        BAL: ['#DF4601', '#27251F'],
        MIL: ['#12284B', '#FFC52F'],
        STL: ['#C41E3A', '#0C2340'],
        CIN: ['#C6011F', '#000000'],
        AZ:  ['#A71930', '#E3D4AD'],
        COL: ['#33006F', '#C4CED4'],
        MIA: ['#00A3E0', '#EF3340'],
        WSH: ['#AB0003', '#14225A'],
        OAK: ['#003831', '#EFB21E'],
      };
      const randomPatchColor = (team) => {
        const colors = teamPatchColors[team] || ['#333333', '#FFFFFF', '#888888'];
        return colors[Math.floor(Math.random() * colors.length)];
      };
      const randomPatch = (team) => {
        const colors = teamPatchColors[team] || ['#333333'];
        const allColors = [...colors, '#FFFFFF'];
        const color1 = allColors[Math.floor(Math.random() * allColors.length)];
        const variant = Math.random() < 0.4 ? (Math.random() < 0.5 ? 1 : 2) : 3;
        if (variant === 3) return { patchColor: color1 };
        // 2-color patch: one side is always white
        let teamColor = randomPatchColor(team);
        while (teamColor === '#FFFFFF' || teamColor === '#f0ede8' || teamColor === color1) teamColor = randomPatchColor(team);
        const side = Math.random() < 0.5;
        return { patchColor: side ? '#FFFFFF' : teamColor, patchColor2: side ? teamColor : '#FFFFFF', patchVariant: variant };
      };

      const cards = positions.map((type, i) => {
        const def = pick(type);
        const fakeNum = def.max_supply ? `${Math.floor(Math.random() * def.max_supply) + 1}/${def.max_supply}` : null;
        let cardData = def.card_data;
        // Randomize patch color + 2-color variant for patch cards
        const patchTypes = ['rare_patch', 'patch_auto', 'auto_patch', 'legendary_patch_auto'];
        if (patchTypes.includes(def.card_type)) {
          cardData = { ...cardData, ...randomPatch(cardData.team) };
        }
        return {
          position: i + 1,
          card_type: def.card_type,
          card_number: def.card_number,
          card_data: cardData,
          numbering: fakeNum,
        };
      });

      setPackRevealType(packType);
      setPackRevealCards(cards);
    } catch (err) {
      console.error('Test pack error:', err.message);
      alert('Test pack failed: ' + err.message);
    } finally {
      setPackBuying(false);
    }
  }, [packBuying]);

  // Test single-card rip — picks a random card from catalog based on tier odds
  const testSingleRip = useCallback((packPrice) => {
    const tiers = [
      { price: 15, ranges: [[1,8,0.65],[8,15,0.25],[15,25,0.09],[25,100,0.01]] },
      { price: 65, ranges: [[25,40,0.65],[40,65,0.25],[65,100,0.09],[100,500,0.01]] },
      { price: 300, ranges: [[100,200,0.65],[200,300,0.25],[300,500,0.09],[500,2000,0.01]] },
      { price: 1300, ranges: [[500,900,0.65],[900,1300,0.25],[1300,2000,0.09],[2000,5000,0.01]] },
      { price: 3750, ranges: [[2000,2800,0.65],[2800,3750,0.25],[3750,5000,0.09],[5000,10000,0.01]] },
      { price: 7999, ranges: [[5000,6500,0.65],[6500,8000,0.25],[8000,10000,0.09],[10000,15000,0.01]] },
      { price: 15999, ranges: [[10000,12000,0.65],[12000,16000,0.25],[16000,25000,0.09],[25000,50000,0.01]] },
    ];
    const tier = tiers.find(t => t.price === packPrice);
    if (!tier) return;

    // Roll for target value
    const roll = Math.random();
    let cumul = 0;
    let targetMin = 0, targetMax = 0;
    for (const [min, max, prob] of tier.ranges) {
      cumul += prob;
      if (roll < cumul) { targetMin = min; targetMax = max; break; }
    }
    const targetValue = targetMin + Math.random() * (targetMax - targetMin);

    // Find closest card from catalog
    const market = getDailyMarket();
    const allCards = catalog.map(entry => {
      const val = applyMarket(calculateCardValue(entry, entry.type, entry.numbering), market);
      return { ...entry, _val: val };
    }).filter(c => c._val > 0);

    // Sort by distance to target value, pick from top 3 closest for variety
    allCards.sort((a, b) => Math.abs(a._val - targetValue) - Math.abs(b._val - targetValue));
    const picked = allCards[Math.floor(Math.random() * Math.min(3, allCards.length))];
    if (!picked) return;

    const fakeNum = picked.numbering || (picked.type !== 'base' ? `${Math.floor(Math.random() * 25) + 1}/25` : null);

    // Build card_data the same way the existing test pack does
    let cardData = { ...picked };
    if (['rare_patch', 'patch_auto', 'auto_patch', 'legendary_patch_auto'].includes(picked.type) && !cardData.patchColor) {
      cardData.patchColor = '#FFFFFF';
    }

    const card = {
      position: 1,
      card_type: picked.type,
      card_number: picked.cardNumber || 'TEST',
      card_data: cardData,
      numbering: fakeNum,
    };

    setPackRevealType('single');
    setPackRevealCards([card]);
  }, []);

  const sellCards = useCallback(async (cardIds) => {
    if (!session?.user?.id || selling || cardIds.length === 0) return;
    const market = getDailyMarket();
    let totalPrice = 0;
    const cardsToSell = userCards.filter(uc => cardIds.has(uc.id));
    cardsToSell.forEach(uc => {
      const base = calculateCardValue(uc.card_data, uc.card_type, uc.numbering);
      totalPrice += Math.round(applyMarket(base, market) * 0.8);
    });

    if (!window.confirm(`Sell ${cardsToSell.length} card${cardsToSell.length > 1 ? 's' : ''} for ${formatValue(totalPrice)}? (80% of market value)`)) return;

    setSelling(true);
    try {
      const { data, error } = await supabase.rpc('sell_cards', {
        p_user_id: session.user.id,
        p_card_ids: [...cardIds],
        p_total_price: totalPrice,
      });
      if (error) throw error;
      if (!data.success) {
        alert(data.error || 'Failed to sell cards');
        return;
      }
      setProfile(prev => prev ? { ...prev, balance: data.new_balance } : prev);
      setSelectedCards(new Set());
      setSelectMode(false);
      fetchUserCards();
    } catch (err) {
      console.error('Sell error:', err.message);
      alert('Failed to sell: ' + err.message);
    } finally {
      setSelling(false);
    }
  }, [session, selling, userCards, fetchUserCards]);

  // Preload collectables page images (hero + shop) when tab opens
  useEffect(() => {
    if (activeTab !== 'cards') { setCollectablesReady(false); return; }
    const urls = [
      './cards/photos/pack-standard-1-hero.webp', './cards/photos/pack-standard-2-hero.webp',
      './cards/photos/pack-chrome-1-hero.webp', './cards/photos/pack-chrome-3-hero.webp',
      './cards/photos/pack-diamond-1-hero.webp', './cards/photos/pack-diamond-2-hero.webp', './cards/photos/pack-diamond-3-hero.webp',
      './cards/photos/pack-standard-1-shop.webp', './cards/photos/pack-standard-2-shop.webp', './cards/photos/pack-standard-3-shop.webp',
      './cards/photos/pack-jumbo-1-shop.webp', './cards/photos/pack-jumbo-2-shop.webp', './cards/photos/pack-jumbo-3-shop.webp',
      './cards/photos/pack-chrome-1-shop.webp', './cards/photos/pack-chrome-2-shop.webp', './cards/photos/pack-chrome-3-shop.webp',
      './cards/photos/pack-diamond-1-shop.webp', './cards/photos/pack-diamond-2-shop.webp', './cards/photos/pack-diamond-3-shop.webp',
    ];
    let loaded = 0;
    const done = () => { loaded++; if (loaded >= urls.length) setCollectablesReady(true); };
    const timeout = setTimeout(() => setCollectablesReady(true), 5000);
    urls.forEach(url => { const img = new Image(); img.onload = done; img.onerror = done; img.src = url; });
    return () => clearTimeout(timeout);
  }, [activeTab]);

  // Preload shop pack card images when shop tab is active
  useEffect(() => {
    if (activeTab !== 'cards' || collectablesSubTab !== 'shop') { setShopReady(false); return; }
    const urls = [
      './cards/photos/pack-standard-1-shop.webp', './cards/photos/pack-standard-2-shop.webp', './cards/photos/pack-standard-3-shop.webp',
      './cards/photos/pack-jumbo-1-shop.webp', './cards/photos/pack-jumbo-2-shop.webp', './cards/photos/pack-jumbo-3-shop.webp',
      './cards/photos/pack-chrome-1-shop.webp', './cards/photos/pack-chrome-2-shop.webp', './cards/photos/pack-chrome-3-shop.webp',
      './cards/photos/pack-diamond-1-shop.webp', './cards/photos/pack-diamond-2-shop.webp', './cards/photos/pack-diamond-3-shop.webp',
    ];
    let loaded = 0;
    const done = () => { loaded++; if (loaded >= urls.length) setShopReady(true); };
    const timeout = setTimeout(() => setShopReady(true), 5000);
    urls.forEach(url => { const img = new Image(); img.onload = done; img.onerror = done; img.src = url; });
    return () => clearTimeout(timeout);
  }, [activeTab, collectablesSubTab]);

  // Fetch catalog when cards tab is active
  useEffect(() => {
    if (activeTab === 'cards' && collectablesSubTab === 'catalog' && profile?.username === 'CL34RY') {
      fetchCatalogCards();
    }
  }, [activeTab, collectablesSubTab, profile?.username, fetchCatalogCards]);

  // Preload catalog card images when data or filters change
  // Must filter AND sort the same way the render does so we preload the actual visible cards
  useEffect(() => {
    if (!catalogCards.length || catalogLoading) { setCatalogImagesReady(false); return; }
    setCatalogImagesReady(false);
    const filtered = catalogCards.filter(def => {
      if (catalogTypeFilter !== 'all' && def.card_type !== catalogTypeFilter) return false;
      if (catalogTeamFilter !== 'all' && def.card_data?.team !== catalogTeamFilter) return false;
      return true;
    }).sort((a, b) => calculateCardValue(b.card_data, b.card_type) - calculateCardValue(a.card_data, a.card_type));
    const first50 = filtered.slice(0, 50);
    preloadCardImages(first50).then(() => setCatalogImagesReady(true));
  }, [catalogCards, catalogTypeFilter, catalogTeamFilter, catalogLoading, preloadCardImages]);

  // Fetch user cards when collection tab is active
  useEffect(() => {
    if (activeTab === 'cards' && collectablesSubTab === 'collection' && session?.user?.id) {
      fetchUserCards();
    }
  }, [activeTab, collectablesSubTab, session?.user?.id, fetchUserCards]);

  // a parlay is never boosted: each leg counts at its normal odds (a Boost Weekend leg ÷ its boost)
  const legOdds = (b) => (b.boost > 1 ? Math.round((b.odds / b.boost) * 100) / 100 : b.odds);
  const getParlayOdds = (legs) => {
    return legs.reduce((acc, b) => acc * legOdds(b), 1);
  };

  // Helper: render a card in a card-slot (same as collection grid)
  const CardSlot = ({ card, onClick, children: extra }) => {
    const cd = { ...card.card_data, numbering: card.numbering };
    const ct = card.card_type;
    const isDual = ct === 'rare_dual' || ct === 'dual_patch';
    const isHorizontal = isDual || ct === 'rare_patch' || ct === 'auto_patch';
    const CardComp = isDual ? DualCard : ct === 'rare_patch' ? RarePatchCard : ct === 'auto_patch' ? AutoPatchCard : ct === 'patch_auto' ? LegendaryPatchAutoCard : ct === 'chrome_auto' ? ChromeAutoCard : ct === 'rare_auto' ? RareAutoCard : ct === 'rare_999' ? Rare999Card : BaseCard;
    return (
      <div className={`card-slot ${isHorizontal ? 'card-slot-rotated' : ''}`} onClick={onClick} style={{ cursor: onClick ? 'pointer' : 'default' }}>
        {isHorizontal ? (
          <div className="card-rotate-wrapper-react">
            <CardComp card={ct === 'dual_patch' ? { ...cd, legendary: true } : cd} player={cd} scale={0.4375} interactive={false} />
          </div>
        ) : (
          <CardComp player={cd} scale={0.4375} interactive={false} />
        )}
        {extra}
      </div>
    );
  };

  // Helper: render a card component from user_cards data
  const renderCardComponent = (card) => {
    const ct = card.card_type;
    const cardData = { ...card.card_data, numbering: card.numbering };
    const isDual = ct === 'rare_dual' || ct === 'dual_patch';
    const CardComp = isDual ? DualCard : ct === 'rare_patch' ? RarePatchCard : ct === 'auto_patch' ? AutoPatchCard : ct === 'patch_auto' ? LegendaryPatchAutoCard : ct === 'chrome_auto' ? ChromeAutoCard : ct === 'rare_auto' ? RareAutoCard : ct === 'rare_999' ? Rare999Card : BaseCard;
    if (isDual) return <CardComp card={{ ...cardData, ...(ct === 'dual_patch' ? { legendary: true } : {}) }} interactive={false} />;
    return <CardComp player={cardData} card={cardData} interactive={false} />;
  };

  // Fetch user's showroom cards
  const fetchShowroom = useCallback(async () => {
    if (!session?.user?.id) return;
    const { data } = await supabase.from('showroom_cards').select('position, card_id').eq('user_id', session.user.id).order('position');
    if (data) {
      setShowroomCards(data);
      setShowroomSelection(new Set(data.map(sc => sc.card_id)));
    }
  }, [session]);

  // Save showroom
  const saveShowroom = useCallback(async () => {
    if (!session?.user?.id) return;
    setShowroomSaving(true);
    try {
      if (showroomSelection.size === 0) {
        // Clear showroom
        await supabase.from('showroom_cards').delete().eq('user_id', session.user.id);
        showNotification('Showroom cleared!', 'success');
      } else {
        const { data, error } = await supabase.rpc('update_showroom', { p_user_id: session.user.id, p_card_ids: [...showroomSelection] });
        if (error) throw error;
        if (!data.success) { showNotification(data.error, 'error'); return; }
        showNotification(`Showroom updated with ${data.cards_added} cards!`, 'success');
      }
      setShowroomEditing(false);
      fetchShowroom();
    } catch (err) { showNotification('Failed to save showroom', 'error'); }
    finally { setShowroomSaving(false); }
  }, [session, showroomSelection, fetchShowroom]);

  // Fetch all marketplace listings once, filter client-side
  const fetchMarketplace = useCallback(async () => {
    setMarketLoading(true);
    const apiBase = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' ? 'http://localhost:3001' : '';
    try {
      const r = await fetch(`${apiBase}/api/marketplace/listings`);
      const data = await r.json();
      setMarketplaceListings(data || []);
    } catch (e) {}
    finally { setMarketLoading(false); }
  }, []);

  // Send card to market (list for trading)
  const sendToMarket = useCallback(async (cardId) => {
    if (!session?.user?.id) return;
    try {
      const { data, error } = await supabase.rpc('marketplace_list_card', { p_seller_id: session.user.id, p_card_id: cardId, p_asking_price: 0 });
      if (error) throw error;
      if (!data.success) { showNotification(data.error, 'error'); return; }
      showNotification('Card sent to market for trading!', 'success');
      setListingCard(null);
      fetchMarketplace();
      fetchUserCards();
    } catch (err) { showNotification('Failed to list: ' + err.message, 'error'); }
  }, [session, fetchMarketplace, fetchUserCards]);

  // Propose a trade
  const proposeTrade = useCallback(async (listingId, offeredCardId) => {
    if (!session?.user?.id) return;
    try {
      const { data, error } = await supabase.rpc('propose_trade', { p_proposer_id: session.user.id, p_listing_id: listingId, p_offered_card_id: offeredCardId });
      if (error) throw error;
      if (!data.success) { showNotification(data.error, 'error'); return; }
      showNotification('Trade offer sent!', 'success');
      setTradeOfferListing(null);
    } catch (err) { showNotification('Failed to propose: ' + err.message, 'error'); }
  }, [session]);

  // Accept trade
  const acceptTrade = useCallback(async (tradeId) => {
    if (!session?.user?.id) return;
    try {
      const { data, error } = await supabase.rpc('accept_trade', { p_user_id: session.user.id, p_trade_id: tradeId });
      if (error) throw error;
      if (!data.success) { showNotification(data.error, 'error'); return; }
      showNotification('Trade accepted! Cards swapped.', 'success');
      fetchMarketplace();
      fetchUserCards();
      fetchTradeProposals();
    } catch (err) { showNotification('Failed to accept: ' + err.message, 'error'); }
  }, [session, fetchMarketplace, fetchUserCards]);

  // Decline trade
  const declineTrade = useCallback(async (tradeId) => {
    if (!session?.user?.id) return;
    try {
      const { data, error } = await supabase.rpc('decline_trade', { p_user_id: session.user.id, p_trade_id: tradeId });
      if (error) throw error;
      if (!data.success) { showNotification(data.error, 'error'); return; }
      showNotification('Trade declined', 'success');
      fetchTradeProposals();
    } catch (err) { showNotification('Failed to decline', 'error'); }
  }, [session]);

  // Cancel listing
  const cancelListing = useCallback(async (listingId) => {
    if (!session?.user?.id) return;
    try {
      const { data, error } = await supabase.rpc('marketplace_cancel_listing', { p_user_id: session.user.id, p_listing_id: listingId });
      if (error) throw error;
      if (!data.success) { showNotification(data.error, 'error'); return; }
      showNotification('Card removed from market', 'success');
      fetchMarketplace();
    } catch (err) { showNotification('Failed to cancel', 'error'); }
  }, [session, fetchMarketplace]);

  // Fetch trade proposals for my listings
  const fetchTradeProposals = useCallback(async () => {
    if (!session?.user?.id) return;
    const apiBase = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' ? 'http://localhost:3001' : '';
    try {
      const r = await fetch(`${apiBase}/api/trades/${session.user.id}`);
      const data = await r.json();
      setTradeProposals(data || []);
    } catch (e) {}
  }, [session]);

  // Fetch all showrooms
  const fetchShowroomList = useCallback(async () => {
    const apiBase = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' ? 'http://localhost:3001' : '';
    try {
      const r = await fetch(`${apiBase}/api/showrooms`);
      const data = await r.json();
      setShowroomList(data || []);
    } catch (e) {}
  }, []);

  // View another user's showroom
  const viewShowroom = useCallback(async (username) => {
    const apiBase = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' ? 'http://localhost:3001' : '';
    try {
      const r = await fetch(`${apiBase}/api/showroom/${encodeURIComponent(username)}`);
      const data = await r.json();
      if (!data.error) setViewingShowroom(data);
    } catch (e) {}
  }, []);

  // Check for public showroom URL on mount
  useEffect(() => {
    const hash = window.location.hash;
    const match = hash.match(/^#\/showroom\/(.+)$/);
    if (match) {
      const apiBase = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' ? 'http://localhost:3001' : '';
      fetch(`${apiBase}/api/showroom/${encodeURIComponent(match[1])}`)
        .then(r => r.json())
        .then(data => { if (!data.error) setPublicShowroom(data); })
        .catch(() => {});
    }
  }, []);

  const fetchGames = useCallback(async () => {
    // Get today, tomorrow, and day after (needed because NHL API uses UTC dates)
    // Evening games in EST are dated as next day in UTC
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const dayAfter = new Date(today);
    dayAfter.setDate(dayAfter.getDate() + 2);

    // Format as YYYY-MM-DD using local timezone
    const formatLocalDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    const todayStr = formatLocalDate(today);
    const tomorrowStr = formatLocalDate(tomorrow);
    const dayAfterStr = formatLocalDate(dayAfter);

    console.log('Fetching games for:', todayStr, tomorrowStr, dayAfterStr);

    try {
      // Use API service (works in both Electron and Web)
      // Fetch 3 days to account for UTC date offset
      const fetchedGames = await api.fetchNHLGames([todayStr, tomorrowStr, dayAfterStr]);
      if (fetchedGames && fetchedGames.length > 0) {
        const activeGames = fetchedGames.filter(stillOnList);
        setGames(prevGames => activeGames.map(newGame => {
          const prev = prevGames.find(g => g.id === newGame.id);
          return {
            ...newGame,
            home_sog: newGame.home_sog || prev?.home_sog || 0,
            away_sog: newGame.away_sog || prev?.away_sog || 0,
            clock: newGame.clock || prev?.clock || '',
            in_intermission: newGame.in_intermission
              ? true
              : (prev?.in_intermission && newGame.period === prev?.period ? true : false),
          };
        }));
      }
    } catch (error) {
      console.error('Error fetching NHL games:', error);
    }
  }, []);

  const fetchMLBGames = useCallback(async () => {
    const today = new Date();
    const tomorrow = new Date(today); tomorrow.setDate(tomorrow.getDate() + 1);
    const dayAfter = new Date(today); dayAfter.setDate(dayAfter.getDate() + 2);
    const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
    try {
      const fetched = await api.fetchMLBGames([fmt(today), fmt(tomorrow), fmt(dayAfter)]);
      if (fetched) {
        const games = fetched.filter(stillOnList);
        setMlbGames(games);
        // Pre-fetch rosters for all teams in today's games (like NHL does at startup)
        const teamIds = [...new Set(games.flatMap(g => [String(g.home_team_id), String(g.away_team_id)]).filter(Boolean))];
        const unfetched = teamIds.filter(id => !mlbRosterFetched.current.has(id));
        unfetched.forEach(id => mlbRosterFetched.current.add(id));
        if (unfetched.length > 0) {
          Promise.all(unfetched.map(id => api.fetchMLBRoster(id).catch(() => ({ batters: [], pitchers: [] }))))
            .then(results => {
              const updates = {};
              unfetched.forEach((id, i) => { updates[id] = results[i]; });
              setMlbRosters(prev => ({ ...prev, ...updates }));
            });
        }
      }
    } catch (err) {
      console.error('Error fetching MLB games:', err);
    }
  }, []);

  // Fetch live scores for real-time updates
  const fetchLiveScores = useCallback(async () => {
    try {
      const liveData = await api.fetchLiveScores();
      if (liveData && liveData.length > 0) {
        setGames(prevGames => {
          const updated = prevGames.map(game => {
            const liveGame = liveData.find(lg => lg.id === game.id);
            if (liveGame) {
              // Detect NHL goal
              const prev = lastNHLScore.current[game.id];
              const newHome = liveGame.home_score ?? 0;
              const newAway = liveGame.away_score ?? 0;
              if (prev && liveGame.status === 'LIVE') {
                let scoringTeam = null;
                let scoringLogo = null;
                if (newHome > prev.home) { scoringTeam = game.home_abbrev; scoringLogo = game.home_logo; }
                else if (newAway > prev.away) { scoringTeam = game.away_abbrev; scoringLogo = game.away_logo; }
                if (scoringTeam) {
                  setGoalAnimations(p => ({ ...p, [game.id]: { team: scoringTeam, logo: scoringLogo } }));
                  vibrate('goal');
                  setTimeout(() => setGoalAnimations(p => { const n = { ...p }; delete n[game.id]; return n; }), 5000);
                }
              }
              lastNHLScore.current[game.id] = { home: newHome, away: newAway };
              return {
                ...game,
                status: liveGame.status,
                home_score: liveGame.home_score,
                away_score: liveGame.away_score,
                period: liveGame.period,
                period_type: liveGame.period_type,
                clock: liveGame.clock,
                in_intermission: liveGame.in_intermission
                  ? true
                  : (game.in_intermission && liveGame.period === game.period ? true : false),
                home_sog: liveGame.home_sog,
                away_sog: liveGame.away_sog,
                situation: liveGame.situation
              };
            }
            return game;
          });
          return updated;
        });
      }
    } catch (error) {
      console.error('Error fetching live scores:', error);
    }
  }, []);

  const fetchGameStats = useCallback(async (gameId) => {
    setLoadingStats(true);
    try {
      const stats = await api.fetchGameStats(gameId);
      setLiveStats(stats);
    } catch (error) {
      setLiveStats(null);
    }
    setLoadingStats(false);
  }, []);

  const fetchMLBGameStats = useCallback(async (gameId) => {
    setLoadingMLBStats(true);
    try {
      const stats = await api.fetchMLBGameStats(gameId);
      setMlbLiveStats(stats);
      // Primary: detect HR by playId from allPlays (persists even after next batter starts)
      const hrPlay = stats?.lastHRPlay;
      if (hrPlay?.playId) {
        if (lastHRPlayId.current[gameId] === undefined) {
          // First fetch — initialize silently, don't animate
          lastHRPlayId.current[gameId] = hrPlay.playId;
        } else if (lastHRPlayId.current[gameId] !== hrPlay.playId) {
          lastHRPlayId.current[gameId] = hrPlay.playId;
          const isGrandSlam = hrPlay.description?.toLowerCase().includes('grand slam');
          const label = isGrandSlam ? 'GRAND SLAM!' : 'HOME RUN!';
          const batter = hrPlay.batter || stats.currentBatter || null;
          setHrAnimations(prev => ({ ...prev, [gameId]: { label, batter, team: hrPlay.team || null } }));
          setTimeout(() => setHrAnimations(prev => { const next = { ...prev }; delete next[gameId]; return next; }), 5000);
        }
      }
      // Fallback: also catch via count change in case playId is missing
      const totalHR = (stats?.home?.stats?.homeRuns ?? 0) + (stats?.away?.stats?.homeRuns ?? 0);
      const prevHR = lastHRCount.current[gameId] ?? null;
      if (prevHR !== null && totalHR > prevHR && !hrPlay?.playId) {
        const label = 'HOME RUN!';
        const batter = stats.currentBatter || null;
        setHrAnimations(prev => ({ ...prev, [gameId]: { label, batter } }));
        setTimeout(() => setHrAnimations(prev => { const next = { ...prev }; delete next[gameId]; return next; }), 5000);
      }
      lastHRCount.current[gameId] = totalHR;
    } catch (error) {
      setMlbLiveStats(null);
    }
    setLoadingMLBStats(false);
  }, []);

  // HR-only check for non-expanded live games (no stats panel update)
  const checkMLBHR = useCallback(async (gameId) => {
    try {
      const stats = await api.fetchMLBGameStats(gameId);
      if (!stats) return;
      const hrPlay = stats?.lastHRPlay;
      if (hrPlay?.playId) {
        if (lastHRPlayId.current[gameId] === undefined) {
          lastHRPlayId.current[gameId] = hrPlay.playId;
        } else if (lastHRPlayId.current[gameId] !== hrPlay.playId) {
          lastHRPlayId.current[gameId] = hrPlay.playId;
          const isGrandSlam = hrPlay.description?.toLowerCase().includes('grand slam');
          const label = isGrandSlam ? 'GRAND SLAM!' : 'HOME RUN!';
          const batter = hrPlay.batter || null;
          setHrAnimations(prev => ({ ...prev, [gameId]: { label, batter, team: hrPlay.team || null } }));
          setTimeout(() => setHrAnimations(prev => { const next = { ...prev }; delete next[gameId]; return next; }), 5000);
        }
      }
      const totalHR = (stats?.home?.stats?.homeRuns ?? 0) + (stats?.away?.stats?.homeRuns ?? 0);
      const prevHR = lastHRCount.current[gameId] ?? null;
      if (prevHR !== null && totalHR > prevHR && !hrPlay?.playId) {
        setHrAnimations(prev => ({ ...prev, [gameId]: { label: 'HOME RUN!', batter: null } }));
        setTimeout(() => setHrAnimations(prev => { const next = { ...prev }; delete next[gameId]; return next; }), 5000);
      }
      lastHRCount.current[gameId] = totalHR;
    } catch (e) {}
  }, []);

  const fetchNBAGames = useCallback(async () => {
    const today = new Date();
    const tomorrow = new Date(today); tomorrow.setDate(tomorrow.getDate() + 1);
    const dayAfter = new Date(today); dayAfter.setDate(dayAfter.getDate() + 2);
    const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
    try {
      const fetched = await api.fetchNBAGames([fmt(today), fmt(tomorrow), fmt(dayAfter)]);
      if (fetched) {
        const games = fetched.filter(stillOnList);
        setNbaGames(games);
        nbaGamesRef.current = games;
        // Pre-fetch rosters for all teams in games (use abbreviations)
        const teamAbbrevs = [...new Set(games.flatMap(g => [g.home_abbrev, g.away_abbrev]).filter(Boolean))];
        const unfetched = teamAbbrevs.filter(a => !nbaRosterFetched.current.has(a));
        unfetched.forEach(a => nbaRosterFetched.current.add(a));
        if (unfetched.length > 0) {
          Promise.all(unfetched.map(a => api.fetchNBARoster(a).catch(() => ({ players: [] }))))
            .then(results => {
              const updates = {};
              unfetched.forEach((a, i) => { updates[a] = results[i]; });
              setNbaRosters(prev => ({ ...prev, ...updates }));
            });
        }
      }
    } catch (err) {
      console.error('Error fetching NBA games:', err);
    }
  }, []);

  const fetchNBAGameStats = useCallback(async (gameId, teams) => {
    setLoadingNBAStats(true);
    try {
      const stats = await api.fetchNBAGameStats(gameId, teams);
      setNbaLiveStats(stats);
    } catch (error) {
      setNbaLiveStats(null);
    }
    setLoadingNBAStats(false);
  }, []);

  // Fetch injuries for all teams in current games
  const fetchInjuriesForGames = useCallback(async (gamesToFetch) => {
    if (!gamesToFetch || gamesToFetch.length === 0) return;

    // Get unique team abbreviations
    const teams = new Set();
    gamesToFetch.forEach(game => {
      if (game.home_abbrev) teams.add(game.home_abbrev);
      if (game.away_abbrev) teams.add(game.away_abbrev);
    });

    // Fetch injuries for each team
    const injuriesMap = {};
    await Promise.all(
      Array.from(teams).map(async (teamAbbrev) => {
        try {
          const data = await api.fetchInjuries(teamAbbrev);
          if (data && data.injuries) {
            injuriesMap[teamAbbrev] = data.injuries;
          }
        } catch (error) {
          console.error(`Error fetching injuries for ${teamAbbrev}:`, error);
          injuriesMap[teamAbbrev] = [];
        }
      })
    );

    setGameInjuries(injuriesMap);
  }, []);

  const fetchPlayers = useCallback(async () => {
    const { data } = await supabase.from('players').select('*').order('name');
    if (data) setPlayers(data);
  }, []);

  const fetchRosters = useCallback(async (attempt = 0) => {
    try {
      const res = await api.fetchRosters();
      if (res.players && res.players.length > 0) {
        // the roster list also carries NHL teams (position "T", for the pools' team round) — betting only wants players
        setRosterPlayers(res.players.filter((p) => p.position !== 'T'));
        setRosterUpdating(false);
        console.log(`Live rosters loaded: ${res.players.length} players`);
      } else if (attempt < 12) {
        // Server still pre-warming — retry every 5s for up to 60s
        setRosterUpdating(true);
        setTimeout(() => fetchRosters(attempt + 1), 5000);
      } else {
        setRosterUpdating(false);
        console.warn('Roster fetch gave up after retries — using default players');
      }
    } catch (e) {
      console.error('Could not load live rosters, using default');
    }
  }, []);

  const fetchActiveBets = useCallback(async () => {
    if (!session) return;
    const { data } = await supabase
      .from('bets')
      .select('*')
      .eq('user_id', session.user.id)
      .eq('status', 'pending')
      .order('created_at', { ascending: false });
    if (data) setActiveBets(data);
  }, [session]);

  const fetchBetHistory = useCallback(async () => {
    if (!session) return;
    const { data } = await supabase
      .from('bets')
      .select('*')
      .eq('user_id', session.user.id)
      .eq('status', 'settled')
      .order('settled_at', { ascending: false })
      .limit(50);
    if (data) setBetHistory(data);
  }, [session]);

  const [allUserBets, setAllUserBets] = useState({});
  const [chatOpen, setChatOpen] = useState(false);
  const [chatUnread, setChatUnread] = useState(0);
  const feed = useFeed(activeTab === 'feed', profile?.id, boostOn ? boostWeek : null);
  // the feed's matchups: a bet finds its game by id, or (older bets) its teams by name
  const feedTeams = useMemo(() => {
    const colors = { nhl: NHL_TEAM_COLORS, mlb: MLB_TEAM_COLORS, nba: NBA_TEAM_COLORS, nfl: NFL_TEAM_COLORS };
    const byId = {};
    const byName = {};
    const side = (sport, abbrev, name, logo) => ({ abbrev, name, logo: teamLogoUrl(sport, abbrev) || logo, color: colors[sport]?.[abbrev]?.primary });
    const add = (list, sport) => (list || []).forEach((g) => {
      if (!g.home_abbrev || !g.away_abbrev) return;
      const info = { home: side(sport, g.home_abbrev, g.home_team, g.home_logo), away: side(sport, g.away_abbrev, g.away_team, g.away_logo) };
      byId[String(g.id)] = info;
      byName[g.home_team] = info.home;
      byName[g.away_team] = info.away;
    });
    add(games, 'nhl'); add(mlbGames, 'mlb'); add(nbaGames, 'nba'); add(nflGames, 'nfl');
    return { byId, byName };
  }, [games, mlbGames, nbaGames, nflGames]);
  // F1 bets in the feed: the driver's face, or the team's logo
  const [f1Drivers, setF1Drivers] = useState({});
  useEffect(() => {
    if (!session) return;
    api.fetchF1Standings().then((d) => d && setF1Drivers(Object.fromEntries((d.drivers || []).map((x) => [x.code, x])))).catch(() => {});
  }, [session]);
  const feedGameInfo = (bet) => {
    if (String(bet.bet_type || '').startsWith('f1_')) {
      const driver = f1Drivers[bet.selection];
      const mark = bet.bet_type === 'f1_constructor'
        ? { abbrev: bet.selection, name: bet.selection, logo: f1TeamLogo(bet.selection) }
        : { abbrev: bet.selection, name: driver?.name, logo: driver?.photo, face: true };
      return { home: mark, away: mark };
    }
    if (feedTeams.byId[String(bet.game_id)]) return feedTeams.byId[String(bet.game_id)];
    const [home, away] = (bet.game_description || '').split(' vs ');
    return feedTeams.byName[home] && feedTeams.byName[away] ? { home: feedTeams.byName[home], away: feedTeams.byName[away] } : null;
  };
  // a bet on a game that's on right now: the feed shows its score
  const feedLive = (bet) => {
    const g = [...games, ...mlbGames, ...nbaGames, ...(nflGames || [])].find((x) => String(x.id) === String(bet.game_id));
    if (!g || !g.home_abbrev) return null;
    const st = getGameStatus(g);
    return st.live ? { period: st.period, away: g.away_abbrev, home: g.home_abbrev, as: g.away_score || 0, hs: g.home_score || 0 } : null;
  };
  const likeKeys = useMemo(() => {
    const board = leaderboard.flatMap((p, i) => withDemoBets(allUserBets[p.id] || [], i).map((b) => `bet:${b.id}`));
    return [...new Set([...board, ...feed.items.map((x) => x.likeKey)])];
  }, [leaderboard, allUserBets, feed.items]);
  const { byKey: likes, toggle: toggleLike } = useLikes(likeKeys, profile?.id, activeTab === 'leaderboard' || activeTab === 'feed');
  const [expandedPlayer, setExpandedPlayer] = useState(null);

  useEffect(() => { if (profile?.id && shouldShowWhatsNew()) setShowWhatsNew(true); }, [profile?.id]);
  // the small update note comes after What's New, never on top of it
  useEffect(() => { if (profile?.id && !showWhatsNew && shouldShowUpdate()) setShowUpdate(true); }, [profile?.id, showWhatsNew]);

  // an open site notices an update within a minute; when it's over, it reloads into the new version
  useEffect(() => {
    let was = false;
    const check = async () => {
      try {
        const st = await api.fetchStatus();
        if (st?.maintenance) { was = true; setUpdating(true); }
        else if (was) window.location.reload();
      } catch (e) { /* server unreachable: keep going */ }
    };
    check();
    const id = setInterval(check, was ? 20000 : 60000);
    return () => clearInterval(id);
  }, []);

  const fetchLeaderboard = useCallback(async () => {
    // Fetch profiles
    const { data: profiles } = await supabase
      .from('profiles')
      .select('id, username, balance, loser_tag_expires_at, favorite_nhl, favorite_mlb, favorite_nba, favorite_nfl, favorite_f1, avatar_team')
      .not('username', 'like', 'CPU-%')   // GM Season CPU GMs aren't players
      .order('balance', { ascending: false })
      .limit(20);
    if (profiles) setLeaderboard(profiles);

    // Fetch all pending bets for all users
    const { data: bets } = await supabase
      .from('bets')
      .select('*')
      .eq('status', 'pending')
      .order('created_at', { ascending: false });

    // others' combined bets: from parlay_board() — stake, odds and number of picks; the picks
    // only once paid for (reveal_parlay). Own combined bets come with the normal query.
    const { data: board, error: boardError } = await supabase.rpc('parlay_board');
    if (bets && !boardError) {
      const me = session?.user?.id;
      for (let i = bets.length - 1; i >= 0; i--) if (bets[i].bet_type === 'parlay' && bets[i].user_id !== me) bets.splice(i, 1);
      (board || []).filter((p) => p.user_id !== me).forEach((p) => bets.push({ ...p, bet_type: 'parlay', status: 'pending', hiddenLegs: p.legs }));
    }
    if (session?.user?.id) {
      const { data: reveals } = await supabase.from('parlay_reveals').select('bet_id, fee').eq('owner_id', session.user.id);
      const counts = {};
      (reveals || []).forEach((r) => { counts[r.bet_id] = counts[r.bet_id] || { n: 0, fees: 0 }; counts[r.bet_id].n += 1; counts[r.bet_id].fees += Number(r.fee); });
      setRevealCounts(counts);
    }

    if (bets) {
      // Group bets by user_id
      const betsByUser = {};
      bets.forEach(bet => {
        if (!betsByUser[bet.user_id]) betsByUser[bet.user_id] = [];
        betsByUser[bet.user_id].push(bet);
      });
      setAllUserBets(betsByUser);
    }
  }, [session]);

  // ── parlay reveals: $10 to the bettor to see the picks of one of their combined bets ──
  const REVEAL_FEE = 10;
  const [revealCounts, setRevealCounts] = useState({});   // my combined bets: bet id -> { n: paid to see it, fees }
  const [revealingId, setRevealingId] = useState(null);
  const revealParlay = async (bet, ownerName) => {
    if (revealingId) return;
    setRevealingId(bet.id);
    const { data, error } = await supabase.rpc('reveal_parlay', { p_bet_id: bet.id });
    setRevealingId(null);
    if (error || !data?.success) {
      const why = data?.error === 'funds' ? t.insufficientBalance
        : data?.error === 'closed' ? (lang === 'fr' ? 'Ce combiné est terminé.' : 'This combined bet is over.')
        : (lang === 'fr' ? 'Impossible de le dévoiler pour le moment.' : 'Could not reveal it right now.');
      showNotification(why, 'error');
      return;
    }
    vibrate('placed');
    setProfile((prev) => (prev ? { ...prev, balance: parseFloat(data.new_balance) } : prev));
    setAllUserBets((prev) => ({ ...prev, [bet.user_id]: (prev[bet.user_id] || []).map((b) => (b.id === bet.id ? { ...b, bet_value: data.bet_value, revealed: true } : b)) }));
    showNotification(lang === 'fr' ? `Dévoilé · ${formatMoney(REVEAL_FEE, lang)} à ${ownerName}` : `Revealed · ${formatMoney(REVEAL_FEE, lang)} to ${ownerName}`);
  };
  // the picks whose game hasn't started go in my slip, at today's odds, as a combined bet
  const copyParlay = (bet) => {
    const legs = JSON.parse(bet.bet_value || '{}').legs || [];
    let added = 0;
    legs.forEach((leg) => { if (addToBetSlip({ id: leg.gameId }, leg.betType, leg.selection, 0, leg.details || {}, null, true)) added += 1; });
    if (!added) { showNotification(lang === 'fr' ? 'Aucune de ces sélections n\'est encore ouverte.' : 'None of these picks is still open.', 'error'); return; }
    vibrate('tap');
    if (added + betSlip.length >= 2) setParlayMode(true);
    showNotification(lang === 'fr' ? `${added} sur ${legs.length} sélections copiées dans ton panier` : `${added} of ${legs.length} picks copied to your slip`);
  };

  const fetchScoutReport = useCallback(async (forceRefresh = false) => {
    setScoutLoading(true);
    setScoutError(false);
    try {
      const [reportData, { data: betData }] = await Promise.all([
        api.fetchScoutReport(forceRefresh),
        supabase.from('bets').select('bet_type, result').not('result', 'eq', 'pending').not('result', 'eq', 'cancelled').not('result', 'eq', 'voided')
      ]);
      const winRates = {};
      if (betData) {
        const counts = {};
        betData.forEach(b => {
          if (!counts[b.bet_type]) counts[b.bet_type] = { won: 0, total: 0 };
          counts[b.bet_type].total++;
          if (b.result === 'won') counts[b.bet_type].won++;
        });
        Object.entries(counts).forEach(([type, { won, total }]) => {
          if (total >= 3) winRates[type] = Math.round((won / total) * 100);
        });
      }
      if (!reportData || reportData.error) throw new Error(reportData?.error || 'empty scout report');
      setScoutReport({ ...reportData, winRates });
    } catch (e) {
      console.error('Scout report error:', e);
      setScoutError(true);
    } finally {
      setScoutLoading(false);
    }
  }, []);

  // Normalize player names by removing diacritics (ü→u, é→e, etc.)
  const normalizeName = (name) => {
    if (!name) return '';
    return name
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')  // Remove combining diacritical marks
      .replace(/ø/g, 'o')   // Danish/Norwegian
      .replace(/Ø/g, 'O')
      .replace(/ł/g, 'l')   // Polish
      .replace(/Ł/g, 'L')
      .replace(/đ/g, 'd')   // Croatian
      .replace(/Đ/g, 'D')
      .replace(/ß/g, 'ss')  // German sharp S
      .replace(/æ/g, 'ae')  // Danish/Norwegian
      .replace(/Æ/g, 'AE')
      .replace(/œ/g, 'oe')  // French
      .replace(/Œ/g, 'OE')
      .toLowerCase()
      .trim();
  };

  // Bet grading rules live in src/shared/betGrading.js (the server settles bets with the same file)
  const { samePlayerName } = betGrading;


  // Settle bets in real-time based on game data
  const settleBets = useCallback(async () => {
    if (!session) return;
    if (isSettlingRef.current) return;
    isSettlingRef.current = true;
    try {
      // The SERVER grades and pays the bets (server/settle.js, same rules as src/shared/betGrading.js).
      // The browser only asks it to look at this player's pending bets now — it never reports a result itself.
      const { settled } = await api.settleMyBets(session.user.id);
      if (settled && settled.length > 0) {
        fetchActiveBets();
        fetchBetHistory();
        fetchProfile();
      }
    } catch (error) {
      console.error('Error settling bets:', error);
    } finally {
      isSettlingRef.current = false;
    }
  }, [session, fetchActiveBets, fetchBetHistory, fetchProfile]);

  // Track if we've initialized
  const initializedRef = React.useRef(false);

  // Initial data fetch - only runs once when session is established
  useEffect(() => {
    if (session && !initializedRef.current) {
      initializedRef.current = true;
      fetchProfile();
      fetchGames();
      fetchPlayers();
      fetchActiveBets();
      fetchBetHistory();
      fetchLeaderboard();

      // Run initial settlement check after 2 seconds
      setTimeout(() => {
        settleBets();
      }, 2000);

      fetchRosters();
      fetchMLBGames();
      fetchNBAGames();

      // Also settle when app regains focus
      const handleVisibilityChange = () => {
        if (document.visibilityState === 'visible') {
          settleBets();
        }
      };
      document.addEventListener('visibilitychange', handleVisibilityChange);
      return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }
    if (!session) {
      initializedRef.current = false;
    }
  }, [session, fetchProfile, fetchGames, fetchPlayers, fetchRosters, fetchMLBGames, fetchNBAGames, fetchActiveBets, fetchBetHistory, fetchLeaderboard, settleBets]);

  // Keep refs current so intervals can access latest values without stale closure
  useEffect(() => { gamesRef.current = games; }, [games]);
  useEffect(() => { mlbGamesRef.current = mlbGames; }, [mlbGames]);
  useEffect(() => { nbaGamesRef.current = nbaGames; }, [nbaGames]);
  useEffect(() => { expandedGameRef.current = expandedGame; }, [expandedGame]);

  // Fetch injuries when games are loaded
  useEffect(() => {
    if (games.length > 0) {
      fetchInjuriesForGames(games);
    }
  }, [games, fetchInjuriesForGames]);

  // Separate effect for intervals - doesn't re-run on function changes
  useEffect(() => {
    if (!session) return;

    // Fetch live scores immediately on mount
    fetchLiveScores();

    // Refresh all sports + live scores every 10 seconds (with overlap guard)
    const refreshInterval = setInterval(async () => {
      if (isRefreshingRef.current) return; // Skip if previous cycle still running
      isRefreshingRef.current = true;
      try {
        await Promise.all([
          fetchGames(),
          fetchMLBGames(),
          fetchNBAGames(),
          fetchLiveScores(),
        ]);
        settleBets();
        // Check HRs for all live MLB games not currently expanded
        mlbGamesRef.current
          .filter(g => g.status === 'LIVE' && g.id !== expandedGameRef.current)
          .forEach(g => checkMLBHR(g.id));
      } finally {
        isRefreshingRef.current = false;
      }
    }, 10 * 1000);

    return () => {
      clearInterval(refreshInterval);
    };
  }, [session]); // Only depends on session - functions are called via closure

  // Secret "BARDOWN" keyword unlocks the Scout Report
  const scoutUnlockRef = useRef(null);
  scoutUnlockRef.current = () => showNotification(t.scoutUnlocked, 'success');
  useEffect(() => {
    const TARGET = 'bardown';
    const handler = (e) => {
      secretBuffer.current = (secretBuffer.current + e.key).slice(-TARGET.length).toLowerCase();
      if (secretBuffer.current === TARGET) {
        setActiveTab('scout');
        fetchScoutReport();
        scoutUnlockRef.current();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [fetchScoutReport]);

  // Auto-refresh stats panel every 10 seconds when a game is expanded
  useEffect(() => {
    if (!expandedGame) return;
    const statsInterval = setInterval(() => {
      const currentExpanded = expandedGameRef.current;
      if (!currentExpanded) return;
      // NHL stats — use gamesRef to avoid stale closure
      const nhlGame = gamesRef.current.find(g => g.id === currentExpanded);
      if (nhlGame && getGameStatus(nhlGame).live) fetchGameStats(currentExpanded);
      // MLB stats
      const mlbGame = mlbGamesRef.current.find(g => g.id === currentExpanded);
      if (mlbGame && getGameStatus(mlbGame).live) fetchMLBGameStats(currentExpanded);
      // NBA stats
      const nbaGame = nbaGamesRef.current.find(g => g.id === currentExpanded);
      if (nbaGame && getGameStatus(nbaGame).live) fetchNBAGameStats(currentExpanded, { home: nbaGame.home_abbrev, away: nbaGame.away_abbrev });
    }, 10 * 1000);
    return () => clearInterval(statsInterval);
  }, [expandedGame]); // stats refresh - uses refs to avoid stale closures

  useEffect(() => {
    if (!session) return;
    const channel = supabase
      .channel('bets-changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'bets', filter: `user_id=eq.${session.user.id}` }, (payload) => {
        if (payload.eventType === 'UPDATE' && payload.new.status === 'settled') {
          const won = payload.new.result === 'won';
          showNotification(won ? `${t.winner} +${formatMoney(parseFloat(payload.new.payout), lang)}` : t.betLost, won ? 'win' : 'error');
          fetchProfile();
          fetchActiveBets();
          fetchBetHistory();
        }
      })
      .subscribe();
    return () => supabase.removeChannel(channel);
  }, [session, fetchProfile, fetchActiveBets, fetchBetHistory, t]);

  const handleSignUp = async (e) => {
    e.preventDefault();
    setAuthError('');
    if (authForm.username.length < 3) { setAuthError(t.usernameMin); return; }
    const { data, error } = await supabase.auth.signUp({
      email: authForm.email,
      password: authForm.password,
      options: { data: { username: authForm.username } }
    });
    if (error) { setAuthError(error.message); return; }
    if (data.user) {
      await supabase.from('profiles').upsert({ id: data.user.id, username: authForm.username, balance: 1000.00 }, { onConflict: 'id' });
    }
    setAuthInfo(t.accountCreated);
    setAuthMode('login');
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setAuthError(''); setAuthInfo('');
    setAuthBusy(true);
    const { error } = await supabase.auth.resetPasswordForEmail(authForm.email, { redirectTo: window.location.origin });
    setAuthBusy(false);
    if (error) setAuthError(error.message);
    else setAuthInfo(t.resetSent);
  };

  const handleSetNewPassword = async (e) => {
    e.preventDefault();
    setAuthError('');
    if (newPw.pw.length < 6) { setAuthError(t.passwordTooShort); return; }
    if (newPw.pw !== newPw.confirm) { setAuthError(t.passwordsDontMatch); return; }
    setAuthBusy(true);
    const { error } = await supabase.auth.updateUser({ password: newPw.pw });
    setAuthBusy(false);
    if (error) { setAuthError(error.message); return; }
    setNewPw({ pw: '', confirm: '' });
    setPasswordRecovery(false);
    showNotification(t.passwordUpdated);
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    setAuthError(''); setAuthInfo('');
    const { error } = await supabase.auth.signInWithPassword({ email: authForm.email, password: authForm.password });
    if (error) setAuthError(error.message);
  };

  // Sign out on THIS device, right away. (The default "global" sign-out waits on a server call first;
  // when that call fails or hangs, nothing happened and you stayed logged in.)
  const handleSignOut = async () => {
    setSession(null);
    setProfile(null);
    setBetSlip([]);
    const clearStoredSession = () => {
      try { Object.keys(localStorage).filter((k) => k.startsWith('sb-') && k.includes('auth-token')).forEach((k) => localStorage.removeItem(k)); } catch (e) { /* private mode */ }
    };
    try {
      const res = await Promise.race([
        supabase.auth.signOut({ scope: 'local' }),
        new Promise((resolve) => setTimeout(() => resolve({ error: new Error('timeout') }), 3000)),
      ]);
      if (res?.error) clearStoredSession();
    } catch (e) {
      clearStoredSession();
    }
  };

  const canUseMulligan = (prof) => {
    if (!prof?.mulligan_used_at) return true;
    const used = new Date(prof.mulligan_used_at);
    const now = new Date();
    return used.getFullYear() !== now.getFullYear() || used.getMonth() !== now.getMonth();
  };

  const hasLoserTag = (prof) => {
    if (!prof?.loser_tag_expires_at) return false;
    return new Date(prof.loser_tag_expires_at) > new Date();
  };

  const handleMulligan = async () => {
    if (!canUseMulligan(profile)) return;
    const msg = lang === 'fr'
      ? 'Réinitialiser votre solde à 1 000 $? Vous recevrez le tag LOSER pendant 30 jours.'
      : 'Reset your balance to $1,000? You will get the LOSER tag on the leaderboard for 30 days.';
    if (!window.confirm(msg)) return;
    const now = new Date();
    const expiresAt = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
    const { error } = await supabase
      .from('profiles')
      .update({ balance: 1000, mulligan_used_at: now.toISOString(), loser_tag_expires_at: expiresAt.toISOString() })
      .eq('id', session.user.id);
    if (!error) {
      setProfile(prev => ({ ...prev, balance: 1000, mulligan_used_at: now.toISOString(), loser_tag_expires_at: expiresAt.toISOString() }));
      showNotification(lang === 'fr' ? 'Solde réinitialisé à 1 000 $.' : 'Balance reset to $1,000.', 'success');
    }
  };

  const addCustomPlayer = (gameId, nameOverride) => {
    const name = (nameOverride || newPlayerName).trim();
    if (!name) return;
    // an added player keeps his real roster id + team (logo, and his own odds)
    const known = rosterPlayers.find((p) => p.position !== 'T' && samePlayerName(p.name, name));
    const player = known ? { ...known, gameId } : { id: `custom-${Date.now()}`, name, gameId };
    setCustomPlayers([...customPlayers, player]);
    setNewPlayerName('');
  };

  // the odds engine's context: player rates + a way to find an NHL player by id or name
  const findNhlPlayer = (id, name) => {
    const nid = id != null ? String(id).split('-').pop() : null;
    return (nid && rosterPlayers.find((p) => String(p.id).split('-').pop() === nid))
      || (name && rosterPlayers.find((p) => p.position !== 'T' && samePlayerName(p.name, name)))
      || null;
  };
  const findNflPlayer = (gameId, playerId) => {
    const pr = nflProps[gameId];
    return pr ? [...pr.home, ...pr.away].find((p) => String(p.id) === String(playerId)) || null : null;
  };
  const oddsCtx = { nhlRates: playerRates.nhl?.players, nhlTeams: playerRates.nhl?.teams, nbaRates: playerRates.nba, mlbRates: playerRates.mlb, findNhlPlayer, findNflPlayer };

  // the slip's text for a bet (also used by the scout report)
  const describeBet = (game, betType, selection, details = {}) => {
    let description = '';
    switch (betType) {
      case 'winner': description = `${selection === 'home' ? game.home_team : game.away_team} ${lang === 'fr' ? 'gagne' : 'to win'}`; break;
      case 'puckline': description = `${details.team} ${details.line > 0 ? '+' : ''}${details.line}`; break;
      case 'total': description = `${details.type} ${details.line} ${game.sport === 'nba' ? (lang === 'fr' ? 'points' : 'points') : game.sport === 'mlb' ? (lang === 'fr' ? 'points' : 'runs') : (lang === 'fr' ? 'buts' : 'goals')}`; break;
      case 'runline': description = `${details.team} ${details.line > 0 ? '+' : ''}${details.line} ${lang === 'fr' ? 'points' : 'runs'}`; break;
      case 'first_goal': description = `${details.playerName} ${lang === 'fr' ? 'premier buteur' : 'first goal scorer'}`; break;
      case 'anytime_goal': description = `${details.playerName} ${lang === 'fr' ? 'buteur' : 'anytime goal scorer'}`; break;
      case 'player_points': description = `${details.playerName} ${lang === 'fr' ? 'enregistre un point' : 'to record a point'}`; break;
      case 'player_shots': description = `${details.playerName} ${details.type} ${details.line} ${t.shots}`; break;
      case 'total_shots': description = `${details.type} ${details.line} ${t.totalShots}`; break;
      case 'winning_goal': description = `${details.playerName} ${lang === 'fr' ? 'buteur gagnant' : 'winning goal scorer'}`; break;
      case 'team_first': description = `${selection === 'home' ? game.home_team : game.away_team} ${lang === 'fr' ? 'marque en premier' : 'to score first'}`; break;
      case 'period_winner': description = `${details.period} ${lang === 'fr' ? 'période' : 'period'}: ${details.pick}`; break;
      case 'both_score': description = lang === 'fr' ? (selection === 'yes' ? 'Les deux marquent - Oui' : 'Les deux marquent - Non') : (selection === 'yes' ? 'Both teams to score - Yes' : 'Both teams to score - No'); break;
      case 'exact_score': description = `${lang === 'fr' ? 'Score exact' : 'Correct score'}: ${details.home}-${details.away}`; break;
      case 'penalty_mins': description = `${details.type} ${details.line} ${lang === 'fr' ? 'min. pénalité' : 'penalty minutes'}`; break;
      case 'fighting': description = lang === 'fr' ? (selection === 'yes' ? 'Bagarre - Oui' : 'Bagarre - Non') : (selection === 'yes' ? 'Fight to occur - Yes' : 'Fight to occur - No'); break;
      case 'shutout': description = lang === 'fr' ? (selection === 'yes' ? 'Blanchissage - Oui' : 'Blanchissage - Non') : (selection === 'yes' ? 'Shutout - Yes' : 'Shutout - No'); break;
      case 'overtime': description = lang === 'fr' ? (selection === 'yes' ? 'Prolongation - Oui' : 'Prolongation - Non') : (selection === 'yes' ? 'Overtime - Yes' : 'Overtime - No'); break;
      case 'mlb_batter_hr': description = `${details.playerName} — O${details.line} ${lang === 'fr' ? 'Circuit(s)' : 'HR'}`; break;
      case 'mlb_batter_rbi': description = `${details.playerName} — O${details.line} ${lang === 'fr' ? 'Point(s) produit(s)' : 'RBI'}`; break;
      case 'mlb_batter_sb': description = `${details.playerName} — O${details.line} ${lang === 'fr' ? 'But(s) volé(s)' : 'SB'}`; break;
      case 'mlb_batter_1b': description = `${details.playerName} — O${details.line} ${lang === 'fr' ? 'Simpl(e)s' : 'Single(s)'}`; break;
      case 'mlb_batter_2b': description = `${details.playerName} — O${details.line} ${lang === 'fr' ? 'Double(s)' : 'Double(s)'}`; break;
      case 'mlb_batter_3b': description = `${details.playerName} — O${details.line} ${lang === 'fr' ? 'Triple(s)' : 'Triple(s)'}`; break;
      case 'mlb_batter_ab': description = `${details.playerName} — O${details.line} ${lang === 'fr' ? 'Présences au bâton' : 'At-Bats'}`; break;
      case 'mlb_batter_cycle': description = `${details.playerName} — ${lang === 'fr' ? 'Cycle complet' : 'Hit for the Cycle'}`; break;
      case 'mlb_pitcher_ks': description = `${details.playerName} — ${details.type} ${details.line} ${lang === 'fr' ? 'Retraits' : 'Strikeouts'}`; break;
      case 'mlb_team_hr': description = `${details.team} — Over ${details.line} ${lang === 'fr' ? "Circuits d'équipe" : 'Team HR'}`; break;
      case 'mlb_team_sb': description = `${details.team} — Over ${details.line} ${lang === 'fr' ? "Buts volés d'équipe" : 'Team SB'}`; break;
      case 'runline': description = `${details.team} ${details.line > 0 ? '+' : ''}${details.line} ${lang === 'fr' ? 'points' : 'runs'}`; break;
      case 'spread': description = `${details.team} ${details.line > 0 ? '+' : ''}${details.line} ${lang === 'fr' ? 'pts' : 'pts'}`; break;
      case 'nfl_anytime_td': description = `${details.playerName} — ${lang === 'fr' ? 'marque un touché' : 'anytime touchdown'}`; break;
      case 'nfl_first_td': description = `${details.playerName} — ${lang === 'fr' ? 'premier touché' : 'first touchdown'}`; break;
      case 'nfl_pass_yds': description = `${details.playerName} — ${details.type} ${details.line} ${lang === 'fr' ? 'verges par la passe' : 'passing yards'}`; break;
      case 'nfl_rush_yds': description = `${details.playerName} — ${details.type} ${details.line} ${lang === 'fr' ? 'verges au sol' : 'rushing yards'}`; break;
      case 'nfl_rec_yds': description = `${details.playerName} — ${details.type} ${details.line} ${lang === 'fr' ? 'verges sur réception' : 'receiving yards'}`; break;
      case 'nfl_half_winner': description = `${lang === 'fr' ? '1re demie' : '1st half'}: ${details.pick}`; break;
      case 'nfl_both_20': description = selection === 'yes' ? (lang === 'fr' ? 'Les deux équipes 20+ points' : 'Both teams 20+ points') : (lang === 'fr' ? 'Pas les deux équipes 20+' : 'Not both teams 20+'); break;
      case 'nfl_overtime': description = lang === 'fr' ? 'Prolongation - Oui' : 'Overtime - Yes'; break;
      case 'f1_winner': description = `${details.driver} ${lang === 'fr' ? 'gagne la course' : 'to win the race'}`; break;
      case 'f1_top3': description = `${details.driver} — ${lang === 'fr' ? 'top 3 (podium)' : 'top 3 (podium)'}`; break;
      case 'f1_top6': description = `${details.driver} — top 6`; break;
      case 'f1_top10': description = `${details.driver} — top 10`; break;
      case 'f1_h2h': description = `${details.driver} ${lang === 'fr' ? 'devant' : 'ahead of'} ${details.vsName}`; break;
      case 'f1_pole': description = `${details.driver} — pole position`; break;
      case 'f1_fastest_lap': description = `${details.driver} — ${lang === 'fr' ? 'tour le plus rapide' : 'fastest lap'}`; break;
      case 'f1_constructor': description = `${details.team} — ${lang === 'fr' ? 'équipe gagnante' : 'winning team'}`; break;
      case 'nba_player_pts':
      case 'nba_player_reb':
      case 'nba_player_ast':
      case 'nba_player_3pm':
      case 'nba_player_stl':
      case 'nba_player_blk':
      case 'nba_player_pra': {
        const lab = { nba_player_pts: 'PTS', nba_player_reb: 'REB', nba_player_ast: 'AST', nba_player_3pm: '3PM', nba_player_stl: 'STL', nba_player_blk: 'BLK', nba_player_pra: 'PTS+REB+AST' }[betType];
        description = `${details.playerName} — ${selection === 'under' ? 'U' : 'O'}${details.line} ${lab}`; break;
      }
      case 'nba_double_double': description = `${details.playerName} — ${lang === 'fr' ? 'double-double' : 'double-double'}: ${selection === 'no' ? (lang === 'fr' ? 'non' : 'no') : (lang === 'fr' ? 'oui' : 'yes')}`; break;
      case 'nba_triple_double': description = `${details.playerName} — ${lang === 'fr' ? 'triple-double' : 'triple-double'}: ${selection === 'no' ? (lang === 'fr' ? 'non' : 'no') : (lang === 'fr' ? 'oui' : 'yes')}`; break;
      case 'nba_top_scorer': description = `${details.playerName} — ${lang === 'fr' ? 'meilleur marqueur du match' : 'top scorer of the game'}`; break;
      case 'nba_first_basket': description = `${details.playerName} — ${lang === 'fr' ? 'premier panier' : 'first basket'}`; break;
      case 'nba_team_total': description = `${details.teamName} — ${selection === 'under' ? (lang === 'fr' ? 'moins de' : 'under') : (lang === 'fr' ? 'plus de' : 'over')} ${details.line} pts`; break;
      case 'nba_quarter_winner': description = `${details.teamName} — ${details.period === 'h1' ? (lang === 'fr' ? '1re demie' : '1st half') : (lang === 'fr' ? '1er quart' : '1st quarter')}`; break;
      case 'nba_half_total': description = `${lang === 'fr' ? '1re demie' : '1st half'} — ${selection === 'under' ? (lang === 'fr' ? 'moins de' : 'under') : (lang === 'fr' ? 'plus de' : 'over')} ${details.line} pts`; break;
      case 'nba_margin': description = `${details.teamName} ${lang === 'fr' ? 'par' : 'by'} ${details.band}`; break;
      case 'nba_overtime': description = `${lang === 'fr' ? 'Prolongation' : 'Overtime'}: ${selection === 'no' ? (lang === 'fr' ? 'non' : 'no') : (lang === 'fr' ? 'oui' : 'yes')}`; break;
      case 'nba_odd_even': description = `${lang === 'fr' ? 'Total' : 'Total points'}: ${selection === 'odd' ? (lang === 'fr' ? 'impair' : 'odd') : (lang === 'fr' ? 'pair' : 'even')}`; break;
      case 'nba_both_110': description = `${lang === 'fr' ? 'Les deux équipes 110+' : 'Both teams 110+'}: ${selection === 'no' ? (lang === 'fr' ? 'non' : 'no') : (lang === 'fr' ? 'oui' : 'yes')}`; break;
      default: description = selection;
    }
    return description;
  };

  // quiet: no messages (copying a revealed parlay says it once for all its picks); returns true if added
  const addToBetSlip = (gameIn, betType, selection, oddsIn, details = {}, stakeOverride = null, quiet = false) => {
    // Every bet is priced by the odds engine, whatever button it came from (scout, strategy…):
    // the real game (with its model) is looked up by id, and a bet it can't price isn't offered.
    const realGame = [...games, ...mlbGames, ...nbaGames, ...(nflGames || []), ...(f1Races || [])].find((g) => String(g.id) === String(gameIn.id));
    const game = realGame ? { ...gameIn, ...realGame } : gameIn;
    const quoted = quoteBet(game, betType, selection, details, oddsCtx);
    if (!quoted) {
      if (!quiet) showNotification(lang === 'fr' ? "Ce pari n'est pas disponible" : 'This bet is not available', 'error');
      return false;
    }
    const odds = quoted;
    // Allow betting on games that haven't started (FUT, PRE, scheduled, etc.)
    const nonBettableStatuses = ['FINAL', 'OFF', 'LIVE', 'CRIT'];
    if (nonBettableStatuses.includes(game.status)) {
      if (!quiet) showNotification(t.gameStarted, 'error');
      return false;
    }
    const betId = `${game.id}-${betType}-${selection}-${JSON.stringify(details)}`;
    if (betSlip.find(b => b.betId === betId)) {
      if (!quiet) showNotification(t.alreadyInSlip, 'error');
      return false;
    }
    const description = describeBet(game, betType, selection, details);
    if (!quiet) vibrate('tap');
    const gameLabel = game.sport === 'f1' ? `F1 · ${game.name}` : `${game.home_team} vs ${game.away_team}`;
    // functional update: several bets added in one go (scout "add all", parlay) all land in the slip
    // boost = how much these odds were boosted: a parlay takes the leg's normal odds (parlays are never boosted)
    const boost = betType === 'winner' ? moneylineBoost() : oddsBoost();
    setBetSlip((prev) => (prev.some((b) => b.betId === betId) ? prev : [...prev, { betId, gameId: game.id, game: gameLabel, betType, selection, description, odds, boost, stake: stakeOverride || 10, details }]));
    if (!quiet) showNotification(t.addedToSlip);
    return true;
  };

  const removeFromBetSlip = (betId) => setBetSlip(betSlip.filter(b => b.betId !== betId));
  const updateStake = (betId, stake) => setBetSlip(betSlip.map(b => b.betId === betId ? { ...b, stake: Math.max(0, parseFloat(stake) || 0) } : b));

  const [placingBets, setPlacingBets] = useState(false);
  const placingRef = useRef(false);   // blocks a double click even before React re-renders

  const placeBets = async () => {
    if (placingBets || placingRef.current) return; // Prevent double submission
    placingRef.current = true;
    try { await placeBetsNow(); } finally { placingRef.current = false; }
  };
  const placeBetsNow = async () => {

    if (!profile || !profile.id) {
      showNotification('Profile not loaded. Please refresh.', 'error');
      return;
    }
    if (boostPreview) {
      showNotification('Boost preview (?boost=2): bets are off on this page.', 'error');
      return;
    }

    if (parlayMode) {
      // ── PARLAY MODE (atomic via place_bet RPC) ──
      if (betSlip.length < 2) { showNotification(t.parlayMinLegs, 'error'); return; }
      if (parlayStake <= 0) { showNotification(t.enterStake, 'error'); return; }
      if (parlayStake > profile.balance) { showNotification(t.insufficientBalance, 'error'); return; }

      const combinedOdds = parseFloat(getParlayOdds(betSlip).toFixed(2));

      const legs = betSlip.map(b => ({
        gameId: b.gameId, game: b.game, betType: b.betType,
        selection: b.selection, description: b.description,
        odds: legOdds(b), details: b.details
      }));

      setPlacingBets(true);
      try {
        const { data, error } = await supabase.rpc('place_bet', {
          p_user_id: profile.id,
          p_game_id: String(betSlip[0].gameId),
          p_game_description: `${t.parlay} (${betSlip.length} ${betSlip.length > 1 ? t.parlayLegs : t.parlayLeg})`,
          p_selection: 'parlay',
          p_odds: combinedOdds,
          p_stake: parlayStake,
          p_bet_type: 'parlay',
          p_bet_value: JSON.stringify({ legs })
        });
        if (error) { showNotification(`Error: ${error.message}`, 'error'); return; }
        if (!data.success) { showNotification(data.error, 'error'); return; }

        setProfile(prev => prev ? { ...prev, balance: parseFloat(data.new_balance) } : prev);
        showNotification(t.parlayPlaced);
        vibrate('placed');
        setBetSlip([]);
        setParlayMode(false);
        fetchActiveBets();
      } catch (e) {
        console.error('Place parlay error:', e);
        showNotification('Error placing parlay', 'error');
      } finally {
        setPlacingBets(false);
      }
    } else {
      // ── SINGLES MODE (atomic via place_bets_batch RPC) ──
      const totalStakeAmount = betSlip.reduce((sum, bet) => sum + bet.stake, 0);
      if (totalStakeAmount > profile.balance) { showNotification(t.insufficientBalance, 'error'); return; }
      if (totalStakeAmount === 0 || betSlip.some(b => !(b.stake > 0))) { showNotification(t.enterStake, 'error'); return; }

      setPlacingBets(true);
      try {
        const betsPayload = betSlip.map(bet => ({
          game_id: String(bet.gameId),
          game_description: bet.game,
          selection: bet.selection,
          odds: bet.odds,
          stake: bet.stake,
          bet_type: bet.betType,
          bet_value: JSON.stringify(bet.details)
        }));

        const { data, error } = await supabase.rpc('place_bets_batch', {
          p_user_id: profile.id,
          p_bets: betsPayload
        });
        if (error) { showNotification(`Error: ${error.message}`, 'error'); return; }
        if (!data.success) { showNotification(data.error, 'error'); return; }

        setProfile(prev => prev ? { ...prev, balance: parseFloat(data.new_balance) } : prev);
        showNotification(`${betSlip.length} ${betSlip.length > 1 ? t.betsPlaced : t.betPlaced}`);
        vibrate('placed');
        setBetSlip([]);
        fetchActiveBets();
      } catch (e) {
        console.error('Place bet error:', e);
        showNotification('Error placing bet', 'error');
      } finally {
        setPlacingBets(false);
      }
    }
  };

  const formatGameTime = (dateStr, timeStr) => {
    try {
      const date = new Date(timeStr || dateStr);
      return fmtDateTime(date.toISOString(), lang);   // device time zone + 12 h / 24 h
    } catch { return dateStr; }
  };

  // Format bet description for display
  const formatBetDescription = (bet) => {
    try {
      const details = bet.bet_value ? JSON.parse(bet.bet_value) : {};
      const isFr = lang === 'fr';

      // Parse team names from game_description (format: "Home Team vs Away Team")
      let homeTeam = 'Home';
      let awayTeam = 'Away';
      if (bet.game_description) {
        const parts = bet.game_description.split(' vs ');
        if (parts.length === 2) {
          homeTeam = parts[0].trim();
          awayTeam = parts[1].trim();
        }
      }

      // Helper to get team name from selection
      const getTeamName = (selection) => {
        if (selection === 'home') return homeTeam;
        if (selection === 'away') return awayTeam;
        return selection;
      };

      switch (bet.bet_type) {
        case 'winner':
          return `${details.team || getTeamName(bet.selection)} ${isFr ? 'gagne' : 'to win'}`;
        case 'puckline':
          return `${details.team || getTeamName(bet.selection)} ${details.line > 0 ? '+' : ''}${details.line}`;
        case 'total':
          return `${details.type} ${details.line} ${isFr ? 'buts' : 'goals'}`;
        case 'first_goal':
          return `${details.playerName} - ${isFr ? 'Premier buteur' : 'First Goal Scorer'}`;
        case 'anytime_goal':
          return `${details.playerName} - ${isFr ? 'Buteur' : 'Anytime Goal'}`;
        case 'winning_goal':
          return `${details.playerName} - ${isFr ? 'But gagnant' : 'Winning Goal'}`;
        case 'player_points':
          return `${details.playerName} - ${isFr ? 'Enregistre un point' : 'To Record a Point'}`;
        case 'player_shots':
          return `${details.playerName} - ${details.type} ${details.line} ${isFr ? 'tirs' : 'shots'}`;
        case 'total_shots':
          return `${details.type} ${details.line} ${isFr ? 'tirs totaux' : 'total shots'}`;
        case 'team_first':
          return `${isFr ? 'Premier but' : 'First to Score'}: ${getTeamName(bet.selection)}`;
        case 'both_score':
          return `${isFr ? 'Les deux marquent' : 'Both Teams Score'}: ${bet.selection === 'yes' ? (isFr ? 'Oui' : 'Yes') : (isFr ? 'Non' : 'No')}`;
        case 'period_winner':
          return `${details.period} ${isFr ? 'période' : 'Period'}: ${details.pick}`;
        case 'overtime':
          return `${isFr ? 'Prolongation' : 'Overtime'}: ${bet.selection === 'yes' ? (isFr ? 'Oui' : 'Yes') : (isFr ? 'Non' : 'No')}`;
        case 'shutout':
          return `${isFr ? 'Blanchissage' : 'Shutout'}: ${bet.selection === 'yes' ? (isFr ? 'Oui' : 'Yes') : (isFr ? 'Non' : 'No')}`;
        case 'fighting':
          return `${isFr ? 'Bagarre' : 'Fight'}: ${bet.selection === 'yes' ? (isFr ? 'Oui' : 'Yes') : (isFr ? 'Non' : 'No')}`;
        case 'penalty_mins':
          return `${details.type} ${details.line} ${isFr ? 'min. pénalité' : 'penalty mins'}`;
        case 'exact_score':
          return `${isFr ? 'Score exact' : 'Correct Score'}: ${details.home}-${details.away}`;
        case 'mlb_team_hr':
          return `${details.team} — Over ${details.line} ${isFr ? "Circuits d'équipe" : 'Team HR'}`;
        case 'mlb_team_sb':
          return `${details.team} — Over ${details.line} ${isFr ? "Buts volés d'équipe" : 'Team SB'}`;
        case 'mlb_batter_hr':
          return `${details.playerName} — O${details.line} ${isFr ? 'Circuit(s)' : 'HR'}`;
        case 'mlb_batter_rbi':
          return `${details.playerName} — O${details.line} RBI`;
        case 'mlb_batter_sb':
          return `${details.playerName} — O${details.line} SB`;
        case 'mlb_batter_1b':
          return `${details.playerName} — O${details.line} ${isFr ? 'Simple(s)' : 'Single(s)'}`;
        case 'mlb_batter_2b':
          return `${details.playerName} — O${details.line} ${isFr ? 'Double(s)' : 'Double(s)'}`;
        case 'mlb_batter_3b':
          return `${details.playerName} — O${details.line} ${isFr ? 'Triple(s)' : 'Triple(s)'}`;
        case 'mlb_batter_ab':
          return `${details.playerName} — O${details.line} ${isFr ? 'Présences au bâton' : 'At-Bats'}`;
        case 'mlb_batter_cycle':
          return `${details.playerName} — ${isFr ? 'Cycle complet' : 'Hit for the Cycle'}`;
        case 'mlb_pitcher_ks':
          return `${details.playerName} — ${details.type} ${details.line} K`;
        case 'runline':
          return `${details.team} ${details.line > 0 ? '+' : ''}${details.line} ${isFr ? 'points' : 'runs'}`;
        case 'parlay':
          return `${isFr ? 'Combiné' : 'Combined'} (${details.legs?.length || 0} ${(details.legs?.length || 0) > 1 ? (isFr ? 'sélections' : 'legs') : (isFr ? 'sélection' : 'leg')})`;
        default:
          // every other bet type (NBA, NFL, F1, spreads): same words as when it was placed
          return describeBet({ home_team: homeTeam, away_team: awayTeam }, bet.bet_type, bet.selection, details);
      }
    } catch {
      return bet.selection;
    }
  };

  // Translate play-by-play descriptions for all sports
  const translatePlay = (play, sport) => {
    const p = play.player || '';

    if (sport === 'nba') {
      const typeMap = {
        '2pt': play.shotResult === 'Made' ? t.pbpMade : t.pbpMissed,
        '3pt': play.shotResult === 'Made' ? t.pbpMade : t.pbpMissed,
        'freethrow': t.pbpFreeThrow,
        'rebound': t.pbpRebound,
        'turnover': t.pbpTurnover,
        'foul': t.pbpFoul,
        'block': t.pbpBlock,
        'steal': t.pbpSteal,
        'substitution': t.pbpSubstitution,
        'timeout': t.pbpTimeout,
        'jumpball': t.pbpJumpBall,
        'violation': t.pbpViolation,
      };
      const typeLower = (play.type || '').toLowerCase();
      const action = typeMap[typeLower];
      if (action) {
        const detail = play.subType ? ` ${play.subType}` : '';
        if (typeLower === '2pt' || typeLower === '3pt') {
          return p ? `${p} — ${action} ${play.type}${detail}` : `${action} ${play.type}${detail}`;
        }
        return p ? `${p} — ${action}${detail}` : `${action}${detail}`;
      }
      return p ? `${p} — ${play.type || ''}` : play.type || '';
    }

    if (sport === 'mlb') {
      const typeMap = {
        home_run: t.pbpHomeRun, single: t.pbpSingle, double: t.pbpDouble, triple: t.pbpTriple,
        walk: t.pbpWalk, strikeout: t.pbpStrikeout, groundout: t.pbpGroundout,
        flyout: t.pbpFlyout, lineout: t.pbpLineout, pop_out: t.pbpPopOut,
        fielders_choice: t.pbpFieldersChoice, sac_fly: t.pbpSacFly, sac_bunt: t.pbpSacBunt,
        hit_by_pitch: t.pbpHitByPitch, field_error: t.pbpError, stolen_base: t.pbpStolenBase,
        caught_stealing: t.pbpCaughtStealing, double_play: t.pbpDoublePlay,
        triple_play: t.pbpTriplePlay, wild_pitch: t.pbpWildPitch, passed_ball: t.pbpPassedBall,
        balk: t.pbpBalk, force_out: t.pbpForceOut, field_out: t.pbpFieldOut,
        grounded_into_double_play: t.pbpDoublePlay, strikeout_double_play: t.pbpDoublePlay,
      };
      const action = typeMap[play.type];
      if (action) {
        const rbi = play.rbi ? ` (${play.rbi} RBI)` : '';
        return p ? `${p} — ${action}${rbi}` : `${action}${rbi}`;
      }
      return p ? `${p} — ${play.event || play.type}` : (play.event || play.type || '');
    }

    // NHL (default)
    const nhlTypeMap = {
      'goal': () => { const assists = (play.assists || []).join(', '); return p ? `${p} — ${t.pbpGoal}${assists ? ` (${assists})` : ''}` : t.pbpGoal; },
      'penalty': () => p ? `${p} — ${play.duration || 2}min ${play.infraction || t.pbpPenalty}` : `${play.duration || 2}min ${t.pbpPenalty}`,
      'shot-on-goal': () => p ? `${p} — ${t.pbpShotOnGoal}` : t.pbpShotOnGoal,
      'missed-shot': () => p ? `${p} — ${t.pbpMissedShot}` : t.pbpMissedShot,
      'blocked-shot': () => p ? `${p} — ${t.pbpBlockedShot}` : t.pbpBlockedShot,
      'hit': () => p ? `${p} ${t.pbpHitOn}${play.player2 ? ` ${play.player2}` : ''}` : t.pbpHit,
      'period-start': () => `${t.pbpPeriodStart} ${play.period}`,
      'period-end': () => `${t.pbpPeriodEnd} ${play.period}`,
      'stoppage': () => t.pbpStoppage,
      'game-end': () => '',
    };
    const handler = nhlTypeMap[play.type];
    if (handler) return handler();
    return play.desc || play.type || '';
  };

  const getGameStatus = (game) => {
    if (game.status === 'FINAL' || game.status === 'OFF') {
      return {
        text: `Final`,
        score: `${game.home_score}-${game.away_score}`,
        live: false,
        final: true
      };
    }
    if (game.status === 'LIVE' || game.status === 'CRIT') {
      const periodText = game.period === 1 ? '1st' : game.period === 2 ? '2nd' : game.period === 3 ? '3rd' : game.period > 3 ? 'OT' : '';
      const clockText = game.clock || '';
      const isIntermission = game.in_intermission || false;

      // Detect power play from situation object
      let powerPlay = null;
      const sit = game.situation;
      if (sit) {
        const homeOnPP = sit.homeTeam?.situationCode === 'PP';
        const awayOnPP = sit.awayTeam?.situationCode === 'PP';
        if (homeOnPP || awayOnPP) {
          powerPlay = {
            team: homeOnPP ? game.home_abbrev : game.away_abbrev,
            side: homeOnPP ? 'home' : 'away',
            timeRemaining: sit.timeRemaining || ''
          };
        }
      }

      return {
        text: lang === 'fr' ? 'EN DIRECT' : 'LIVE',
        period: periodText,
        clock: clockText,
        intermission: isIntermission,
        powerPlay,
        score: `${game.home_score || 0}-${game.away_score || 0}`,
        sog: game.home_sog && game.away_sog ? `SOG: ${game.home_sog}-${game.away_sog}` : '',
        situation: game.situation,
        live: true,
        final: false
      };
    }
    return { text: formatGameTime(game.game_date, game.game_time), live: false, final: false };
  };

  const isGameBettable = (game) => {
    // NHL API statuses: FUT (future), PRE (pre-game), LIVE, CRIT, FINAL, OFF
    // Allow betting on any game that hasn't started or finished
    const nonBettableStatuses = ['FINAL', 'OFF', 'LIVE', 'CRIT'];
    return !nonBettableStatuses.includes(game.status);
  };

  // Check if a bet can be cancelled (game hasn't started)
  const isBetCancellable = (bet) => {
    const game = games.find(g => String(g.id) === String(bet.game_id))
               || mlbGames.find(g => String(g.id) === String(bet.game_id))
               || nbaGames.find(g => String(g.id) === String(bet.game_id));
    if (!game) return true; // Show cancel button if game not found — server will validate
    const nonCancellableStatuses = ['FINAL', 'OFF', 'LIVE', 'CRIT'];
    return !nonCancellableStatuses.includes(game.status);
  };

  // Cancel a bet and refund the stake
  const cancelBet = async (bet) => {
    if (cancellingBetId) return;

    if (!window.confirm(t.cancelBetConfirm)) return;

    setCancellingBetId(bet.id);

    try {
      // Check if game has started using local state
      const game = games.find(g => String(g.id) === String(bet.game_id))
                || mlbGames.find(g => String(g.id) === String(bet.game_id))
                || nbaGames.find(g => String(g.id) === String(bet.game_id));
      // Only block if we FOUND the game and it has started — if game not found, let server validate
      if (game && ['LIVE', 'CRIT', 'FINAL', 'OFF'].includes(game.status)) {
        showNotification(t.cancelNotAllowed, 'error');
        setCancellingBetId(null);
        return;
      }

      // Call the cancel_bet database function
      const { data, error } = await supabase.rpc('cancel_bet', {
        p_bet_id: bet.id,
        p_user_id: session.user.id
      });

      if (error) {
        console.error('Cancel bet error:', error);
        showNotification(`Error: ${error.message}`, 'error');
        setCancellingBetId(null);
        return;
      }

      if (!data.success) {
        showNotification(data.error, 'error');
        setCancellingBetId(null);
        return;
      }

      // Success - update UI
      showNotification(`${t.betCancelled} +${formatMoney(parseFloat(data.refunded), lang)}`);
      fetchProfile();
      fetchActiveBets();

    } catch (e) {
      console.error('Cancel bet error:', e);
      showNotification('Error cancelling bet', 'error');
    } finally {
      setCancellingBetId(null);
    }
  };

  const getGamePlayers = (game) => {
    // Team name to abbreviation mapping - comprehensive list
    const teamMap = {
      // Canadian Teams
      'canadiens': 'MTL', 'montreal': 'MTL', 'montréal': 'MTL', 'habs': 'MTL', 'mtl': 'MTL',
      'maple leafs': 'TOR', 'toronto': 'TOR', 'leafs': 'TOR', 'tor': 'TOR',
      'oilers': 'EDM', 'edmonton': 'EDM', 'edm': 'EDM',
      'senators': 'OTT', 'ottawa': 'OTT', 'sens': 'OTT', 'ott': 'OTT',
      'canucks': 'VAN', 'vancouver': 'VAN', 'van': 'VAN',
      'jets': 'WPG', 'winnipeg': 'WPG', 'wpg': 'WPG',
      'flames': 'CGY', 'calgary': 'CGY', 'cgy': 'CGY',
      // US East
      'bruins': 'BOS', 'boston': 'BOS', 'bos': 'BOS',
      'sabres': 'BUF', 'buffalo': 'BUF', 'buf': 'BUF',
      'red wings': 'DET', 'detroit': 'DET', 'det': 'DET', 'wings': 'DET',
      'panthers': 'FLA', 'florida': 'FLA', 'fla': 'FLA',
      'lightning': 'TBL', 'tampa': 'TBL', 'tampa bay': 'TBL', 'tbl': 'TBL',
      'hurricanes': 'CAR', 'carolina': 'CAR', 'car': 'CAR', 'canes': 'CAR',
      'capitals': 'WSH', 'washington': 'WSH', 'wsh': 'WSH', 'caps': 'WSH',
      'flyers': 'PHI', 'philadelphia': 'PHI', 'phi': 'PHI',
      'penguins': 'PIT', 'pittsburgh': 'PIT', 'pit': 'PIT', 'pens': 'PIT',
      'rangers': 'NYR', 'new york r': 'NYR', 'nyr': 'NYR', 'ny rangers': 'NYR',
      'islanders': 'NYI', 'new york i': 'NYI', 'nyi': 'NYI', 'ny islanders': 'NYI', 'isles': 'NYI',
      'devils': 'NJD', 'new jersey': 'NJD', 'njd': 'NJD',
      'blue jackets': 'CBJ', 'columbus': 'CBJ', 'cbj': 'CBJ', 'jackets': 'CBJ',
      // US West
      'avalanche': 'COL', 'colorado': 'COL', 'col': 'COL', 'avs': 'COL',
      'golden knights': 'VGK', 'vegas': 'VGK', 'vgk': 'VGK', 'knights': 'VGK',
      'stars': 'DAL', 'dallas': 'DAL', 'dal': 'DAL',
      'wild': 'MIN', 'minnesota': 'MIN', 'min': 'MIN',
      'predators': 'NSH', 'nashville': 'NSH', 'nsh': 'NSH', 'preds': 'NSH',
      'blues': 'STL', 'st. louis': 'STL', 'st louis': 'STL', 'stl': 'STL',
      'blackhawks': 'CHI', 'chicago': 'CHI', 'chi': 'CHI', 'hawks': 'CHI',
      'kraken': 'SEA', 'seattle': 'SEA', 'sea': 'SEA',
      'ducks': 'ANA', 'anaheim': 'ANA', 'ana': 'ANA',
      'kings': 'LAK', 'los angeles': 'LAK', 'la kings': 'LAK', 'lak': 'LAK', 'la': 'LAK',
      'sharks': 'SJS', 'san jose': 'SJS', 'sjs': 'SJS',
      'coyotes': 'UTA', 'utah': 'UTA', 'arizona': 'ARI', 'ari': 'ARI', 'uta': 'UTA', 'utah hockey': 'UTA',
    };

    const getTeamAbbrev = (teamName) => {
      if (!teamName) return '';
      const lower = teamName.toLowerCase().trim();

      // Check if teamName itself is an abbreviation (3 letters)
      if (lower.length === 3) {
        const upperName = lower.toUpperCase();
        const allAbbrevs = [...new Set(Object.values(teamMap))];
        if (allAbbrevs.includes(upperName)) return upperName;
      }

      // First try exact match
      if (teamMap[lower]) return teamMap[lower];

      // Then try partial match - check if team name contains key
      for (const [key, abbrev] of Object.entries(teamMap)) {
        if (lower.includes(key)) return abbrev;
      }

      // Try reverse - check if key contains team name
      for (const [key, abbrev] of Object.entries(teamMap)) {
        if (key.includes(lower)) return abbrev;
      }

      console.warn('⚠️ No team match found for:', teamName);
      return '';
    };

    // Use direct abbreviations from API if available, otherwise look up
    const homeAbbrev = game.home_abbrev || getTeamAbbrev(game.home_team);
    const awayAbbrev = game.away_abbrev || getTeamAbbrev(game.away_team);



    // Get TOP scorers from each team (5 home + 4 away = 9 total)
    // Try rosterPlayers first; if empty (abbrev mismatch), fall back to defaultPlayers
    let homePlayers = rosterPlayers.filter(p => p.team === homeAbbrev && p.position !== 'G').sort((a, b) => (b.points || 0) - (a.points || 0)).slice(0, 5);
    let awayPlayers = rosterPlayers.filter(p => p.team === awayAbbrev && p.position !== 'G').sort((a, b) => (b.points || 0) - (a.points || 0)).slice(0, 4);
    if (homePlayers.length === 0 && homeAbbrev) homePlayers = defaultPlayers.filter(p => p.team === homeAbbrev && p.position !== 'G').slice(0, 5);
    if (awayPlayers.length === 0 && awayAbbrev) awayPlayers = defaultPlayers.filter(p => p.team === awayAbbrev && p.position !== 'G').slice(0, 4);

    // Get custom players for this game
    const gameCustomPlayers = customPlayers.filter(p => p.gameId === game.id);

    // Combine: 5 home + 4 away + custom players
    const shown = new Set([...homePlayers, ...awayPlayers].map((p) => String(p.id)));
    return [...homePlayers, ...awayPlayers, ...gameCustomPlayers.filter((p) => !shown.has(String(p.id)))];
  };

  const sourceGames = selectedSport === 'mlb' ? mlbGames : selectedSport === 'nba' ? nbaGames
    : selectedSport === 'nfl' ? (nflGames || []) : selectedSport === 'f1' ? (f1Races || []) : games;
  const noFavorites = selectedSport === 'f1';   // F1 races aren't between two teams: the favorite team is marked in the standings instead
  const currentFav = noFavorites ? null : selectedSport === 'mlb' ? favoriteMLB : selectedSport === 'nba' ? favoriteNBA : selectedSport === 'nfl' ? favoriteNFL : favoriteNHL;

  // Does a game match a quick filter? (shared by the game list and the filter counts)
  const gameMatchesFilter = useMemo(() => {
    const getLocalDateFromGame = (gameTime) => {
      if (!gameTime) return null;
      const d = new Date(gameTime);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    };
    const now = new Date();
    const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    const tomorrowDate = new Date(now);
    tomorrowDate.setDate(tomorrowDate.getDate() + 1);
    const tomorrowStr = `${tomorrowDate.getFullYear()}-${String(tomorrowDate.getMonth() + 1).padStart(2, '0')}-${String(tomorrowDate.getDate()).padStart(2, '0')}`;

    return (game, f) => {
      const gameLocalDate = getLocalDateFromGame(game.game_time) || game.game_date;
      if (f === 'today') return gameLocalDate === todayStr;
      if (f === 'tomorrow') return gameLocalDate === tomorrowStr;
      if (f === 'live') return game.status === 'LIVE' || game.status === 'CRIT';
      if (f === 'canadiens') return selectedSport === 'nhl' && game.canadiens_playing;
      if (f === 'favorites') {
        if (noFavorites) return false;
        if (!currentFav) return selectedSport === 'nhl' && game.canadiens_playing;
        return game.home_abbrev === currentFav || game.away_abbrev === currentFav;
      }
      return true;
    };
  }, [selectedSport, currentFav, noFavorites]);

  // how many games each quick filter shows (for the counts next to them)
  const filterCounts = useMemo(() => {
    const c = {};
    ['all', 'today', 'tomorrow', 'live', 'favorites'].forEach((f) => { c[f] = sourceGames.filter((g) => gameMatchesFilter(g, f)).length; });
    return c;
  }, [sourceGames, gameMatchesFilter]);

  // Memoize filtered + sorted games to avoid recalculating on every render
  const filteredGames = useMemo(() => {
    const filtered = sourceGames.filter(game => gameMatchesFilter(game, filter));

    // Sort: fav+live → live → fav+upcoming → today's upcoming → today's finished → later days
    const today = new Date().toDateString();
    const lowerOf = (g, fav) => {
      if (g.status === 'OFF' || g.status === 'FINAL') return -0.5 - fav;
      return !fav && new Date(g.game_time).toDateString() !== today ? -1 : 0;
    };
    filtered.sort((a, b) => {
      const aFav = currentFav && (a.home_abbrev === currentFav || a.away_abbrev === currentFav) ? 1 : 0;
      const bFav = currentFav && (b.home_abbrev === currentFav || b.away_abbrev === currentFav) ? 1 : 0;
      const aLive = (a.status === 'LIVE' || a.status === 'CRIT') ? 1 : 0;
      const bLive = (b.status === 'LIVE' || b.status === 'CRIT') ? 1 : 0;
      const aPri = aLive * 2 + aFav + lowerOf(a, aFav);
      const bPri = bLive * 2 + bFav + lowerOf(b, bFav);
      return bPri - aPri;
    });

    return filtered;
  }, [sourceGames, filter, currentFav, gameMatchesFilter]);

  // Reset filter (and back to the games) when switching sports
  useEffect(() => { setFilter('all'); setSportView('games'); }, [selectedSport]);

  // Reset collection pagination when filter changes
  useEffect(() => { setCollectionVisible(50); }, [collectionFilter]);

  // Memoize collection: filter + compute values + sort (once, not per-render)
  const processedCollection = useMemo(() => {
    const market = getDailyMarket();
    return userCards
      .filter(uc => {
        if (collectionFilter === 'all') return true;
        if (collectionFilter === 'base') return uc.card_type === 'base';
        if (collectionFilter === 'rare') return ['rare_999', 'rare_dual', 'rare_patch', 'rare_auto', 'chrome_auto', 'auto_patch'].includes(uc.card_type);
        if (collectionFilter === 'legendary') return ['patch_auto', 'dual_patch'].includes(uc.card_type);
        return true;
      })
      .map(uc => {
        const baseVal = calculateCardValue(uc.card_data, uc.card_type, uc.numbering);
        return { ...uc, _baseVal: baseVal, _marketVal: applyMarket(baseVal, market) };
      })
      .sort((a, b) => b._baseVal - a._baseVal);
  }, [userCards, collectionFilter]);

  // NFL games: loaded all the time (banner + NFL tab), refreshed every minute; a game's players when it's opened
  useEffect(() => {
    if (!session) return;
    const load = () => api.fetchNFLGames().then((d) => d && setNflGames(demoNfl(d))).catch(() => {});
    load();
    const id = setInterval(load, 60 * 1000);
    return () => clearInterval(id);
  }, [session]);
  useEffect(() => {
    if (selectedSport !== 'nfl' || !expandedGame || nflProps[expandedGame]) return;
    const g = (nflGames || []).find((x) => x.id === expandedGame);
    if (!g) return;
    api.fetchNFLProps(g.home_abbrev, g.away_abbrev).then((d) => d && setNflProps((p) => ({ ...p, [g.id]: d }))).catch(() => {});
  }, [selectedSport, expandedGame, nflGames, nflProps]);

  // F1 races: loaded all the time (banner + F1 tab), refreshed every minute (status, odds, results)
  useEffect(() => {
    if (!session) return;
    // races get a game_time (= lights out) so the quick filters and the banner treat them like games
    const load = () => api.fetchF1Races().then((d) => d && setF1Races(demoF1(d.map((r) => ({ ...r, game_time: r.race_time }))))).catch(() => {});
    load();
    const id = setInterval(load, 60 * 1000);
    return () => clearInterval(id);
  }, [session]);

  // Player rates for the odds engine (NHL + NBA: every player, one call each)
  useEffect(() => {
    if (!session) return;
    // right after a server restart the NHL stats are still warming up: try again every 90 s until they're in
    let nhlTimer = null, stopped = false;
    const loadNhl = () => api.fetchPlayerRates('nhl').then((d) => {
      if (stopped) return;
      if (d && d.players && Object.keys(d.players).length) setPlayerRates((p) => ({ ...p, nhl: d }));
      else nhlTimer = setTimeout(loadNhl, 90 * 1000);
    }).catch(() => { if (!stopped) nhlTimer = setTimeout(loadNhl, 90 * 1000); });
    loadNhl();
    api.fetchPlayerRates('nba').then((d) => d && setPlayerRates((p) => ({ ...p, nba: d }))).catch(() => {});
    return () => { stopped = true; clearTimeout(nhlTimer); };
  }, [session]);
  // MLB batters' rates, per team, when a game is opened
  useEffect(() => {
    if (!expandedGame || selectedSport !== 'mlb') return;
    const game = mlbGames.find(g => g.id === expandedGame);
    if (!game) return;
    [game.home_team_id, game.away_team_id].filter(id => id && !mlbRatesFetched.current.has(String(id))).forEach((id) => {
      mlbRatesFetched.current.add(String(id));
      api.fetchPlayerRates(`mlb/${id}`).then((d) => d && setPlayerRates((p) => ({ ...p, mlb: { ...p.mlb, ...d } }))).catch(() => {});
    });
  }, [expandedGame, selectedSport, mlbGames]);

  // Fetch MLB rosters when a game is expanded
  useEffect(() => {
    if (!expandedGame || selectedSport !== 'mlb') return;
    const game = mlbGames.find(g => g.id === expandedGame);
    if (!game) return;
    const toFetch = [game.home_team_id, game.away_team_id].filter(id => id && !mlbRosterFetched.current.has(String(id)));
    if (toFetch.length === 0) return;
    toFetch.forEach(id => mlbRosterFetched.current.add(String(id)));
    Promise.all(toFetch.map(id => api.fetchMLBRoster(id).catch(() => ({ batters: [], pitchers: [] }))))
      .then(results => {
        const updates = {};
        toFetch.forEach((id, i) => { updates[String(id)] = results[i]; });
        setMlbRosters(prev => ({ ...prev, ...updates }));
      });
  }, [expandedGame, selectedSport, mlbGames]);

  // Fetch NBA rosters when a game is expanded (on-demand)
  useEffect(() => {
    if (!expandedGame || selectedSport !== 'nba') return;
    const game = nbaGames.find(g => g.id === expandedGame);
    if (!game) return;
    const toFetch = [game.home_abbrev, game.away_abbrev].filter(a => a && !nbaRosterFetched.current.has(a));
    if (toFetch.length === 0) return;
    toFetch.forEach(a => nbaRosterFetched.current.add(a));
    Promise.all(toFetch.map(a => api.fetchNBARoster(a).catch(() => ({ players: [] }))))
      .then(results => {
        const updates = {};
        toFetch.forEach((a, i) => { updates[a] = results[i]; });
        setNbaRosters(prev => ({ ...prev, ...updates }));
      });
  }, [expandedGame, selectedSport, nbaGames]);

  // Scout report: while it's open, load the player data of every upcoming game (normally loaded
  // only when a game is opened) so it can scan the player props too
  useEffect(() => {
    if (activeTab !== 'scout') return;
    const soon = (g) => g && !['FINAL', 'OFF', 'LIVE', 'CRIT'].includes(g.status) && new Date(g.game_time).getTime() - Date.now() < 40 * 3600 * 1000;
    const mlbIds = mlbGames.filter(soon).flatMap((g) => [g.home_team_id, g.away_team_id]).filter(Boolean).map(String);
    const rateIds = mlbIds.filter((id) => !mlbRatesFetched.current.has(id));
    rateIds.forEach((id) => {
      mlbRatesFetched.current.add(id);
      api.fetchPlayerRates(`mlb/${id}`).then((d) => d && setPlayerRates((p) => ({ ...p, mlb: { ...p.mlb, ...d } }))).catch(() => {});
    });
    const rosterIds = mlbIds.filter((id) => !mlbRosterFetched.current.has(id));
    rosterIds.forEach((id) => mlbRosterFetched.current.add(id));
    if (rosterIds.length) Promise.all(rosterIds.map((id) => api.fetchMLBRoster(id).catch(() => ({ batters: [], pitchers: [] }))))
      .then((res) => setMlbRosters((prev) => ({ ...prev, ...Object.fromEntries(rosterIds.map((id, i) => [id, res[i]])) })));
    const nbaAbbrevs = [...new Set(nbaGames.filter(soon).flatMap((g) => [g.home_abbrev, g.away_abbrev]).filter(Boolean))].filter((a) => !nbaRosterFetched.current.has(a));
    nbaAbbrevs.forEach((a) => nbaRosterFetched.current.add(a));
    if (nbaAbbrevs.length) Promise.all(nbaAbbrevs.map((a) => api.fetchNBARoster(a).catch(() => ({ players: [] }))))
      .then((res) => setNbaRosters((prev) => ({ ...prev, ...Object.fromEntries(nbaAbbrevs.map((a, i) => [a, res[i]])) })));
    (nflGames || []).filter((g) => g.status === 'FUT' && g.model && !nflProps[g.id] && !nflPropsAsked.current.has(g.id)).forEach((g) => {
      nflPropsAsked.current.add(g.id);
      api.fetchNFLProps(g.home_abbrev, g.away_abbrev).then((d) => d && setNflProps((p) => ({ ...p, [g.id]: d }))).catch(() => {});
    });
  }, [activeTab, mlbGames, nbaGames, nflGames, nflProps]);

  // every bet of every upcoming game, scored (only computed while the report is open)
  const scoutBets = useMemo(() => {
    if (activeTab !== 'scout') return [];
    const all = scanBets({ games, mlbGames, nbaGames, nflGames, f1Races, rosterPlayers, gameInjuries, mlbRosters, nbaRosters, nflProps, playerRates }, oddsCtx);
    return scoreBets(all, scoutSignals(scoutReport, { games, mlbGames, nbaGames, nflGames }));
  }, [activeTab, games, mlbGames, nbaGames, nflGames, f1Races, rosterPlayers, gameInjuries, mlbRosters, nbaRosters, nflProps, playerRates, scoutReport]);

  // The Scout's track record: save the picks it shows (top 10 of each list + the day's plan), once each, at
  // the odds of that moment — the server grades them after the game (server/settle.js gradeScoutPicks)
  useEffect(() => {
    if (activeTab !== 'scout' || !profile?.id || scoutLoading || !scoutReport || !scoutBets.length) return;
    const rows = [];
    const startsAt = (g) => new Date(g.game_time || g.race_time || 0).getTime();
    const add = (list, b) => {
      const k = `${list}|${b.key}`;
      if (scoutLogged.current.has(k) || !startsAt(b.game)) return;
      scoutLogged.current.add(k);
      rows.push({
        list, sport: b.sport, game_id: String(b.game.id), game_time: new Date(startsAt(b.game)).toISOString(),
        bet_type: b.betType, selection: b.selection, bet_value: JSON.stringify(b.details || {}),
        description: describeBet(b.game, b.betType, b.selection, b.details), odds: b.odds,
        chance: Math.round(b.chance * 1000) / 1000, vegas: b.vegas == null ? null : Math.round(b.vegas * 1000) / 1000,
        value: b.value == null ? null : Math.round(b.value * 1000) / 1000,
      });
    };
    ['value', 'safest', 'long'].forEach((m) => rankBets(scoutBets, m, 'all', 10).forEach((b) => add(m, b)));
    (buildPlan(scoutBets, parseFloat(profile.balance) || 0, 'normal')?.legs || []).forEach((b) => add('plan', b));
    const future = rows.filter((r) => new Date(r.game_time).getTime() > Date.now());
    if (future.length) supabase.from('scout_picks').upsert(future, { onConflict: 'list,game_id,bet_type,selection,bet_value', ignoreDuplicates: true }).then(() => {});
  }, [activeTab, profile?.id, profile?.balance, scoutLoading, scoutReport, scoutBets]);
  useEffect(() => {
    if (activeTab !== 'scout') return;
    const since = new Date(Date.now() - 30 * 24 * 3600 * 1000).toISOString();
    supabase.from('scout_picks').select('list, odds, result').gte('game_time', since).limit(5000)
      .then(({ data }) => setScoutRecord(data || []));
  }, [activeTab, scoutReport]);

  const totalStake = betSlip.reduce((sum, bet) => sum + bet.stake, 0);
  const totalPotentialWin = betSlip.reduce((sum, bet) => sum + (bet.stake * bet.odds), 0);

  // Loading Screen
  if (loading) {
    return (
      <div className="app loading-screen">
        <div className="logo-lg">BarDownBets.com</div>
        <div className="loader"></div>
      </div>
    );
  }

  // Auth Screen — split: brand panel (left) · form (right). On phones the brand panel is a strip on top.
  if (!session || passwordRecovery) {
    const switchMode = (mode) => { setAuthMode(mode); setAuthError(''); setAuthInfo(''); };
    const Icon = ({ d }) => (
      <svg className="lg-perk-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{d}</svg>
    );
    const perks = [
      [t.loginPerkBets, <><path d="M3 7h18v4a2 2 0 0 0 0 4v4H3v-4a2 2 0 0 0 0-4z" /><path d="M13 7v12" strokeDasharray="2 2" /></>],
      [t.loginPerkPools, <><circle cx="9" cy="8" r="3" /><path d="M3 20c0-3.3 2.7-6 6-6s6 2.7 6 6" /><circle cx="17" cy="9" r="2.5" /><path d="M16 14.2c2.8.3 5 2.7 5 5.8" /></>],
      [t.loginPerkBoard, <><path d="M8 21h8M12 17v4" /><path d="M7 4h10v5a5 5 0 0 1-10 0z" /><path d="M7 6H4a3 3 0 0 0 3 4M17 6h3a3 3 0 0 1-3 4" /></>],
      [t.loginPerkStart, <><circle cx="12" cy="12" r="9" /><path d="M14.8 9.2c-.5-.8-1.6-1.2-2.8-1.2-1.7 0-2.8.8-2.8 2s1.1 1.7 2.8 2 2.8.8 2.8 2-1.1 2-2.8 2c-1.3 0-2.4-.5-2.9-1.3M12 6.5V8m0 8v1.5" /></>],
    ];
    const recovering = passwordRecovery && session;
    return (
      <div className="app lg-screen">
        <aside className="lg-brand">
          <div className="lg-brand-inner">
            <img className="lg-logo" src={logoSrc} alt="BarDownBets.com" />
            <p className="lg-tagline">{t.loginTagline}</p>
            <ul className="lg-perks">
              {perks.map(([label, d]) => <li key={label}><Icon d={d} /><span>{label}</span></li>)}
            </ul>
            <div className="lg-leagues">
              {['NHL', 'NBA', 'MLB'].map((l) => <span key={l}>{l}</span>)}
            </div>
          </div>
        </aside>

        <main className="lg-panel">
          <div className="lang-switch lg-lang">
            <button className={lang === 'en' ? 'active' : ''} onClick={() => changeLang('en')}>EN</button>
            <button className={lang === 'fr' ? 'active' : ''} onClick={() => changeLang('fr')}>FR</button>
          </div>
          <div className="lg-card">
            {recovering ? (
              <form onSubmit={handleSetNewPassword} className="lg-form">
                <h2 className="lg-title">{t.newPasswordTitle}</h2>
                {authError && <div className="lg-msg error">{authError}</div>}
                <label className="lg-field"><span>{t.newPassword}</span>
                  <input type="password" autoComplete="new-password" value={newPw.pw} onChange={(e) => setNewPw({ ...newPw, pw: e.target.value })} required />
                </label>
                <label className="lg-field"><span>{t.confirmPassword}</span>
                  <input type="password" autoComplete="new-password" value={newPw.confirm} onChange={(e) => setNewPw({ ...newPw, confirm: e.target.value })} required />
                </label>
                <button type="submit" className="lg-btn" disabled={authBusy}>{t.savePassword}</button>
              </form>
            ) : authMode === 'forgot' ? (
              <form onSubmit={handleForgotPassword} className="lg-form">
                <h2 className="lg-title">{t.resetTitle}</h2>
                <p className="lg-sub">{t.resetDesc}</p>
                {authError && <div className="lg-msg error">{authError}</div>}
                {authInfo && <div className="lg-msg ok">{authInfo}</div>}
                <label className="lg-field"><span>{t.email}</span>
                  <input type="email" autoComplete="email" placeholder={t.enterEmail} value={authForm.email} onChange={(e) => setAuthForm({ ...authForm, email: e.target.value })} required />
                </label>
                <button type="submit" className="lg-btn" disabled={authBusy}>{t.sendResetLink}</button>
                <button type="button" className="lg-link lg-back" onClick={() => switchMode('login')}>{t.backToSignIn}</button>
              </form>
            ) : (
              <>
                <div className="lg-tabs">
                  <button className={authMode === 'login' ? 'active' : ''} onClick={() => switchMode('login')}>{t.signIn}</button>
                  <button className={authMode === 'register' ? 'active' : ''} onClick={() => switchMode('register')}>{t.signUp}</button>
                </div>
                <form onSubmit={authMode === 'login' ? handleSignIn : handleSignUp} className="lg-form">
                  {authError && <div className="lg-msg error">{authError}</div>}
                  {authInfo && <div className="lg-msg ok">{authInfo}</div>}
                  {authMode === 'register' && (
                    <label className="lg-field"><span>{t.username}</span>
                      <input type="text" autoComplete="username" placeholder={t.chooseName} value={authForm.username} onChange={(e) => setAuthForm({ ...authForm, username: e.target.value })} required />
                    </label>
                  )}
                  <label className="lg-field"><span>{t.email}</span>
                    <input type="email" autoComplete="email" placeholder={t.enterEmail} value={authForm.email} onChange={(e) => setAuthForm({ ...authForm, email: e.target.value })} required />
                  </label>
                  <label className="lg-field"><span>{t.password}</span>
                    <input type="password" autoComplete={authMode === 'login' ? 'current-password' : 'new-password'} placeholder={t.enterPassword} value={authForm.password} onChange={(e) => setAuthForm({ ...authForm, password: e.target.value })} required />
                  </label>
                  {authMode === 'login' && (
                    <div className="lg-row">
                      <label className="lg-check">
                        <input type="checkbox" checked={authForm.stayLoggedIn} onChange={(e) => setAuthForm({ ...authForm, stayLoggedIn: e.target.checked })} />
                        {t.staySignedIn}
                      </label>
                      <button type="button" className="lg-link" onClick={() => switchMode('forgot')}>{t.forgotPassword}</button>
                    </div>
                  )}
                  <button type="submit" className="lg-btn">{authMode === 'login' ? t.signIn : t.createAccount}</button>
                  {authMode === 'register' && <p className="lg-bonus">{t.bonus}</p>}
                </form>
              </>
            )}
          </div>
        </main>
      </div>
    );
  }

  const sports = [
    { id: 'nhl', label: 'NHL', icon: '🏒' },
    { id: 'nba', label: 'NBA', icon: '🏀' },
    { id: 'mlb', label: 'MLB', icon: '⚾' },
    { id: 'nfl', label: 'NFL', icon: '🏈' },
    { id: 'f1',  label: 'F1',  icon: '🏎' },
  ];

  // Derive favorite team logo for background watermark (uses theme team, global)
  const favTeamLogo = (() => {
    if (!themeTeam || !themeTeamSport) return null;
    if (themeTeamSport === 'nhl') return `https://assets.nhle.com/logos/nhl/svg/${themeTeam}_light.svg`;
    if (themeTeamSport === 'nfl' || themeTeamSport === 'f1') return teamLogoUrl(themeTeamSport, themeTeam);
    if (themeTeamSport === 'nba') {
      const nbaId = NBA_TEAM_IDS[themeTeam];
      if (nbaId) return `https://cdn.nba.com/logos/nba/${nbaId}/global/L/logo.svg`;
      return null;
    }
    // MLB: try loaded games first, fall back to static team ID map
    const mlbGame = mlbGames.find(g => g.home_abbrev === themeTeam || g.away_abbrev === themeTeam);
    if (mlbGame) return mlbGame.home_abbrev === themeTeam ? mlbGame.home_logo : mlbGame.away_logo;
    const mlbId = MLB_TEAM_IDS[themeTeam];
    if (mlbId) return `https://www.mlbstatic.com/team-logos/${mlbId}.svg`;
    return null;
  })();

  // Wallet summary shown at the top of My Bets (staked / potential)
  // short injury status for the game view: out / day-to-day / injured
  const injuryShort = (st) => {
    const k = String(st || '').toLowerCase().replace(/[^a-z]/g, '');
    if (k === 'out' || k === 'o') return t.out.toLowerCase();
    if (k === 'daytoday' || k === 'dtd' || k === 'questionable' || k === 'd') return lang === 'fr' ? 'JàJ' : 'DTD';
    if (k === 'injured' || k === 'ir') return t.injured.toLowerCase();
    return st;
  };

  // Quick filters: label · game count; Live gets a live dot, Favorites your team's logo
  // no games to show: say why, and offer "show all" when a quick filter is hiding them
  const renderNoGames = (sportLabel) => {
    const fr = lang === 'fr';
    if (filter !== 'all' && sourceGames.length > 0) {
      return <EmptyState icon="calendar" title={fr ? 'Aucun match pour ce filtre' : 'No games for this filter'}
        hint={fr ? `Il y a ${sourceGames.length} match${sourceGames.length > 1 ? 's' : ''} ${sportLabel} au total.` : `There ${sourceGames.length === 1 ? 'is 1 game' : `are ${sourceGames.length} games`} in ${sportLabel} overall.`}
        action={fr ? 'Voir tous les matchs' : 'Show all games'} onAction={() => setFilter('all')} />;
    }
    return <EmptyState icon="calendar" title={fr ? `Aucun match ${sportLabel} pour le moment` : `No ${sportLabel} games right now`}
      hint={fr ? 'Les prochains matchs apparaîtront ici dès qu’ils seront au calendrier.' : 'Upcoming games show up here as soon as they’re scheduled.'} />;
  };

  const renderQuickFilters = (onPicked) => {
    const favLogo = currentFav ? teamLogoUrl(selectedSport, currentFav) : teamLogoUrl('nhl', 'MTL');
    const items = [
      ['all', t.allGames],
      ['today', t.today],
      ['tomorrow', t.tomorrow],
      ['live', t.live],
      ['favorites', currentFav ? t.favorites : t.habs],
    ].filter(([key]) => !(noFavorites && key === 'favorites'));
    return items.map(([key, label]) => (
      <button key={key} className={`filter-btn qf-btn ${filter === key ? 'active' : ''} ${key === 'live' && filterCounts.live > 0 ? 'is-live' : ''}`}
        onClick={() => { setFilter(key); onPicked?.(); }}>
        {key === 'live' && <i className="qf-live-dot" aria-hidden="true" />}
        {key === 'favorites' && favLogo && <img className={`qf-logo ${currentFav ? selectedSport : 'nhl'}`} alt="" src={favLogo} onError={(e) => { e.currentTarget.style.display = 'none'; }} />}
        <span className="qf-label">{label}</span>
        <span className="qf-count">{filterCounts[key] ?? 0}</span>
      </button>
    ));
  };

  // live stat bars: each side in its team's main colour
  const statColors = (game) => {
    const map = game.sport === 'mlb' || String(game.id).startsWith('mlb-') ? MLB_TEAM_COLORS
      : game.sport === 'nba' || String(game.id).startsWith('nba-') ? NBA_TEAM_COLORS : NHL_TEAM_COLORS;
    const away = map[game.away_abbrev], home = map[game.home_abbrev];
    const rgb = (hex) => { const n = parseInt(String(hex || '').replace('#', ''), 16); return [n >> 16 & 255, n >> 8 & 255, n & 255]; };
    const close = (x, y) => { if (!x || !y) return false; const [a1, b1, c1] = rgb(x), [a2, b2, c2] = rgb(y); return Math.hypot(a1 - a2, b1 - b2, c1 - c2) < 110; };
    // two similar colours (PHI red vs ATL red): the home side uses its second colour
    const homeC = close(away?.primary, home?.primary) && home?.secondary && !close(away?.primary, home.secondary) ? home.secondary : home?.primary;
    return { '--away-c': away?.primary || 'var(--text-muted)', '--home-c': homeC || 'var(--accent)' };
  };

  // Scores ticker under the header: today's & tomorrow's games of every sport, scrolling right → left.
  // Live games first (score + period), then upcoming (time + odds), then finals. It never pauses; click a game = open it.
  const renderTicker = () => {
    const tagged = [
      ...games.map((g) => ({ ...g, _sport: 'nhl' })),
      ...nbaGames.map((g) => ({ ...g, _sport: 'nba' })),
      ...mlbGames.map((g) => ({ ...g, _sport: 'mlb' })),
      ...(nflGames || []).map((g) => ({ ...g, _sport: 'nfl' })),
      ...(f1Races || []).map((g) => ({ ...g, _sport: 'f1' })),
    ];
    const rank = (g) => (['LIVE', 'CRIT'].includes(g.status) ? 0 : ['FINAL', 'OFF'].includes(g.status) ? 2 : 1);
    // today's games only (local day): live, still to come today, and today's finals
    const today = new Date().toDateString();
    const allItems = tagged
      .filter((g) => {
        if (['LIVE', 'CRIT'].includes(g.status)) return true;
        const d = g.game_time ? new Date(g.game_time) : (g.game_date ? new Date(g.game_date + 'T12:00:00') : null);
        return !!d && d.toDateString() === today;
      })
      .sort((a, b) => rank(a) - rank(b) || new Date(a.game_time) - new Date(b.game_time))
      .slice(0, 40);
    // one section per sport, in this order
    const SECTIONS = [['nhl', 'NHL'], ['nba', 'NBA'], ['mlb', 'MLB'], ['nfl', 'NFL'], ['f1', 'F1']];
    const sections = SECTIONS.map(([sp, label]) => [sp, label, allItems.filter((g) => g._sport === sp)]).filter(([, , list]) => list.length > 0);
    const items = sections.flatMap(([, , list]) => list);
    if (items.length === 0) return null;
    const timeLabel = (iso) => fmtTime(iso, lang);
    const open = (g) => { setActiveTab('games'); setSelectedSport(g._sport); setExpandedGame(g.id); };
    // each game in the home team's main colour; light colours (Boston / Vegas gold…) get dark text + light-background logos
    const COLORS = { nhl: NHL_TEAM_COLORS, mlb: MLB_TEAM_COLORS, nba: NBA_TEAM_COLORS };
    const isLight = (hex) => {
      const m = /^#?([0-9a-f]{6})$/i.exec(hex || ''); if (!m) return false;
      const n = parseInt(m[1], 16); const [r, gg, b] = [n >> 16 & 255, n >> 8 & 255, n & 255];
      return (0.299 * r + 0.587 * gg + 0.114 * b) / 255 > 0.6;
    };
    const item = (g, key) => {
      const st = getGameStatus(g);
      // F1: flag + race + time (or live / the winner)
      if (g._sport === 'f1') {
        const winner = g.podium?.[0];
        return (
          <button key={key} type="button" className={`tk-item tk-f1 ${st.live ? 'live' : ''} ${st.final ? 'final' : ''} on-dark`} style={{ '--tk-bg': '#1c1c1c' }} onClick={() => open(g)} tabIndex={-1}>
            <span className="tk-team">{g.flag && <img className="tk-flag" alt="" src={`https://flagcdn.com/w40/${g.flag}.png`} />}<b>{g.name.replace(/ Grand Prix/, ' GP')}</b></span>
            <span className="tk-meta">
              {st.live ? <><i className="tk-dot" />{st.text || 'LIVE'}</>
                : st.final ? <>{winner ? `P1 ${winner.code}` : 'FINAL'}</>
                : timeLabel(g.game_time)}
            </span>
          </button>
        );
      }
      const bg = boostOn ? '#efe5cc' : g._sport === 'nfl' ? (g.home_color || '#1c1c1c') : (COLORS[g._sport]?.[g.home_abbrev]?.primary || '#1c1c1c');   // Boost Weekend: cream retro tiles
      const light = isLight(bg);
      const logo = (abbrev) => (g._sport === 'nfl'
        ? <img className="tk-logo nfl" alt="" src={abbrev === g.home_abbrev ? g.home_logo : g.away_logo} onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }} />
        : <img className={`tk-logo ${g._sport}`} alt="" src={light ? teamLogoUrl(g._sport, abbrev) : teamLogoUrlDark(g._sport, abbrev)} onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }} />);
      return (
        <button key={key} type="button" className={`tk-item ${st.live ? 'live' : ''} ${st.final ? 'final' : ''} ${light ? 'on-light' : 'on-dark'}`} style={{ '--tk-bg': bg }} onClick={() => open(g)} tabIndex={-1}>
          <span className="tk-team">{logo(g.away_abbrev)}<b>{g.away_abbrev}</b>{(st.live || st.final) && <em>{g.away_score ?? 0}</em>}</span>
          <span className="tk-team">{logo(g.home_abbrev)}<b>{g.home_abbrev}</b>{(st.live || st.final) && <em>{g.home_score ?? 0}</em>}</span>
          <span className="tk-meta">
            {st.live ? <><i className="tk-dot" />{st.text || 'LIVE'}</>
              : st.final ? (lang === 'fr' ? 'FINAL' : 'FINAL')
              : <>{timeLabel(g.game_time)}{g.away_odds && g.home_odds ? <span className="tk-odds">{parseFloat(g.away_odds).toFixed(2)} · {parseFloat(g.home_odds).toFixed(2)}</span> : null}</>}
          </span>
        </button>
      );
    };
    // The list is repeated in a row and the track moves by exactly ONE copy, so the loop is seamless.
    // Few games (a short list) = more copies: together they must always be wider than the screen + one copy,
    // else the end of the track shows up as an empty black banner.
    const copyWidth = Math.max(1, items.length * 150 + sections.length * 60);   // low estimate on purpose
    const screenW = typeof window !== 'undefined' ? window.innerWidth : 1600;
    const copies = Math.min(12, Math.max(2, Math.ceil(screenW / copyWidth) + 1));
    return (
      <div className="ticker" aria-label={lang === 'fr' ? 'Matchs' : 'Games'}>
        <div className="ticker-track" style={{ '--tk-dur': `${Math.max(20, items.length * 6)}s`, '--tk-shift': `${-100 / copies}%` }}>
          {Array.from({ length: copies }, (_, i) => String.fromCharCode(97 + i)).map((copy) => (
            <div key={copy} className="ticker-copy" aria-hidden={copy !== 'a' ? 'true' : undefined}>
              {sections.map(([sp, label, list]) => (
                <React.Fragment key={sp}>
                  <span className="tk-section">{label}</span>
                  {list.map((g) => item(g, `${copy}-${sp}-${g.id}`))}
                </React.Fragment>
              ))}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderBetsSummary = () => {
    const staked = activeBets.reduce((sum, bet) => sum + parseFloat(bet.stake), 0);
    const potential = activeBets.reduce((sum, bet) => sum + parseFloat(bet.potential_win), 0);
    return (
      <div className="bets-wallet">
        <div className="bets-wallet-stat">
          <span className="bets-wallet-label">{lang === 'fr' ? 'Misé' : 'Staked'}</span>
          <span className="bets-wallet-value staked">{formatMoney(staked, lang)}</span>
        </div>
        <div className="bets-wallet-stat">
          <span className="bets-wallet-label">{lang === 'fr' ? 'Gains potentiels' : 'Potential'}</span>
          <span className="bets-wallet-value potential">{formatMoney(potential, lang)}</span>
        </div>
      </div>
    );
  };

  // Main App

  // Public showroom view
  if (publicShowroom) {
    const market = getDailyMarket();
    return (
      <div style={{ background: '#111', minHeight: '100vh', color: '#fff', fontFamily: "'Segoe UI', system-ui, sans-serif" }}>
        <div style={{ maxWidth: 900, margin: '0 auto', padding: '40px 20px' }}>
          <div style={{ textAlign: 'center', marginBottom: 30 }}>
            <div style={{ fontSize: 14, color: '#888', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 4 }}>BarDownCollectables</div>
            <div style={{ fontSize: 32, fontWeight: 900 }}>{publicShowroom.username}'s Showroom</div>
            <div style={{ fontSize: 14, color: '#666', marginTop: 8 }}>{publicShowroom.cards.length} cards on display</div>
          </div>
          <div className="cards-grid">
            {publicShowroom.cards.map(card => (
              <CardSlot key={card.id} card={card}>
                <div className={`card-value-tag ${applyMarket(calculateCardValue(card.card_data, card.card_type, card.numbering), market) >= 50 ? 'card-value-high' : ''}`}>{formatValue(applyMarket(calculateCardValue(card.card_data, card.card_type, card.numbering), market))}</div>
              </CardSlot>
            ))}
          </div>
          <div style={{ textAlign: 'center', marginTop: 40 }}>
            <a href={window.location.origin + window.location.pathname} style={{ color: 'var(--accent, #ff5722)', textDecoration: 'none', fontWeight: 700 }}>Visit BarDownBets.com</a>
          </div>
        </div>
      </div>
    );
  }

  // Maintenance mode — everyone except CL34RY sees updating screen
  const MAINTENANCE_MODE = false;
  if (MAINTENANCE_MODE && profile?.username !== 'CL34RY') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: '#1a1a2e', color: '#fff', fontFamily: "'Segoe UI', system-ui, sans-serif", textAlign: 'center', padding: 40 }}>
        <div style={{ fontSize: 48, fontWeight: 900, marginBottom: 8 }}>BarDownBets<span style={{ color: '#ff5722' }}>.</span>com</div>
        <div style={{ width: 60, height: 60, border: '4px solid #333', borderTopColor: '#ff5722', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '30px 0' }} />
        <div style={{ fontSize: 22, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 12 }}>Updating</div>
        <div style={{ fontSize: 14, color: '#888', maxWidth: 400 }}>We're making things better. Be back shortly!</div>
        {session && <button onClick={() => supabase.auth.signOut()} style={{ marginTop: 30, padding: '10px 24px', background: 'transparent', border: '1px solid #555', borderRadius: 8, color: '#888', fontSize: 13, cursor: 'pointer', letterSpacing: 1 }}>Log Out</button>}
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div className="app">
      {favTeamLogo && themeMode !== 'none' && (<>
        <div className="fav-side-logo left" style={{ backgroundImage: `url(${favTeamLogo})` }} />
        <div className="fav-side-logo right" style={{ backgroundImage: `url(${favTeamLogo})` }} />
      </>)}
      {updating && (
        <div className="updating-screen" role="alert">
          <div className="updating-card">
            <div className="updating-rink" aria-hidden="true"><div className="updating-puck" /></div>
            <span className="updating-kicker">{lang === 'fr' ? 'Grosse mise à jour' : 'Major update'}</span>
            <h2>{lang === 'fr' ? 'On fait la mise à jour!' : 'We’re updating!'}</h2>
            <p>{lang === 'fr' ? 'Un tout nouveau BarDownBets arrive. On revient dans quelques minutes — la page se recharge toute seule.' : 'A whole new BarDownBets is on its way. Back in a few minutes — this page reloads by itself.'}</p>
          </div>
        </div>
      )}
      {showWhatsNew && <WhatsNew lang={lang} onClose={() => setShowWhatsNew(false)} />}
      {!showWhatsNew && showUpdate && <UpdateNote lang={lang} onClose={() => setShowUpdate(false)} />}
      <PredictWin lang={lang} profile={profile} blocked={showWhatsNew || showUpdate} />
      <JackpotWin lang={lang} profile={profile} blocked={showWhatsNew || showUpdate} />
      {notification && <div className={`notification ${notification.type}`} onClick={notification.type === 'win' ? dismissNotification : undefined} style={notification.type === 'win' ? { cursor: 'pointer' } : { top: Math.max(8, (headerRef.current?.getBoundingClientRect().bottom || 0) + 12) } /* toasts sit just under the header + banner */}>{notification.message}{notification.type === 'win' && notifQueue.current.length > 0 && <span style={{ marginLeft: 8, fontSize: 12, opacity: 0.8 }}>({notifQueue.current.length} more)</span>}</div>}

      {/* Boost Weekend: while it's on, the big retro banner sits on the Games page; before it, a countdown bar */}
      {boostWeek && !boostOn && !boostWeek.active && (
        <div className="boost-banner boost-countdown">
          <span>{lang === 'fr'
            ? `Week-end Boost 2X dans ${boostWeek.daysUntil} jour${boostWeek.daysUntil !== 1 ? 's' : ''}`
            : `2X Boost Weekend in ${boostWeek.daysUntil} day${boostWeek.daysUntil !== 1 ? 's' : ''}`}</span>
        </div>
      )}

      {/* Header */}
      <header className="header" ref={headerRef}>
        {favTeamLogo && themeMode !== 'none' && (
          <div className="header-bg-logo" style={{ backgroundImage: `url(${favTeamLogo})`, ...((themeTeamSport === 'mlb' || themeTeamSport === 'nba') ? { backgroundSize: '350px', ...(themeTeam === 'TOR' && themeTeamSport === 'mlb' ? { backgroundPosition: 'center 40%' } : {}) } : {}) }} />
        )}
        <div className="header-inner">
          <div className="header-left">
            {/* the sticker wordmark (same style as the BDB logo, in the theme's colours); a Boost Weekend shows the retro script text instead */}
            <span className="logo" role="button" title="Back to top" onClick={scrollToTop}>
              <Wordmark className="logo-sticker" />
              <span className="logo-text"><span className="logo-name">BarDownBets</span><span className="logo-dot">.</span><span className="logo-domain">com</span></span>
            </span>
            {updateStatus && (
              <span className={`update-status ${updateStatus.status}`}>
                {updateStatus.status === 'downloading' && '⬇️ '}
                {updateStatus.status === 'downloaded' && '✅ '}
                {updateStatus.status === 'available' && '🔄 '}
                {updateStatus.message}
              </span>
            )}
          </div>
          <nav className="main-nav">
            <button className={activeTab === 'games' ? 'active' : ''} onClick={() => setActiveTab('games')}>{t.games}</button>
            <button className={activeTab === 'mybets' ? 'active' : ''} onClick={() => setActiveTab('mybets')}>
              {t.myBets} {activeBets.length > 0 && <span className="nav-badge" key={activeBets.length}>{activeBets.length}</span>}
            </button>
            <button className={activeTab === 'leaderboard' ? 'active' : ''} onClick={() => { setActiveTab('leaderboard'); fetchLeaderboard(); }}>{t.leaderboard}</button>
            <button className={activeTab === 'pools' ? 'active' : ''} onClick={() => setActiveTab('pools')}>{t.pools}</button>
            <button className={activeTab === 'feed' ? 'active' : ''} onClick={() => setActiveTab('feed')}>Feed</button>
          </nav>
          <div className="header-right">
            <div className="balance-wrapper">
              <div className="balance-box" onClick={() => setActiveTab('mybets')} title={t.myBets}>
                <span className="balance-label">{t.balance}</span>
                <div className="balance-row">
                  <span className="balance-value"><AnimatedNumber value={profile?.balance || 0} format={(n) => formatMoney(n, lang)} duration={900} /></span>
                </div>
              </div>
            </div>
            <div className="header-actions">
              <span className="user-name">{profile?.username}</span>
              <div className="lang-switch">
                <button className={lang === 'en' ? 'active' : ''} onClick={() => changeLang('en')}>EN</button>
                <button className={lang === 'fr' ? 'active' : ''} onClick={() => changeLang('fr')}>FR</button>
              </div>
              <button className="logout-btn" onClick={handleSignOut}>{t.signOut}</button>
            </div>
            <button className="hamburger-btn" onClick={() => setMobileMenuOpen(o => !o)} aria-label="Menu">
              <span></span><span></span><span></span>
              {betSlip.length > 0 && <span className="hamburger-bet-badge" key={betSlip.length}>{betSlip.length}</span>}
            </button>
          </div>
        </div>
      </header>
      {session && tickerOn && renderTicker()}

      {/* Menu Panel */}
      {mobileMenuOpen && (
        <div className="mobile-menu-overlay" onClick={() => setMobileMenuOpen(false)} />
      )}
      <div className={`mobile-menu-panel${mobileMenuOpen ? ' open' : ''}`} style={headerRef.current ? (() => {
        // Start right under the header, counting the boost banner above it
        const headerBottom = Math.max(0, Math.round(headerRef.current.getBoundingClientRect().bottom));
        return { top: headerBottom + 'px', maxHeight: `calc(100vh - ${headerBottom}px)` };
      })() : undefined}>
        {/* Balance — mobile only */}
        <div className="mobile-menu-section mobile-menu-balance mobile-only">
          <h4 className="mobile-menu-label">{t.balance}</h4>
          <div className="mobile-balance-grid">
            <div className="mobile-balance-item">
              <span className="mobile-balance-val">{formatMoney(profile?.balance || 0, lang)}</span>
              <span className="mobile-balance-lbl">{t.balance}</span>
            </div>
          </div>
        </div>

        {/* Monthly jackpot — mobile only */}
        <div className="mobile-menu-section mobile-only">
          <Jackpot lang={lang} profile={profile} onBought={fetchProfile} />
        </div>

        {/* Navigation — mobile only */}
        <div className="mobile-menu-section mobile-only">
          <h4 className="mobile-menu-label">Navigation</h4>
          <div className="mobile-menu-nav">
            <button className={activeTab === 'games' ? 'active' : ''} onClick={() => { setActiveTab('games'); setMobileMenuOpen(false); }}>{t.games}</button>
            <button className={activeTab === 'mybets' ? 'active' : ''} onClick={() => { setActiveTab('mybets'); setMobileMenuOpen(false); }}>
              {t.myBets}{activeBets.length > 0 && <span className="nav-badge" key={activeBets.length}>{activeBets.length}</span>}
            </button>
            <button className={activeTab === 'leaderboard' ? 'active' : ''} onClick={() => { setActiveTab('leaderboard'); fetchLeaderboard(); setMobileMenuOpen(false); }}>{t.leaderboard}</button>
            <button className={activeTab === 'pools' ? 'active' : ''} onClick={() => { setActiveTab('pools'); setMobileMenuOpen(false); }}>{t.pools}</button>
            <button className={activeTab === 'feed' ? 'active' : ''} onClick={() => { setActiveTab('feed'); setMobileMenuOpen(false); }}>Feed</button>
          </div>
        </div>

        {/* Filters — mobile only */}
        <div className="mobile-menu-section mobile-only">
          <h4 className="mobile-menu-label">{t.quickFilters}</h4>
          <div className="mobile-menu-filters">
            {renderQuickFilters(() => setMobileMenuOpen(false))}
          </div>
        </div>

        {/* Bet Slip — mobile only */}
        <div className="mobile-menu-section mobile-only">
          <h4 className="mobile-menu-label">{t.betSlip}{betSlip.length > 0 && <span className="nav-badge" key={betSlip.length}>{betSlip.length}</span>}</h4>
          {betSlip.length === 0 ? (
            <div className="slip-empty">
              <span className="es-icon" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 7h18v4a2 2 0 0 0 0 4v4H3v-4a2 2 0 0 0 0-4z" /><path d="M13 7v12" strokeDasharray="2 2" /></svg></span>
              <p>{t.betSlipEmpty}</p>
              <small>{t.clickOdds}</small>
            </div>
          ) : (
            <>
              {betSlip.length >= 2 && (
                <div className="slip-mode-toggle">
                  <button className={!parlayMode ? 'active' : ''} onClick={() => setParlayMode(false)}>{t.singles}</button>
                  <button className={parlayMode ? 'active' : ''} onClick={() => setParlayMode(true)}>{t.parlay} ({betSlip.length})</button>
                </div>
              )}
              <div className="slip-bets">
                {betSlip.map(bet => (
                  <div key={bet.betId} className="slip-bet">
                    <button className="slip-remove" onClick={() => removeFromBetSlip(bet.betId)}>×</button>
                    <div className="slip-game">{bet.game}</div>
                    <div className="slip-pick">{bet.description}</div>
                    <div className="slip-odd">@ {bet.boost > 1 ? <><s className="odd-was">{(bet.odds / bet.boost).toFixed(2)}</s>{bet.odds.toFixed(2)}</> : bet.odds.toFixed(2)}</div>
                    {!parlayMode && (
                      <>
                        <div className="slip-stake">
                          <span>$</span>
                          <input type="number" value={bet.stake} onChange={(e) => updateStake(bet.betId, e.target.value)} min="0" />
                        </div>
                        <div className="slip-win">{t.toWin}: {formatMoney(bet.stake * bet.odds, lang)}</div>
                      </>
                    )}
                  </div>
                ))}
              </div>
              {parlayMode ? (
                <>
                  <div className="slip-summary">
                    <div className="summary-row"><span>{t.combinedOdds}</span><span>{getParlayOdds(betSlip).toFixed(2)}</span></div>
                    <div className="summary-row">
                      <span>{t.stake}</span>
                      <div className="slip-stake parlay-stake">
                        <span>$</span>
                        <input type="number" value={parlayStake} onChange={(e) => setParlayStake(Math.max(0, parseFloat(e.target.value) || 0))} min="0" />
                      </div>
                    </div>
                    <div className="summary-row total"><span>{t.potentialWin}</span><span>{formatMoney(parlayStake * getParlayOdds(betSlip), lang)}</span></div>
                  </div>
                  <button className="place-btn parlay-btn" onClick={() => { placeBets(); setMobileMenuOpen(false); }} disabled={placingBets || parlayStake <= 0 || parlayStake > profile?.balance || betSlip.length < 2}>
                    {placingBets ? t.processing : `${t.placeParlay} (${formatMoney(parlayStake, lang)})`}
                  </button>
                </>
              ) : (
                <>
                  <div className="slip-summary">
                    <div className="summary-row"><span>{t.totalStake}</span><span>{formatMoney(totalStake, lang)}</span></div>
                    <div className="summary-row total"><span>{t.potentialWin}</span><span>{formatMoney(totalPotentialWin, lang)}</span></div>
                  </div>
                  <button className="place-btn" onClick={() => { placeBets(); setMobileMenuOpen(false); }} disabled={placingBets || totalStake === 0 || totalStake > profile?.balance}>
                    {placingBets ? t.processing : `${betSlip.length > 1 ? t.placeBets : t.placeBet} (${formatMoney(totalStake, lang)})`}
                  </button>
                </>
              )}
            </>
          )}
        </div>

        {/* Favorites & Theme */}
        <div className="mobile-menu-section settings-section">
          {/* My teams */}
          <div className="menu-card">
            <h4 className="menu-card-title">{lang === 'fr' ? 'Mes équipes' : 'My Teams'}</h4>
            {[
              { sport: 'nhl', label: 'NHL', value: favoriteNHL, teams: NHL_TEAM_COLORS },
              { sport: 'mlb', label: 'MLB', value: favoriteMLB, teams: MLB_TEAM_COLORS },
              { sport: 'nba', label: 'NBA', value: favoriteNBA, teams: NBA_TEAM_COLORS },
              { sport: 'nfl', label: 'NFL', value: favoriteNFL, teams: NFL_TEAM_COLORS },
              { sport: 'f1', label: 'F1', value: favoriteF1, teams: F1_TEAM_COLORS },
            ].map(({ sport, label, value, teams }) => (
              <div key={sport} className="team-pick-row">
                <span className="team-pick-league">{label}</span>
                <div className="team-pick-select">
                  {value
                    ? <img className={`team-pick-logo ${sport}`} src={teamLogoUrl(sport, value)} alt="" onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }} />
                    : <span className="team-pick-logo empty" />}
                  <select value={value} onChange={(e) => setFavoriteTeam(sport, e.target.value)}>
                    <option value="">{t.noFavoriteTeam}</option>
                    {Object.keys(teams).sort().map(abbr => (
                      <option key={abbr} value={abbr}>{sport === 'f1' ? TEAM_NICKNAMES.f1[abbr] : `${abbr}${TEAM_NICKNAMES[sport][abbr] ? ` — ${TEAM_NICKNAMES[sport][abbr]}` : ''}`}</option>
                    ))}
                  </select>
                </div>
              </div>
            ))}
            {[favoriteNHL, favoriteMLB, favoriteNBA, favoriteNFL, favoriteF1].filter(Boolean).length > 1 && (() => {
              const me = { avatar_team: avatarTeam, favorite_nhl: favoriteNHL, favorite_mlb: favoriteMLB, favorite_nba: favoriteNBA, favorite_nfl: favoriteNFL, favorite_f1: favoriteF1 };
              const shown = me[`favorite_${avatarTeam}`] ? avatarTeam : 'nhl';
              return (
                <>
                  <span className="menu-card-sub">{lang === 'fr' ? 'Photo au classement' : 'Leaderboard picture'}</span>
                  <div className="theme-radios pill-group avatar-pick">
                    {['nhl', 'mlb', 'nba', 'nfl', 'f1'].filter((sp) => me[`favorite_${sp}`]).map((sp) => (
                      <label key={sp}>
                        <input type="radio" name="avatarTeam" value={sp} checked={shown === sp} onChange={() => saveAvatarTeam(sp === 'nhl' ? '' : sp)} />
                        <TeamAvatar team={avatarOf({ ...me, avatar_team: sp })} size={24} />
                        {sp.toUpperCase()}
                      </label>
                    ))}
                  </div>
                </>
              );
            })()}
          </div>

          {/* Theme */}
          <div className="menu-card">
            <h4 className="menu-card-title">{lang === 'fr' ? 'Thème' : 'Theme'}</h4>
            {(favoriteNHL || favoriteMLB || favoriteNBA || favoriteNFL || favoriteF1) && (
              <>
                <span className="menu-card-sub">{lang === 'fr' ? 'Basé sur' : 'Based on'}</span>
                <div className="theme-radios pill-group">
                  {favoriteNHL && <label><input type="radio" name="themeSource" value="nhl" checked={themeSource === 'nhl'} onChange={() => saveThemeSource('nhl')} /> NHL · {favoriteNHL}</label>}
                  {favoriteMLB && <label><input type="radio" name="themeSource" value="mlb" checked={themeSource === 'mlb'} onChange={() => saveThemeSource('mlb')} /> MLB · {favoriteMLB}</label>}
                  {favoriteNBA && <label><input type="radio" name="themeSource" value="nba" checked={themeSource === 'nba'} onChange={() => saveThemeSource('nba')} /> NBA · {favoriteNBA}</label>}
                  {favoriteNFL && <label><input type="radio" name="themeSource" value="nfl" checked={themeSource === 'nfl'} onChange={() => saveThemeSource('nfl')} /> NFL · {favoriteNFL}</label>}
                  {favoriteF1 && <label><input type="radio" name="themeSource" value="f1" checked={themeSource === 'f1'} onChange={() => saveThemeSource('f1')} /> F1 · {TEAM_NICKNAMES.f1[favoriteF1]}</label>}
                  <label><input type="radio" name="themeSource" value="auto" checked={themeSource === 'auto'} onChange={() => saveThemeSource('auto')} /> {lang === 'fr' ? 'Auto — par sport' : 'Auto — per sport'}</label>
                </div>
              </>
            )}
            <span className="menu-card-sub">{lang === 'fr' ? 'Style' : 'Style'}</span>
            <div className="theme-radios pill-group">
              {['none','accent','cards','custom'].map(m => (
                <label key={m}><input type="radio" name="themeModeM" value={m} checked={themeMode === m} onChange={() => saveThemeMode(m)} /> {t[`theme_${m}`]}</label>
              ))}
            </div>
          </div>

          {/* Scores ticker on / off */}
          <div className="menu-card">
            <label className="menu-switch-row">
              <span>{lang === 'fr' ? 'Bandeau des matchs' : 'Scores ticker'}</span>
              <input type="checkbox" role="switch" className="menu-switch" checked={tickerOn} onChange={(e) => {
                const on = e.target.checked;
                setTickerOn(on);
                try { localStorage.setItem('tickerEnabled', on ? 'on' : 'off'); } catch (err) { /* private mode */ }
              }} />
            </label>
          </div>

          {/* Vibrations — only on phones / tablets that can vibrate */}
          {canVibrate() && (
            <div className="menu-card">
              <label className="menu-switch-row">
                <span>{lang === 'fr' ? 'Vibrations' : 'Vibrations'}</span>
                <input type="checkbox" role="switch" className="menu-switch" checked={hapticsOn} onChange={(e) => {
                  const on = e.target.checked;
                  setHapticsOn(on);
                  setHapticsEnabled(on);
                  if (on) vibrate('tap');
                }} />
              </label>
            </div>
          )}

          {/* Mulligan */}
          <div className="menu-card menu-mulligan">
            <div className="menu-mulligan-text">
              <span className="menu-card-title" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="4" y="4" width="16" height="16" rx="3" /><circle cx="9" cy="9" r="1" fill="currentColor" /><circle cx="15" cy="15" r="1" fill="currentColor" /><circle cx="15" cy="9" r="1" fill="currentColor" /><circle cx="9" cy="15" r="1" fill="currentColor" /></svg>Mulligan</span>
              <span className="menu-mulligan-sub">
                {canUseMulligan(profile)
                  ? (lang === 'fr' ? 'Réinitialiser votre solde à 1 000$ · 1 fois par mois' : 'Reset your balance to $1,000 · once a month')
                  : (lang === 'fr' ? 'Déjà utilisé ce mois-ci' : 'Already used this month')}
              </span>
            </div>
            <button
              className="mulligan-btn menu-mulligan-btn"
              onClick={() => { handleMulligan(); setMobileMenuOpen(false); }}
              disabled={!canUseMulligan(profile)}
            >
              {lang === 'fr' ? 'Réinitialiser' : 'Reset'}
            </button>
          </div>
        </div>

        {/* Account */}
        <div className="mobile-menu-section">
          <h4 className="mobile-menu-label">{profile?.username}</h4>
          <div className="mobile-menu-account">
            <div className="mobile-menu-lang">
              <button className={lang === 'en' ? 'active' : ''} onClick={() => changeLang('en')}>EN</button>
              <button className={lang === 'fr' ? 'active' : ''} onClick={() => changeLang('fr')}>FR</button>
            </div>
            <button className="logout-btn" onClick={() => { handleSignOut(); setMobileMenuOpen(false); }}>{t.signOut}</button>
          </div>
          <span className="app-version">BarDownBets v{APP_VERSION}</span>
        </div>
      </div>

      <div className="main-layout">
        {/* Sidebar */}
        <aside className="sidebar" style={(activeTab === 'cards' || activeTab === 'pools') ? { display: 'none' } : undefined}>
          <div className="sidebar-section">
            <Jackpot lang={lang} profile={profile} onBought={fetchProfile} />
          </div>
          <div className="sidebar-section">
            <h4>{t.quickFilters}</h4>
            {renderQuickFilters()}
          </div>
          <div className="sidebar-section">
            <h4>{t.betTypes}</h4>
            <div className="bet-type-list">
              {selectedSport === 'nfl' ? (
                <>
                  <span>{t.moneyline}</span>
                  <span>{lang === 'fr' ? 'Écart de points' : 'Point Spread'}</span>
                  <span>{lang === 'fr' ? 'Total de points' : 'Total Points'}</span>
                  <span>{lang === 'fr' ? 'Touchés' : 'Touchdowns'}</span>
                  <span>{lang === 'fr' ? 'Verges des joueurs' : 'Player Yards'}</span>
                  <span>{lang === 'fr' ? 'Paris spéciaux' : 'Game Props'}</span>
                </>
              ) : selectedSport === 'f1' ? (
                <>
                  <span>{lang === 'fr' ? 'Vainqueur' : 'Race winner'}</span>
                  <span>Top 3 / 6 / 10</span>
                  <span>{lang === 'fr' ? 'Duel de coéquipiers' : 'Head-to-head'}</span>
                  <span>Pole position</span>
                  <span>{lang === 'fr' ? 'Tour le plus rapide' : 'Fastest lap'}</span>
                  <span>{lang === 'fr' ? 'Équipe gagnante' : 'Winning team'}</span>
                </>
              ) : selectedSport === 'mlb' ? (
                <>
                  <span>{t.moneyline}</span>
                  <span>{lang === 'fr' ? 'Ligne de points' : 'Run Line'}</span>
                  <span>{lang === 'fr' ? 'Total de points' : 'Total Runs'}</span>
                </>
              ) : selectedSport === 'nba' ? (
                <>
                  <span>{t.moneyline}</span>
                  <span>{lang === 'fr' ? 'Écart de points' : 'Spread'}</span>
                  <span>{lang === 'fr' ? 'Total de points' : 'Total Points'}</span>
                  <span>{t.nbaPlayerProps}</span>
                </>
              ) : (
                <>
                  <span>{t.moneyline}</span>
                  <span>{t.puckLine}</span>
                  <span>{t.totalGoals}</span>
                  <span>{t.firstGoal}</span>
                  <span>{t.gameProps}</span>
                  <span>{t.correctScore}</span>
                </>
              )}
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <main className="main-content page-in" key={activeTab} style={{ position: 'relative' }}>
          {favTeamLogo && themeMode !== 'none' && activeTab !== 'cards' && activeTab !== 'pools' && (
            <div className="fav-bg-logo-wrapper"><div className="fav-bg-logo" style={{ backgroundImage: `url(${favTeamLogo})` }} /></div>
          )}
          {activeTab === 'games' && (
            <>
              {/* Sport Tab Bar */}
              <div className="sport-tab-bar">
                {sports.map(s => (
                  <button
                    key={s.id}
                    className={`sport-tab${selectedSport === s.id ? ' active' : ''}`}
                    onClick={() => setSelectedSport(s.id)}
                  >
                    <span className="sport-tab-label">{s.label}</span>
                  </button>
                ))}
              </div>
              {boostOn && (() => {
                // the real time left: days, then hours / minutes on the last day (it ends at midnight Sunday, Montréal time)
                const ms = boostWeek.endsAt ? new Date(boostWeek.endsAt).getTime() - boostNow : boostWeek.daysLeft * 86400000;
                const fr = lang === 'fr';
                const big = ms > 24 * 3600000 ? Math.ceil(ms / 86400000) : ms >= 3600000 ? Math.floor(ms / 3600000) : Math.max(1, Math.ceil(ms / 60000));
                const unit = ms > 24 * 3600000 ? (fr ? (big > 1 ? 'JOURS' : 'JOUR') : (big > 1 ? 'DAYS LEFT' : 'DAY LEFT'))
                  : ms >= 3600000 ? (fr ? 'HEURES' : 'HOURS LEFT') : (fr ? 'MINUTES' : 'MIN LEFT');
                return (
                  <section className="boost-hero" aria-label={fr ? 'Week-end Boost' : 'Boost Weekend'}>
                    <img className="boost-hero-patch" src="./brand/bdb-patch.svg" alt="" />
                    <div className="boost-hero-text">
                      <span className="boost-hero-kicker">{fr ? 'CE WEEK-END SEULEMENT' : 'THIS WEEKEND ONLY'}</span>
                      <h2 className="boost-hero-title">{fr ? 'Week-end Boost' : 'Boost Weekend'}</h2>
                      <span className="boost-hero-sub">{boostWeek.mode === 'moneyline'
                        ? (fr ? 'Gagnant du match ×2 · pas les combinés · jusqu\'à dimanche minuit' : 'Moneylines ×2 · not parlays · ends Sunday at midnight')
                        : (fr ? 'Cotes ×2 sur tout sauf le gagnant du match et les combinés · jusqu\'à dimanche minuit' : 'Odds ×2 on everything but moneylines and parlays · ends Sunday at midnight')}</span>
                    </div>
                    <div className="boost-hero-left"><b>{big}</b><span>{unit}</span></div>
                  </section>
                );
              })()}
              <PredictNight
                lang={lang}
                userId={session?.user?.id}
                games={games} nbaGames={nbaGames} mlbGames={mlbGames} nflGames={nflGames}
                logoUrl={teamLogoUrl}
                logoUrlDark={teamLogoUrlDark}
                teamColor={(sport, abbrev, g, side) => (sport === 'nfl' ? g?.[`${side}_color`] : ({ nhl: NHL_TEAM_COLORS, nba: NBA_TEAM_COLORS, mlb: MLB_TEAM_COLORS }[sport] || {})[abbrev]?.primary)}
                teamColor2={(sport, abbrev) => ({ nhl: NHL_TEAM_COLORS, nba: NBA_TEAM_COLORS, mlb: MLB_TEAM_COLORS }[sport] || {})[abbrev]?.secondary}
                haptic={vibrate}
                balance={parseFloat(profile?.balance) || 0}
                onBalance={(b) => setProfile((prev) => (prev ? { ...prev, balance: b } : prev))}
                money={(x) => formatMoney(x, lang)}
              />
              <SlotMachine
                lang={lang}
                userId={session?.user?.id}
                haptic={vibrate}
                balance={parseFloat(profile?.balance) || 0}
                onBalance={(b) => setProfile((prev) => (prev ? { ...prev, balance: b } : prev))}
                money={(x) => formatMoney(x, lang)}
              />
              <div className="content-header">
                <h2>{selectedSport === 'f1' ? (lang === 'fr' ? 'Courses F1' : 'F1 Races') : selectedSport === 'nhl' && lang === 'fr' ? t.nhlGames : `${sports.find(s => s.id === selectedSport)?.label} Games`}</h2>
                {(selectedSport === 'nhl' || selectedSport === 'mlb' || selectedSport === 'nba' || selectedSport === 'nfl') && <span className="game-count">{filteredGames.length} {t.gamesCount}</span>}
              </div>
              {['nhl', 'nba', 'mlb', 'nfl'].includes(selectedSport) && (
                <div className="f1-page-tabs">
                  <button type="button" className={sportView === 'games' ? 'active' : ''} onClick={() => setSportView('games')}>{lang === 'fr' ? 'Matchs' : 'Games'}</button>
                  <button type="button" className={sportView === 'standings' ? 'active' : ''} onClick={() => setSportView('standings')}>{lang === 'fr' ? 'Classement' : 'Standings'}</button>
                  <button type="button" className={sportView === 'players' ? 'active' : ''} onClick={() => setSportView('players')}>{lang === 'fr' ? 'Joueurs' : 'Players'}</button>
                </div>
              )}
              {rosterUpdating && sportView === 'games' && selectedSport !== 'f1' && selectedSport !== 'nfl' && (
                <div className="roster-update-banner">
                  <span className="roster-update-spinner" /> {lang === 'fr' ? 'Mise à jour des effectifs...' : 'Updating Rosters...'}
                </div>
              )}
              {(sportView === 'standings' && ['nhl', 'nba', 'mlb', 'nfl'].includes(selectedSport)) ? (
                <Standings sport={selectedSport} lang={lang} favorite={currentFav} />
              ) : (sportView === 'players' && ['nhl', 'nba', 'mlb', 'nfl'].includes(selectedSport)) ? (
                <Leaders sport={selectedSport} lang={lang} favorite={selectedSport === 'nba' ? ({ GSW: 'GS', NYK: 'NY', NOP: 'NO', SAS: 'SA', UTA: 'UTAH', WAS: 'WSH' }[currentFav] || currentFav) : currentFav} />
              ) : selectedSport === 'nfl' ? (
                <NflGames favorite={favoriteNFL} games={nflGames ? filteredGames : null} empty={renderNoGames('NFL')} lang={lang} props={nflProps} expandedId={expandedGame} onToggle={(id) => setExpandedGame(expandedGame === id ? null : id)}
                  onBet={(game, betType, selection, details) => addToBetSlip(game, betType, selection, 0, details)} />
              ) : selectedSport === 'f1' ? (
                <F1Races favTeam={favoriteF1 ? F1_TEAM_COLORS[favoriteF1]?.name : ''} races={f1Races ? filteredGames : null} empty={renderNoGames('F1')} lang={lang} expandedId={expandedGame} onToggle={(id) => setExpandedGame(expandedGame === id ? null : id)}
                  onBet={(race, betType, selection, details) => addToBetSlip(race, betType, selection, 0, details)} />
              ) : selectedSport === 'mlb' ? (
                <div className="games-grid" key={`${selectedSport}-${filter}`}>
                  {filteredGames.length === 0 ? (
                    renderNoGames('MLB')
                  ) : filteredGames.map(game => {
                    const status = getGameStatus(game);
                    const expanded = expandedGame === game.id;
                    const bettable = isGameBettable(game);
                    const mm = mlbMarkets(game);   // odds engine: run line, total, team HR, exact score, strikeouts
                    const runLineHome = mm?.runline.home ?? null;   // home -1.5
                    const runLineAway = mm?.runline.away ?? null;   // away +1.5
                    const totalLine = mm?.total.line ?? 8.5;
                    const gameScore = customScore[game.id] || { home: '', away: '' };
                    return (
                      <div key={game.id} className={`game-card${expanded ? ' expanded' : ''}${status.live ? ' live-game' : ''}${status.final ? ' finished-game' : ''}${hrAnimations[game.id] ? ' hr-active' : ''}${(game.home_abbrev === currentFav || game.away_abbrev === currentFav) ? ' fav-game' : ''}`} style={(themeMode !== 'none' && currentFav && (game.home_abbrev === currentFav || game.away_abbrev === currentFav)) ? { borderLeft: `3px solid ${(selectedSport === 'mlb' ? MLB_TEAM_COLORS : NHL_TEAM_COLORS)[currentFav]?.primary}`, boxShadow: `0 0 8px ${(selectedSport === 'mlb' ? MLB_TEAM_COLORS : NHL_TEAM_COLORS)[currentFav]?.primary}22` } : undefined}>
                        <button className={`fav-star${currentFav && (game.home_abbrev === currentFav || game.away_abbrev === currentFav) ? ' active' : ''}`} onClick={(e) => { e.stopPropagation(); const fav = selectedSport === 'mlb' ? favoriteMLB : favoriteNHL; if (fav && (game.home_abbrev === fav || game.away_abbrev === fav)) { setFavoriteTeam(selectedSport, ''); } else { setFavoriteTeam(selectedSport, game.home_abbrev); } }}>{currentFav && (game.home_abbrev === currentFav || game.away_abbrev === currentFav) ? '★' : '☆'}</button>
                        <div className="game-header" onClick={() => {
                          if (!(bettable || status.live || status.final)) return;
                          if (expanded) { setExpandedGame(null); setMlbLiveStats(null); }
                          else { setExpandedGame(game.id); if (status.live) fetchMLBGameStats(game.id); }
                        }}>
                          <div className="nhl-scoreboard nhl-scoreboard--mlb">
                            <div className="nhl-main-row">
                            <div className="nhl-team away">
                              <img src={game.away_logo} alt={game.away_abbrev} className="team-logo mlb-logo" onError={(e) => { e.target.style.display='none'; }} />
                              <span className="nhl-name">{game.away_team}</span>
                            </div>
                            <div className="nhl-score-row">
                              {(status.live || status.final) ? (
                                <>
                                  <span className={`nhl-score away${game.away_score > game.home_score ? ' winning' : game.away_score < game.home_score ? ' losing' : ''}`}>{game.away_score}</span>
                                  <div className="mlb-center-group">
                                    <div className="mlb-inning-col">
                                      <div className={`nhl-period${status.live ? ' live' : ''}${status.final ? ' final' : ''}`}>
                                        {status.live && <span className="live-dot"></span>}
                                        <span>{status.live ? `${game.inning_half === 'Top' ? '▲' : '▼'}${game.inning}` : 'FINAL'}</span>
                                      </div>
                                      {status.live && (
                                        <div className="mlb-count">{game.balls ?? 0} - {game.strikes ?? 0}</div>
                                      )}
                                    </div>
                                    {status.live && (
                                      <div className="mlb-situation">
                                        <div className="mlb-bases-display">
                                          <div className={`mlb-base mlb-base--second${game.on_second ? ' occupied' : ''}`}></div>
                                          <div className="mlb-bases-row">
                                            <div className={`mlb-base mlb-base--third${game.on_third ? ' occupied' : ''}`}></div>
                                            <div className={`mlb-base mlb-base--first${game.on_first ? ' occupied' : ''}`}></div>
                                          </div>
                                        </div>
                                        <div className="mlb-outs">
                                          <div className={`mlb-out${game.outs >= 1 ? ' out' : ''}`}></div>
                                          <div className={`mlb-out${game.outs >= 2 ? ' out' : ''}`}></div>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                  <span className={`nhl-score home${game.home_score > game.away_score ? ' winning' : game.home_score < game.away_score ? ' losing' : ''}`}>{game.home_score}</span>
                                </>
                              ) : (
                                <div className="nhl-upcoming">
                                  <span className="nhl-time-main">{status.text}</span>
                                  <span className="nhl-venue">{game.venue}</span>
                                </div>
                              )}
                            </div>
                            <div className="nhl-team home">
                              <img src={game.home_logo} alt={game.home_abbrev} className="team-logo mlb-logo" onError={(e) => { e.target.style.display='none'; }} />
                              <span className="nhl-name">{game.home_team}</span>
                            </div>
                            </div>
                          </div>
                          {bettable && (
                            <div className="quick-odds">
                              <button className="odd-btn" onClick={(e) => { e.stopPropagation(); addToBetSlip(game, 'winner', 'away', parseFloat(game.away_odds)); }}>
                                <span>{game.away_abbrev}</span>
                                {moneylineBoost() > 1 && <s className="odd-was">{((parseFloat(game.away_odds)) / moneylineBoost()).toFixed(2)}</s>}<span className="odd"><AnimatedNumber value={parseFloat(game.away_odds)} format={(n) => n.toFixed(2)} duration={400} /></span>
                              </button>
                              <button className="odd-btn" onClick={(e) => { e.stopPropagation(); addToBetSlip(game, 'winner', 'home', parseFloat(game.home_odds)); }}>
                                <span>{game.home_abbrev}</span>
                                {moneylineBoost() > 1 && <s className="odd-was">{((parseFloat(game.home_odds)) / moneylineBoost()).toFixed(2)}</s>}<span className="odd"><AnimatedNumber value={parseFloat(game.home_odds)} format={(n) => n.toFixed(2)} duration={400} /></span>
                              </button>
                            </div>
                          )}

                          {hrAnimations[game.id] && (() => {
                            const anim = hrAnimations[game.id];
                            const teamColor = (anim.team && MLB_TEAM_COLORS[anim.team]?.primary) || '#1e5aa8';
                            const isGS = anim.label.includes('GRAND');
                            const teamLogo = anim.team === game.away_abbrev ? game.away_logo : anim.team === game.home_abbrev ? game.home_logo : null;
                            return (
                              <div className={`hr-overlay${isGS ? ' grand-slam' : ''}`}>
                                <div className="hr-flash" />
                                <div className="hr-rows-container">
                                  {[-0, -15, -30, -45].map((delay, ri) => {
                                    const t = anim.label.replace('!','');
                                    const copies = Array(15).fill(t);
                                    return (
                                      <div key={ri} className="hr-row">
                                        <div className="hr-row-inner" style={{ animationDelay: `${delay}s` }}>
                                          {[...copies, ...copies].map((word, i) => <span key={i}>{word}</span>)}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                                <div className="hr-overlay-content">
                                  <div className="hr-overlay-main">
                                    {teamLogo && <img src={teamLogo} alt={anim.team} className="hr-team-logo" onError={e => e.target.style.display='none'} />}
                                    <span className="hr-overlay-text" style={{ color: teamColor, textShadow: `0 4px 16px rgba(0,0,0,0.7), 0 0 50px ${teamColor}88, 0 2px 0 rgba(0,0,0,0.6)` }}>{anim.label}</span>
                                    {teamLogo && <img src={teamLogo} alt={anim.team} className="hr-team-logo" onError={e => e.target.style.display='none'} />}
                                  </div>
                                  {anim.batter && <span className="hr-overlay-batter">{anim.batter}</span>}
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                        {/* MLB Live Stats Panel */}
                        {status.final && <GameRecap sport="mlb" game={game} final />}
                        {expanded && status.live && <GameRecap sport="mlb" game={game} />}
                        {expanded && status.live && (
                          <div className="live-stats-panel" style={statColors(game)}>
                            <h4>{t.statistics}</h4>
                            {loadingMLBStats && !mlbLiveStats ? (
                              <div className="stats-loading">{t.loadingStats}</div>
                            ) : mlbLiveStats ? (<>
                              <div className="stats-grid">
                                {(mlbLiveStats.currentBatter || mlbLiveStats.currentPitcher) && (
                                  <div className="mlb-matchup">
                                    {mlbLiveStats.currentPitcher && (
                                      <div className="mlb-matchup-row">
                                        <span className="mlb-matchup-label">{lang === 'fr' ? 'Lanceur' : 'Pitching'}</span>
                                        <span className="mlb-matchup-name">
                                          {mlbLiveStats.currentPitcher}
                                          {mlbLiveStats.pitchHand && <span className="mlb-matchup-hand">({mlbLiveStats.pitchHand[0]}HP)</span>}
                                        </span>
                                      </div>
                                    )}
                                    {mlbLiveStats.currentBatter && (
                                      <div className="mlb-matchup-row">
                                        <span className="mlb-matchup-label">{lang === 'fr' ? 'Au bâton' : 'At bat'}</span>
                                        <span className="mlb-matchup-name">
                                          {mlbLiveStats.currentBatter}
                                          {mlbLiveStats.batSide && <span className="mlb-matchup-hand">({mlbLiveStats.batSide[0]}HB)</span>}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                )}
                                {[
                                  { key: 'hits', label: t.statHits, higherIsBetter: true },
                                  { key: 'homeRuns', label: t.statHomeRuns, higherIsBetter: true },
                                  { key: 'rbi', label: t.statRBI, higherIsBetter: true },
                                  { key: 'stolenBases', label: t.statStolenBases, higherIsBetter: true },
                                  { key: 'strikeOuts', label: t.statStrikeoutsBat, higherIsBetter: false },
                                ].map(({ key, label, higherIsBetter }) => {
                                  const away = mlbLiveStats.away?.stats?.[key] || 0;
                                  const home = mlbLiveStats.home?.stats?.[key] || 0;
                                  const awayWins = higherIsBetter ? away > home : away < home;
                                  const homeWins = higherIsBetter ? home > away : home < away;
                                  const awayClass = awayWins ? 'winning' : away !== home ? 'losing' : 'tied';
                                  const homeClass = homeWins ? 'winning' : home !== away ? 'losing' : 'tied';
                                  return (
                                    <div key={key} className="stat-bar-row">
                                      <span className="stat-val left">{away}</span>
                                      <div className="stat-bar-container">
                                        <div className="stat-bar-label">{label}</div>
                                        <div className="stat-bar">
                                          <div className={`stat-bar-fill away ${awayClass}`} style={{width:`${away/Math.max(away+home,1)*100}%`}}></div>
                                          <div className={`stat-bar-fill home ${homeClass}`} style={{width:`${home/Math.max(away+home,1)*100}%`}}></div>
                                        </div>
                                      </div>
                                      <span className="stat-val right">{home}</span>
                                    </div>
                                  );
                                })}
                                {[
                                  { key: 'baseOnBalls', label: t.statWalks },
                                  { key: 'leftOnBase', label: t.statLeftOnBase },
                                  { key: 'pitchingStrikeOuts', label: t.statStrikeoutsPitch },
                                  { key: 'numberOfPitches', label: t.statPitchesThrown },
                                ].map(({ key, label }) => (
                                  <div key={key} className="stat-bar-row">
                                    <span className="stat-val left">{mlbLiveStats.away?.stats?.[key] || 0}</span>
                                    <div className="stat-bar-container">
                                      <div className="stat-bar-label">{label}</div>
                                      <div className="stat-bar">
                                        <div className="stat-bar-fill away tied" style={{ width: `${(mlbLiveStats.away?.stats?.[key] || 0) / Math.max((mlbLiveStats.away?.stats?.[key] || 0) + (mlbLiveStats.home?.stats?.[key] || 0), 1) * 100}%` }}></div>
                                        <div className="stat-bar-fill home tied" style={{ width: `${(mlbLiveStats.home?.stats?.[key] || 0) / Math.max((mlbLiveStats.away?.stats?.[key] || 0) + (mlbLiveStats.home?.stats?.[key] || 0), 1) * 100}%` }}></div>
                                      </div>
                                    </div>
                                    <span className="stat-val right">{mlbLiveStats.home?.stats?.[key] || 0}</span>
                                  </div>
                                ))}
                              </div>
                              {mlbLiveStats?.recentPlays?.length > 0 && (
                                <div className="pbp-feed">
                                  <h5>{t.recentPlays}</h5>
                                  <div className="pbp-list">
                                    {mlbLiveStats.recentPlays.map((play, i) => (
                                      <div key={i} className={`pbp-play ${play.type}`}>
                                        <span className="pbp-time">{play.half === 'top' ? '▲' : '▼'}{play.inning}</span>
                                        <span className="pbp-team">{play.team}</span>
                                        <span className="pbp-desc">{translatePlay(play, 'mlb')}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </>) : (
                              <div className="stats-error">{t.statsUnavailable}</div>
                            )}
                          </div>
                        )}

                        {expanded && bettable && (() => {
                          const homeRoster = mlbRosters[String(game.home_team_id)] || { batters: [], pitchers: [] };
                          const awayRoster = mlbRosters[String(game.away_team_id)] || { batters: [], pitchers: [] };
                          return (
                          <div className="markets">
                            <div className="market">
                              <h5>{t.moneyline}</h5>
                              <p className="market-desc">{t.moneylineDesc}</p>
                              <div className="market-row">
                                <button onClick={() => addToBetSlip(game, 'winner', 'away', parseFloat(game.away_odds))}>
                                  {game.away_team} <span>{fmtMoneyline(parseFloat(game.away_odds))}</span>
                                </button>
                                <button onClick={() => addToBetSlip(game, 'winner', 'home', parseFloat(game.home_odds))}>
                                  {game.home_team} <span>{fmtMoneyline(parseFloat(game.home_odds))}</span>
                                </button>
                              </div>
                            </div>
                            <div className="market">
                              <h5>{lang === 'fr' ? 'Ligne de points' : 'Run Line'}</h5>
                              <p className="market-desc">{t.runLineDesc}</p>
                              <div className="market-row puckline-row">
                                <button onClick={() => addToBetSlip(game, 'runline', 'away', runLineAway, { team: game.away_abbrev, line: 1.5 })} className="puckline-btn">
                                  <div className="puckline-team">{game.away_team}</div>
                                  <div className="puckline-line">+1.5</div>
                                  <div className="puckline-explain">{t.wontLoseBy2}</div>
                                  <span className="puckline-odds">{fmtOdds(runLineAway)}</span>
                                </button>
                                <button onClick={() => addToBetSlip(game, 'runline', 'home', runLineHome, { team: game.home_abbrev, line: -1.5 })} className="puckline-btn">
                                  <div className="puckline-team">{game.home_team}</div>
                                  <div className="puckline-line">-1.5</div>
                                  <div className="puckline-explain">{t.winsBy2}</div>
                                  <span className="puckline-odds">{fmtOdds(runLineHome)}</span>
                                </button>
                              </div>
                            </div>
                            <div className="market">
                              <h5>{lang === 'fr' ? `Total de points (O/U ${totalLine})` : `Total Runs (O/U ${totalLine})`}</h5>
                              <p className="market-desc">{t.totalRunsDesc}</p>
                              <div className="market-row">
                                <button onClick={() => addToBetSlip(game, 'total', 'over', 0, { type: 'Over', line: totalLine })}>
                                  Over {totalLine} <span>{fmtOdds(mm?.total.over)}</span>
                                </button>
                                <button onClick={() => addToBetSlip(game, 'total', 'under', 0, { type: 'Under', line: totalLine })}>
                                  Under {totalLine} <span>{fmtOdds(mm?.total.under)}</span>
                                </button>
                              </div>
                            </div>
                            <div className="market">
                              <h5>{t.teamHR}</h5>
                              <p className="market-desc">{t.teamHRDesc}</p>
                              <div className="market-row">
                                <button onClick={() => addToBetSlip(game, 'mlb_team_hr', 'away', 0, { team: game.away_abbrev, line: 0.5 })}>
                                  {game.away_abbrev} Over 0.5 HR <span>{fmtOdds(mm?.teamHr.away[0].odds)}</span>
                                </button>
                                <button onClick={() => addToBetSlip(game, 'mlb_team_hr', 'home', 0, { team: game.home_abbrev, line: 0.5 })}>
                                  {game.home_abbrev} Over 0.5 HR <span>{fmtOdds(mm?.teamHr.home[0].odds)}</span>
                                </button>
                              </div>
                              <div className="market-row">
                                <button onClick={() => addToBetSlip(game, 'mlb_team_hr', 'away', 0, { team: game.away_abbrev, line: 1.5 })}>
                                  {game.away_abbrev} Over 1.5 HR <span>{fmtOdds(mm?.teamHr.away[1].odds)}</span>
                                </button>
                                <button onClick={() => addToBetSlip(game, 'mlb_team_hr', 'home', 0, { team: game.home_abbrev, line: 1.5 })}>
                                  {game.home_abbrev} Over 1.5 HR <span>{fmtOdds(mm?.teamHr.home[1].odds)}</span>
                                </button>
                              </div>
                              <div className="market-row">
                                <button onClick={() => addToBetSlip(game, 'mlb_team_hr', 'away', 0, { team: game.away_abbrev, line: 2.5 })}>
                                  {game.away_abbrev} Over 2.5 HR <span>{fmtOdds(mm?.teamHr.away[2].odds)}</span>
                                </button>
                                <button onClick={() => addToBetSlip(game, 'mlb_team_hr', 'home', 0, { team: game.home_abbrev, line: 2.5 })}>
                                  {game.home_abbrev} Over 2.5 HR <span>{fmtOdds(mm?.teamHr.home[2].odds)}</span>
                                </button>
                              </div>
                            </div>
                            {/* Batter Props — locked during Spring Training */}
                            {game.game_type === 'S' ? (
                              <div className="market">
                                <h5>{t.mlbBatterProps}</h5>
                                <p className="market-desc">{t.mlbBatterPropsDesc}</p>
                                <div className="spring-training-lock">
                                  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ verticalAlign: '-2px', marginRight: 6 }}><rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V8a4 4 0 0 1 8 0v3" /></svg>{lang === 'fr' ? 'Paris sur joueurs disponibles en saison régulière (à partir du 1er avril)' : 'Player props available for Regular Season games (starting April 1st)'}
                                </div>
                              </div>
                            ) : (() => {
                              // one section for both teams (like hockey): away batters first, then home, each with its team logo
                              const batters = [
                                ...awayRoster.batters.slice(0, 13).map((p) => ({ ...p, _abbrev: game.away_abbrev })),
                                ...homeRoster.batters.slice(0, 13).map((p) => ({ ...p, _abbrev: game.home_abbrev })),
                              ];
                              if (!batters.length) return null;
                              const CATS = [
                                ['Home Runs', [['mlb_batter_hr', 'over', 3.50, 0.5, '1+ HR'], ['mlb_batter_hr', 'over', 12.00, 1.5, '2+ HR'], ['mlb_batter_hr', 'over', 35.00, 2.5, '3+ HR']]],
                                ['RBI', [['mlb_batter_rbi', 'over', 2.20, 0.5, '1+ RBI'], ['mlb_batter_rbi', 'over', 4.50, 1.5, '2+ RBI'], ['mlb_batter_rbi', 'over', 9.00, 2.5, '3+ RBI'], ['mlb_batter_rbi', 'over', 20.00, 3.5, '4+ RBI']]],
                                ['Stolen Bases', [['mlb_batter_sb', 'over', 3.50, 0.5, '1+ SB'], ['mlb_batter_sb', 'over', 12.00, 1.5, '2+ SB']]],
                                ['Hits', [['mlb_batter_1b', 'over', 2.00, 0.5, '1+ 1B'], ['mlb_batter_2b', 'over', 3.50, 0.5, '1+ 2B'], ['mlb_batter_3b', 'over', 15.00, 0.5, '1+ 3B'], ['mlb_batter_cycle', 'yes', 150.00, null, 'Cycle']]],
                                ['At Bats', [['mlb_batter_ab', 'over', 1.85, 3.5, 'O3.5 AB'], ['mlb_batter_ab', 'over', 2.50, 4.5, 'O4.5 AB']]],
                              ];
                              return (
                                <div className="market">
                                  <h5>{t.mlbBatterProps}</h5>
                                  <p className="market-desc">{t.mlbBatterPropsDesc}</p>
                                  {CATS.map(([title, bets]) => (
                                    <div className="prop-category" key={title}>
                                      <div className="prop-category-header">{title}</div>
                                      <div className="player-prop-list">
                                        {batters.map((p) => (
                                          <div key={`${title}-${p.id}`} className="player-prop-row">
                                            <span className="player-prop-name">
                                              <img className="prop-team-logo" alt="" src={teamLogoUrl('mlb', p._abbrev)} onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }} />
                                              <span>{p.name}</span>
                                            </span>
                                            <div className="prop-btns" style={{ '--n': bets.length }}>
                                              {bets.map(([type, sel, odds, line, label]) => (
                                                <button key={label} onClick={() => addToBetSlip(game, type, sel, odds, line == null ? { playerId: p.id, playerName: p.name } : { playerId: p.id, playerName: p.name, line })}>
                                                  {label} <span>{fmtOdds(mlbBatterMarkets(playerRates.mlb[String(p.id)])?.[type]?.[String(line)])}</span>
                                                </button>
                                              ))}
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              );
                            })()}
                            {/* Pitcher Strikeouts — one section for both teams */}
                            {game.game_type !== 'S' && (() => {
                              // today's probable starters only (a reliever never gets to 5 strikeouts)
                              const pitchers = [
                                { id: game.away_pitcher_id, name: game.away_pitcher, _abbrev: game.away_abbrev, ks: mm?.ks(false) },
                                { id: game.home_pitcher_id, name: game.home_pitcher, _abbrev: game.home_abbrev, ks: mm?.ks(true) },
                              ].filter((p) => p.id && p.ks);
                              if (!pitchers.length) return null;
                              return (
                                <div className="market">
                                  <h5>{t.mlbPitcherProps}</h5>
                                  <p className="market-desc">{t.mlbPitcherPropsDesc}</p>
                                  <div className="player-prop-list">
                                    {pitchers.map((p) => (
                                      <div key={p.id} className="player-prop-row">
                                        <span className="player-prop-name">
                                          <img className="prop-team-logo" alt="" src={teamLogoUrl('mlb', p._abbrev)} onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }} />
                                          <span>{p.name}</span>
                                        </span>
                                        <div className="prop-btns" style={{ '--n': 2 }}>
                                          <button onClick={() => addToBetSlip(game, 'mlb_pitcher_ks', 'over', 0, { playerId: p.id, playerName: p.name, type: 'Over', line: p.ks.line })}>
                                            O{p.ks.line} K <span>{fmtOdds(p.ks.over)}</span>
                                          </button>
                                          <button onClick={() => addToBetSlip(game, 'mlb_pitcher_ks', 'under', 0, { playerId: p.id, playerName: p.name, type: 'Under', line: p.ks.line })}>
                                            U{p.ks.line} K <span>{fmtOdds(p.ks.under)}</span>
                                          </button>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              );
                            })()}
                            {/* Correct Score */}
                            <div className="market">
                              <h5>{t.correctScore}</h5>
                              <p className="market-desc">{t.correctScoreDesc}</p>
                              <div className="score-grid">
                                {[[3, 1], [4, 2], [5, 3], [3, 2], [2, 1], [6, 4], [1, 3], [2, 4], [3, 5]].map(([h, a]) => (
                                  <button key={`${h}-${a}`} onClick={() => addToBetSlip(game, 'exact_score', 'home', 0, { home: h, away: a })}>
                                    {h}-{a} <span>{fmtOdds(mlbMarkets(game)?.exact(h, a))}</span>
                                  </button>
                                ))}
                              </div>
                              <p className="market-sub">{lang === 'fr' ? 'Ou entrez un score personnalisé:' : 'Or enter a custom score:'}</p>
                              <div className="score-input-row">
                                <div className="score-input">
                                  <label>{t.homeScore} ({game.home_team})</label>
                                  <input type="number" min="0" placeholder="0" value={gameScore.home}
                                    onChange={(e) => setCustomScore({ ...customScore, [game.id]: { ...gameScore, home: e.target.value } })}
                                    onClick={(e) => e.stopPropagation()} />
                                </div>
                                <span className="score-separator">-</span>
                                <div className="score-input">
                                  <label>{t.awayScore} ({game.away_team})</label>
                                  <input type="number" min="0" placeholder="0" value={gameScore.away}
                                    onChange={(e) => setCustomScore({ ...customScore, [game.id]: { ...gameScore, away: e.target.value } })}
                                    onClick={(e) => e.stopPropagation()} />
                                </div>
                                <button className="add-score-btn"
                                  disabled={gameScore.home === '' || gameScore.away === ''}
                                  onClick={() => {
                                    if (gameScore.home !== '' && gameScore.away !== '') {
                                      addToBetSlip(game, 'exact_score', 'home', 0, { home: parseInt(gameScore.home), away: parseInt(gameScore.away) });
                                    }
                                  }}>
                                  {t.addScore} ({gameScore.home || '?'}-{gameScore.away || '?'})
                                </button>
                              </div>
                            </div>
                          </div>
                          );
                        })()}
                      </div>
                    );
                  })}
                </div>
              ) : selectedSport === 'nba' ? (
                <div className="games-grid" key={`${selectedSport}-${filter}`}>
                  {filteredGames.length === 0 ? (
                    renderNoGames('NBA')
                  ) : filteredGames.map(game => {
                    const status = getGameStatus(game);
                    const expanded = expandedGame === game.id;
                    const bettable = isGameBettable(game);
                    const nm = nbaMarkets(game);   // odds engine: spread + total set to the matchup
                    const homeLine = nm ? -nm.spread.line : -2.5;   // home −x = must win by more than x
                    const awayLine = -homeLine;
                    const spreadHomeOdds = nm?.spread.home ?? null;
                    const spreadAwayOdds = nm?.spread.away ?? null;
                    const totalLine = nm?.total.line ?? 226.5;
                    const spreadExplain = (teamLine) => (teamLine < 0
                      ? (lang === 'fr' ? `Gagne par ${Math.ceil(-teamLine)}+` : `Wins by ${Math.ceil(-teamLine)}+`)
                      : (lang === 'fr' ? `Ne perdra pas par ${Math.ceil(teamLine)}+` : `Won't lose by ${Math.ceil(teamLine)}+`));
                    const currentFavNBA = favoriteNBA;

                    // Format game clock for display
                    const formatClock = (clock) => {
                      if (!clock) return '';
                      // Handle ISO duration like PT05M30.00S
                      const match = clock.match(/PT(\d+)M([\d.]+)S/);
                      if (match) return `${match[1]}:${Math.floor(parseFloat(match[2])).toString().padStart(2, '0')}`;
                      return clock;
                    };
                    const periodLabel = (p) => p <= 4 ? `Q${p}` : p === 5 ? 'OT' : `${p - 4}OT`;

                    return (
                      <div key={game.id} className={`game-card${expanded ? ' expanded' : ''}${status.live ? ' live-game' : ''}${status.final ? ' finished-game' : ''}${(game.home_abbrev === currentFavNBA || game.away_abbrev === currentFavNBA) ? ' fav-game' : ''}`} style={(themeMode !== 'none' && currentFavNBA && (game.home_abbrev === currentFavNBA || game.away_abbrev === currentFavNBA)) ? { borderLeft: `3px solid ${NBA_TEAM_COLORS[currentFavNBA]?.primary}`, boxShadow: `0 0 8px ${NBA_TEAM_COLORS[currentFavNBA]?.primary}22` } : undefined}>
                        <button className={`fav-star${currentFavNBA && (game.home_abbrev === currentFavNBA || game.away_abbrev === currentFavNBA) ? ' active' : ''}`} onClick={(e) => { e.stopPropagation(); if (favoriteNBA && (game.home_abbrev === favoriteNBA || game.away_abbrev === favoriteNBA)) { setFavoriteTeam('nba', ''); } else { setFavoriteTeam('nba', game.home_abbrev); } }}>{currentFavNBA && (game.home_abbrev === currentFavNBA || game.away_abbrev === currentFavNBA) ? '★' : '☆'}</button>
                        <div className="game-header" onClick={() => {
                          if (!(bettable || status.live || status.final)) return;
                          if (expanded) { setExpandedGame(null); setNbaLiveStats(null); }
                          else { setExpandedGame(game.id); if (status.live) fetchNBAGameStats(game.id, { home: game.home_abbrev, away: game.away_abbrev }); }
                        }}>
                          <div className="nhl-scoreboard nhl-scoreboard--nba">
                            <div className="nhl-main-row">
                            <div className="nhl-team away">
                              <img src={game.away_logo} alt={game.away_abbrev} className="team-logo nba-logo" onError={(e) => { e.target.style.display='none'; }} />
                              <span className="nhl-name">{game.away_team}</span>
                            </div>
                            <div className="nhl-score-row">
                              {(status.live || status.final) ? (
                                <>
                                  <span className={`nhl-score away${game.away_score > game.home_score ? ' winning' : game.away_score < game.home_score ? ' losing' : ''}`}>{game.away_score}</span>
                                  <div className={`nhl-period${status.live ? ' live' : ''}${status.final ? ' final' : ''}`}>
                                    {status.live && <span className="live-dot"></span>}
                                    <span>{status.live ? `${periodLabel(game.period)} ${formatClock(game.game_clock)}` : 'FINAL'}</span>
                                  </div>
                                  <span className={`nhl-score home${game.home_score > game.away_score ? ' winning' : game.home_score < game.away_score ? ' losing' : ''}`}>{game.home_score}</span>
                                </>
                              ) : (
                                <div className="nhl-upcoming">
                                  <span className="nhl-time-main">{status.text}</span>
                                  <span className="nhl-venue">{game.venue}</span>
                                </div>
                              )}
                            </div>
                            <div className="nhl-team home">
                              <img src={game.home_logo} alt={game.home_abbrev} className="team-logo nba-logo" onError={(e) => { e.target.style.display='none'; }} />
                              <span className="nhl-name">{game.home_team}</span>
                            </div>
                            </div>
                          </div>
                          {bettable && (
                            <div className="quick-odds">
                              <button className="odd-btn" onClick={(e) => { e.stopPropagation(); addToBetSlip(game, 'winner', 'away', parseFloat(game.away_odds)); }}>
                                <span>{game.away_abbrev}</span>
                                {moneylineBoost() > 1 && <s className="odd-was">{((parseFloat(game.away_odds)) / moneylineBoost()).toFixed(2)}</s>}<span className="odd"><AnimatedNumber value={parseFloat(game.away_odds)} format={(n) => n.toFixed(2)} duration={400} /></span>
                              </button>
                              <button className="odd-btn" onClick={(e) => { e.stopPropagation(); addToBetSlip(game, 'winner', 'home', parseFloat(game.home_odds)); }}>
                                <span>{game.home_abbrev}</span>
                                {moneylineBoost() > 1 && <s className="odd-was">{((parseFloat(game.home_odds)) / moneylineBoost()).toFixed(2)}</s>}<span className="odd"><AnimatedNumber value={parseFloat(game.home_odds)} format={(n) => n.toFixed(2)} duration={400} /></span>
                              </button>
                            </div>
                          )}
                        </div>

                        {/* NBA Live Stats Panel */}
                        {status.final && <GameRecap sport="nba" game={game} final />}
                        {expanded && status.live && <GameRecap sport="nba" game={game} />}
                        {expanded && status.live && (
                          <div className="live-stats-panel" style={statColors(game)}>
                            <h4>{t.statistics}</h4>
                            {loadingNBAStats && !nbaLiveStats ? (
                              <div className="stats-loading">{t.loadingStats}</div>
                            ) : nbaLiveStats ? (<>
                              <div className="stats-grid">
                                {[
                                  { key: 'points', label: t.statPoints, higherIsBetter: true },
                                  { key: 'rebounds', label: t.statRebounds, higherIsBetter: true },
                                  { key: 'assists', label: t.statAssists, higherIsBetter: true },
                                  { key: 'steals', label: t.statSteals, higherIsBetter: true },
                                  { key: 'blocks', label: t.statBlocks, higherIsBetter: true },
                                  { key: 'turnovers', label: t.statTurnovers, higherIsBetter: false },
                                ].map(({ key, label, higherIsBetter }) => {
                                  const away = nbaLiveStats.away?.stats?.[key] || 0;
                                  const home = nbaLiveStats.home?.stats?.[key] || 0;
                                  const awayWins = higherIsBetter ? away > home : away < home;
                                  const homeWins = higherIsBetter ? home > away : home < away;
                                  const awayClass = awayWins ? 'winning' : away !== home ? 'losing' : 'tied';
                                  const homeClass = homeWins ? 'winning' : home !== away ? 'losing' : 'tied';
                                  return (
                                    <div key={key} className="stat-bar-row">
                                      <span className="stat-val left">{away}</span>
                                      <div className="stat-bar-container">
                                        <div className="stat-bar-label">{label}</div>
                                        <div className="stat-bar">
                                          <div className={`stat-bar-fill away ${awayClass}`} style={{width:`${away/Math.max(away+home,1)*100}%`}}></div>
                                          <div className={`stat-bar-fill home ${homeClass}`} style={{width:`${home/Math.max(away+home,1)*100}%`}}></div>
                                        </div>
                                      </div>
                                      <span className="stat-val right">{home}</span>
                                    </div>
                                  );
                                })}
                                {[
                                  { key: 'fgPct', label: t.statFGPct, format: v => `${(v * 100).toFixed(1)}%` },
                                  { key: 'threePct', label: t.stat3PTPct, format: v => `${(v * 100).toFixed(1)}%` },
                                  { key: 'ftPct', label: t.statFTPct, format: v => `${(v * 100).toFixed(1)}%` },
                                  { key: 'fastBreakPoints', label: t.statFastBreak },
                                  { key: 'pointsInPaint', label: t.statPaintPts },
                                  { key: 'benchPoints', label: t.statBenchPts },
                                ].map(({ key, label, format }) => (
                                  <div key={key} className="stat-bar-row">
                                    <span className="stat-val left">{format ? format(nbaLiveStats.away?.stats?.[key] || 0) : (nbaLiveStats.away?.stats?.[key] || 0)}</span>
                                    <div className="stat-bar-container">
                                      <div className="stat-bar-label">{label}</div>
                                    </div>
                                    <span className="stat-val right">{format ? format(nbaLiveStats.home?.stats?.[key] || 0) : (nbaLiveStats.home?.stats?.[key] || 0)}</span>
                                  </div>
                                ))}
                              </div>
                              {nbaLiveStats?.recentPlays?.length > 0 && (
                                <div className="pbp-feed">
                                  <h5>{t.recentPlays}</h5>
                                  <div className="pbp-list">
                                    {nbaLiveStats.recentPlays.map((play, i) => (
                                      <div key={i} className={`pbp-play ${play.type}`}>
                                        <span className="pbp-time">{periodLabel(play.period)} {play.clock?.replace(/^PT/, '').replace(/\.00S$/, 's').replace('M', ':')}</span>
                                        <span className="pbp-team">{play.team}</span>
                                        <span className="pbp-desc">{translatePlay(play, 'nba')}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </>) : (
                              <div className="stats-error">{t.statsUnavailable}</div>
                            )}
                          </div>
                        )}

                        {/* NBA Betting Markets */}
                        {expanded && bettable && (() => {
                          const homeRoster = nbaRosters[game.home_abbrev] || { players: [] };
                          const awayRoster = nbaRosters[game.away_abbrev] || { players: [] };
                          return (
                          <div className="markets">
                            <div className="market">
                              <h5>{t.moneyline}</h5>
                              <p className="market-desc">{t.moneylineDesc}</p>
                              <div className="market-row">
                                <button onClick={() => addToBetSlip(game, 'winner', 'away', parseFloat(game.away_odds))}>
                                  {game.away_team} <span>{fmtMoneyline(parseFloat(game.away_odds))}</span>
                                </button>
                                <button onClick={() => addToBetSlip(game, 'winner', 'home', parseFloat(game.home_odds))}>
                                  {game.home_team} <span>{fmtMoneyline(parseFloat(game.home_odds))}</span>
                                </button>
                              </div>
                            </div>
                            <div className="market">
                              <h5>{t.nbaSpread}</h5>
                              <p className="market-desc">{t.nbaSpreadDesc}</p>
                              <div className="market-row puckline-row">
                                <button onClick={() => addToBetSlip(game, 'spread', 'away', 0, { team: game.away_abbrev, line: awayLine })} className="puckline-btn">
                                  <div className="puckline-team">{game.away_team}</div>
                                  <div className="puckline-line">{fmtLine(awayLine)}</div>
                                  <div className="puckline-explain">{spreadExplain(awayLine)}</div>
                                  <span className="puckline-odds">{fmtOdds(spreadAwayOdds)}</span>
                                </button>
                                <button onClick={() => addToBetSlip(game, 'spread', 'home', 0, { team: game.home_abbrev, line: homeLine })} className="puckline-btn">
                                  <div className="puckline-team">{game.home_team}</div>
                                  <div className="puckline-line">{fmtLine(homeLine)}</div>
                                  <div className="puckline-explain">{spreadExplain(homeLine)}</div>
                                  <span className="puckline-odds">{fmtOdds(spreadHomeOdds)}</span>
                                </button>
                              </div>
                            </div>
                            <div className="market">
                              <h5>{t.nbaTotalPoints} (O/U {totalLine})</h5>
                              <p className="market-desc">{t.nbaTotalPointsDesc}</p>
                              <div className="market-row">
                                <button onClick={() => addToBetSlip(game, 'total', 'over', 0, { type: 'Over', line: totalLine })}>
                                  Over {totalLine} <span>{fmtOdds(nm?.total.over)}</span>
                                </button>
                                <button onClick={() => addToBetSlip(game, 'total', 'under', 0, { type: 'Under', line: totalLine })}>
                                  Under {totalLine} <span>{fmtOdds(nm?.total.under)}</span>
                                </button>
                              </div>
                            </div>
                            {/* ── NBA: team totals · quarters & halves · winning margin · overtime & extras ── */}
                            {nm && (() => {
                              const fr = lang === 'fr';
                              const OV = fr ? 'Plus de' : 'Over', UN = fr ? 'Moins de' : 'Under', YES = fr ? 'Oui' : 'Yes', NO = fr ? 'Non' : 'No';
                              const sides = [['away', game.away_team], ['home', game.home_team]];
                              const bet = (bt, sel, details) => addToBetSlip(game, bt, sel, 0, details);
                              return (
                                <>
                                  <div className="market">
                                    <h5>{fr ? 'Total par équipe' : 'Team totals'}</h5>
                                    <p className="market-desc">{fr ? 'Les points d’une seule équipe, plus ou moins que la ligne' : 'One team’s own points, over or under its line'}</p>
                                    {sides.map(([side, name]) => {
                                      const tt = nm.teamTotal[side];
                                      return (
                                        <div className="market-row" key={side}>
                                          <button disabled={!tt.over} onClick={() => bet('nba_team_total', 'over', { team: side, teamName: name, line: tt.line })}>{name} {OV} {tt.line} <span>{fmtOdds(tt.over)}</span></button>
                                          <button disabled={!tt.under} onClick={() => bet('nba_team_total', 'under', { team: side, teamName: name, line: tt.line })}>{name} {UN} {tt.line} <span>{fmtOdds(tt.under)}</span></button>
                                        </div>
                                      );
                                    })}
                                  </div>

                                  <div className="market">
                                    <h5>{fr ? 'Quarts et demies' : 'Quarters and halves'}</h5>
                                    <p className="market-desc">{fr ? 'Qui mène après le 1er quart ou à la demie (égalité = mise remboursée), et le total de la 1re demie' : 'Who leads after the 1st quarter or at the half (level = stake back), and the 1st-half total'}</p>
                                    {[['q1', fr ? '1er quart' : '1st quarter'], ['h1', fr ? '1re demie' : '1st half']].map(([period, label]) => (
                                      <React.Fragment key={period}>
                                        <div className="market-sub">{label}</div>
                                        <div className="market-row">
                                          {sides.map(([side, name]) => (
                                            <button key={side} disabled={!nm[period][side]} onClick={() => bet('nba_quarter_winner', side, { period, teamName: name })}>{name} <span>{fmtOdds(nm[period][side])}</span></button>
                                          ))}
                                        </div>
                                      </React.Fragment>
                                    ))}
                                    <div className="market-sub">{fr ? 'Total de la 1re demie' : '1st-half total'}</div>
                                    <div className="market-row">
                                      <button disabled={!nm.halfTotal.over} onClick={() => bet('nba_half_total', 'over', { line: nm.halfTotal.line })}>{OV} {nm.halfTotal.line} <span>{fmtOdds(nm.halfTotal.over)}</span></button>
                                      <button disabled={!nm.halfTotal.under} onClick={() => bet('nba_half_total', 'under', { line: nm.halfTotal.line })}>{UN} {nm.halfTotal.line} <span>{fmtOdds(nm.halfTotal.under)}</span></button>
                                    </div>
                                  </div>

                                  <div className="market">
                                    <h5>{fr ? 'Écart de la victoire' : 'Winning margin'}</h5>
                                    <p className="market-desc">{fr ? 'Le gagnant ET par combien de points' : 'The winner AND by how many points'}</p>
                                    {sides.map(([side, name]) => (
                                      <React.Fragment key={side}>
                                        <div className="market-sub">{name}</div>
                                        <div className="market-row four">
                                          {NBA_MARGIN_BANDS.map((band) => (
                                            <button key={band} disabled={!nm.margin[side][band]} onClick={() => bet('nba_margin', side, { band, teamName: name })}>{band.replace('-', '–')} <span>{fmtOdds(nm.margin[side][band])}</span></button>
                                          ))}
                                        </div>
                                      </React.Fragment>
                                    ))}
                                  </div>

                                  <div className="market">
                                    <h5>{fr ? 'Prolongation et extras' : 'Overtime and extras'}</h5>
                                    <p className="market-desc">{fr ? 'Y aura-t-il une prolongation, le total sera-t-il pair, les deux équipes atteindront-elles 110' : 'Will it go to overtime, will the total be odd or even, will both teams reach 110'}</p>
                                    <div className="market-sub">{fr ? 'Prolongation' : 'Overtime'}</div>
                                    <div className="market-row">
                                      <button disabled={!nm.overtime.yes} onClick={() => bet('nba_overtime', 'yes', {})}>{YES} <span>{fmtOdds(nm.overtime.yes)}</span></button>
                                      <button disabled={!nm.overtime.no} onClick={() => bet('nba_overtime', 'no', {})}>{NO} <span>{fmtOdds(nm.overtime.no)}</span></button>
                                    </div>
                                    <div className="market-sub">{fr ? 'Total de points pair ou impair' : 'Total points odd or even'}</div>
                                    <div className="market-row">
                                      <button onClick={() => bet('nba_odd_even', 'odd', {})}>{fr ? 'Impair' : 'Odd'} <span>{fmtOdds(nm.oddEven.odd)}</span></button>
                                      <button onClick={() => bet('nba_odd_even', 'even', {})}>{fr ? 'Pair' : 'Even'} <span>{fmtOdds(nm.oddEven.even)}</span></button>
                                    </div>
                                    <div className="market-sub">{fr ? 'Les deux équipes marquent 110+' : 'Both teams score 110+'}</div>
                                    <div className="market-row">
                                      <button disabled={!nm.both110.yes} onClick={() => bet('nba_both_110', 'yes', {})}>{YES} <span>{fmtOdds(nm.both110.yes)}</span></button>
                                      <button disabled={!nm.both110.no} onClick={() => bet('nba_both_110', 'no', {})}>{NO} <span>{fmtOdds(nm.both110.no)}</span></button>
                                    </div>
                                  </div>
                                </>
                              );
                            })()}

                            {/* ── NBA players: props (Over | Under), combos, top scorer, first basket ── */}
                            {(() => {
                              const fr = lang === 'fr';
                              const under = nbaPropSide === 'under';
                              const teams = [{ team: game.away_team, abbrev: game.away_abbrev, roster: awayRoster }, { team: game.home_team, abbrev: game.home_abbrev, roster: homeRoster }];
                              // each team's players, the ones who play the most first (only players we have numbers for)
                              const listOf = (roster, n) => roster.players
                                .map((p) => ({ ...p, _mk: nbaPlayerMarkets(playerRates.nba?.[String(p.id)]), _min: playerRates.nba?.[String(p.id)]?.min || 0 }))
                                .filter((p) => p._mk)
                                .sort((x, y) => y._min - x._min)
                                .slice(0, n);
                              const logo = (abbrev) => <img className="prop-team-logo nba" alt="" src={teamLogoUrl('nba', abbrev)} onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }} />;
                              const PROPS = [['nba_player_pts', 'pts', 'PTS'], ['nba_player_reb', 'reb', 'REB'], ['nba_player_ast', 'ast', 'AST'], ['nba_player_3pm', 'tpm', '3PM'], ['nba_player_stl', 'stl', 'STL'], ['nba_player_blk', 'blk', 'BLK']];
                              // top scorer / first basket: the 9 main players of each team, priced against each other
                              // (9 + 9 = 18: full rows in the 3-column grid, and in the 2-column one on phones)
                              const field = teams.flatMap(({ abbrev, roster }) => listOf(roster, 9).map((p) => ({ id: String(p.id), name: p.name, abbrev, rates: playerRates.nba[String(p.id)] })));
                              const fm = field.length >= 6 ? nbaFieldMarkets(field) : null;
                              const fieldIds = field.map((x) => x.id);
                              return (
                                <>
                                  {teams.map(({ team, abbrev, roster }) => roster.players.length > 0 && (
                                    <div className="market" key={`nba-props-${abbrev}`}>
                                      <div className="market-head">
                                        <div>
                                          <h5>{t.nbaPlayerProps} — {team}</h5>
                                          <p className="market-desc">{t.nbaPlayerPropsDesc}</p>
                                        </div>
                                        <div className="ou-switch" role="group">
                                          <button type="button" className={!under ? 'active' : ''} onClick={() => setNbaPropSide('over')}>{fr ? 'Plus de' : 'Over'}</button>
                                          <button type="button" className={under ? 'active' : ''} onClick={() => setNbaPropSide('under')}>{fr ? 'Moins de' : 'Under'}</button>
                                        </div>
                                      </div>
                                      <div className="player-prop-list">
                                        {listOf(roster, 12).map((p) => (
                                          <div key={p.id} className="player-prop-row">
                                            <span className="player-prop-name">{logo(abbrev)}<span>{p.name}</span></span>
                                            {/* the 6 lines in ONE even group; the switch above shows every Over or every Under */}
                                            <div className="prop-btns six" style={{ '--n': 6 }}>
                                              {PROPS.map(([bt, key, label]) => {
                                                const mk = p._mk[key];
                                                const odds = mk ? (under ? mk.under : mk.odds) : null;
                                                return (
                                                  <button key={bt} disabled={!odds} onClick={() => odds && addToBetSlip(game, bt, under ? 'under' : 'over', 0, { playerId: p.id, playerName: p.name, line: mk.line })}>
                                                    {label} {mk ? (under ? `U${mk.line}` : `${mk.line}+`) : ''} <span>{fmtOdds(odds)}</span>
                                                  </button>
                                                );
                                              })}
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  ))}

                                  {teams.map(({ team, abbrev, roster }) => listOf(roster, 8).length > 0 && (
                                    <div className="market" key={`nba-combos-${abbrev}`}>
                                      <h5>{fr ? 'Combinés' : 'Combos'} — {team}</h5>
                                      <p className="market-desc">{fr ? 'Points + rebonds + passes (PRA), double-double (DD) et triple-double (TD)' : 'Points + rebounds + assists (PRA), double-double (DD) and triple-double (TD)'}</p>
                                      <div className="player-prop-list">
                                        {listOf(roster, 8).map((p) => {
                                          const { pra, dd, td } = p._mk;
                                          const who = { playerId: p.id, playerName: p.name };
                                          const cells = [
                                            ['pra-o', pra ? `PRA ${pra.line}+` : 'PRA', pra?.odds, () => addToBetSlip(game, 'nba_player_pra', 'over', 0, { ...who, line: pra.line })],
                                            ['pra-u', pra ? `PRA U${pra.line}` : 'PRA', pra?.under, () => addToBetSlip(game, 'nba_player_pra', 'under', 0, { ...who, line: pra.line })],
                                            ['dd-y', fr ? 'DD oui' : 'DD yes', dd?.yes, () => addToBetSlip(game, 'nba_double_double', 'yes', 0, who)],
                                            ['dd-n', fr ? 'DD non' : 'DD no', dd?.no, () => addToBetSlip(game, 'nba_double_double', 'no', 0, who)],
                                            ['td-y', fr ? 'TD oui' : 'TD yes', td?.yes, () => addToBetSlip(game, 'nba_triple_double', 'yes', 0, who)],
                                            ['td-n', fr ? 'TD non' : 'TD no', td?.no, () => addToBetSlip(game, 'nba_triple_double', 'no', 0, who)],
                                          ];
                                          return (
                                            <div key={p.id} className="player-prop-row">
                                              <span className="player-prop-name">{logo(abbrev)}<span>{p.name}</span></span>
                                              <div className="prop-btns six" style={{ '--n': 6 }}>
                                                {cells.map(([k, label, odds, go]) => (
                                                  <button key={k} disabled={!odds} onClick={() => odds && go()}>{label} <span>{fmtOdds(odds)}</span></button>
                                                ))}
                                              </div>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  ))}

                                  {fm && [['nba_top_scorer', 'topScorer', fr ? 'Meilleur marqueur du match' : 'Top scorer of the game', fr ? 'Le joueur qui marque le plus de points, les deux équipes confondues (égalité = mise remboursée)' : 'The player who scores the most points, both teams together (a tie = stake back)'],
                                    ['nba_first_basket', 'firstBasket', fr ? 'Premier panier' : 'First basket', fr ? 'Le joueur qui marque le premier panier du match (les lancers francs ne comptent pas)' : 'The player who scores the first basket of the game (free throws don’t count)']].map(([bt, key, title, desc]) => (
                                    <div className="market" key={bt}>
                                      <h5>{title}</h5>
                                      <p className="market-desc">{desc}</p>
                                      <div className="player-grid">
                                        {field.map((x) => (
                                          <button key={`${bt}-${x.id}`} disabled={!fm[key][x.id]} onClick={() => fm[key][x.id] && addToBetSlip(game, bt, x.id, 0, { playerId: x.id, playerName: x.name, field: fieldIds })}>
                                            <span className="pg-player">{logo(x.abbrev)}<span className="pg-name">{x.name}</span></span> <span>{fmtOdds(fm[key][x.id])}</span>
                                          </button>
                                        ))}
                                      </div>
                                    </div>
                                  ))}
                                </>
                              );
                            })()}
                          </div>
                          );
                        })()}
                      </div>
                    );
                  })}
                </div>
              ) : selectedSport !== 'nhl' ? (
                <div className="sport-coming-soon">
                  <span>{sports.find(s => s.id === selectedSport)?.icon}</span>
                  <p>{selectedSport.toUpperCase()} — {lang === 'fr' ? 'Bientôt disponible' : 'Coming Soon'}</p>
                </div>
              ) : (
              <div className="games-grid" key={`${selectedSport}-${filter}`}>
                {filteredGames.length === 0 ? (
                  renderNoGames(lang === 'fr' ? 'LNH' : 'NHL')
                ) : filteredGames.map(game => {
                  const status = getGameStatus(game);
                  const expanded = expandedGame === game.id;
                  const bettable = isGameBettable(game);
                  const gamePlayers = getGamePlayers(game);
                  const gameTotal = customTotal[game.id] || '';
                  const gameScore = customScore[game.id] || { home: '', away: '' };

                  // Get resolved team abbreviations from gamePlayers (handles fallback resolution)
                  // First 5 players are home, next 4 are away
                  const resolvedHomeAbbrev = gamePlayers[0]?.team || game.home_abbrev;
                  const resolvedAwayAbbrev = gamePlayers[5]?.team || game.away_abbrev;

                  // Get injuries for this game
                  const homeInjuries = gameInjuries[resolvedHomeAbbrev] || [];
                  const awayInjuries = gameInjuries[resolvedAwayAbbrev] || [];
                  const totalInjuries = homeInjuries.length + awayInjuries.length;

                  // Get injured player names (normalized for comparison)
                  const injuredNames = new Set([
                    ...homeInjuries.map(i => i.name.toLowerCase()),
                    ...awayInjuries.map(i => i.name.toLowerCase())
                  ]);

                  // Filter out injured players and replace with backups from same team
                  const healthyGamePlayers = gamePlayers.filter(player =>
                    !injuredNames.has(player.name.toLowerCase())
                  );

                  // Count how many players were removed per team
                  const homeInjuredCount = gamePlayers.filter(p =>
                    p.team === resolvedHomeAbbrev && injuredNames.has(p.name.toLowerCase())
                  ).length;
                  const awayInjuredCount = gamePlayers.filter(p =>
                    p.team === resolvedAwayAbbrev && injuredNames.has(p.name.toLowerCase())
                  ).length;

                  // Get replacement players from defaultPlayers (players not already in the list)
                  const currentPlayerNames = new Set(healthyGamePlayers.map(p => p.name.toLowerCase()));

                  const homeReplacements = homeInjuredCount > 0
                    ? rosterPlayers
                        .filter(p => p.team === resolvedHomeAbbrev && p.position !== 'G' && !currentPlayerNames.has(p.name.toLowerCase()) && !injuredNames.has(p.name.toLowerCase()))
                        .slice(0, homeInjuredCount)
                    : [];

                  const awayReplacements = awayInjuredCount > 0
                    ? rosterPlayers
                        .filter(p => p.team === resolvedAwayAbbrev && p.position !== 'G' && !currentPlayerNames.has(p.name.toLowerCase()) && !injuredNames.has(p.name.toLowerCase()))
                        .slice(0, awayInjuredCount)
                    : [];

                  const availablePlayers = [...healthyGamePlayers, ...homeReplacements, ...awayReplacements];
                  // odds engine: every NHL market from the game model + each player's own rates
                  const hm = nhlMarkets(game);
                  const ht = nhlTeamTotals(game, playerRates.nhl?.teams);
                  const pmOf = (pl) => nhlPlayerMarkets(game, findNhlPlayer(pl.id, pl.name) || pl, playerRates.nhl?.players);

                  const handleGameClick = () => {
                    if (expanded) {
                      setExpandedGame(null);
                      setLiveStats(null);
                    } else {
                      setExpandedGame(game.id);
                      if (status.live) {
                        fetchGameStats(game.id);
                      }
                    }
                  };

                  return (
                    <div key={game.id} className={`game-card ${expanded ? 'expanded' : ''} ${!bettable && !status.live ? 'locked' : ''} ${status.live ? 'live-game' : ''} ${status.final ? 'finished-game' : ''}${goalAnimations[game.id] ? ' hr-active' : ''}${(game.home_abbrev === currentFav || game.away_abbrev === currentFav) ? ' fav-game' : ''}`} style={(themeMode !== 'none' && currentFav && (game.home_abbrev === currentFav || game.away_abbrev === currentFav)) ? { borderLeft: `3px solid ${(selectedSport === 'mlb' ? MLB_TEAM_COLORS : NHL_TEAM_COLORS)[currentFav]?.primary}`, boxShadow: `0 0 8px ${(selectedSport === 'mlb' ? MLB_TEAM_COLORS : NHL_TEAM_COLORS)[currentFav]?.primary}22` } : undefined}>
                      <button className={`fav-star${currentFav && (game.home_abbrev === currentFav || game.away_abbrev === currentFav) ? ' active' : ''}`} onClick={(e) => { e.stopPropagation(); const fav = selectedSport === 'mlb' ? favoriteMLB : favoriteNHL; if (fav && (game.home_abbrev === fav || game.away_abbrev === fav)) { setFavoriteTeam(selectedSport, ''); } else { setFavoriteTeam(selectedSport, game.home_abbrev); } }}>{currentFav && (game.home_abbrev === currentFav || game.away_abbrev === currentFav) ? '★' : '☆'}</button>
                      <div className="game-header" onClick={() => (bettable || status.live || status.final) && handleGameClick()}>
                        {/* NHL Style Scoreboard for all games */}
                        <div className="nhl-scoreboard nhl-scoreboard--nhl">
                          <div className="nhl-main-row">
                            {/* Away Team */}
                            <div className={`nhl-team away ${game.canadiens_playing === 'away' ? 'habs' : ''}`}>
                              {game.away_logo && <img src={game.away_logo} alt={game.away_abbrev} className="team-logo nhl-logo" onError={(e) => { e.target.style.display='none'; }} />}
                              <span className="nhl-name">{game.away_team}</span>
                              {status.live && <span className="nhl-sog">SOG: {game.away_sog ?? 0}</span>}
                            </div>

                            {/* Score Row: Away Score | Time/Period | Home Score */}
                            <div className="nhl-score-row">
                              {(status.live || status.final) ? (
                                <>
                                  <span className={`nhl-score away ${game.away_score > game.home_score ? 'winning' : game.away_score < game.home_score ? 'losing' : ''}`}>{game.away_score}</span>
                                  <div className={`nhl-period ${status.live && !status.intermission ? 'live' : ''} ${status.final ? 'final' : ''}`}>
                                    {status.live && !status.intermission && <span className="live-dot"></span>}
                                    <span>{status.live ? `${status.period}` : 'FINAL'}</span>
                                    {status.live && !status.intermission && <span className="nhl-clock">{status.clock || '0:00'}</span>}
                                    {status.live && status.powerPlay && (
                                      <span className="nhl-pp-badge">
                                        PP · {status.powerPlay.team} {status.powerPlay.timeRemaining && `${status.powerPlay.timeRemaining}`}
                                      </span>
                                    )}
                                  </div>
                                  <span className={`nhl-score home ${game.home_score > game.away_score ? 'winning' : game.home_score < game.away_score ? 'losing' : ''}`}>{game.home_score}</span>
                                </>
                              ) : (
                                <div className="nhl-upcoming">
                                  <span className="nhl-time-main">{status.text}</span>
                                  <span className="nhl-venue">{game.venue}</span>
                                </div>
                              )}
                            </div>

                            {/* Home Team */}
                            <div className={`nhl-team home ${game.canadiens_playing === 'home' ? 'habs' : ''}`}>
                              {game.home_logo && <img src={game.home_logo} alt={game.home_abbrev} className="team-logo nhl-logo" onError={(e) => { e.target.style.display='none'; }} />}
                              <span className="nhl-name">{game.home_team}</span>
                              {status.live && <span className="nhl-sog">SOG: {game.home_sog ?? 0}</span>}
                            </div>
                          </div>

                          {/* Intermission banner */}
                          {status.live && status.intermission && (
                            <div className="nhl-intermission-banner">
                              <span className="nhl-intermission-icon">⏸</span>
                              <span>{lang === 'fr' ? `FIN DE ${status.period === '1st' ? '1ère' : status.period === '2nd' ? '2e' : '3e'} PÉRIODE` : `END OF ${status.period ? status.period.toUpperCase() : ''}`}</span>
                              {status.clock && <span className="nhl-intermission-clock">{status.clock}</span>}
                              <span className="nhl-intermission-icon">⏸</span>
                            </div>
                          )}
                        </div>

                        {bettable && (
                          <div className="quick-odds">
                            <button className="odd-btn" onClick={(e) => { e.stopPropagation(); addToBetSlip(game, 'winner', 'away', parseFloat(game.away_odds) || 1.90); }}>
                              <span>{game.away_abbrev}</span>
                              {moneylineBoost() > 1 && <s className="odd-was">{((parseFloat(game.away_odds) || 1.90) / moneylineBoost()).toFixed(2)}</s>}<span className="odd"><AnimatedNumber value={parseFloat(game.away_odds) || 1.90} format={(n) => n.toFixed(2)} duration={400} /></span>
                            </button>
                            <button className="odd-btn" onClick={(e) => { e.stopPropagation(); addToBetSlip(game, 'winner', 'home', parseFloat(game.home_odds) || 1.90); }}>
                              <span>{game.home_abbrev}</span>
                              {moneylineBoost() > 1 && <s className="odd-was">{((parseFloat(game.home_odds) || 1.90) / moneylineBoost()).toFixed(2)}</s>}<span className="odd"><AnimatedNumber value={parseFloat(game.home_odds) || 1.90} format={(n) => n.toFixed(2)} duration={400} /></span>
                            </button>
                          </div>
                        )}

                        {goalAnimations[game.id] && (() => {
                          const ganim = goalAnimations[game.id];
                          const copies = Array(15).fill('GOAL');
                          return (
                            <div className="hr-overlay">
                              <div className="hr-flash" />
                              <div className="hr-rows-container">
                                {[-0, -15, -30, -45].map((delay, ri) => (
                                  <div key={ri} className="hr-row">
                                    <div className="hr-row-inner" style={{ animationDelay: `${delay}s` }}>
                                      {[...copies, ...copies].map((word, i) => <span key={i}>{word}</span>)}
                                    </div>
                                  </div>
                                ))}
                              </div>
                              <div className="hr-overlay-content">
                                <div className="hr-overlay-main">
                                  {ganim.logo && <img src={ganim.logo} alt={ganim.team} className="hr-team-logo" style={{ width: 100, height: 100 }} onError={e => e.target.style.display='none'} />}
                                  <span className="hr-overlay-text" style={{ color: '#2d2d2d', textShadow: '0 4px 16px rgba(0,0,0,0.3), 0 2px 0 rgba(0,0,0,0.15)' }}>GOAL!</span>
                                  {ganim.logo && <img src={ganim.logo} alt={ganim.team} className="hr-team-logo" style={{ width: 100, height: 100 }} onError={e => e.target.style.display='none'} />}
                                </div>
                              </div>
                            </div>
                          );
                        })()}
                      </div>

                      {/* Live Stats Panel */}
                      {status.final && <GameRecap sport="nhl" game={game} final />}
                      {expanded && status.live && <GameRecap sport="nhl" game={game} />}
                      {expanded && status.live && (
                        <div className="live-stats-panel" style={statColors(game)}>
                          <h4>{t.statistics}</h4>
                          {loadingStats && !liveStats ? (
                            <div className="stats-loading">{t.loadingStats}</div>
                          ) : liveStats ? (<>
                            <div className="stats-grid">
                              {/* Shots on Goal with bar */}
                              {(() => {
                                const away = liveStats.away?.stats?.sog || 0;
                                const home = liveStats.home?.stats?.sog || 0;
                                const awayClass = away > home ? 'winning' : away < home ? 'losing' : 'tied';
                                const homeClass = home > away ? 'winning' : home < away ? 'losing' : 'tied';
                                return (
                                  <div className="stat-bar-row">
                                    <span className="stat-val left">{away}</span>
                                    <div className="stat-bar-container">
                                      <div className="stat-bar-label">{t.statSOG}</div>
                                      <div className="stat-bar">
                                        <div className={`stat-bar-fill away ${awayClass}`} style={{width: `${away / Math.max(away + home, 1) * 100}%`}}></div>
                                        <div className={`stat-bar-fill home ${homeClass}`} style={{width: `${home / Math.max(away + home, 1) * 100}%`}}></div>
                                      </div>
                                    </div>
                                    <span className="stat-val right">{home}</span>
                                  </div>
                                );
                              })()}

                              {/* Faceoff % with bar */}
                              {(() => {
                                const away = parseFloat(liveStats.away?.stats?.faceoffWinPct) || 0;
                                const home = parseFloat(liveStats.home?.stats?.faceoffWinPct) || 0;
                                const awayClass = away > home ? 'winning' : away < home ? 'losing' : 'tied';
                                const homeClass = home > away ? 'winning' : home < away ? 'losing' : 'tied';
                                return (
                                  <div className="stat-bar-row">
                                    <span className="stat-val left">{away}%</span>
                                    <div className="stat-bar-container">
                                      <div className="stat-bar-label">{t.statFaceoffPct}</div>
                                      <div className="stat-bar">
                                        <div className={`stat-bar-fill away ${awayClass}`} style={{width: `${away}%`}}></div>
                                        <div className={`stat-bar-fill home ${homeClass}`} style={{width: `${home}%`}}></div>
                                      </div>
                                    </div>
                                    <span className="stat-val right">{home}%</span>
                                  </div>
                                );
                              })()}

                              {/* Power Play */}
                              <div className="stat-bar-row">
                                <span className="stat-val left">{liveStats.away?.stats?.powerPlay || '0/0'}</span>
                                <div className="stat-bar-container">
                                  <div className="stat-bar-label">{t.statPowerPlay}</div>
                                </div>
                                <span className="stat-val right">{liveStats.home?.stats?.powerPlay || '0/0'}</span>
                              </div>

                              {/* Hits with bar */}
                              {(() => {
                                const away = liveStats.away?.stats?.hits || 0;
                                const home = liveStats.home?.stats?.hits || 0;
                                const awayClass = away > home ? 'winning' : away < home ? 'losing' : 'tied';
                                const homeClass = home > away ? 'winning' : home < away ? 'losing' : 'tied';
                                return (
                                  <div className="stat-bar-row">
                                    <span className="stat-val left">{away}</span>
                                    <div className="stat-bar-container">
                                      <div className="stat-bar-label">{t.statHits}</div>
                                      <div className="stat-bar">
                                        <div className={`stat-bar-fill away ${awayClass}`} style={{width: `${away / Math.max(away + home, 1) * 100}%`}}></div>
                                        <div className={`stat-bar-fill home ${homeClass}`} style={{width: `${home / Math.max(away + home, 1) * 100}%`}}></div>
                                      </div>
                                    </div>
                                    <span className="stat-val right">{home}</span>
                                  </div>
                                );
                              })()}

                              {/* Blocks */}
                              {(() => {
                                const away = liveStats.away?.stats?.blocks || 0;
                                const home = liveStats.home?.stats?.blocks || 0;
                                const awayClass = away > home ? 'winning' : away < home ? 'losing' : 'tied';
                                const homeClass = home > away ? 'winning' : home < away ? 'losing' : 'tied';
                                return (
                                  <div className="stat-bar-row">
                                    <span className="stat-val left">{away}</span>
                                    <div className="stat-bar-container">
                                      <div className="stat-bar-label">{t.statBlockedShots}</div>
                                      <div className="stat-bar">
                                        <div className={`stat-bar-fill away ${awayClass}`} style={{width: `${away / Math.max(away + home, 1) * 100}%`}}></div>
                                        <div className={`stat-bar-fill home ${homeClass}`} style={{width: `${home / Math.max(away + home, 1) * 100}%`}}></div>
                                      </div>
                                    </div>
                                    <span className="stat-val right">{home}</span>
                                  </div>
                                );
                              })()}

                              {/* PIM */}
                              <div className="stat-bar-row">
                                <span className="stat-val left">{liveStats.away?.stats?.pim || 0}</span>
                                <div className="stat-bar-container">
                                  <div className="stat-bar-label">{t.statPenaltyMins}</div>
                                </div>
                                <span className="stat-val right">{liveStats.home?.stats?.pim || 0}</span>
                              </div>

                              {/* Giveaways */}
                              <div className="stat-bar-row">
                                <span className="stat-val left">{liveStats.away?.stats?.giveaways || 0}</span>
                                <div className="stat-bar-container">
                                  <div className="stat-bar-label">{t.statGiveaways}</div>
                                </div>
                                <span className="stat-val right">{liveStats.home?.stats?.giveaways || 0}</span>
                              </div>

                              {/* Takeaways */}
                              <div className="stat-bar-row">
                                <span className="stat-val left">{liveStats.away?.stats?.takeaways || 0}</span>
                                <div className="stat-bar-container">
                                  <div className="stat-bar-label">{t.statTakeaways}</div>
                                </div>
                                <span className="stat-val right">{liveStats.home?.stats?.takeaways || 0}</span>
                              </div>
                            </div>
                            {liveStats?.recentPlays?.length > 0 && (
                              <div className="pbp-feed">
                                <h5>{t.recentPlays}</h5>
                                <div className="pbp-list">
                                  {liveStats.recentPlays.map((play, i) => (
                                    <div key={i} className={`pbp-play ${play.type}`}>
                                      <span className="pbp-time">P{play.period} {play.time}</span>
                                      <span className={`pbp-team ${play.team ? '' : 'neutral'}`}>{play.team || ''}</span>
                                      <span className="pbp-desc">{translatePlay(play, 'nhl')}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </>) : (
                            <div className="stats-error">{t.statsUnavailable}</div>
                          )}
                        </div>
                      )}

                      {expanded && bettable && (
                        <div className="markets">
                          {/* Moneyline */}
                          <div className="market">
                            <h5>{t.moneyline}</h5>
                            <p className="market-desc">{t.moneylineDesc}</p>
                            <div className="market-row">
                              <button onClick={() => addToBetSlip(game, 'winner', 'away', parseFloat(game.away_odds) || 1.90)}>
                                {game.away_team} <span>{fmtMoneyline(parseFloat(game.away_odds) || 1.90)}</span>
                              </button>
                              <button onClick={() => addToBetSlip(game, 'winner', 'home', parseFloat(game.home_odds) || 1.90)}>
                                {game.home_team} <span>{fmtMoneyline(parseFloat(game.home_odds) || 1.90)}</span>
                              </button>
                            </div>
                          </div>

                          {/* Puck Line */}
                          <div className="market">
                            <h5>{t.puckLine}</h5>
                            <p className="market-desc">{t.puckLineDesc}</p>
                            <div className="market-row puckline-row">
                              <button onClick={() => addToBetSlip(game, 'puckline', 'away', 0, { team: game.away_team, line: 1.5 })} className="puckline-btn">
                                <div className="puckline-team">{game.away_team}</div>
                                <div className="puckline-line">+1.5</div>
                                <div className="puckline-explain">{t.wontLoseBy2}</div>
                                <span className="puckline-odds">{fmtOdds(hm?.puckline.away)}</span>
                              </button>
                              <button onClick={() => addToBetSlip(game, 'puckline', 'home', 0, { team: game.home_team, line: -1.5 })} className="puckline-btn">
                                <div className="puckline-team">{game.home_team}</div>
                                <div className="puckline-line">-1.5</div>
                                <div className="puckline-explain">{t.winsBy2}</div>
                                <span className="puckline-odds">{fmtOdds(hm?.puckline.home)}</span>
                              </button>
                            </div>
                          </div>

                          {/* Total Goals - Preset Lines */}
                          <div className="market">
                            <h5>{t.totalGoals}</h5>
                            <p className="market-desc">{t.totalGoalsDesc}</p>
                            <div className="totals-grid">
                              {(hm?.totals || []).map(({ line, over, under }) => ({ line, overOdds: over, underOdds: under })).map(total => (
                                <div key={total.line} className="total-line">
                                  <span className="total-line-label">{total.line}</span>
                                  <button onClick={() => addToBetSlip(game, 'total', 'over', 0, { type: t.over, line: total.line })}>
                                    {t.over} <span>{fmtOdds(total.overOdds)}</span>
                                  </button>
                                  <button onClick={() => addToBetSlip(game, 'total', 'under', 0, { type: t.under, line: total.line })}>
                                    {t.under} <span>{fmtOdds(total.underOdds)}</span>
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Team to Score First */}
                          <div className="market">
                            <h5>{t.teamFirst}</h5>
                            <p className="market-desc">{t.teamFirstDesc}</p>
                            <div className="market-row">
                              <button onClick={() => addToBetSlip(game, 'team_first', 'away', 0)}>{game.away_team} <span>{fmtOdds(hm?.teamFirst.away)}</span></button>
                              <button onClick={() => addToBetSlip(game, 'team_first', 'home', 0)}>{game.home_team} <span>{fmtOdds(hm?.teamFirst.home)}</span></button>
                            </div>
                          </div>

                          {/* Both Teams Score */}
                          <div className="market">
                            <h5>{t.bothScore}</h5>
                            <p className="market-desc">{t.bothScoreDesc}</p>
                            <div className="market-row">
                              <button onClick={() => addToBetSlip(game, 'both_score', 'yes', 0)}>{t.yes} <span>{fmtOdds(hm?.bothScore.yes)}</span></button>
                              <button onClick={() => addToBetSlip(game, 'both_score', 'no', 0)}>{t.no} <span>{fmtOdds(hm?.bothScore.no)}</span></button>
                            </div>
                          </div>

                          {/* Period Winner */}
                          <div className="market">
                            <h5>{t.periodWinner}</h5>
                            <p className="market-desc">{t.periodWinnerDesc}</p>
                            <p className="period-label">{t.period1}</p>
                            <div className="market-row three">
                              <button onClick={() => addToBetSlip(game, 'period_winner', 'away', 0, { period: '1st', pick: game.away_team })}>{game.away_team} <span>{fmtOdds(hm?.periods[0].away)}</span></button>
                              <button onClick={() => addToBetSlip(game, 'period_winner', 'draw', 0, { period: '1st', pick: t.draw })}>{t.draw} <span>{fmtOdds(hm?.periods[0].draw)}</span></button>
                              <button onClick={() => addToBetSlip(game, 'period_winner', 'home', 0, { period: '1st', pick: game.home_team })}>{game.home_team} <span>{fmtOdds(hm?.periods[0].home)}</span></button>
                            </div>
                            <p className="period-label">{t.period2}</p>
                            <div className="market-row three">
                              <button onClick={() => addToBetSlip(game, 'period_winner', 'away', 0, { period: '2nd', pick: game.away_team })}>{game.away_team} <span>{fmtOdds(hm?.periods[1].away)}</span></button>
                              <button onClick={() => addToBetSlip(game, 'period_winner', 'draw', 0, { period: '2nd', pick: t.draw })}>{t.draw} <span>{fmtOdds(hm?.periods[1].draw)}</span></button>
                              <button onClick={() => addToBetSlip(game, 'period_winner', 'home', 0, { period: '2nd', pick: game.home_team })}>{game.home_team} <span>{fmtOdds(hm?.periods[1].home)}</span></button>
                            </div>
                            <p className="period-label">{t.period3}</p>
                            <div className="market-row three">
                              <button onClick={() => addToBetSlip(game, 'period_winner', 'away', 0, { period: '3rd', pick: game.away_team })}>{game.away_team} <span>{fmtOdds(hm?.periods[2].away)}</span></button>
                              <button onClick={() => addToBetSlip(game, 'period_winner', 'draw', 0, { period: '3rd', pick: t.draw })}>{t.draw} <span>{fmtOdds(hm?.periods[2].draw)}</span></button>
                              <button onClick={() => addToBetSlip(game, 'period_winner', 'home', 0, { period: '3rd', pick: game.home_team })}>{game.home_team} <span>{fmtOdds(hm?.periods[2].home)}</span></button>
                            </div>
                          </div>

                          {/* Game Props */}
                          <div className="market">
                            <h5>{t.gameProps}</h5>
                            <p className="market-desc">{t.gamePropsDesc}</p>
                            <div className="market-row three">
                              <button onClick={() => addToBetSlip(game, 'overtime', 'yes', 0)}>{t.overtime} <span>{fmtOdds(hm?.overtime)}</span></button>
                              <button onClick={() => addToBetSlip(game, 'shutout', 'yes', 0)}>{t.shutout} <span>{fmtOdds(hm?.shutout)}</span></button>
                              <button onClick={() => addToBetSlip(game, 'fighting', 'yes', 0)}>{t.fight} <span>{fmtOdds(hm?.fight)}</span></button>
                            </div>
                          </div>

                          {/* Penalty Minutes */}
                          <div className="market">
                            <h5>{t.penaltyMins}</h5>
                            <p className="market-desc">{t.penaltyMinsDesc}</p>
                            <div className="market-row">
                              {ht?.pim ? (<>
                              <button onClick={() => addToBetSlip(game, 'penalty_mins', 'over', 0, { type: t.over, line: ht.pim.line })}>{t.over} {ht.pim.line} PIM <span>{fmtOdds(ht.pim.over)}</span></button>
                              <button onClick={() => addToBetSlip(game, 'penalty_mins', 'under', 0, { type: t.under, line: ht.pim.line })}>{t.under} {ht.pim.line} PIM <span>{fmtOdds(ht.pim.under)}</span></button>
                              </>) : <p className="market-desc">{lang === 'fr' ? 'Chargement…' : 'Loading…'}</p>}
                            </div>
                          </div>

                          {/* Total Shots on Goal */}
                          <div className="market">
                            <h5>{t.totalShots}</h5>
                            <p className="market-desc">{t.totalShotsDesc}</p>
                            {(ht?.shots || []).map((sl) => (
                              <div className="market-row" key={sl.line}>
                                <button onClick={() => addToBetSlip(game, 'total_shots', 'over', 0, { type: t.over, line: sl.line })}>{t.over} {sl.line} <span>{fmtOdds(sl.over)}</span></button>
                                <button onClick={() => addToBetSlip(game, 'total_shots', 'under', 0, { type: t.under, line: sl.line })}>{t.under} {sl.line} <span>{fmtOdds(sl.under)}</span></button>
                              </div>
                            ))}
                          </div>

                          {/* Add Players Section */}
                          <div className="market">
                            <h5>{t.enterPlayers}</h5>
                            <p className="market-desc">{t.enterPlayersDesc}</p>
                            <div className="add-player-row">
                              <select
                                value=""
                                onChange={(e) => { if (e.target.value) addCustomPlayer(game.id, e.target.value); }}
                                onClick={(e) => e.stopPropagation()}
                                className="player-select"
                              >
                                <option value="" disabled>{t.playerName}</option>
                                <optgroup label={game.home_abbrev}>
                                  {rosterPlayers.filter(p => p.team === game.home_abbrev && p.position !== 'G' && !injuredNames.has(p.name.toLowerCase())).sort((a, b) => (b.points || 0) - (a.points || 0)).map(p => (
                                    <option key={p.id} value={p.name}>{p.name}{p.points ? ` (${p.points} pts)` : ''}</option>
                                  ))}
                                </optgroup>
                                <optgroup label={game.away_abbrev}>
                                  {rosterPlayers.filter(p => p.team === game.away_abbrev && p.position !== 'G' && !injuredNames.has(p.name.toLowerCase())).sort((a, b) => (b.points || 0) - (a.points || 0)).map(p => (
                                    <option key={p.id} value={p.name}>{p.name}{p.points ? ` (${p.points} pts)` : ''}</option>
                                  ))}
                                </optgroup>
                              </select>
                            </div>
                          </div>

                          {/* First Goal Scorer */}
                          <div className="market">
                            <h5>{t.firstGoal}</h5>
                            <p className="market-desc">{t.firstGoalDesc}</p>
                            <div className="player-grid">
                              {availablePlayers.length === 0 ? (
                                <p className="no-players">{lang === 'fr' ? 'Ajoutez des joueurs ci-dessus' : 'Add players above'}</p>
                              ) : availablePlayers.filter(p => p.position !== 'G').slice(0, 12).map(player => (
                                <button key={`first-${player.id}`} onClick={() => addToBetSlip(game, 'first_goal', 'player', 0, { playerId: player.id, playerName: player.name })}>
                                  <span className="pg-player"><img className="prop-team-logo nhl" alt="" src={teamLogoUrl('nhl', player.team)} onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }} /><span className="pg-name">{player.name}</span></span> <span>{fmtOdds(pmOf(player)?.first)}</span>
                                </button>
                              ))}
                            </div>
                          </div>

                          {/* Anytime Goal Scorer */}
                          <div className="market">
                            <h5>{t.anytimeGoal}</h5>
                            <p className="market-desc">{t.anytimeGoalDesc}</p>
                            <div className="player-grid">
                              {availablePlayers.length === 0 ? (
                                <p className="no-players">{lang === 'fr' ? 'Ajoutez des joueurs ci-dessus' : 'Add players above'}</p>
                              ) : availablePlayers.filter(p => p.position !== 'G').slice(0, 12).map(player => (
                                <button key={`any-${player.id}`} onClick={() => addToBetSlip(game, 'anytime_goal', 'player', 0, { playerId: player.id, playerName: player.name })}>
                                  <span className="pg-player"><img className="prop-team-logo nhl" alt="" src={teamLogoUrl('nhl', player.team)} onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }} /><span className="pg-name">{player.name}</span></span> <span>{fmtOdds(pmOf(player)?.anytime)}</span>
                                </button>
                              ))}
                            </div>
                          </div>

                          {/* Winning Goal Scorer */}
                          <div className="market">
                            <h5>{t.winningGoal}</h5>
                            <p className="market-desc">{t.winningGoalDesc}</p>
                            <div className="player-grid">
                              {availablePlayers.length === 0 ? (
                                <p className="no-players">{lang === 'fr' ? 'Ajoutez des joueurs ci-dessus' : 'Add players above'}</p>
                              ) : availablePlayers.filter(p => p.position !== 'G').slice(0, 12).map(player => (
                                <button key={`gwg-${player.id}`} onClick={() => addToBetSlip(game, 'winning_goal', 'player', 0, { playerId: player.id, playerName: player.name })}>
                                  <span className="pg-player"><img className="prop-team-logo nhl" alt="" src={teamLogoUrl('nhl', player.team)} onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }} /><span className="pg-name">{player.name}</span></span> <span>{fmtOdds(pmOf(player)?.winning)}</span>
                                </button>
                              ))}
                            </div>
                          </div>

                          {/* Player Points */}
                          <div className="market">
                            <h5>{t.playerPoints}</h5>
                            <p className="market-desc">{t.playerPointsDesc}</p>
                            <div className="player-grid">
                              {availablePlayers.length === 0 ? (
                                <p className="no-players">{lang === 'fr' ? 'Ajoutez des joueurs ci-dessus' : 'Add players above'}</p>
                              ) : availablePlayers.filter(p => p.position !== 'G').slice(0, 12).map(player => (
                                <button key={`pts-${player.id}`} onClick={() => addToBetSlip(game, 'player_points', 'player', 0, { playerId: player.id, playerName: player.name })}>
                                  <span className="pg-player"><img className="prop-team-logo nhl" alt="" src={teamLogoUrl('nhl', player.team)} onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }} /><span className="pg-name">{player.name}</span></span> <span>{fmtOdds(pmOf(player)?.point)}</span>
                                </button>
                              ))}
                            </div>
                          </div>

                          {/* Player Shots on Goal */}
                          <div className="market">
                            <h5>{t.playerShots}</h5>
                            <p className="market-desc">{t.playerShotsDesc}</p>
                            <div className="player-shots-grid">
                              {availablePlayers.length === 0 ? (
                                <p className="no-players">{lang === 'fr' ? 'Ajoutez des joueurs ci-dessus' : 'Add players above'}</p>
                              ) : availablePlayers.filter(p => p.position !== 'G').slice(0, 8).map(player => (
                                <div key={`shots-${player.id}`} className="player-shots-row">
                                  <span className="player-shots-name"><img className="prop-team-logo nhl" alt="" src={teamLogoUrl('nhl', player.team)} onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }} /><span>{player.name}</span></span>
                                  <div className="shots-buttons">
                                    {(pmOf(player)?.shots || []).flatMap((sl) => [
                                      <button key={`o${sl.line}`} onClick={() => addToBetSlip(game, 'player_shots', 'over', 0, { playerId: player.id, playerName: player.name, type: t.over, line: sl.line })}>
                                        {t.over} {sl.line} <span>{fmtOdds(sl.over)}</span>
                                      </button>,
                                      <button key={`u${sl.line}`} onClick={() => addToBetSlip(game, 'player_shots', 'under', 0, { playerId: player.id, playerName: player.name, type: t.under, line: sl.line })}>
                                        {t.under} {sl.line} <span>{fmtOdds(sl.under)}</span>
                                      </button>,
                                    ])}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Correct Score */}
                          <div className="market">
                            <h5>{t.correctScore}</h5>
                            <p className="market-desc">{t.correctScoreDesc}</p>
                            {/* Popular scores */}
                            <div className="score-grid">
                              {[[3, 2], [4, 2], [3, 1], [4, 3], [2, 1], [5, 3], [2, 3], [1, 2], [1, 3]].map(([h, a]) => (
                                <button key={`${h}-${a}`} onClick={() => addToBetSlip(game, 'exact_score', 'home', 0, { home: h, away: a })}>
                                  {h}-{a} <span>{fmtOdds(hm?.exact(h, a))}</span>
                                </button>
                              ))}
                            </div>
                            {/* Custom score entry */}
                            <p className="market-sub">{lang === 'fr' ? 'Ou entrez un score personnalisé:' : 'Or enter a custom score:'}</p>
                            <div className="score-input-row">
                              <div className="score-input">
                                <label>{t.homeScore} ({game.home_team})</label>
                                <input
                                  type="number"
                                  min="0"
                                  placeholder="0"
                                  value={gameScore.home}
                                  onChange={(e) => setCustomScore({ ...customScore, [game.id]: { ...gameScore, home: e.target.value } })}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              </div>
                              <span className="score-separator">-</span>
                              <div className="score-input">
                                <label>{t.awayScore} ({game.away_team})</label>
                                <input
                                  type="number"
                                  min="0"
                                  placeholder="0"
                                  value={gameScore.away}
                                  onChange={(e) => setCustomScore({ ...customScore, [game.id]: { ...gameScore, away: e.target.value } })}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              </div>
                              <button
                                className="add-score-btn"
                                disabled={gameScore.home === '' || gameScore.away === ''}
                                onClick={() => {
                                  if (gameScore.home !== '' && gameScore.away !== '') {
                                    addToBetSlip(game, 'exact_score', 'home', 0, { home: parseInt(gameScore.home), away: parseInt(gameScore.away) });
                                  }
                                }}
                              >
                                {t.addScore} ({gameScore.home || '?'}-{gameScore.away || '?'})
                              </button>
                            </div>
                          </div>
                          {/* Injuries for these two teams — right where you bet (replaces the sidebar list) */}
                          <div className="market game-injuries">
                            <h5>{t.injuryReport}</h5>
                            {totalInjuries === 0 ? (
                              <p className="market-desc">{t.noInjuriesForGames}</p>
                            ) : (
                              [[resolvedAwayAbbrev, awayInjuries], [resolvedHomeAbbrev, homeInjuries]]
                                .filter(([, list]) => list.length > 0)
                                .map(([abbrev, list]) => (
                                  <div key={abbrev} className="game-inj-team">
                                    <img className="game-inj-logo" alt="" src={teamLogoUrl('nhl', abbrev)} onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }} />
                                    <span className="game-inj-abbrev">{abbrev}</span>
                                    <span className="game-inj-list">
                                      {list.map((inj, k) => (
                                        <span key={k} className="game-inj-player">
                                          {inj.name} <small>({injuryShort(inj.status)})</small>
                                        </span>
                                      ))}
                                    </span>
                                  </div>
                                ))
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              )}
            </>
          )}

          {activeTab === 'mybets' && (
            <>
              <div className="content-header">
                <h2>{t.myBets}</h2>
              </div>
              {renderBetsSummary()}
              <div className="bets-tabs">
                <button className="active">{t.active} ({activeBets.length})</button>
                <button onClick={() => setActiveTab('history')}>{t.history}</button>
              </div>
              <div className="bets-list">
                {activeBets.length === 0 ? (
                  <EmptyState icon="ticket" title={t.noActiveBets} hint={t.placeBetStart} />
                ) : activeBets.map(bet => {
                  const canCancel = isBetCancellable(bet);
                  const isCancelling = cancellingBetId === bet.id;
                  const isParlay = bet.bet_type === 'parlay';
                  const parlayLegs = isParlay ? (JSON.parse(bet.bet_value || '{}').legs || []) : [];
                  return (
                    <div key={bet.id} className={`bet-card${isParlay ? ' parlay-card' : ''}`}>
                      <div className="bet-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                        <div className="bet-status pending">{isParlay ? <span className="parlay-badge">{parlayLegs.length} {parlayLegs.length > 1 ? t.parlayLegs : t.parlayLeg} — {t.parlay}</span> : t.pending}</div>
                        {canCancel && (
                          <button className="bet-cancel-btn" onClick={() => cancelBet(bet)} disabled={isCancelling}>
                            {isCancelling ? t.cancelling : t.cancelBet}
                          </button>
                        )}
                      </div>
                      {isParlay && revealCounts[bet.id] && (
                        <div className="lb-parlay-sold">{lang === 'fr' ? `Dévoilé ${revealCounts[bet.id].n} fois · +${formatMoney(revealCounts[bet.id].fees, lang)}` : `Revealed ${revealCounts[bet.id].n} ${revealCounts[bet.id].n > 1 ? 'times' : 'time'} · +${formatMoney(revealCounts[bet.id].fees, lang)}`}</div>
                      )}
                      {isParlay ? (
                        <div className="parlay-legs">
                          {parlayLegs.map((leg, i) => (
                            <div key={i} className="parlay-leg">
                              <span className="leg-game">{leg.game}</span>
                              <span className="leg-pick">{leg.description}</span>
                              <span className="leg-odds">@ {leg.odds.toFixed(2)}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <>
                          <div className="bet-game">{bet.game_description}</div>
                          <div className="bet-pick">{formatBetDescription(bet)}</div>
                        </>
                      )}
                      <div className="bet-info">
                        <span>{isParlay ? t.combinedOdds : t.odds}: <b>{parseFloat(bet.odds).toFixed(2)}</b></span>
                        <span>{t.stake}: <b>{formatMoney(parseFloat(bet.stake), lang)}</b></span>
                        <span className="potential">{t.toWin}: <b>{formatMoney(parseFloat(bet.potential_win), lang)}</b></span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {activeTab === 'history' && (
            <>
              <div className="content-header"><h2>{t.history}</h2></div>
              {renderBetsSummary()}
              <div className="bets-tabs">
                <button onClick={() => setActiveTab('mybets')}>{t.active} ({activeBets.length})</button>
                <button className="active">{t.history}</button>
              </div>
              <div className="bets-list">
                {betHistory.length === 0 ? (
                  <EmptyState icon="history" title={t.noHistory} hint={lang === 'fr' ? 'Tes paris réglés apparaîtront ici.' : 'Your settled bets will show up here.'} />
                ) : betHistory.map(bet => {
                  const isParlay = bet.bet_type === 'parlay';
                  const parlayLegs = isParlay ? (JSON.parse(bet.bet_value || '{}').legs || []) : [];
                  return (
                    <div key={bet.id} className={`bet-card ${bet.result}${isParlay ? ' parlay-card' : ''}`}>
                      <div className={`bet-status ${bet.result}`}>{isParlay ? <span className="parlay-badge">{parlayLegs.length} {parlayLegs.length > 1 ? t.parlayLegs : t.parlayLeg} {t.parlay} — {bet.result?.toUpperCase()}</span> : bet.result?.toUpperCase()}</div>
                      {isParlay ? (
                        <div className="parlay-legs">
                          {parlayLegs.map((leg, i) => (
                            <div key={i} className="parlay-leg">
                              <span className="leg-game">{leg.game}</span>
                              <span className="leg-pick">{leg.description}</span>
                              <span className="leg-odds">@ {leg.odds.toFixed(2)}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <>
                          <div className="bet-game">{bet.game_description}</div>
                          <div className="bet-pick">{formatBetDescription(bet)}</div>
                        </>
                      )}
                      <div className="bet-info">
                        <span>{isParlay ? t.combinedOdds : t.odds}: <b>{parseFloat(bet.odds).toFixed(2)}</b></span>
                        <span>{t.stake}: <b>{formatMoney(parseFloat(bet.stake), lang)}</b></span>
                        <span className={bet.result === 'won' ? 'won' : ''}>{t.payout}: <b>{formatMoney(parseFloat(bet.payout || 0), lang)}</b></span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {activeTab === 'feed' && (
            <Feed items={feed.items} loaded={feed.loaded} failed={feed.failed} betsCap={FEED_BETS} liveOf={feedLive} likes={likes} onLike={toggleLike} profile={profile} players={leaderboard} avatarOf={avatarOf} gameInfo={feedGameInfo} lang={lang} money={(n) => formatMoney(n, lang)} betText={formatBetDescription} />
          )}

          {activeTab === 'leaderboard' && (
            <>
              <div className="content-header"><h2>{t.leaderboard}</h2></div>
              {/* Leaderboard — slim standings rows (medals for the top 3) */}
              <div className="pool-standings-list lb-list">
                {leaderboard.map((player, i) => {
                  const playerBets = withDemoBets(allUserBets[player.id] || [], i);
                  const isExpanded = expandedPlayer === player.id;
                  const totalStaked = playerBets.reduce((sum, b) => sum + parseFloat(b.stake), 0);
                  const isMe = player.id === profile?.id;

                  return (
                    <div key={player.id} className={`pool-standing-row lb-srow ${isMe ? 'me' : ''} ${isExpanded ? 'open' : ''}`}>
                      <div
                        className="pool-standing-summary pl-srow"
                        onClick={() => setExpandedPlayer(isExpanded ? null : player.id)}
                        style={{ cursor: playerBets.length > 0 ? 'pointer' : 'default' }}
                      >
                        <span className="pl-srow-rank">{i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : i + 1}</span>
                        <TeamAvatar team={avatarOf(player)} name={player.username} size={36} />
                        <span className="pl-srow-main">
                          <span className="pl-srow-name">
                            {player.username}
                            {isMe && <span className="dr-upnext-tag">{lang === 'fr' ? 'toi' : 'you'}</span>}
                            {hasLoserTag(player) && <span className="loser-tag">LOSER</span>}
                          </span>
                        </span>
                        <span className="pl-srow-score"><b>{formatMoney(parseFloat(player.balance), lang)}</b></span>
                        <span className="pl-srow-toggle" aria-hidden="true">{playerBets.length > 0 ? (isExpanded ? '▴' : '▾') : ''}</span>
                      </div>
                      {isExpanded && playerBets.length > 0 && (
                        <div className="lb-bets">
                          <div className="lb-bets-header">
                            <span>{lang === 'fr' ? 'Paris en cours' : 'Active Bets'}</span>
                            <span>{lang === 'fr' ? 'Total misé' : 'Total Staked'}: {formatMoney(totalStaked, lang)}</span>
                          </div>
                          {playerBets.map(bet => {
                            const isParlay = bet.bet_type === 'parlay';
                            const legs = isParlay && bet.bet_value ? (JSON.parse(bet.bet_value).legs || []) : null;
                            const nLegs = legs ? legs.length : bet.hiddenLegs || 0;
                            const sold = isMe && isParlay ? revealCounts[bet.id] : null;
                            return (
                              <BetLike key={bet.id} className={`lb-bet-item${isParlay ? ' lb-parlay' : ''}`} liked={(likes[`bet:${bet.id}`] || []).some((l) => l.user_id === profile?.id)} count={(likes[`bet:${bet.id}`] || []).length} onToggle={() => toggleLike(`bet:${bet.id}`)}>
                                {isParlay ? (
                                  <>
                                    <div className="lb-parlay-head">
                                      <span className="parlay-badge">{nLegs} {nLegs > 1 ? t.parlayLegs : t.parlayLeg} — {t.parlay}</span>
                                      {!isMe && !legs && (
                                        <button type="button" className="lb-parlay-btn primary" onClick={(e) => { e.stopPropagation(); revealParlay(bet, player.username); }} disabled={revealingId === bet.id || (parseFloat(profile?.balance) || 0) < REVEAL_FEE}>
                                          <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V8a4 4 0 0 1 8 0v3" /></svg>
                                          {revealingId === bet.id ? (lang === 'fr' ? 'Dévoilement…' : 'Revealing…') : `${lang === 'fr' ? 'Dévoiler' : 'Reveal'} · ${formatMoney(REVEAL_FEE, lang)}`}
                                        </button>
                                      )}
                                      {!isMe && legs && (
                                        <button type="button" className="lb-parlay-btn" onClick={(e) => { e.stopPropagation(); copyParlay(bet); }}>
                                          <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="9" y="9" width="11" height="11" rx="2" /><path d="M5 15V6a2 2 0 0 1 2-2h9" /></svg>
                                          {lang === 'fr' ? 'Copier' : 'Copy'}
                                        </button>
                                      )}
                                    </div>
                                    {legs ? legs.map((leg, i) => (
                                      <div key={i} className="lb-parlay-leg">
                                        <div className="lb-bet-game">{leg.game}</div>
                                        <div className="lb-bet-pick">{leg.description} <span className="lb-parlay-odds">@ {Number(leg.odds).toFixed(2)}</span></div>
                                      </div>
                                    )) : (
                                      <div className="lb-bet-game">{lang === 'fr' ? 'Sélections cachées' : 'Picks hidden'}</div>
                                    )}
                                    {sold && <div className="lb-parlay-sold">{lang === 'fr' ? `Dévoilé ${sold.n} fois · +${formatMoney(sold.fees, lang)}` : `Revealed ${sold.n} ${sold.n > 1 ? 'times' : 'time'} · +${formatMoney(sold.fees, lang)}`}</div>}
                                  </>
                                ) : (
                                  <>
                                    <div className="lb-bet-game">{bet.game_description}</div>
                                    <div className="lb-bet-pick">{formatBetDescription(bet)}</div>
                                  </>
                                )}
                                <div className="lb-bet-info">
                                  <span>{formatMoney(parseFloat(bet.stake), lang)}</span>
                                  <span>@ {parseFloat(bet.odds).toFixed(2)}</span>
                                  <span className="potential">${lang === 'fr' ? 'Gain' : 'Win'}: {formatMoney(parseFloat(bet.potential_win), lang)}</span>
                                </div>
                              </BetLike>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {activeTab === 'cards' && (
            <div className="collectables-tab">
              {!collectablesReady && (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '80px 20px', gap: 12 }}>
                  <div className="pack-loading-spinner" />
                  <p style={{ color: '#666', fontSize: 14 }}>Loading Collectables...</p>
                </div>
              )}
              {collectablesReady && (() => {
                const tc = themeTeamColors;
                const heroPrimary = tc?.primary || '#2d2d2d';
                const heroSecondary = tc?.secondary || '#ff5722';
                const heroGradient = `linear-gradient(160deg, ${heroPrimary} 0%, ${heroPrimary}dd 30%, ${heroPrimary} 60%, ${heroPrimary}cc 100%)`;
                const accentLine = `linear-gradient(90deg, ${heroPrimary}, ${heroSecondary}, ${heroPrimary})`;
                return (
                  <>
                    <div className="collectables-nav" style={{ '--team-secondary': heroSecondary }}>
                      <div className="collectables-subtabs">
                        <button className={collectablesSubTab === 'shop' ? 'active' : ''} onClick={() => setCollectablesSubTab('shop')}>{lang === 'fr' ? 'Boutique' : 'Shop'}</button>
                        <button className={collectablesSubTab === 'collection' ? 'active' : ''} onClick={() => setCollectablesSubTab('collection')}>{lang === 'fr' ? 'Collection' : 'Collection'}</button>
                        <button className={collectablesSubTab === 'marketplace' ? 'active' : ''} onClick={() => { setCollectablesSubTab('marketplace'); fetchMarketplace(); fetchTradeProposals(); }}>Market</button>
                        <button className={collectablesSubTab === 'showroom' ? 'active' : ''} onClick={() => { setCollectablesSubTab('showroom'); fetchShowroom(); fetchShowroomList(); fetchUserCards(); setViewingShowroom(null); }}>Showroom</button>
                        {profile?.username === 'CL34RY' && <button className={collectablesSubTab === 'catalog' ? 'active' : ''} onClick={() => setCollectablesSubTab('catalog')}>Catalog</button>}
                      </div>
                    </div>
                    <div className="collectables-hero" style={{ background: heroGradient, display: collectablesSubTab === 'shop' ? '' : 'none' }}>
                      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 3, background: accentLine, zIndex: 3 }} />
                      <div className="hero-top">
                        <div className="hero-brand">BarDown Collectables</div>
                        <div className="hero-series"><span style={{ color: heroSecondary }}>Chrome</span> Series</div>
                        <div className="hero-year">2026 MLB Collection</div>
                      </div>
                      <div className="hero-cards">
                        {[
                          { pos: 'hc-outer', src: './cards/photos/pack-standard-2-hero.webp', w: 100, h: 141 },
                          { pos: 'hc-mid', src: './cards/photos/pack-chrome-3-hero.webp', w: 110, h: 158 },
                          { pos: 'hc-inner', src: './cards/photos/pack-diamond-3-hero.webp', w: 110, h: 158 },
                          { pos: 'hc-center pack-card-horizontal', src: './cards/photos/pack-diamond-2-hero.webp', w: 197, h: 140 },
                          { pos: 'hc-inner', src: './cards/photos/pack-diamond-1-hero.webp', w: 110, h: 158 },
                          { pos: 'hc-mid', src: './cards/photos/pack-chrome-1-hero.webp', w: 110, h: 158 },
                          { pos: 'hc-outer', src: './cards/photos/pack-standard-1-hero.webp', w: 100, h: 141 },
                        ].map((item, i) => (
                          <div key={i} className={`hero-card ${item.pos}`}>
                            <img src={item.src} alt="" width={item.w} height={item.h} />
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                );
              })()}

              {/* ═══ COLLECTION TAB ═══ */}
              {collectablesReady && collectablesSubTab === 'collection' && (
                <div className="collectables-content">
                  {collectionLoading ? (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '60px 20px', gap: 12 }}>
                      <div className="pack-loading-spinner" />
                      <p style={{ color: '#666', fontSize: 14 }}>Loading collection...</p>
                    </div>
                  ) : userCards.length === 0 ? (
                    <>
                      <p className="collectables-subtitle">{lang === 'fr' ? 'Votre collection est vide' : 'Your collection is empty'}</p>
                      <p className="collectables-hint">{lang === 'fr' ? 'Ouvrez des packs dans la boutique!' : 'Open packs in the Shop!'}</p>
                    </>
                  ) : (
                    <>
                      {(() => {
                        const market = getDailyMarket();
                        const marketInfo = getMarketLabel(market);
                        const totalMarket = processedCollection.reduce((sum, uc) => sum + uc._marketVal, 0);
                        return (
                          <div className="collection-top-bar">
                            <div className="collection-stats">
                              <div className="stat-item"><span className="stat-num">{userCards.length}</span><span className="stat-label">Cards</span></div>
                              <div className="stat-item"><span className="stat-num">{new Set(userCards.map(c => c.card_definition_id)).size}</span><span className="stat-label">Unique</span></div>
                              <div className="stat-item stat-rare"><span className="stat-num">{userCards.filter(c => c.card_type !== 'base').length}</span><span className="stat-label">Rares</span></div>
                              <div className="stat-item stat-value"><span className="stat-num">{formatValue(totalMarket)}</span><span className="stat-label">Value</span></div>
                            </div>
                            <div className="collection-market-pill">
                              <span className="market-dot" style={{ background: marketInfo.color }} />
                              <span className="market-name" style={{ color: marketInfo.color }}>{marketInfo.label}</span>
                              <span className="market-pct" style={{ color: marketInfo.color }}>{market >= 1 ? '+' : ''}{Math.round((market - 1) * 100)}%</span>
                            </div>
                          </div>
                        );
                      })()}
                      <div className="collection-controls">
                        <div className="collection-filters">
                          {['all', 'base', 'rare', 'legendary'].map(f => (
                            <button key={f} className={collectionFilter === f ? 'active' : ''} onClick={() => setCollectionFilter(f)}>
                              {f.charAt(0).toUpperCase() + f.slice(1)}
                            </button>
                          ))}
                        </div>
                        <div style={{ display: 'flex', gap: 6 }}>
                          {selectMode && (
                            <button
                              className="collection-select-btn"
                              onClick={() => {
                                const filtered = userCards.filter(uc => {
                                  if (collectionFilter === 'all') return true;
                                  if (collectionFilter === 'base') return uc.card_type === 'base';
                                  if (collectionFilter === 'rare') return ['rare_999', 'rare_dual', 'rare_patch', 'rare_auto', 'chrome_auto', 'auto_patch'].includes(uc.card_type);
                                  if (collectionFilter === 'legendary') return ['patch_auto', 'dual_patch'].includes(uc.card_type);
                                  return true;
                                });
                                setSelectedCards(new Set(filtered.map(uc => uc.id)));
                              }}
                            >
                              Select All
                            </button>
                          )}
                          <button
                            className={`collection-select-btn ${selectMode ? 'active' : ''}`}
                            onClick={() => { setSelectMode(!selectMode); setSelectedCards(new Set()); }}
                          >
                            {selectMode ? 'Cancel' : 'Select'}
                          </button>
                        </div>
                      </div>
                      <div className="cards-grid">
                        {processedCollection
                          .slice(0, collectionVisible)
                          .map((uc) => {
                            const cd = { ...uc.card_data, numbering: uc.numbering };
                            const ct = uc.card_type;
                            const isDual = ct === 'rare_dual' || ct === 'dual_patch';
                            const isHorizontal = isDual || ct === 'rare_patch' || ct === 'auto_patch';
                            const CardComp = isDual ? DualCard : ct === 'rare_patch' ? RarePatchCard : ct === 'auto_patch' ? AutoPatchCard : ct === 'patch_auto' ? LegendaryPatchAutoCard : ct === 'chrome_auto' ? ChromeAutoCard : ct === 'rare_auto' ? RareAutoCard : ct === 'rare_999' ? Rare999Card : BaseCard;
                            const cardScale = 0.4375;
                            const cardVal = uc._marketVal;
                            const isSelected = selectedCards.has(uc.id);
                            return (
                              <div key={uc.id} className={`card-slot ${isHorizontal ? 'card-slot-rotated' : ''} ${isSelected ? 'card-slot-selected' : ''}`}
                                onClick={() => {
                                  if (selectMode) {
                                    setSelectedCards(prev => {
                                      const next = new Set(prev);
                                      if (next.has(uc.id)) next.delete(uc.id); else next.add(uc.id);
                                      return next;
                                    });
                                  } else {
                                    setSelectedCard({ ...cd, _type: ct, id: uc.id });
                                  }
                                }}
                              >
                                {isHorizontal ? (
                                  <div className="card-rotate-wrapper-react">
                                    <CardComp card={ct === 'dual_patch' ? { ...cd, legendary: true } : cd} player={cd} scale={cardScale} interactive={false} />
                                  </div>
                                ) : (
                                  <CardComp player={cd} scale={cardScale} interactive={false} />
                                )}
                                <div className={`card-value-tag ${cardVal >= 50 ? 'card-value-high' : cardVal >= 10 ? 'card-value-mid' : ''}`}>{formatValue(cardVal)}</div>
                                {selectMode && <div className={`card-select-check ${isSelected ? 'checked' : ''}`}>{isSelected ? '✓' : ''}</div>}
                              </div>
                            );
                          })}
                      </div>
                      {collectionVisible < processedCollection.length && (
                        <button className="catalog-load-more" onClick={() => setCollectionVisible(v => v + 50)}>
                          Load More ({processedCollection.length - collectionVisible} remaining)
                        </button>
                      )}
                      {selectMode && selectedCards.size > 0 && (() => {
                        let sellTotal = 0;
                        processedCollection.filter(uc => selectedCards.has(uc.id)).forEach(uc => {
                          sellTotal += Math.round(uc._marketVal * 0.8);
                        });
                        return (
                          <div className="sell-bar">
                            <span className="sell-bar-count">{selectedCards.size} card{selectedCards.size > 1 ? 's' : ''} selected</span>
                            <span className="sell-bar-value">Sell for <b>{formatValue(sellTotal)}</b> <small>(80% market)</small></span>
                            <button className="sell-bar-btn" disabled={selling} onClick={() => sellCards(selectedCards)}>
                              {selling ? 'Selling...' : 'Sell'}
                            </button>
                          </div>
                        );
                      })()}
                    </>
                  )}
                </div>
              )}

              {/* ═══ SHOP TAB ═══ */}
              {collectablesReady && collectablesSubTab === 'shop' && (
                <div className="collectables-content">
                  {!shopReady ? (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '60px 20px', gap: 12 }}>
                      <div className="pack-loading-spinner" />
                      <p style={{ color: '#666', fontSize: 14 }}>Loading shop...</p>
                    </div>
                  ) : (
                  <>
                  <div className="collection-filters" style={{ marginBottom: 16 }}>
                    <button className="active">MLB</button>
                  </div>
                  <div className="pack-shop-grid">
                    {[
                      { type: 'standard', cls: 'pack-standard', price: 39.99, title: 'Standard Pack', desc: <>8 cards — <strong>1 hot slot</strong> with a chance at Rare Auto or Chrome Auto</>, cardImages: ['./cards/photos/pack-standard-1-shop.webp','./cards/photos/pack-standard-2-shop.webp','./cards/photos/pack-standard-3-shop.webp'] },
                      { type: 'jumbo', cls: 'pack-jumbo', price: 244.99, title: 'Jumbo Pack', desc: <>24 cards — <strong>Guaranteed Auto</strong> + 2 hot slots with Chrome Auto chance</>, cardImages: ['./cards/photos/pack-jumbo-1-shop.webp','./cards/photos/pack-jumbo-2-shop.webp','./cards/photos/pack-jumbo-3-shop.webp'] },
                      { type: 'premium', cls: 'pack-chrome', price: 2699.99, title: 'Chrome Pack', desc: <>5 cards — <strong>4 guaranteed Autos</strong> + Chrome slot for Chrome Auto Patch</>, cardImages: ['./cards/photos/pack-chrome-1-shop.webp','./cards/photos/pack-chrome-2-shop.webp','./cards/photos/pack-chrome-3-shop.webp'] },
                      { type: 'ultimate', cls: 'pack-diamond', price: 4849.99, title: 'Diamond Pack', desc: <>5 cards — <strong>Chrome Auto + Chrome Auto Patch</strong> guaranteed + Diamond slot</>, cardImages: ['./cards/photos/pack-diamond-1-shop.webp','./cards/photos/pack-diamond-2-shop.webp','./cards/photos/pack-diamond-3-shop.webp'], horizontalCenter: true },
                    ].map(pack => (
                      <div key={pack.type} className={`pack-shop-card ${pack.cls}`}>
                        <div className="pack-card-fan">
                          {(pack.cardImages || pack.photos.map(pid => `./cards/photos/players/${pid}.jpg`)).map((src, i) => (
                            <div key={i} className={`pack-featured-card ${i === 1 && pack.horizontalCenter ? 'pack-card-horizontal' : ''}`}>
                              <img src={src} alt="" />
                            </div>
                          ))}
                        </div>
                        <div className="pack-name">{pack.title}</div>
                        <div className="pack-desc">{pack.desc}</div>
                        <div className="pack-bottom">
                          <div className="pack-shop-price">${pack.price.toLocaleString()}</div>
                          <button
                            className="pack-shop-buy"
                            disabled={true}
                            onClick={() => openPack(pack.type)}
                          >
                            Open Pack
                          </button>
                          {profile?.username === 'CL34RY' && (
                            <button
                              className="pack-shop-test"
                              disabled={packBuying}
                              onClick={() => testOpenPack(pack.type)}
                            >
                              Test (Free)
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  </>
                  )}
                </div>
              )}

              {/* ═══ MARKETPLACE TAB (Trading) ═══ */}
              {collectablesReady && collectablesSubTab === 'marketplace' && marketLoading && (
                <div className="collectables-content">
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '60px 20px', gap: 12 }}>
                    <div className="pack-loading-spinner" />
                    <p style={{ color: '#666', fontSize: 14 }}>Loading market...</p>
                  </div>
                </div>
              )}
              {collectablesReady && collectablesSubTab === 'marketplace' && !marketLoading && (() => {
                const myListings = marketplaceListings.filter(l => l.seller_id === session?.user?.id);
                let otherListings = marketplaceListings.filter(l => l.seller_id !== session?.user?.id);
                if (marketplaceFilter !== 'all') otherListings = otherListings.filter(l => l.card_type === marketplaceFilter);
                if (marketplaceSort === 'price_asc') otherListings.sort((a, b) => a.asking_price - b.asking_price);
                else if (marketplaceSort === 'price_desc') otherListings.sort((a, b) => b.asking_price - a.asking_price);

                return (
                  <div className="collectables-content">
                    <div className="collection-top-bar">
                      <div className="collection-stats">
                        <div className="stat-item"><span className="stat-num">{marketplaceListings.length}</span><span className="stat-label">Available</span></div>
                        {myListings.length > 0 && <div className="stat-item stat-rare"><span className="stat-num">{myListings.length}</span><span className="stat-label">My Cards</span></div>}
                        {tradeProposals.length > 0 && <div className="stat-item stat-value"><span className="stat-num">{tradeProposals.length}</span><span className="stat-label">Offers</span></div>}
                      </div>
                    </div>

                    <div className="collection-filters">
                      {['all', 'rare_999', 'rare_auto', 'chrome_auto', 'auto_patch', 'patch_auto', 'rare_patch', 'rare_dual'].map(t => (
                        <button key={t} className={marketplaceFilter === t ? 'active' : ''} onClick={() => setMarketplaceFilter(t)}>{t === 'all' ? 'All' : t.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</button>
                      ))}
                    </div>

                    {/* Trade proposals on my cards */}
                    {tradeProposals.length > 0 && (
                      <div style={{ marginBottom: 24 }}>
                        <h4 style={{ fontSize: 13, fontWeight: 800, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>Trade Offers</h4>
                        {tradeProposals.map(tp => {
                          const myCard = myListings.find(l => l.id === tp.listing_id);
                          return (
                            <div key={tp.id} style={{ display: 'flex', alignItems: 'center', gap: 16, padding: 12, background: '#f9f9f9', borderRadius: 8, marginBottom: 8 }}>
                              <div style={{ textAlign: 'center', flex: 1 }}>
                                <div style={{ fontSize: 10, color: '#888', marginBottom: 4 }}>YOUR CARD</div>
                                <div style={{ fontSize: 12, fontWeight: 700 }}>{myCard?.card_data?.name || myCard?.card_data?.player1?.name || '?'}</div>
                                <div style={{ fontSize: 10, color: '#888' }}>{myCard?.card_type?.replace(/_/g, ' ')}</div>
                              </div>
                              <div style={{ fontSize: 20, color: '#ccc' }}>⇄</div>
                              <div style={{ textAlign: 'center', flex: 1 }}>
                                <div style={{ fontSize: 10, color: '#888', marginBottom: 4 }}>OFFERED BY {tp.proposer_username}</div>
                                <div style={{ fontSize: 12, fontWeight: 700 }}>{tp.offered_card_data?.name || tp.offered_card_data?.player1?.name || '?'}</div>
                                <div style={{ fontSize: 10, color: '#888' }}>{tp.offered_card_type?.replace(/_/g, ' ')}</div>
                              </div>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                <button onClick={() => acceptTrade(tp.id)} style={{ padding: '6px 14px', background: '#22c55e', color: '#fff', border: 'none', borderRadius: 6, fontSize: 11, fontWeight: 800, cursor: 'pointer' }}>ACCEPT</button>
                                <button onClick={() => declineTrade(tp.id)} style={{ padding: '6px 14px', background: '#ef4444', color: '#fff', border: 'none', borderRadius: 6, fontSize: 11, fontWeight: 800, cursor: 'pointer' }}>DECLINE</button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* My listed cards */}
                    {myListings.length > 0 && (
                      <div style={{ marginBottom: 24 }}>
                        <h4 style={{ fontSize: 13, fontWeight: 800, color: '#666', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>My Cards on Market</h4>
                        <div className="cards-grid">
                          {myListings.map(listing => (
                            <CardSlot key={listing.id} card={listing} onClick={() => setSelectedCard({ ...listing.card_data, numbering: listing.numbering, _type: listing.card_type, _listingId: listing.id })} />
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Available for trade */}
                    {otherListings.length === 0 && myListings.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: 60, color: '#999' }}>
                        <div style={{ marginBottom: 12 }}><svg viewBox="0 0 24 24" width="40" height="40" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 8h13l-3-3" /><path d="M20 16H7l3 3" /></svg></div>
                        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>No cards available for trade</div>
                        <div style={{ fontSize: 13 }}>Send cards from your collection to start trading!</div>
                      </div>
                    ) : otherListings.length > 0 && (
                      <>
                        <h4 style={{ fontSize: 13, fontWeight: 800, color: '#666', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>Available for Trade</h4>
                        <div className="cards-grid">
                          {otherListings.map(listing => (
                            <CardSlot key={listing.id} card={listing} onClick={() => setSelectedCard({ ...listing.card_data, numbering: listing.numbering, _type: listing.card_type, _tradeListing: listing })} />
                          ))}
                        </div>
                      </>
                    )}

                    {/* Trade offer modal — pick your card to offer */}
                    {tradeOfferListing && (
                      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }} onClick={() => setTradeOfferListing(null)}>
                        <div style={{ background: '#fff', borderRadius: 12, padding: 24, maxWidth: 500, width: '90%', maxHeight: '80vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
                          <h3 style={{ fontSize: 16, fontWeight: 900, marginBottom: 4 }}>Propose Trade</h3>
                          <div style={{ fontSize: 12, color: '#888', marginBottom: 16 }}>You want: <b>{tradeOfferListing.card_data?.name || tradeOfferListing.card_data?.player1?.name}</b> ({tradeOfferListing.card_type?.replace(/_/g, ' ')})</div>
                          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10 }}>Select a card to offer:</div>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 8 }}>
                            {(userCards || []).map(card => (
                              <div key={card.id} onClick={() => { if (window.confirm(`Offer ${card.card_data?.name || card.card_data?.player1?.name || 'this card'}?`)) { proposeTrade(tradeOfferListing.id, card.id); } }} style={{ cursor: 'pointer', padding: 6, border: '2px solid #e0e0e0', borderRadius: 6, textAlign: 'center' }}>
                                <div style={{ fontSize: 11, fontWeight: 700, color: '#2d2d2d' }}>{card.card_data?.name || card.card_data?.player1?.name}</div>
                                <div style={{ fontSize: 10, color: '#888' }}>{card.card_type?.replace(/_/g, ' ')}</div>
                              </div>
                            ))}
                          </div>
                          <button onClick={() => setTradeOfferListing(null)} style={{ marginTop: 12, width: '100%', padding: 10, background: '#eee', border: 'none', borderRadius: 6, fontWeight: 700, cursor: 'pointer' }}>Cancel</button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* ═══ SHOWROOM TAB ═══ */}
              {collectablesReady && collectablesSubTab === 'showroom' && (() => {
                const market = getDailyMarket();
                const myCards = userCards || [];
                const showroomCardIds = new Set(showroomCards.map(sc => sc.card_id));

                if (showroomEditing) {
                  // Editor: pick cards from collection
                  return (
                    <div className="collectables-content">
                      <div className="collection-top-bar">
                        <div className="collection-stats">
                          <div className="stat-item"><span className="stat-num">{showroomSelection.size}/20</span><span className="stat-label">Selected</span></div>
                        </div>
                      </div>
                      <div className="collection-filters">
                        <button onClick={() => setShowroomEditing(false)}>Cancel</button>
                        <button className="active" onClick={saveShowroom} disabled={showroomSaving}>{showroomSaving ? 'Saving...' : 'Save Showroom'}</button>
                      </div>
                      <div className="cards-grid">
                        {myCards.map(card => {
                          const selected = showroomSelection.has(card.id);
                          const cd = { ...card.card_data, numbering: card.numbering };
                          const ct = card.card_type;
                          const isDual = ct === 'rare_dual' || ct === 'dual_patch';
                          const isHorizontal = isDual || ct === 'rare_patch' || ct === 'auto_patch';
                          const CardComp = isDual ? DualCard : ct === 'rare_patch' ? RarePatchCard : ct === 'auto_patch' ? AutoPatchCard : ct === 'patch_auto' ? LegendaryPatchAutoCard : ct === 'chrome_auto' ? ChromeAutoCard : ct === 'rare_auto' ? RareAutoCard : ct === 'rare_999' ? Rare999Card : BaseCard;
                          return (
                            <div key={card.id} className={`card-slot ${isHorizontal ? 'card-slot-rotated' : ''} ${selected ? 'card-slot-selected' : ''}`} onClick={() => {
                              setShowroomSelection(prev => {
                                const next = new Set(prev);
                                if (next.has(card.id)) next.delete(card.id);
                                else if (next.size < 20) next.add(card.id);
                                return next;
                              });
                            }} style={{ cursor: 'pointer', border: selected ? '3px solid var(--accent)' : '3px solid transparent' }}>
                              {isHorizontal ? (
                                <div className="card-rotate-wrapper-react">
                                  <CardComp card={ct === 'dual_patch' ? { ...cd, legendary: true } : cd} player={cd} scale={0.4375} interactive={false} />
                                </div>
                              ) : (
                                <CardComp player={cd} scale={0.4375} interactive={false} />
                              )}
                              {selected && <div style={{ position: 'absolute', top: 4, right: 4, background: 'var(--accent)', color: '#fff', fontSize: 9, fontWeight: 900, padding: '2px 6px', borderRadius: 4, zIndex: 10 }}>IN SHOWROOM</div>}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                }

                // Showroom display
                const displayCards = myCards.filter(c => showroomCardIds.has(c.id));
                const totalValue = displayCards.reduce((sum, c) => sum + applyMarket(calculateCardValue(c.card_data, c.card_type, c.numbering), market), 0);
                const shareUrl = `${window.location.origin}${window.location.pathname}#/showroom/${profile?.username}`;

                return (
                  <div className="collectables-content">
                    <div className="collection-top-bar">
                      <div className="collection-stats">
                        <div className="stat-item"><span className="stat-num">{displayCards.length}</span><span className="stat-label">Cards</span></div>
                        <div className="stat-item stat-value"><span className="stat-num">{formatValue(totalValue)}</span><span className="stat-label">Value</span></div>
                      </div>
                    </div>
                    <div className="collection-filters">
                      <button className="active" onClick={() => { setShowroomEditing(true); fetchUserCards(); }}>Edit Showroom</button>
                      {displayCards.length > 0 && (
                        <button onClick={() => { navigator.clipboard.writeText(shareUrl); showNotification('Showroom link copied!', 'success'); }}>Share Link</button>
                      )}
                    </div>
                    {displayCards.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: 60, color: '#999' }}>
                        <div style={{ marginBottom: 12 }}><svg viewBox="0 0 24 24" width="40" height="40" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M8 21h8M12 17v4" /><path d="M7 4h10v5a5 5 0 0 1-10 0z" /><path d="M7 6H4a3 3 0 0 0 3 4M17 6h3a3 3 0 0 1-3 4" /></svg></div>
                        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>Your showroom is empty</div>
                        <div style={{ fontSize: 13 }}>Click "Edit Showroom" to add your best cards!</div>
                      </div>
                    ) : (
                      <div className="cards-grid">
                        {displayCards.map(card => (
                          <CardSlot key={card.id} card={card} onClick={() => setSelectedCard({ ...card.card_data, numbering: card.numbering, _type: card.card_type })}>
                            <div className={`card-value-tag ${applyMarket(calculateCardValue(card.card_data, card.card_type, card.numbering), market) >= 50 ? 'card-value-high' : ''}`}>{formatValue(applyMarket(calculateCardValue(card.card_data, card.card_type, card.numbering), market))}</div>
                          </CardSlot>
                        ))}
                      </div>
                    )}

                    {/* Viewing another user's showroom */}
                    {viewingShowroom && (
                      <div style={{ marginTop: 30, borderTop: '1px solid #e0e0e0', paddingTop: 20 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                          <h3 style={{ color: '#2d2d2d', fontSize: 18, fontWeight: 800 }}>{viewingShowroom.username}'s Showroom</h3>
                          <button onClick={() => setViewingShowroom(null)} style={{ padding: '6px 14px', background: '#eee', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 700, fontSize: 12 }}>Close</button>
                        </div>
                        <div className="cards-grid">
                          {viewingShowroom.cards.map(card => (
                            <CardSlot key={card.id} card={card} onClick={() => setSelectedCard({ ...card.card_data, numbering: card.numbering, _type: card.card_type })}>
                              <div className={`card-value-tag ${applyMarket(calculateCardValue(card.card_data, card.card_type, card.numbering), market) >= 50 ? 'card-value-high' : ''}`}>{formatValue(applyMarket(calculateCardValue(card.card_data, card.card_type, card.numbering), market))}</div>
                            </CardSlot>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Browse other showrooms */}
                    {!showroomEditing && showroomList.length > 0 && (
                      <div style={{ marginTop: 30, borderTop: '1px solid #e0e0e0', paddingTop: 20 }}>
                        <h3 style={{ color: '#2d2d2d', fontSize: 16, fontWeight: 800, marginBottom: 12 }}>Browse Showrooms</h3>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          {showroomList.filter(s => s.username !== profile?.username).map(s => (
                            <div key={s.username} onClick={() => viewShowroom(s.username)} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: viewingShowroom?.username === s.username ? 'rgba(var(--accent-rgb, 255,87,34), 0.08)' : '#f5f5f5', borderRadius: 6, cursor: 'pointer', transition: 'background 0.15s' }}>
                              <span style={{ fontWeight: 700, color: '#2d2d2d' }}>{s.username}</span>
                              <span style={{ fontSize: 12, color: '#888' }}>{s.cardCount} cards</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* ═══ CATALOG TAB (CL34RY only) ═══ */}
              {collectablesReady && collectablesSubTab === 'catalog' && profile?.username === 'CL34RY' && (() => {
                if (catalogLoading || !catalogImagesReady) return (
                  <div className="collectables-content">
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '60px 20px', gap: 12 }}>
                      <div className="pack-loading-spinner" />
                      <p style={{ color: '#666', fontSize: 14 }}>Loading catalog...</p>
                    </div>
                  </div>
                );
                const teams = [...new Set(catalogCards.map(d => d.card_data?.team).filter(Boolean))].sort();
                const filtered = catalogCards.filter(def => {
                  if (catalogTypeFilter !== 'all' && def.card_type !== catalogTypeFilter) return false;
                  if (catalogTeamFilter !== 'all' && def.card_data?.team !== catalogTeamFilter) return false;
                  return true;
                }).sort((a, b) => calculateCardValue(b.card_data, b.card_type) - calculateCardValue(a.card_data, a.card_type));
                const visible = filtered.slice(0, catalogVisible);
                return (
                  <div className="collectables-content">
                    <div className="catalog-filters">
                      <div className="catalog-type-filters">
                        {[
                          { key: 'all', label: `All (${catalogCards.length})` },
                          { key: 'base', label: 'Base' },
                          { key: 'rare_999', label: '/999' },
                          { key: 'rare_dual', label: 'Rare Dual' },
                          { key: 'rare_auto', label: 'Rare Auto' },
                          { key: 'chrome_auto', label: 'Chrome Auto' },
                          { key: 'auto_patch', label: 'Auto Patch' },
                          { key: 'patch_auto', label: 'Patch Auto' },
                          { key: 'dual_patch', label: 'Dual Patch' },
                        ].map(f => (
                          <button key={f.key} className={catalogTypeFilter === f.key ? 'active' : ''} onClick={() => { setCatalogTypeFilter(f.key); setCatalogVisible(50); }}>
                            {f.label}
                          </button>
                        ))}
                      </div>
                      <select className="catalog-team-select" value={catalogTeamFilter} onChange={e => { setCatalogTeamFilter(e.target.value); setCatalogVisible(50); }}>
                        <option value="all">All Teams ({teams.length})</option>
                        {teams.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                    <div className="catalog-count">{filtered.length} card{filtered.length !== 1 ? 's' : ''}</div>
                    <div className="cards-grid">
                      {visible.map((def) => {
                        const card = { ...def.card_data, numbering: def.max_supply ? `${def.current_supply}/${def.max_supply}` : undefined };
                        const ct = def.card_type;
                        const isDual = ct === 'rare_dual' || ct === 'dual_patch';
                        const isHorizontal = isDual || ct === 'rare_patch' || ct === 'auto_patch';
                        const CardComponent = isDual ? DualCard : ct === 'rare_patch' ? RarePatchCard : ct === 'auto_patch' ? AutoPatchCard : ct === 'patch_auto' ? LegendaryPatchAutoCard : ct === 'chrome_auto' ? ChromeAutoCard : ct === 'rare_auto' ? RareAutoCard : ct === 'rare_999' ? Rare999Card : BaseCard;
                        const cardScale = 0.4375;
                        return (
                          <div key={def.id} className={`card-slot ${isHorizontal ? 'card-slot-rotated' : ''}`} onClick={() => setSelectedCard({ ...card, _type: ct })}>
                            {isHorizontal ? (
                              <div className="card-rotate-wrapper-react">
                                <CardComponent card={ct === 'dual_patch' ? { ...card, legendary: true } : card} player={card} scale={cardScale} interactive={false} />
                              </div>
                            ) : (
                              <CardComponent player={card} scale={cardScale} interactive={false} />
                            )}
                            {def.max_supply && (
                              <div className="card-supply-badge">{def.current_supply}/{def.max_supply}</div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    {catalogVisible < filtered.length && (
                      <button className="catalog-load-more" onClick={() => setCatalogVisible(prev => prev + 50)}>
                        Load More ({filtered.length - catalogVisible} remaining)
                      </button>
                    )}
                  </div>
                );
              })()}

              {/* Card viewer overlay */}
              {selectedCard && (selectedCard.name || selectedCard.player1) && (
                <div className="card-viewer-overlay active" onClick={() => setSelectedCard(null)}>
                  <div className="card-viewer" onClick={(e) => e.stopPropagation()}>
                    {(() => {
                      const ct = selectedCard._type || selectedCard.type;
                      const cardVal = applyMarket(calculateCardValue(selectedCard, ct, selectedCard.numbering), getDailyMarket());
                      const cardComp = ct === 'rare_dual' ? <DualCard card={selectedCard} />
                        : ct === 'dual_patch' || ct === 'legendary_dual' ? <DualCard card={{ ...selectedCard, legendary: true }} />
                        : ct === 'rare_patch' ? <RarePatchCard card={selectedCard} />
                        : ct === 'auto_patch' ? <AutoPatchCard card={selectedCard} />
                        : ct === 'patch_auto' || ct === 'legendary_patch_auto' ? <LegendaryPatchAutoCard card={selectedCard} />
                        : ct === 'chrome_auto' ? <ChromeAutoCard player={selectedCard} />
                        : ct === 'rare_auto' ? <RareAutoCard player={selectedCard} />
                        : ct === 'rare_999' ? <Rare999Card player={selectedCard} />
                        : <BaseCard player={selectedCard} />;

                      // Protection tier based on value
                      const protectionOn = cardProtection;
                      const playerName = selectedCard.name || selectedCard.player1?.name || '';
                      const teamFull = selectedCard.teamFull || '';
                      const typeLabel = ct === 'base' ? 'Base' : ct === 'rare_999' ? 'Rare /999' : ct === 'rare_auto' ? 'Rare Auto' : ct === 'chrome_auto' ? 'Chrome Auto' : ct === 'auto_patch' ? 'Auto Patch' : ct === 'patch_auto' ? 'Chrome Auto Patch' : ct === 'rare_dual' ? 'Rare Dual' : ct === 'dual_patch' ? 'Dual Patch' : 'Card';

                      if (!protectionOn || cardVal < 50) {
                        // Raw — no protection
                        return <>{cardComp}<div className="card-viewer-value">{formatValue(cardVal)}</div></>;
                      } else if (cardVal < 200) {
                        // Penny sleeve
                        return <><div className="bdc-penny-sleeve">{cardComp}</div><div className="card-viewer-value">{formatValue(cardVal)}</div></>;
                      } else if (cardVal < 1000) {
                        // Top loader + penny sleeve
                        return <><div className="bdc-toploader"><div className="bdc-penny-sleeve">{cardComp}</div></div><div className="card-viewer-value">{formatValue(cardVal)}</div></>;
                      } else {
                        // One-touch case — horizontal or vertical based on card type
                        const isHorizontal = ['rare_dual', 'dual_patch', 'legendary_dual', 'rare_patch', 'auto_patch'].includes(ct);
                        const caseClass = `bdc-onetouch${isHorizontal ? ' bdc-onetouch-h' : ''}`;
                        return (
                          <>
                            <div className={caseClass}>
                              <div className="bdc-onetouch-sticker">
                                <div className="bdc-onetouch-sticker-logo"><span>BD</span></div>
                              </div>
                              <div className="bdc-onetouch-body">
                                <div className="bdc-onetouch-corner bdc-corner-tl"></div>
                                <div className="bdc-onetouch-corner bdc-corner-tr"></div>
                                <div className="bdc-onetouch-corner bdc-corner-bl"></div>
                                <div className="bdc-onetouch-corner bdc-corner-br"></div>
                                <div className="bdc-onetouch-seam-top"></div>
                                <div className="bdc-onetouch-seam-bottom"></div>
                                <div className="bdc-onetouch-reflection"></div>
                                <div className="bdc-onetouch-inner">
                                  <div className="bdc-onetouch-card">{cardComp}</div>
                                </div>
                              </div>
                            </div>
                            <div className="card-viewer-value">{formatValue(cardVal)}</div>
                          </>
                        );
                      }
                    })()}
                  </div>
                  <button className="card-viewer-close" onClick={() => setSelectedCard(null)}>✕</button>
                  {activeTab === 'cards' && (
                    <div style={{ position: 'fixed', bottom: 30, left: '50%', transform: 'translateX(-50%)', zIndex: 3001 }}>
                      {/* Collection card: Options menu */}
                      {selectedCard?.id && !selectedCard._listingId && !selectedCard._tradeListing && (
                        <>
                          {cardOptionsOpen && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 8 }}>
                              <button onClick={(e) => { e.stopPropagation(); if (window.confirm('Send this card to the market for trading?')) { sendToMarket(selectedCard.id); setSelectedCard(null); setCardOptionsOpen(false); } }} style={{ padding: '10px 24px', background: '#2d2d2d', color: '#fff', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 800, cursor: 'pointer', letterSpacing: 1 }}>Send to Market</button>
                              <button onClick={(e) => { e.stopPropagation(); const market = getDailyMarket(); const val = Math.round(applyMarket(calculateCardValue(selectedCard, selectedCard._type, selectedCard.numbering), market) * 0.8); if (window.confirm(`Sell for ${formatValue(val)}? (80% market)`)) { sellCards(new Set([selectedCard.id])); setSelectedCard(null); setCardOptionsOpen(false); } }} style={{ padding: '10px 24px', background: '#ef4444', color: '#fff', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 800, cursor: 'pointer', letterSpacing: 1 }}>Sell ({formatValue(Math.round(applyMarket(calculateCardValue(selectedCard, selectedCard._type, selectedCard.numbering), getDailyMarket()) * 0.8))})</button>
                            </div>
                          )}
                          <button onClick={(e) => { e.stopPropagation(); setCardOptionsOpen(!cardOptionsOpen); }} style={{ padding: '10px 24px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 800, cursor: 'pointer', letterSpacing: 1, width: '100%' }}>{cardOptionsOpen ? 'Close' : 'Options'}</button>
                        </>
                      )}
                      {/* My market listing: Remove button */}
                      {selectedCard?._listingId && (
                        <button onClick={(e) => { e.stopPropagation(); cancelListing(selectedCard._listingId); setSelectedCard(null); }} style={{ padding: '10px 24px', background: '#ef4444', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 800, cursor: 'pointer', letterSpacing: 1 }}>REMOVE FROM MARKET</button>
                      )}
                      {/* Other user's listing: Propose trade */}
                      {selectedCard?._tradeListing && (
                        <button onClick={(e) => { e.stopPropagation(); setTradeOfferListing(selectedCard._tradeListing); fetchUserCards(); setSelectedCard(null); }} style={{ padding: '10px 24px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 800, cursor: 'pointer', letterSpacing: 1 }}>PROPOSE TRADE</button>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Pack reveal overlay */}
              {packRevealCards && (
                <PackReveal
                  cards={packRevealCards}
                  packType={packRevealType}
                  onClose={() => { setPackRevealCards(null); setPackRevealType(''); }}
                  onOpenAnother={() => { setPackRevealCards(null); setPackRevealType(''); openPack(packRevealType); }}
                  onSellCards={async (cardIds, totalPrice) => {
                    try {
                      const { data, error } = await supabase.rpc('sell_cards', { p_user_id: session.user.id, p_card_ids: cardIds, p_total_price: totalPrice });
                      if (error) throw error;
                      if (!data.success) { showNotification(data.error || 'Failed to sell', 'error'); return; }
                      setProfile(prev => prev ? { ...prev, balance: data.new_balance } : prev);
                      showNotification(`Sold ${cardIds.length} card${cardIds.length > 1 ? 's' : ''} for ${formatValue(totalPrice)}!`, 'success');
                      fetchUserCards();
                    } catch (err) { showNotification('Sell failed: ' + err.message, 'error'); }
                  }}
                />
              )}
            </div>
          )}

          {activeTab === 'pools' && session?.user?.id && (
            <PoolsView
              userId={session.user.id}
              lang={lang}
              onBalanceChange={(newBalance) => setProfile(prev => prev ? { ...prev, balance: parseFloat(newBalance) } : prev)}
            />
          )}

          {activeTab === 'scout' && (
            <ScoutReport
              lang={lang}
              bets={scoutBets}
              loading={scoutLoading}
              error={scoutError}
              generatedAt={scoutReport?.meta?.generatedAt}
              onRefresh={() => fetchScoutReport(true)}
              onBack={() => setActiveTab('games')}
              onAdd={(b, stake) => addToBetSlip(b.game, b.betType, b.selection, b.odds, b.details, stake || null)}
              onAddPlan={(legs) => { setParlayMode(false); legs.forEach((b) => addToBetSlip(b.game, b.betType, b.selection, b.odds, b.details, b.stake)); }}
              balance={parseFloat(profile?.balance) || 0}
              money={(x) => formatMoney(x, lang)}
              onAddParlay={(legs, stake) => {
                const keys = new Set(legs.map((b) => b.key));
                if (betSlip.some((x) => !keys.has(x.betId)) && !window.confirm(lang === 'fr' ? 'Remplacer votre panier par ce combiné?' : 'Replace your bet slip with this parlay?')) return;
                setBetSlip((prev) => prev.filter((x) => keys.has(x.betId)));   // the slip = exactly these legs
                legs.forEach((b) => addToBetSlip(b.game, b.betType, b.selection, b.odds, b.details));
                setParlayMode(true);
                if (stake > 0) setParlayStake(stake);
              }}
              describe={(b) => describeBet(b.game, b.betType, b.selection, b.details)}
              record={scoutRecord}
              slipKeys={new Set(betSlip.map((x) => x.betId))}
            />
          )}
        </main>

        {/* Bet Slip */}
        <aside className="bet-slip" style={(activeTab === 'cards' || activeTab === 'pools') ? { display: 'none' } : undefined}>
          <div className="slip-header">
            <h3>{t.betSlip}</h3>
            {betSlip.length > 0 && <span className="slip-count" key={betSlip.length}>{betSlip.length}</span>}
          </div>
          {betSlip.length === 0 ? (
            <div className="slip-empty">
              <span className="es-icon" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 7h18v4a2 2 0 0 0 0 4v4H3v-4a2 2 0 0 0 0-4z" /><path d="M13 7v12" strokeDasharray="2 2" /></svg></span>
              <p>{t.betSlipEmpty}</p>
              <small>{t.clickOdds}</small>
            </div>
          ) : (
            <>
              {betSlip.length >= 2 && (
                <div className="slip-mode-toggle">
                  <button className={!parlayMode ? 'active' : ''} onClick={() => setParlayMode(false)}>{t.singles}</button>
                  <button className={parlayMode ? 'active' : ''} onClick={() => setParlayMode(true)}>{t.parlay} ({betSlip.length})</button>
                </div>
              )}
              <div className="slip-bets">
                {betSlip.map(bet => (
                  <div key={bet.betId} className="slip-bet">
                    <button className="slip-remove" onClick={() => removeFromBetSlip(bet.betId)}>×</button>
                    <div className="slip-game">{bet.game}</div>
                    <div className="slip-pick">{bet.description}</div>
                    <div className="slip-odd">@ {bet.boost > 1 ? <><s className="odd-was">{(bet.odds / bet.boost).toFixed(2)}</s>{bet.odds.toFixed(2)}</> : bet.odds.toFixed(2)}</div>
                    {!parlayMode && (
                      <>
                        <div className="slip-stake">
                          <span>$</span>
                          <input type="number" value={bet.stake} onChange={(e) => updateStake(bet.betId, e.target.value)} min="0" />
                        </div>
                        <div className="slip-win">{t.toWin}: {formatMoney(bet.stake * bet.odds, lang)}</div>
                      </>
                    )}
                  </div>
                ))}
              </div>
              {parlayMode ? (
                <>
                  <div className="slip-summary">
                    <div className="summary-row"><span>{t.combinedOdds}</span><span>{getParlayOdds(betSlip).toFixed(2)}</span></div>
                    <div className="summary-row">
                      <span>{t.stake}</span>
                      <div className="slip-stake parlay-stake">
                        <span>$</span>
                        <input type="number" value={parlayStake} onChange={(e) => setParlayStake(Math.max(0, parseFloat(e.target.value) || 0))} min="0" />
                      </div>
                    </div>
                    <div className="summary-row total"><span>{t.potentialWin}</span><span>{formatMoney(parlayStake * getParlayOdds(betSlip), lang)}</span></div>
                  </div>
                  <button className="place-btn parlay-btn" onClick={placeBets} disabled={placingBets || parlayStake <= 0 || parlayStake > profile?.balance || betSlip.length < 2}>
                    {placingBets ? t.processing : `${t.placeParlay} (${formatMoney(parlayStake, lang)})`}
                  </button>
                </>
              ) : (
                <>
                  <div className="slip-summary">
                    <div className="summary-row"><span>{t.totalStake}</span><span>{formatMoney(totalStake, lang)}</span></div>
                    <div className="summary-row total"><span>{t.potentialWin}</span><span>{formatMoney(totalPotentialWin, lang)}</span></div>
                  </div>
                  <button className="place-btn" onClick={placeBets} disabled={placingBets || totalStake === 0 || totalStake > profile?.balance}>
                    {placingBets ? t.processing : `${betSlip.length > 1 ? t.placeBets : t.placeBet} (${formatMoney(totalStake, lang)})`}
                  </button>
                </>
              )}
            </>
          )}
        </aside>
      </div>
      {profile && !chatOpen && <ChatFab unread={chatUnread} onOpen={() => setChatOpen(true)} />}
      {profile && <Chat open={chatOpen} onClose={() => setChatOpen(false)} onUnread={setChatUnread} profile={profile} players={leaderboard} avatarOf={avatarOf} lang={lang} myBets={activeBets} gameInfo={feedGameInfo} betText={formatBetDescription} money={(n) => formatMoney(n, lang)} />}
    </div>
  );
}

export default App;
